import { Vector3, Color, Quaternion, Matrix4, Euler, Vector2 } from 'flavr-math';

function getAddedSquares(dx, dy) {
    return dx * dx + dy * dy;
}
function getSqDist(p1, p2) {
    return getAddedSquares(p1.x - p2.x, p1.y - p2.y);
}
function getSqSegDist(p, p1, p2) {
    let x = p1.x;
    let y = p1.y;
    const dx = p2.x - x;
    const dy = p2.y - y;
    if (dx !== 0 || dy !== 0) {
        const t = ((p.x - x) * dx + (p.y - y) * dy) / getAddedSquares(dx, dy);
        if (t > 1) {
            x = p2.x;
            y = p2.y;
        }
        else if (t > 0) {
            x += dx * t;
            y += dy * t;
        }
    }
    return getAddedSquares(p.x - x, p.y - y);
}
function simplifyRadialDist(points, sqTolerance) {
    let prevPoint = points[0];
    let point;
    const newPoints = [prevPoint];
    for (let i = 1, len = points.length; i < len; i++) {
        point = points[i];
        if (getSqDist(point, prevPoint) > sqTolerance) {
            newPoints.push(point);
            prevPoint = point;
        }
    }
    if (prevPoint !== point && point) {
        newPoints.push(point);
    }
    return newPoints;
}
function simplifyDPStep(points, first, last, sqTolerance, simplified) {
    let maxSqDist = sqTolerance;
    let index;
    for (let i = first + 1; i < last; i++) {
        const sqDist = getSqSegDist(points[i], points[first], points[last]);
        if (sqDist > maxSqDist) {
            index = i;
            maxSqDist = sqDist;
        }
    }
    if (maxSqDist > sqTolerance && index != undefined) {
        if (index - first > 1) {
            simplifyDPStep(points, first, index, sqTolerance, simplified);
        }
        simplified.push(points[index]);
        if (last - index > 1) {
            simplifyDPStep(points, index, last, sqTolerance, simplified);
        }
    }
}
function simplifyDouglasPeucker(points, sqTolerance) {
    const last = points.length - 1;
    const simplified = [points[0]];
    simplifyDPStep(points, 0, last, sqTolerance, simplified);
    simplified.push(points[last]);
    return simplified;
}
/**
 * Simplifies a 2D polyline while preserving its overall shape within a tolerance.
 * @param points Input polyline points in order.
 * @param tolerance Maximum tolerated deviation from the original line.
 * @param highestQuality When enabled, skips the fast radial pre-pass and only uses Douglas-Peucker.
 * @returns Reduced point list that approximates the original polyline.
 */
function Simplify(points, tolerance = 1, highestQuality = false) {
    if (points.length <= 2) {
        return points;
    }
    const sqTolerance = tolerance * tolerance;
    points = highestQuality ? points : simplifyRadialDist(points, sqTolerance);
    points = simplifyDouglasPeucker(points, sqTolerance);
    return points;
}

/** Creates a detached copy of plain data structures and cloneable values. */
function deepClone(value) {
    if (typeof structuredClone === "function") {
        return structuredClone(value);
    }
    return JSON.parse(JSON.stringify(value));
}
/** Normalizes path separators so a path can be compared or stored in POSIX form. */
function asPosixPath(path) {
    return path.replace(/\\\\|\\/g, "/");
}
/** Returns whether a value behaves like a promise. */
function isPromise(p) {
    return typeof p === "object" && typeof p.then === "function";
}
/** Rejects a promise when it does not settle within the given timeout. */
const promiseWithTimeout = async (p, ms = 1000) => {
    return await new Promise((resolve, reject) => {
        const t = setTimeout(() => { reject(new Error("requestSession timeout")); }, ms);
        p.then(v => { clearTimeout(t); resolve(v); }, e => { clearTimeout(t); reject(e); });
    });
};
/** Shared unit vector that points along the positive x-axis. */
const UNIT_X$1 = new Vector3(1, 0, 0);
/** Shared unit vector that points along the positive y-axis. */
const UNIT_Y$1 = new Vector3(0, 1, 0);
/** Shared unit vector that points along the positive z-axis. */
const UNIT_Z$1 = new Vector3(0, 0, 1);
/** Wraps a promise together with externally accessible resolve and reject functions. */
class DeferredPromise {
    /** Promise that is settled through {@link resolve} or {@link reject}. */
    promise;
    /** Resolves the wrapped promise. */
    resolve = Function;
    /** Rejects the wrapped promise. */
    reject = Function;
    /** Creates a deferred promise together with its externally stored settle functions. */
    constructor() {
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
    }
}
/** Collects bus messages temporarily until they are consumed in one batch. */
class PendingMessages {
    array = [];
    /** Returns whether no messages are currently queued. */
    isEmpty() {
        return this.array.length === 0;
    }
    /** Returns whether at least one queued message is available. */
    hasMessages() {
        return this.array.length > 0;
    }
    /** Appends one or more messages to the pending queue. */
    push(...messages) {
        this.array.push(...messages);
    }
    /** Removes all queued messages. */
    clear() {
        this.array = [];
    }
    /** Returns the current queue array without cloning it. */
    get() {
        return this.array;
    }
}
/** Type guard for vector-like objects with x, y and z components. */
const isVector3 = (v) => {
    return v.x !== undefined && v.y !== undefined && v.z !== undefined;
};
/** Type guard for quaternion-like objects with x, y, z and w components. */
const isQuaternion = (q) => {
    return q.x !== undefined && q.y !== undefined && q.z !== undefined && q.w !== undefined;
};
/** Type guard for 2-number tuples. */
const isNumber2 = (n) => {
    return Array.isArray(n) && n.length === 2 && n.every(v => typeof v === "number");
};
/** Type guard for 3-number tuples. */
const isNumber3 = (n) => {
    return Array.isArray(n) && n.length === 3 && n.every(v => typeof v === "number");
};
/** Type guard for 4-number tuples. */
const isNumber4 = (n) => {
    return Array.isArray(n) && n.length === 4 && n.every(v => typeof v === "number");
};
/** Type guard for flat 4x4 matrix tuples. */
const isNumber16 = (n) => {
    return Array.isArray(n) && n.length === 16 && n.every(v => typeof v === "number");
};
/** Assigns a property even when TypeScript visibility would normally hide it. */
function overridePrivateProperty(obj, key, value, force = false) {
    if (force || key in obj) {
        obj[key] = value;
    }
}
/** Compares two scalar values with an epsilon tolerance. */
function checkScalarNear(v1, v2, eps = Number.EPSILON) {
    return Math.abs(v2 - v1) < eps;
}
/** Compares two positions or vectors with a tolerance per component. */
function checkVector3Near(v1, v2, eps = Number.EPSILON) {
    v1 = isVector3(v1) ? v1 : { x: v1[0], y: v1[1], z: v1[2] };
    v2 = isVector3(v2) ? v2 : { x: v2[0], y: v2[1], z: v2[2] };
    return Math.abs(v2.x - v1.x) < eps && Math.abs(v2.y - v1.y) < eps && Math.abs(v2.z - v1.z) < eps;
}
/** Compares two quaternions or quaternion tuples with a tolerance per component. */
function checkQuaternionNear(q1, q2, eps = Number.EPSILON) {
    q1 = isQuaternion(q1) ? q1 : { x: q1[0], y: q1[1], z: q1[2], w: q1[3] };
    q2 = isQuaternion(q2) ? q2 : { x: q2[0], y: q2[1], z: q2[2], w: q2[3] };
    return (Math.abs(q2.x - q1.x) < eps &&
        Math.abs(q2.y - q1.y) < eps &&
        Math.abs(q2.z - q1.z) < eps &&
        Math.abs(q2.w - q1.w) < eps);
}
/** Compares two matrices element-by-element with a tolerance. */
function checkMatrix4Near(m1, m2, eps = Number.EPSILON) {
    const te = m1.elements;
    const me = m2.elements;
    for (let i = 0; i < 16; i++) {
        if (Math.abs(te[i] - me[i]) >= eps) {
            return false;
        }
    }
    return true;
}
/** Converts a vector object into a tuple that can be serialized or stored easily. */
function convertToNumber2(v) {
    return [v.x, v.y];
}
/** Converts a vector object into a tuple that can be serialized or stored easily. */
function convertToNumber3(v) {
    return [v.x, v.y, v.z];
}
/** Converts a quaternion into its tuple representation. */
function convertToNumber4(q) {
    return [q.x, q.y, q.z, q.w];
}
/** Converts a matrix into a flat tuple representation. */
function convertToNumber16(m) {
    return [
        m.elements[0],
        m.elements[1],
        m.elements[2],
        m.elements[3],
        m.elements[4],
        m.elements[5],
        m.elements[6],
        m.elements[7],
        m.elements[8],
        m.elements[9],
        m.elements[10],
        m.elements[11],
        m.elements[12],
        m.elements[13],
        m.elements[14],
        m.elements[15]
    ];
}
/** Copies a vector into an existing tuple to avoid new allocations. */
function copyToNumber2(from, to) {
    to[0] = from.x;
    to[1] = from.y;
}
/** Copies a vector into an existing tuple to avoid new allocations. */
function copyToNumber3(from, to) {
    to[0] = from.x;
    to[1] = from.y;
    to[2] = from.z;
}
/** Copies a quaternion into an existing tuple to avoid new allocations. */
function copyToNumber4(from, to) {
    to[0] = from.x;
    to[1] = from.y;
    to[2] = from.z;
    to[3] = from.w;
}
/** Copies a matrix into an existing flat tuple to avoid new allocations. */
function copyToNumber16(from, to) {
    for (let i = 0; i < 16; ++i) {
        to[i] = from.elements[i];
    }
}
/** Returns whether two numeric arrays contain exactly the same values in the same order. */
function equalsNumber(v1, v2) {
    const length = v1.length;
    if (length != v2.length)
        return false;
    for (let i = 0; i < length; ++i) {
        if (v1[i] !== v2[i])
            return false;
    }
    return true;
}
/** Conversion factor from degrees to radians. */
const DEG2RAD = Math.PI / 180;
/** Conversion factor from radians to degrees. */
const RAD2DEG = 180 / Math.PI;
/** Wraps an angle into the interval from `-PI` to `PI`. */
function clampAngle(angle) {
    if (angle > Math.PI) {
        angle -= Math.PI * 2;
    }
    else if (angle < -Math.PI) {
        angle += Math.PI * 2;
    }
    return angle;
}
/** Type guard for string values. */
function isString(obj) {
    return typeof obj === "string";
}
/** Returns the first matching query parameter from a URL-like string. */
function getQueryParameter(locationHref, name, defaultValue = "") {
    name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
    const regex = new RegExp(`[\\?&]${name}=([^&#]*)`, "gi");
    const results = regex.exec(locationHref);
    return results === null ? defaultValue : decodeURIComponent(results[1].replace(/\+/g, " "));
}
/** Returns all occurrences of a repeated query parameter. */
function getQueryParameterAll(locationHref, name) {
    const results = [];
    name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
    const regex = new RegExp(`[\\?&]${name}=([^&#]*)`, "gi");
    let array;
    while ((array = regex.exec(locationHref))) {
        results.push(decodeURIComponent(array[1].replace(/\+/g, " ")));
    }
    return results;
}
/** Adds, updates or removes a single query parameter in a relative URL. */
function changeQueryParamter(relativeUrl, name, value) {
    if (value) {
        if (relativeUrl.includes(`${name}=`)) {
            const reg = new RegExp(`${name}=([^&#]*)`, "gi");
            return relativeUrl.replace(reg, `${name}=${value}`);
        }
        else {
            if (!relativeUrl.includes("?")) {
                relativeUrl += "?";
            }
            else if (relativeUrl[relativeUrl.length - 1] !== "&") {
                relativeUrl += "&";
            }
            return relativeUrl + `${name}=${value}`;
        }
    }
    const reg = new RegExp(`[?&]?${name}=([^&#]*)`, "gi");
    return relativeUrl.replace(reg, "");
}
/** Applies multiple query parameter updates to a relative URL. */
function changeQueryParamters(relativeUrl, params) {
    for (const name in params) {
        relativeUrl = changeQueryParamter(relativeUrl, name, params[name]);
    }
    return relativeUrl;
}
/** Returns whether a URL contains a given query parameter name. */
function hrefHasParameter(locationHref, name) {
    name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
    const regex = new RegExp(`[\\?&]${name}([=&#]|$)`, "gi");
    const results = regex.exec(locationHref);
    return results !== null;
}
/** Default base URL for avatar images hosted by Selfcare. */
const SELFCARE_HOST = "https://selfcare.flavia-it.de";
/** Builds an avatar image URL for a user name. */
function avatarUrl(username, selfcare_host = SELFCARE_HOST) {
    return `${selfcare_host}/user/image/${username}?noCrop=true&no404=true&size=512`;
}
const lut = [];
for (let i = 0; i < 256; i++) {
    lut[i] = (i < 16 ? "0" : "") + i.toString(16);
}
/** Generates a random UUID-like identifier string. */
function generateUUID() {
    const d0 = (Math.random() * 0xffffffff) | 0;
    const d1 = (Math.random() * 0xffffffff) | 0;
    const d2 = (Math.random() * 0xffffffff) | 0;
    const d3 = (Math.random() * 0xffffffff) | 0;
    const uuid = lut[d0 & 0xff] +
        lut[(d0 >> 8) & 0xff] +
        lut[(d0 >> 16) & 0xff] +
        lut[(d0 >> 24) & 0xff] +
        "-" +
        lut[d1 & 0xff] +
        lut[(d1 >> 8) & 0xff] +
        "-" +
        lut[((d1 >> 16) & 0x0f) | 0x40] +
        lut[(d1 >> 24) & 0xff] +
        "-" +
        lut[(d2 & 0x3f) | 0x80] +
        lut[(d2 >> 8) & 0xff] +
        "-" +
        lut[(d2 >> 16) & 0xff] +
        lut[(d2 >> 24) & 0xff] +
        lut[d3 & 0xff] +
        lut[(d3 >> 8) & 0xff] +
        lut[(d3 >> 16) & 0xff] +
        lut[(d3 >> 24) & 0xff];
    // .toUpperCase() here flattens concatenated strings to save heap memory space.
    return uuid.toUpperCase();
}
const date = new Date();
/** Formats a unix timestamp as an ISO-like local debug string. */
function timestampToString(timestamp) {
    date.setTime(timestamp);
    return date.toISOString().slice(0, 19).replace("T", " ");
}
/**
 * maps a value linearly from one interval to an other.
 * Values equal to inMin will be map to outMin.
 * Values equal to inMax will be map to outMax.
 * Values between inMin and inMax will be map to values between outMin and outMax.
 */
function mapLinear(value, inMin, inMax, outMin = 0, outMax = 1) {
    return outMin + ((value - inMin) * (outMax - outMin)) / (inMax - inMin);
}
/** Restricts a number to the closed interval between `min` and `max`. */
function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}
/** Restricts a number to the normalized range from `0` to `1`. */
function clamp01(value) {
    if (value < 0)
        return 0;
    else if (value > 1)
        return 1;
    else
        return value;
}
/** Wraps a value into a repeating interval of the given length. */
function repeat(t, length) {
    return clamp(t - Math.floor(t / length) * length, 0.0, length);
}
/**
 * Returns `1` for non-negative numbers and `-1` for negative numbers.
 * @param x Number whose sign bucket should be determined.
 */
function sign(x) {
    return x >= 0.0 ? 1 : -1;
}
/** Linearly interpolates between two numeric values. */
function lerp(x, y, t) {
    return (1 - t) * x + t * y;
}
/** Interpolates between two angles while respecting wraparound at 360 degrees. */
function lerpAngle(a, b, t) {
    let delta = repeat(b - a, 360);
    if (delta > 180)
        delta -= 360;
    return a + delta * clamp01(t);
}
/** Returns the shortest angular difference needed to reach a target angle. */
function deltaAngle(current, target) {
    let delta = repeat(target - current, 360.0);
    if (delta > 180.0) {
        delta -= 180.0;
    }
    return delta;
}
/** Moves a value toward a target without overshooting by more than `maxDelta`. */
function moveTowards(current, target, maxDelta) {
    if (Math.abs(target - current) <= maxDelta) {
        return target;
    }
    return current + sign(target - current) * maxDelta;
}
/** Moves an angle toward a target angle along the shortest path. */
function moveTowardsAngle(current, target, maxDelta) {
    const dAngle = deltaAngle(current, target);
    if (-maxDelta < dAngle && dAngle < maxDelta) {
        return target;
    }
    target = current + dAngle;
    return moveTowards(current, target, maxDelta);
}
/** Applies a smooth Hermite interpolation between 0 and 1 inside a numeric range. */
function smoothstep(x, min, max) {
    if (x <= min)
        return 0;
    if (x >= max)
        return 1;
    x = (x - min) / (max - min);
    return x * x * (3 - 2 * x);
}
/** Returns a random integer inside the inclusive range `[low, high]`. */
function randInt(low, high) {
    return low + Math.floor(Math.random() * (high - low + 1));
}
/** Returns a random floating point number inside the half-open range `[low, high)`. */
function randFloat(low, high) {
    return low + Math.random() * (high - low);
}
/** Converts degrees into radians. */
function degToRad(degrees) {
    return degrees * DEG2RAD;
}
/** Converts radians into degrees. */
function radToDeg(radians) {
    return radians * RAD2DEG;
}
/**
 * Returns whether a number is a power of two.
 * @param value Number that should be checked.
 */
function isPowerOfTwo(value) {
    return (value & (value - 1)) === 0 && value !== 0;
}
/** Rounds a number up to the next power of two. */
function ceilPowerOfTwo(value) {
    return Math.pow(2, Math.ceil(Math.log(value) / Math.LN2));
}
/** Rounds a number down to the previous power of two. */
function floorPowerOfTwo(value) {
    return Math.pow(2, Math.floor(Math.log(value) / Math.LN2));
}
/** Mirrors a repeating value back and forth between `0` and `length`. */
function pingPong(t, length) {
    t = repeat(t, length * 2);
    return length - Math.abs(t - length);
}
const colorHelper = new Color();
/** Converts HSL components into a packed hexadecimal color. */
function hslToHexColor(h, s, l) {
    return colorHelper.setHSL(h, s, l).getHex();
}
/** Breaks a long string into multiple lines near word boundaries. */
function cutIntoLines(inputString, maxCharacters, cutLines) {
    let lines = [];
    if (inputString.length <= maxCharacters)
        return inputString;
    if (cutLines && cutLines.length > 0) {
        lines = cutLines;
    }
    const position = inputString.substring(0, maxCharacters).lastIndexOf(" ");
    if (inputString.substring(0, position).trim() !== "")
        lines.push(inputString.substring(0, position).trim());
    const residual = inputString.substring(position, inputString.length);
    if (residual.length > maxCharacters && position > 0)
        cutIntoLines(residual, maxCharacters, lines);
    else
        lines.push(residual.trim());
    let final = "";
    for (const line of lines)
        final += line + "\n";
    return final;
}
/** Estimates perceived brightness from normalized RGB channels. */
function getRgbBrightness(r, g, b) {
    return r * 0.299 + g * 0.587 + b * 0.114;
}
/** Estimates perceived brightness from a color object. */
function getColorBrightness(color) {
    return getRgbBrightness(color.r, color.g, color.b);
}
function cyrb32(str) {
    let h = 0;
    for (let i = 0, l = str.length; i < l; ++i) {
        h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
    }
    return h;
}
/** Computes a stable 32-bit hash code for a string. */
function hashCode(str) {
    return cyrb32(str);
}

const tmpVector3_1 = new Vector3();
const tmpVector3_2 = new Vector3();
const tmpVector3_3 = new Vector3();
const tmpVector3_4 = new Vector3();
const tmpVector3_5 = new Vector3();
const ROTATE_90_DEGRE_AROUND_Y_AXIS = new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), degToRad(90));
function createNode(data) {
    return {
        data,
        velocity: new Vector3(),
        position: new Vector3(),
        incomingEdges: [],
        outgoingEdges: []
    };
}
/** Arranges connected graph data by simulating attraction, repulsion and angular separation forces. */
class ForceBasedGraphLayout {
    /** All nodes currently managed by the layout. */
    nodes = [];
    /** All directed edges currently managed by the layout. */
    edges = [];
    /**
     * Adds a new node with the provided payload to the graph.
     * @param data Payload that should be attached to the new layout node.
     */
    addNode(data) {
        const node = createNode(data);
        this.nodes.push(node);
        return node;
    }
    /**
     * Adds a directed edge unless an identical connection already exists.
     * @param from Source node of the edge.
     * @param to Target node of the edge.
     */
    addEdge(from, to) {
        if (this.edges.find((it) => it.from === from && it.to === to))
            return; // edge already exists
        const edge = { from, to, angle: 0 };
        this.edges.push(edge);
        from.outgoingEdges.push(edge);
        to.incomingEdges.push(edge);
    }
    /**
     * Splits the graph into separate connected subgraphs.
     * @returns Independent subgraphs that can be laid out separately.
     */
    getConnectedGroupes() {
        const assigedNodes = new Set();
        const groupes = [];
        for (const node of this.nodes) {
            if (!assigedNodes.has(node)) {
                const graph = new ForceBasedGraphLayout();
                this.getConnectedSubGraph(node, graph, assigedNodes);
                for (const n of graph.nodes) {
                    for (const e of n.outgoingEdges) {
                        graph.edges.push(e);
                    }
                }
                groupes.push(graph);
            }
        }
        return groupes;
    }
    getConnectedSubGraph(node, graph, assigedNodes) {
        if (!assigedNodes.has(node)) {
            graph.nodes.push(node);
            assigedNodes.add(node);
            for (const e of node.incomingEdges) {
                this.getConnectedSubGraph(e.from, graph, assigedNodes);
            }
            for (const e of node.outgoingEdges) {
                this.getConnectedSubGraph(e.to, graph, assigedNodes);
            }
        }
    }
    /**
     * Runs the iterative layout solver and centers the result afterwards.
     * @param numIterations Number of simulation steps to perform.
     * @param randomizePositions When enabled, starts the simulation from random initial positions.
     */
    calculateLayout(numIterations, randomizePositions = true) {
        if (randomizePositions) {
            for (const n of this.nodes) {
                n.position.set(randFloat(-25, 25), 0.0, randFloat(-25, 25));
            }
        }
        for (let i = 0; i < numIterations; ++i) {
            for (const n of this.nodes) {
                n.velocity.multiplyScalar(0.5);
            }
            const f = i / numIterations;
            this.applyAttractionForceForConnectedNodes(0.02, 2);
            this.applyRepulsiveForceForNodes(0.01 * f + 0.05);
            this.applyAngleForce(0.001);
            for (const n of this.nodes) {
                n.position.addScaledVector(n.velocity, 0.1);
            }
        }
        this.normalizeLayout();
    }
    normalizeLayout() {
        let centerX = 0;
        let centerZ = 0;
        for (const n of this.nodes) {
            centerX += n.position.x;
            centerZ += n.position.z;
        }
        centerX /= this.nodes.length;
        centerZ /= this.nodes.length;
        for (const n of this.nodes) {
            n.position.x -= centerX;
            n.position.z -= centerZ;
        }
    }
    applyAttractionForceForConnectedNodes(strength, targetLength) {
        for (const e of this.edges) {
            const dir = tmpVector3_1.subVectors(e.from.position, e.to.position);
            const dist = dir.length();
            if (dist > 0) {
                dir.normalize();
                const d = targetLength - dist;
                const f = strength * d;
                e.from.velocity.addScaledVector(dir, f);
                e.to.velocity.addScaledVector(dir, -f);
            }
        }
    }
    applyRepulsiveForceForNodes(strength) {
        for (let i = 0; i < this.nodes.length; ++i) {
            const a = this.nodes[i];
            for (let j = i + 1; j < this.nodes.length; ++j) {
                const b = this.nodes[j];
                const dir = tmpVector3_1.subVectors(a.position, b.position);
                let dist = dir.length();
                const minDist = 5;
                if (dist < Number.EPSILON) {
                    dist = 0;
                    dir.set(randFloat(-1, 1), randFloat(-1, 1), randFloat(-1, 1));
                }
                dir.normalize();
                const diff = minDist - dist;
                const f = dist < minDist ? strength * diff * diff : 0;
                a.velocity.addScaledVector(dir, +f);
                b.velocity.addScaledVector(dir, -f);
            }
        }
    }
    applyAngleForce(strength) {
        for (const node of this.nodes) {
            const edges = [...node.incomingEdges, ...node.outgoingEdges];
            if (edges.length > 1) {
                for (let i = 0; i < edges.length; ++i) {
                    const e1 = edges[i];
                    const dir1 = tmpVector3_1.subVectors(e1.from.position, node.position).normalize();
                    const tanget1 = tmpVector3_2.copy(dir1).applyQuaternion(ROTATE_90_DEGRE_AROUND_Y_AXIS);
                    for (let j = i + 1; j < edges.length; ++j) {
                        const e2 = edges[j];
                        const dir2 = tmpVector3_3.subVectors(e2.from.position, node.position).normalize();
                        const tanget2 = tmpVector3_4.copy(dir2).applyQuaternion(ROTATE_90_DEGRE_AROUND_Y_AXIS);
                        const cos = clamp(dir1.dot(dir2), -1, 1);
                        const alpha = Math.acos(cos);
                        const targetAngle = degToRad(360) / edges.length;
                        const dir = tmpVector3_5.subVectors(dir1, dir2).normalize();
                        const f = strength * Math.max(targetAngle - alpha, 0);
                        e1.from.velocity.addScaledVector(tanget1, +f * Math.sign(dir.dot(tanget1)));
                        e2.from.velocity.addScaledVector(tanget2, -f * Math.sign(dir.dot(tanget2)));
                    }
                }
            }
        }
    }
}

/**
 * Shared state used while parsing assets into model helpers.
 */
class AssetParserContext {
    /**
     * Resolves asset-level names to the model helpers created for them.
     */
    modelNameToModel = new Map();
}

/** Separator between individual path segments in a model id. */
const PATH_SEPERATOR = "/";
/** Prefix marking a relative id as a room id. */
const ROOM_PREFIX = "[";
/** Postfix marking a relative id as a room id. */
const ROOM_POSTFIX = "]";
const ROOT_ID = "root";
const PLAYERS_ID = "players";
const LOCAL_PLAYER_ID = "local_player";
const REPLAY_ID = "replay";
const UTILITY_ID = "_utility";
const MODEL_ID_VALIDATE_REGEXP = /'[^a-zA-Z0-9_]'/;
// to globally identify models
/**
 * Stable hierarchical identifier for models, rooms, and utility nodes.
 */
class ModelId {
    relativeId;
    parent;
    /**
     * Numeric id used on the wire and in facet messages.
     */
    uid;
    /**
     * Full path from the root model to this model.
     */
    absolutePath;
    /**
     * Number of enclosing room levels above this model.
     */
    roomDepth;
    /**
     * Direct children currently registered below this model.
     */
    children = [];
    static hashToModelIdMap = new Map();
    static registry = new Map();
    /**
     * Indicates whether the model belongs to the active session hierarchy.
     */
    isSessionModel;
    static waitingAbsolutePaths = new Map();
    /**
     * @param uid Optional preassigned uid, otherwise derived from the path.
     */
    constructor(
    /**
     * Relative identifier below the parent model.
     */
    relativeId, 
    /**
     * Parent model in the hierarchy.
     */
    parent, uid) {
        this.relativeId = relativeId;
        this.parent = parent;
        if (!ModelId.isRelativeIdValid(relativeId)) {
            throw new Error(`relativeId ${relativeId} is invalid!`);
        }
        this.roomDepth = this.computeRoomDepth(this.parent);
        this.absolutePath = this.computeAbsolutePath(this.parent, this.relativeId);
        ModelId.registry.set(this.absolutePath, this);
        this.uid = this.getOrCreateUid(uid, this.absolutePath);
        this.isSessionModel = this.computeIsSessionModel(this.getRelativeId(), this.parent, this.isRoot());
        const waitings = ModelId.waitingAbsolutePaths.get(this.absolutePath);
        if (waitings) {
            for (const data of waitings) {
                clearTimeout(data.timeoutHandle);
                data.resolve(this);
            }
            ModelId.waitingAbsolutePaths.delete(this.absolutePath);
        }
        if (!this.isRoot())
            this.parent?.children.push(this);
    }
    /**
     * Removes this model id from the registry and from its parent.
     */
    delete() {
        this.removeFromParent(this.parent);
        ModelId.registry.delete(this.absolutePath);
        ModelId.hashToModelIdMap.delete(this.uid);
    }
    /**
     * Normalizes a relative id into room syntax using the room prefix and postfix.
     */
    static generateRoomRelativeId(relativeId) {
        if (relativeId.startsWith(ROOM_PREFIX)) {
            relativeId = relativeId.substring(1);
        }
        if (relativeId.endsWith(ROOM_POSTFIX)) {
            relativeId = relativeId.substring(0, relativeId.length - 1);
        }
        return `${ROOM_PREFIX}${relativeId}${ROOM_POSTFIX}`;
    }
    /**
     * Returns or creates a direct child id below this model.
     */
    createChildId(relativeId) {
        const absolutePath = ModelId.getModelPath(relativeId, this.absolutePath);
        const existing = ModelId.registry.get(absolutePath);
        if (existing !== undefined) {
            return existing;
        }
        const modelId = new ModelId(relativeId, this);
        if (modelId !== undefined && ModelId.hashToModelIdMap.has(modelId.uid)) {
            const modelIdFromMap = ModelId.hashToModelIdMap.get(modelId.uid);
            const absolutePath = modelIdFromMap !== undefined ? modelIdFromMap.absolutePath : -1;
            throw new Error(`Collision by create with id ${modelId.uid} and path ${absolutePath} | ${absolutePath}`);
        }
        ModelId.hashToModelIdMap.set(modelId.uid, modelId);
        return modelId;
    }
    /**
     * Looks up a direct child by relative id.
     */
    getChildId(relativeId) {
        return ModelId.getFromAbsolutePath(ModelId.getModelPath(relativeId, this.toString()));
    }
    /**
     * Returns the currently registered direct children.
     */
    getChildren() {
        return this.children;
    }
    createChildRoomId(layout) {
        const relativeId = ROOM_PREFIX + layout + ROOM_POSTFIX;
        return this.createChildId(relativeId);
    }
    /**
     * Creates or returns a child id below the model identified by `parentUid`.
     */
    static create(relativeId, parentUid) {
        const parent = ModelId.getModelId(parentUid);
        if (parent === undefined) {
            throw new Error(`Parent modelId not found for id ${parentUid} and relative id ${relativeId}`);
        }
        return parent.createChildId(relativeId);
    }
    /**
     * Creates or returns a room id below the given parent uid.
     */
    static createRoomId(layout, parentUid = ROOT_MODEL_UID) {
        const relativeId = ROOM_PREFIX + layout + ROOM_POSTFIX;
        return ModelId.create(relativeId, parentUid);
    }
    /**
     * Creates missing parent ids as needed and returns the id for the absolute path.
     */
    static createFromAbsolutePath(path) {
        if (path === ROOT_ID) {
            return ROOT_MODEL_ID;
        }
        const index = path.lastIndexOf(PATH_SEPERATOR);
        if (index <= 0) {
            throw new Error(`path ${path} should derive from root`);
        }
        const relativePath = path.substr(index + 1);
        const parentPath = path.substr(0, index);
        let parent = ModelId.getFromAbsolutePath(parentPath);
        if (!parent) {
            parent = this.createFromAbsolutePath(parentPath);
        }
        return ModelId.create(relativePath, parent.uid);
    }
    /**
     * Registers a ModelId for the given path. An uid can be provided for the path, otherwise it will be generated
     * automatically. An error is thrown if the uid is already in use for a different path or if the path is invalid.
     * @param path Path to a model
     * @param uid If provided the uid will be used as hash for the given path otherwise it will be generated
     * @returns A new ModelId for the given path
     */
    static register(path, uid) {
        const existing = this.registry.get(path);
        if (existing !== undefined) {
            return existing;
        }
        if (uid === undefined)
            uid = ModelId.hashCodeFromPath(path);
        if (this.hashToModelIdMap.has(uid)) {
            throw new Error(`Collision by register with id ${uid} and path ${path}}`);
        }
        const index = path.lastIndexOf(PATH_SEPERATOR);
        if (index <= 0) {
            throw new Error(`[modelid] path "${path}" (${uid}) is invalid`);
        }
        const relativePath = path.substring(index + 1);
        const parentPath = path.substring(0, index);
        const parent = ModelId.getFromAbsolutePath(parentPath);
        if (!parent) {
            throw new Error(`[modelid] Parent not found for path '${path}' with parentPath ${parentPath}!`);
        }
        const modelId = new ModelId(relativePath, parent, uid);
        this.hashToModelIdMap.set(uid, modelId);
        return modelId;
    }
    /**
     * Checks whether a relative id uses only valid characters and valid room syntax.
     */
    static isRelativeIdValid(relativeId) {
        if (relativeId.startsWith(ROOM_PREFIX) && relativeId.endsWith(ROOM_POSTFIX)) {
            // relative id defines a room
            const layout = relativeId.substring(ROOM_PREFIX.length, relativeId.length - ROOM_PREFIX.length - ROOM_POSTFIX.length + 1);
            return !MODEL_ID_VALIDATE_REGEXP.test(layout);
        }
        else if (relativeId.startsWith(ROOM_PREFIX) || relativeId.endsWith(ROOM_POSTFIX)) {
            // relative Id start/ends with ROOM_PREFIX/ROOM_POSTFIX but the
            // corresponding ROOM_POSTFIX/ROOM_PREFIX is missing
            return false;
        }
        return !MODEL_ID_VALIDATE_REGEXP.test(relativeId);
    }
    /**
     * Checks whether every segment in a full path is a valid model id.
     */
    static isPathValidModelId(path) {
        const relativeIds = path.split(PATH_SEPERATOR);
        if (relativeIds.length === 0 || relativeIds[0] !== ROOT_ID) {
            return false;
        }
        for (const id of relativeIds) {
            if (!ModelId.isRelativeIdValid(id))
                return false;
        }
        return true;
    }
    /**
     * Returns an existing id for the path or creates the missing hierarchy.
     */
    static fromAbsolutePath(path) {
        const existing = this.getFromAbsolutePath(path);
        if (existing) {
            return existing;
        }
        return this.createFromAbsolutePath(path);
    }
    static getModelId(uid) {
        const modelId = this.hashToModelIdMap.get(uid);
        return modelId;
    }
    /**
     * The model name is either the relative path of the model or simply the uid if no
     * model with the specified uid has been registered yet.
     * @param uid - model uid
     * @returns associated name of the model with the given uid
     */
    static getModelName(uid) {
        if (uid === undefined)
            return "Undefined Model";
        const modelId = this.getModelId(uid);
        if (modelId === undefined)
            return `Unknown Model ${uid}`;
        return modelId.getRelativeId();
    }
    static hasModelId(uid) {
        return this.hashToModelIdMap.has(uid);
    }
    static hasModelIdWithAbsolutePath(path) {
        return this.registry.has(path);
    }
    static getAbsolutePath(uid) {
        const modelId = this.hashToModelIdMap.get(uid);
        return modelId ? modelId.absolutePath : "";
    }
    /**
     * Resolves a model id directly from its absolute path.
     */
    static getFromAbsolutePath(path) {
        return this.registry.get(path);
    }
    /**
     * Waits until a model id with the given absolute path becomes registered.
     */
    static async waitForModelWithAbsolutePath(path, timeout = 60000) {
        const modelId = this.registry.get(path);
        if (modelId !== undefined)
            return modelId;
        return await new Promise((resolve, reject) => {
            let pendings = this.waitingAbsolutePaths.get(path);
            if (pendings === undefined) {
                this.waitingAbsolutePaths.set(path, (pendings = []));
            }
            const timeoutHandle = setTimeout(() => {
                const pendings = this.waitingAbsolutePaths.get(path);
                if (pendings !== undefined) {
                    for (let i = pendings.length - 1; i >= 0; i--) {
                        const data = pendings[i];
                        if (data.timeoutHandle !== timeoutHandle)
                            continue;
                        pendings.splice(i, 1);
                    }
                }
                reject(new Error(`waiting for model with path ${path} timeout`));
            }, timeout);
            pendings.push({
                resolve,
                reject,
                timeoutHandle
            });
        });
    }
    /**
     * Looks up a direct child by relative id under the parent uid.
     */
    static getChildByParentUid(relativeId, parentUid) {
        const parentId = this.hashToModelIdMap.get(parentUid);
        if (parentId === undefined)
            return undefined;
        return this.getFromAbsolutePath(this.getModelPath(relativeId, parentId.toString()));
    }
    /**
     * Recursively searches the descendants of a parent for the first matching relative id.
     */
    static findChildInHirarchy(relativeId, parentUid) {
        const parentId = this.hashToModelIdMap.get(parentUid);
        if (parentId === undefined)
            return undefined;
        for (const child of parentId.children) {
            if (child.relativeId === relativeId) {
                return child;
            }
            const found = this.findChildInHirarchy(relativeId, child.uid);
            if (found !== undefined)
                return found;
        }
        return undefined;
    }
    /**
     * Computes the uid hash for an absolute path.
     */
    hashCode(absolutePath) {
        return hashCode(absolutePath ?? this.absolutePath);
    }
    /** Computes the stable uid hash for an absolute model path. */
    static hashCodeFromPath(path) {
        return hashCode(path);
    }
    static getModelPath(relativeId, parentAbsolutePath) {
        return `${parentAbsolutePath}${PATH_SEPERATOR}${relativeId}`;
    }
    /**
     * Extracts the last path segment from a full model path.
     */
    static getRelativeIdOfPath(path) {
        const index = path.lastIndexOf(PATH_SEPERATOR);
        if (index > 0) {
            return path.substring(index + 1);
        }
    }
    isRoot() {
        return this.absolutePath === ROOT_ID;
    }
    isRoom() {
        return this.relativeId.startsWith(ROOM_PREFIX) && this.relativeId.endsWith(ROOM_POSTFIX);
    }
    static isPathRoom(path) {
        return path.endsWith(ROOM_POSTFIX);
    }
    /**
     * returns the relative id (without pre- and postfixes)
     */
    getRelativeId() {
        if (this.isRoom()) {
            return this.relativeId.substring(ROOM_PREFIX.length, this.relativeId.length - ROOM_PREFIX.length - ROOM_POSTFIX.length + 1);
        }
        else {
            return this.relativeId;
        }
    }
    /**
     * Returns how many enclosing rooms sit above this model.
     */
    getRoomDepth() {
        return this.roomDepth;
    }
    /**
     * Computes the room depth directly from an absolute path.
     */
    static getRoomDepthOfPath(path) {
        const relativeIds = path.split(PATH_SEPERATOR);
        let roomDepth = 0;
        for (let i = 0, l = relativeIds.length - 1; i < l; ++i) {
            if (relativeIds[i].startsWith(ROOM_PREFIX) && relativeIds[i].endsWith(ROOM_POSTFIX))
                ++roomDepth;
        }
        return roomDepth;
    }
    /**
     * Returns the nearest parent room, if any.
     */
    getParentRoom() {
        if (this.isRoot())
            return;
        let room = this.parent;
        while (room && !room.isRoom() && !room.isRoot()) {
            room = room.parent;
        }
        return room;
    }
    /**
     * Extracts the absolute path of the nearest parent room from a model path.
     */
    static getParentRoomOfPath(path) {
        const index = path.lastIndexOf(ROOM_POSTFIX + PATH_SEPERATOR);
        if (index > 0) {
            return path.substring(0, index + ROOM_POSTFIX.length);
        }
    }
    /**
     * Extracts the room name from a room path.
     */
    static getRoomOfPath(path) {
        if (path.endsWith(ROOM_POSTFIX)) {
            const index = path.lastIndexOf(ROOM_PREFIX);
            if (index >= 0) {
                return path.substring(index + ROOM_PREFIX.length, path.length - ROOM_POSTFIX.length);
            }
        }
    }
    /**
     * Returns the top-level path segment of an absolute path.
     */
    static getRootOfPath(path) {
        const index = path.indexOf(PATH_SEPERATOR);
        if (index > 0) {
            return path.substring(0, index);
        }
        return path;
    }
    /**
     * Collects all room segments present in the path from deepest to shallowest.
     */
    static getAllRoomsOfPath(absolutePath) {
        const rooms = [];
        let index = 0;
        while ((index = absolutePath.lastIndexOf(ROOM_PREFIX)) > 0) {
            const postIndex = absolutePath.lastIndexOf(ROOM_POSTFIX);
            if (postIndex > 0) {
                const roomId = absolutePath.substring(0, postIndex + ROOM_POSTFIX.length);
                rooms.push({
                    name: absolutePath.substring(index + ROOM_PREFIX.length, postIndex),
                    realtiveRoomId: roomId
                });
            }
            absolutePath = absolutePath.substr(0, index);
        }
        return rooms;
    }
    /**
     * Returns the absolute path representation of this id.
     */
    toString() {
        return this.absolutePath;
    }
    getParentAbsolutePath() {
        return this.parent.absolutePath;
    }
    static getParentPathFromAbsolutePath(path) {
        const index = path.lastIndexOf(PATH_SEPERATOR);
        if (index <= 0)
            return undefined;
        return path.substr(0, index);
    }
    /**
     * Compares two ids by absolute path.
     */
    equals(another) {
        return this.absolutePath === another.absolutePath;
    }
    /**
     * Compares this id against a raw absolute path.
     */
    pathEquals(path) {
        return this.absolutePath === path;
    }
    /**
     * Returns whether this id is a strict child of another id.
     */
    isChildOf(another) {
        return (this.absolutePath.startsWith(another.absolutePath) && this.absolutePath !== another.absolutePath && !another.absolutePath.startsWith(this.absolutePath));
    }
    /**
     * Returns whether this id is a direct child of the given path.
     */
    isChildOfPath(path) {
        return this.parent.absolutePath === path;
    }
    /**
     * Returns whether this id is the direct parent of another id.
     */
    isParentOf(another) {
        return this.absolutePath === another.parent.absolutePath;
    }
    isAnchestorOf(another) {
        return another.parent.absolutePath.startsWith(this.absolutePath);
    }
    isAnchestorOfPath(path) {
        return path.startsWith(this.absolutePath) && path !== this.absolutePath;
    }
    static isPathAnchestorOfPath(anchestor, descendant) {
        return descendant.startsWith(anchestor) && descendant !== anchestor;
    }
    isDescendantOf(another) {
        return this.parent.absolutePath.startsWith(another.absolutePath);
    }
    isDescendantOfPath(path) {
        return this.parent.absolutePath.startsWith(path);
    }
    static isPathDescendantOfPath(descendant, anchestor) {
        return descendant.startsWith(anchestor) && descendant !== anchestor;
    }
    isEqualsToOrAnchestorOf(another) {
        return another.absolutePath.startsWith(this.absolutePath);
    }
    isEqualsToOrAnchestorOfPath(path) {
        return path.startsWith(this.absolutePath);
    }
    static isPathEqualsToOrAnchestorOfPath(anchestor, descendant) {
        return descendant.startsWith(anchestor);
    }
    isEqualsToOrDescendantOf(another) {
        return this.absolutePath.startsWith(another.absolutePath);
    }
    isEqualsToOrDescendantOfPath(path) {
        return this.absolutePath.startsWith(path);
    }
    static isPathEqualsToOrDescendantOfPath(descendant, anchestor) {
        return descendant.startsWith(anchestor);
    }
    /**
     * Rewrites a path if it belongs to the subtree rooted at `src`.
     */
    static replacePathIfInChildren(path, src, dst) {
        if (!src.pathEquals(path) && !src.isAnchestorOfPath(path))
            return path;
        const absolutePath = path.toString().replace(src.toString(), dst.toString());
        return ModelId.fromAbsolutePath(absolutePath).toString();
    }
    /**
     * Rewrites a uid if it belongs to the subtree rooted at `src`.
     */
    static replaceUidIfInChildren(uid, src, dst) {
        const modelId = ModelId.getModelId(uid);
        if (!modelId)
            return uid;
        if (!src.equals(modelId) && !src.isAnchestorOf(modelId))
            return uid;
        const absolutePath = modelId.toString().replace(src.toString(), dst.toString());
        return ModelId.fromAbsolutePath(absolutePath).uid;
    }
    /// Private helper methods ///
    computeAbsolutePath(parent, relativeId) {
        if (relativeId !== ROOT_ID)
            return `${parent.absolutePath}${PATH_SEPERATOR}${relativeId}`;
        return relativeId;
    }
    computeRoomDepth(parent) {
        return parent.roomDepth + (parent.isRoom?.() ? 1 : 0);
    }
    getOrCreateUid(uid, absolutePath) {
        if (uid !== undefined)
            return uid;
        return this.hashCode(absolutePath);
    }
    computeIsSessionModel(relativeId, parent, isRoot) {
        let isSessionModel = false;
        if (isRoot) {
            isSessionModel = false;
        }
        else if (parent.isRoot()) {
            const id = relativeId;
            if (id !== PLAYERS_ID && id !== REPLAY_ID && id !== UTILITY_ID) {
                isSessionModel = true;
            }
        }
        else {
            isSessionModel = parent.isSessionModel;
        }
        return isSessionModel;
    }
    removeFromParent(parent) {
        const indexOf = parent.children.findIndex((child) => child.uid === this.uid);
        if (indexOf !== -1)
            parent.children.splice(indexOf);
    }
}
/**
 * Dedicated root id implementation used to bootstrap the model hierarchy.
 */
class RootModelId extends ModelId {
    constructor() {
        super(ROOT_ID, {
            relativeId: "",
            absolutePath: "",
            roomDepth: 0,
            parent: undefined
        });
        this.absolutePath = ROOT_ID;
        ModelId.registry.set(this.absolutePath, this);
        ModelId.hashToModelIdMap.set(this.uid, this);
        const waitings = ModelId.waitingAbsolutePaths.get(this.absolutePath);
        if (waitings) {
            for (const data of waitings) {
                clearTimeout(data.timeoutHandle);
                data.resolve(this);
            }
            ModelId.waitingAbsolutePaths.delete(this.absolutePath);
        }
    }
}
/** Root model id used as the parent of the entire hierarchy. */
const ROOT_MODEL_ID = new RootModelId();
const ROOT_MODEL_UID = ROOT_MODEL_ID.uid;
/** Room id that contains player and controller related models. */
const PLAYERS_ROOM_ID = ModelId.createRoomId(PLAYERS_ID, ROOT_MODEL_UID);
const PLAYERS_ROOM_UID = PLAYERS_ROOM_ID.uid;
// the parent modelId of all controllers.
// The Controller Module takes care of sending WorldTransform Messages for these models
// since they are not children of the main room or any of its sub rooms
/** Root id used for controller models that live outside room content. */
const CONTROLLER_ROOT_ID = PLAYERS_ROOM_ID;
const CONTROLLER_ROOT_UID = CONTROLLER_ROOT_ID.uid;
/** Root id of the local player's model subtree. */
const LOCAL_PLAYER_ROOT_ID = ModelId.create(LOCAL_PLAYER_ID, PLAYERS_ROOM_UID);
const LOCAL_PLAYER_ROOT_UID = LOCAL_PLAYER_ROOT_ID.uid;
/** Parent id used for the local player's controller models. */
const LOCAL_PLAYER_CONTROLLERS_ID = LOCAL_PLAYER_ROOT_ID;
const LOCAL_PLAYER_CONTROLLERS_UID = LOCAL_PLAYER_CONTROLLERS_ID.uid;
/** Room id that contains replay-only models. */
const REPLAY_ROOM_ID = ModelId.createRoomId(REPLAY_ID, ROOT_MODEL_UID);
const REPLAY_ROOM_UID = REPLAY_ROOM_ID.uid;
/** Room id for utility/helper models that are not regular session content. */
const UTILITY_ROOM_ID = ModelId.createRoomId(UTILITY_ID, ROOT_MODEL_UID);
const UTILITY_ROOM_UID = UTILITY_ROOM_ID.uid;
/** Sentinel uid used for invalid or missing model references. */
const INVALID_UID = -1;
/**
 * Returns whether a model id belongs to the visible scene hierarchy instead of replay-only content.
 */
function isModelIdPartOfScene(modelId) {
    return (modelId.isRoom() || modelId.getRoomDepth() > 0) && !modelId.isEqualsToOrDescendantOf(REPLAY_ROOM_ID);
}
/** Relative id prefix used for avatar models. */
const AVATAR_RELATIVE_ID_PREFIX = `@avatar_`;
/** Absolute path prefix used for avatar models. */
const AVATAR_ID_PREFIX = `${PATH_SEPERATOR}${AVATAR_RELATIVE_ID_PREFIX}`;
/** Absolute path prefix used for controller models. */
const CONTROLLER_ID_PREFIX = `${PATH_SEPERATOR}@controller_`;

/**
 * Reads loosely typed asset properties and converts them into strongly typed values.
 */
class PropertyParser {
    /**
     * Returns a normalized property value or a fallback when the property is missing or unusable.
     * @param defaultValue Fallback value used when the property is missing or invalid.
     */
    static any(obj, propName, defaultValue) {
        let value = obj[propName];
        if (value === undefined) {
            return defaultValue;
        }
        const valueType = typeof value;
        switch (valueType) {
            case "string": {
                try {
                    value = JSON.parse(value);
                }
                catch {
                    value = value.toString();
                }
                break;
            }
            case "number": {
                if (isNaN(value)) {
                    value = defaultValue;
                }
                break;
            }
        }
        return value;
    }
    static boolean(obj, propName, defaultValue) {
        return Boolean(this.any(obj, propName, defaultValue)).valueOf();
    }
    static booleanSafe(obj, propName, defaultValue) {
        if (obj === undefined) {
            return defaultValue;
        }
        const value = this.any(obj, propName, defaultValue);
        if (value === undefined) {
            return undefined;
        }
        return Boolean(value).valueOf();
    }
    static number(obj, propName, defaultValue) {
        const value = this.any(obj, propName, defaultValue);
        if (value === undefined)
            return value;
        return Number(value);
    }
    static string(obj, propName, defaultValue) {
        return String(this.any(obj, propName, defaultValue));
    }
    static stringSafe(obj, propName, defaultValue) {
        if (obj === undefined) {
            return defaultValue;
        }
        const value = this.any(obj, propName, defaultValue);
        if (value === undefined) {
            return defaultValue;
        }
        return String(value);
    }
    static number3(obj, propName, defaultValue) {
        const value = this.any(obj, propName, defaultValue);
        if (!Array.isArray(value)) {
            return defaultValue;
        }
        if (value.length !== 3) {
            return defaultValue;
        }
        for (const v of value) {
            if (typeof v !== "number") {
                return defaultValue;
            }
        }
        return value;
    }
    static number4(obj, propName, defaultValue) {
        const value = this.any(obj, propName, defaultValue);
        if (!Array.isArray(value)) {
            return defaultValue;
        }
        if (value.length !== 4) {
            return defaultValue;
        }
        for (const v of value) {
            if (typeof v !== "number") {
                return defaultValue;
            }
        }
        return value;
    }
    /**
     * Reads a property as a flattened 4x4 matrix.
     */
    static number16(obj, propName, defaultValue) {
        const value = this.any(obj, propName, defaultValue);
        if (!Array.isArray(value)) {
            return defaultValue;
        }
        if (value.length !== 16) {
            return defaultValue;
        }
        for (const v of value) {
            if (typeof v !== "number") {
                return defaultValue;
            }
        }
        return value;
    }
    static array(obj, propName, defaultValue) {
        const value = this.any(obj, propName, defaultValue);
        if (!Array.isArray(value)) {
            return defaultValue;
        }
        return value;
    }
    /**
     * Resolves a property to a model uid by accepting names, serialized object references, or raw uids.
     * @param content Parsing context used to resolve authored model names.
     */
    static model(obj, propName, content) {
        const value = this.any(obj, propName, undefined);
        if (value === undefined)
            return undefined;
        const typeValue = typeof value;
        switch (typeValue) {
            case "object":
                if (value.type !== "Object" || !value.name)
                    return undefined;
                const model = content.modelNameToModel.get(value.name);
                if (!model)
                    return undefined;
                return model.modelId.uid;
            case "string": {
                const model = content.modelNameToModel.get(value);
                if (!model)
                    return undefined;
                return model.modelId.uid;
            }
            case "number": {
                const model = ModelId.getModelId(value);
                // ToDo check if model in room
                if (!model)
                    return undefined;
                return value;
            }
        }
    }
    static modelSafe(obj, propName, content) {
        if (!obj) {
            return undefined;
        }
        return this.model(obj, propName, content);
    }
}

function enumToPropertyItems(value) {
    const dict = {};
    const entries = Object.entries(value);
    for (let i = 0; i < entries.length / 2; i++) {
        const [k, v] = entries[i];
        const iK = Number(k);
        dict[iK] = v;
    }
    return dict;
}
/**
 * Checks whether a facet instance belongs to the given facet type.
 * @param type Facet constructor to compare against.
 * @param facet Facet instance to test.
 */
function isFacetType(type, facet) {
    return facet.type === type.typestatic;
}
/**
 * Base class for all facets. A facet is a small, serializable component of data attached to a model that describes
 * part of its state - a model can have multiple facets at the same time, and together they describe how it should
 * behave, look or be interpreted. Facets only contain data; the logic that reacts to them lives in modules, usually
 * via the `@FacetHandler`/`@FacetRemoveHandler` decorators.
 *
 * Every concrete facet must:
 * - declare `static readonly typestatic: string`, a unique runtime type identifier
 * - implement {@link Facet.byteLength} and {@link Facet.serialization} for (de)serialization
 * - implement a `static is(facet: Facet): facet is ThisFacet` type guard
 * - register itself with `FacetFactory.register(ThisFacet)`, so it can be reconstructed after deserialization
 *
 * A facet that should define the model's own visual appearance, rather than adding extra rendering next to it,
 * extends {@link RenderableFacet} instead of `Facet` directly, so it can be used with a `Renderable`.
 *
 * Facets are also what the Blender addon exports content as: the optional `static readonly metaData` and
 * `static readonly propTypes` describe how a facet should appear and which of its properties are editable in
 * Blender.
 */
class Facet {
    subType;
    /**
     * The facet's runtime type string, equal to its class' `typestatic`. Set automatically from the constructor and
     * used, among other things, to key a model's facets and to look up the right class in `FacetFactory` again on
     * deserialization.
     */
    type;
    /**
     * @param subType Optional secondary key some facets set to distinguish multiple logical "instances" of the same
     * facet `type` for bus routing, combined with `type` into {@link Facet.pattern}. For example
     * {@link ControllerButton} uses the button index as its `subType` so a module can subscribe to updates for one
     * specific button via `subscribeByFacetType(ControllerButton, callback, buttonIndex.toString())`, and
     * {@link MetaDataFacet} uses its own `data.type` so several differently-typed metadata entries can be routed
     * independently. Most facets have only one logical instance and leave `subType` `undefined`.
     */
    constructor(subType) {
        this.subType = subType;
        this.type = this.constructor.typestatic;
    }
    /// Facet getter ///
    /**
     * The numeric hash of {@link Facet.type} (`typeHashStatic`, assigned by `FacetFactory.register(...)`). Written to
     * the byte stream instead of the string `type` when a facet is serialized, since it is much cheaper - see for
     * example `stream.typeHash(this.facet.typeHash)` in `UpdateFacetOnModelMessage.serialization`.
     */
    get typeHash() {
        return this.constructor.typeHashStatic;
    }
    /**
     * `type` and `subType` combined (`subType` appended if set). This is the actual key bus subscriptions are matched
     * against - `subscribeByFacetType`/`subscribeByRemoveFacetType` build the exact same string from the `type` and
     * `subType` they are given, so a handler only receives updates for a facet with a matching `pattern`. It is also
     * used by `Model.getFacet(...)`/`hasFacet(...)` when called with a facet instance (rather than a facet class) to
     * find the matching facet on that model.
     */
    get pattern() {
        // ToDo between type and subType must be a separator
        return `${this.type}${this.subType ?? ""}`;
    }
    /// Public helper methods ///
    /**
     * Creates a clone of this facet. All primitive, object, array, `Map` and `Set` properties are deep-copied
     * automatically by this default implementation.
     *
     * The `facetCloneContext` only matters for facets that store a UID referencing another model within the same
     * hierarchy that is being cloned, for example a facet pointing at a specific descendant model. Such a facet has to
     * override `clone()`, call `super.clone(facetCloneContext)` first and then rewrite that UID using
     * `facetCloneContext.findEquivalentDescendant(uid)`, which returns the equivalent model in the cloned hierarchy or
     * `undefined` if the referenced model isn't part of the hierarchy being cloned - in that case the UID should
     * usually be left unchanged, as {@link CameraRecorderViewFacet} does for its `cameraUid`:
     * ```ts
     * // CameraRecorderFacet.ts
     * clone<T extends Facet>(cloneContext: FacetCloneContext): T {
     *   const clonedFacet = super.clone(cloneContext) as CameraRecorderViewFacet
     *   const cameraCloneUID = cloneContext.findEquivalentDescendant(this.cameraUid)
     *   if (cameraCloneUID !== undefined)
     *     clonedFacet.cameraUid = cameraCloneUID
     *   return clonedFacet as unknown as T
     * }
     * ```
     * Facets without such a UID reference don't need to override `clone()` at all, and can simply ignore the context.
     *
     * Callers that clone a whole model hierarchy build the context once per hierarchy and reuse it for every facet on
     * every model in it, for example
     * {@link flavr-client!modules/model/ModelPool.modelPool | ModelPool}'s private `cloneModelHierarchyFacets`:
     * ```ts
     * // ModelPool.ts
     * const cloneFacetContext = new ModelFacetCloneContext(model, clonedModel)
     * for (const facet of currentModel.getFacets()) {
     *   const clonedFacet = facet.clone(cloneFacetContext)
     *   currentClonedModel.setFacet(clonedFacet, props)
     * }
     * ```
     * @param _facetCloneContext the context holds information about the original and cloned model as well as their children
     */
    clone(_facetCloneContext) {
        const clonedFacet = Object.assign(Object.create(Object.getPrototypeOf(this)), this);
        for (const [key, facetValue] of Object.entries(clonedFacet)) {
            if (key === "type")
                continue;
            if (key === "subType")
                continue;
            clonedFacet[key] = cloneFacetProperty(facetValue);
        }
        return clonedFacet;
    }
    /**
     * Deep-compares this facet's properties against `other`'s, ignoring `type` and `subType`. Used, for example, to
     * skip broadcasting a facet update when the new facet is equal to the model's current one, as
     * `Model.setTransform` does for `LocalTransformFacet` before queuing an `UpdateFacetOnModelMessage`.
     * @param other the facet to compare this one against; typically a facet of the same `type`
     */
    equals(other) {
        for (const [key, otherFacetValue] of Object.entries(other)) {
            if (key === "type")
                continue;
            if (key === "subType")
                continue;
            const thisFacetValue = this[key];
            if (!this.facetPropertyIsEqual(thisFacetValue, otherFacetValue)) {
                return false;
            }
        }
        return true;
    }
    facetPropertyIsEqual(a, b) {
        if (a === b)
            return true;
        if (typeof a === "object" && typeof b === "object") {
            for (const [key, objValue] of Object.entries(b)) {
                if (!(key in a))
                    return false;
                if (!this.facetPropertyIsEqual(a[key], objValue)) {
                    return false;
                }
            }
        }
        return false;
    }
}
const cloneFacetProperty = (value) => {
    switch (typeof value) {
        case "number":
        case "string":
        case "boolean":
        case "undefined":
            return value;
        case "object":
            if (value === null)
                return value;
            if (Array.isArray(value))
                return value.map(v => cloneFacetProperty(v));
            if (value instanceof Map)
                return new Map(Array.from(value.entries(), ([key, val]) => [key, cloneFacetProperty(val)]));
            if (value instanceof Set)
                return new Set(Array.from(value.values(), v => cloneFacetProperty(v)));
            return cloneObject(value);
        default:
            console.error(`[model] Cloning of facet type ${typeof value} is not supported`);
            return undefined;
    }
};
/**
 * Clones a plain object while preserving its prototype and recursively cloning its properties.
 * @param value Plain object instance to clone.
 */
const cloneObject = (value) => {
    const proto = Object.getPrototypeOf(value);
    const clone = Object.create(proto);
    for (const [key, val] of Object.entries(value)) {
        (clone)[key] = cloneFacetProperty(val);
    }
    return clone;
};

/**
 * Tracks the mapping between facet type strings and their numeric hashes.
 */
class FacetTypeTable {
    static table = new Map();
    static typeToHash = new Map();
    /**
     * Registers a type-to-hash mapping and guards against collisions.
     * @param type Runtime facet type string.
     * @param hash Numeric hash associated with the type.
     */
    static register(type, hash) {
        const existing = this.table.get(hash);
        if (existing != undefined) {
            if (existing !== type) {
                throw new Error(`Collision with (type: ${type} hash: ${hash}) and ${this.table.get(hash)}`);
            }
            return;
        }
        this.table.set(hash, type);
        this.typeToHash.set(type, hash);
    }
    /**
     * Returns the hash for a type, generating and registering it on first use.
     * @param type Runtime facet type string.
     */
    static hashCode(type) {
        let hash = this.typeToHash.get(type);
        if (hash === undefined) {
            hash = hashCode(type);
            this.register(type, hash);
        }
        return hash;
    }
    /**
     * Returns the registered hash for a type if present.
     * @param type Runtime facet type string.
     */
    static getHash(type) {
        return this.typeToHash.get(type);
    }
    /**
     * Checks whether a hash is already associated with a facet type.
     * @param hash Numeric facet type hash.
     */
    static hasHashType(hash) {
        return this.table.has(hash);
    }
    /**
     * Resolves a registered type string from its hash.
     * @param hash Numeric facet type hash.
     */
    static toString(hash) {
        const type = this.table.get(hash);
        if (type === undefined)
            throw new Error(`Type for hash ${hash} not found!`);
        return type;
    }
    static toStringSafe(hash) {
        return this.table.get(hash);
    }
}

/** Enumerates the supported serialization and facet property descriptor kinds. */
var PropertyType;
(function (PropertyType) {
    PropertyType[PropertyType["Boolean"] = 0] = "Boolean";
    /** Boolean value that may also be undefined. */
    PropertyType[PropertyType["BooleanSafe"] = 1] = "BooleanSafe";
    /** Numeric model or entity uid. */
    PropertyType[PropertyType["Uid"] = 2] = "Uid";
    /** Numeric uid that may also be undefined. */
    PropertyType[PropertyType["UidSafe"] = 3] = "UidSafe";
    PropertyType[PropertyType["Int8"] = 4] = "Int8";
    PropertyType[PropertyType["Uint8"] = 5] = "Uint8";
    /** Unsigned 8-bit integer that may also be undefined. */
    PropertyType[PropertyType["Uint8Safe"] = 6] = "Uint8Safe";
    PropertyType[PropertyType["Int16"] = 7] = "Int16";
    PropertyType[PropertyType["Uint16"] = 8] = "Uint16";
    /** Unsigned 16-bit integer that may also be undefined. */
    PropertyType[PropertyType["Uint16Safe"] = 9] = "Uint16Safe";
    PropertyType[PropertyType["Int32"] = 10] = "Int32";
    PropertyType[PropertyType["Uint32"] = 11] = "Uint32";
    /** Unsigned 32-bit integer that may also be undefined. */
    PropertyType[PropertyType["Uint32Safe"] = 12] = "Uint32Safe";
    PropertyType[PropertyType["Float32"] = 13] = "Float32";
    /** 32-bit floating-point number that may also be undefined. */
    PropertyType[PropertyType["Float32Safe"] = 14] = "Float32Safe";
    PropertyType[PropertyType["Float64"] = 15] = "Float64";
    /** 64-bit floating-point number that may also be undefined. */
    PropertyType[PropertyType["Float64Safe"] = 16] = "Float64Safe";
    PropertyType[PropertyType["Utf16String"] = 17] = "Utf16String";
    /** UTF-16 string that may also be undefined. */
    PropertyType[PropertyType["Utf16StringSafe"] = 18] = "Utf16StringSafe";
    /** Nested bus message instance. */
    PropertyType[PropertyType["BusMessage"] = 19] = "BusMessage";
    /** Variable-length array. */
    PropertyType[PropertyType["Array"] = 20] = "Array";
    /** Variable-length array that may also be undefined. */
    PropertyType[PropertyType["ArraySafe"] = 21] = "ArraySafe";
    /** Fixed-length array. */
    PropertyType[PropertyType["FixedSizeArray"] = 22] = "FixedSizeArray";
    /** Dictionary keyed by strings. */
    PropertyType[PropertyType["Dictionary"] = 23] = "Dictionary";
    /** Dictionary keyed by strings that may also be undefined. */
    PropertyType[PropertyType["DictionarySafe"] = 24] = "DictionarySafe";
    /** Structured object with a known property map. */
    PropertyType[PropertyType["Struct"] = 25] = "Struct";
    /** Structured object with a known property map that may also be undefined. */
    PropertyType[PropertyType["StructSafe"] = 26] = "StructSafe";
    /** Tuple or vector with three numeric elements. */
    PropertyType[PropertyType["Number3"] = 27] = "Number3";
    /** Three-number tuple that may also be undefined. */
    PropertyType[PropertyType["Number3Safe"] = 28] = "Number3Safe";
    /** Tuple or quaternion-like value with four numeric elements. */
    PropertyType[PropertyType["Number4"] = 29] = "Number4";
    /** Four-number tuple that may also be undefined. */
    PropertyType[PropertyType["Number4Safe"] = 30] = "Number4Safe";
    /** Matrix-like value with sixteen numeric elements. */
    PropertyType[PropertyType["Number16"] = 31] = "Number16";
    /** Sixteen-number tuple that may also be undefined. */
    PropertyType[PropertyType["Number16Safe"] = 32] = "Number16Safe";
    /** Localized string or translation descriptor. */
    PropertyType[PropertyType["LocalizationString"] = 33] = "LocalizationString";
    /** Arbitrary JSON-like object. */
    PropertyType[PropertyType["Object"] = 34] = "Object";
    /** Arbitrary object that may also be undefined. */
    PropertyType[PropertyType["ObjectSafe"] = 35] = "ObjectSafe";
    /** Unrestricted value. */
    PropertyType[PropertyType["Any"] = 36] = "Any";
    /** Value that may match one of several descriptor variants. */
    PropertyType[PropertyType["Multiple"] = 37] = "Multiple";
    /** Conditionally applied descriptor based on another property. */
    PropertyType[PropertyType["If"] = 38] = "If";
    /** Enum stored as an unsigned byte. */
    PropertyType[PropertyType["EnumUint8"] = 39] = "EnumUint8";
    /** Byte-sized enum that may also be undefined. */
    PropertyType[PropertyType["EnumUint8Safe"] = 40] = "EnumUint8Safe";
    /** Packed hexadecimal color value. */
    PropertyType[PropertyType["ColorHex"] = 41] = "ColorHex";
    /** Packed hexadecimal color value that may also be undefined. */
    PropertyType[PropertyType["ColorHexSafe"] = 42] = "ColorHexSafe";
    PropertyType[PropertyType["AssetPath"] = 43] = "AssetPath";
    /** Asset path string that may also be undefined. */
    PropertyType[PropertyType["AssetPathSafe"] = 44] = "AssetPathSafe";
    /** Bitmask or grouped flags stored as an unsigned 32-bit integer. */
    PropertyType[PropertyType["GroupUint32"] = 45] = "GroupUint32";
    /** Grouped unsigned 32-bit flags that may also be undefined. */
    PropertyType[PropertyType["GroupUint32Safe"] = 46] = "GroupUint32Safe";
    /** Property that should be ignored by the descriptor pipeline. */
    PropertyType[PropertyType["Ignore"] = 47] = "Ignore";
})(PropertyType || (PropertyType = {}));
/**
 * Maps a full {@link TypeDescriptor} to the coarser facet property categories used by tools.
 * @param desc Detailed descriptor that should be reduced to editor-facing categories.
 */
function facetPropertyTypeOf(desc) {
    const type = desc.type;
    switch (type) {
        case PropertyType.Boolean:
        case PropertyType.BooleanSafe:
            return "boolean";
        case PropertyType.Uid:
        case PropertyType.UidSafe:
            return "UID";
        case PropertyType.Int8:
        case PropertyType.Uint8:
        case PropertyType.Uint8Safe:
        case PropertyType.Int16:
        case PropertyType.Uint16:
        case PropertyType.Uint16Safe:
        case PropertyType.Int32:
        case PropertyType.Uint32:
        case PropertyType.Uint32Safe:
        case PropertyType.Float32:
        case PropertyType.Float32Safe:
        case PropertyType.Float64:
        case PropertyType.Float64Safe:
            return "number";
        case PropertyType.Number3:
        case PropertyType.Number3Safe:
            return "Number3";
        case PropertyType.Number4:
        case PropertyType.Number4Safe:
            return "Number4";
        case PropertyType.Number16:
            return "Number16";
        case PropertyType.Utf16String:
        case PropertyType.Utf16StringSafe:
        case PropertyType.AssetPath:
            return "string";
        case PropertyType.LocalizationString:
            return "LocalizationString";
        case PropertyType.Multiple:
            {
                const types = [];
                for (const subDesc of desc.types) {
                    const t = facetPropertyTypeOf(subDesc);
                    if (Array.isArray(t)) {
                        types.push("any");
                    }
                    else {
                        types.push(t);
                    }
                }
                return types;
            }
        case PropertyType.EnumUint8:
            return "any";
        case PropertyType.ColorHex:
            return "number";
        default:
            return "any";
    }
}
/**
 * Returns whether a property type can legally remain undefined.
 * @param type Property descriptor kind that should be checked.
 */
function canFacetPropertyBeUndefined(type) {
    switch (type) {
        case PropertyType.UidSafe:
        case PropertyType.BooleanSafe:
        case PropertyType.Uint8Safe:
        case PropertyType.Uint16Safe:
        case PropertyType.Uint32Safe:
        case PropertyType.Float32Safe:
        case PropertyType.Float64Safe:
        case PropertyType.Utf16StringSafe:
        case PropertyType.ObjectSafe:
        case PropertyType.Number3Safe:
        case PropertyType.Number4Safe:
        case PropertyType.Number16Safe:
            return true;
        default:
            return false;
    }
}
/**
 * Normalizes and validates a property value against its descriptor.
 * It applies defaults, recursively repairs nested values and optionally lets callers resolve custom values first.
 * @param _obj Source object that owns the property.
 * @param propDesc Descriptor that defines the property's expected shape.
 * @param propName Property name used for diagnostics and nested resolution.
 * @param propValue Current property value to validate and normalize.
 * @param facetType Facet type that owns the property.
 * @param parentData Optional parent metadata for nested validation.
 * @param onResolveProperty Optional hook that can replace values before built-in normalization runs.
 */
function validFacetPropertyFromObject(_obj, propDesc, propName, propValue, facetType, parentData, onResolveProperty) {
    if (propDesc.type === PropertyType.Ignore) {
        return undefined;
    }
    if (propValue === undefined) {
        if (!canFacetPropertyBeUndefined(propDesc.type)) {
            throw new Error(`Property ${propName} for facet ${facetType} can't be undefined`);
        }
        return propValue;
    }
    if (onResolveProperty) {
        const newValue = onResolveProperty(propDesc, propValue, {
            propName,
            facetType,
            parentData
        });
        if (newValue !== propValue) {
            return newValue;
        }
    }
    const valueType = typeof propValue;
    switch (propDesc.type) {
        case PropertyType.Int8:
        case PropertyType.Uint8:
        case PropertyType.Int16:
        case PropertyType.Uint16:
        case PropertyType.Int32:
        case PropertyType.Uint32:
        case PropertyType.Float32:
        case PropertyType.Float64:
        case PropertyType.Uint8Safe:
        case PropertyType.Uint16Safe:
        case PropertyType.Uint32Safe:
        case PropertyType.Float32Safe:
        case PropertyType.Float64Safe: {
            let newValue = propValue;
            if (valueType !== "number") {
                if (newValue != undefined || !canFacetPropertyBeUndefined(propDesc.type)) {
                    if ("default" in propDesc) {
                        newValue = propDesc.default;
                    }
                }
            }
            if (newValue != undefined) {
                if (propDesc.min !== undefined && newValue < propDesc.min) {
                    newValue = propDesc.min;
                }
                if (propDesc.max !== undefined && newValue > propDesc.max) {
                    newValue = propDesc.max;
                }
            }
            return newValue;
        }
        case PropertyType.Array:
        case PropertyType.ArraySafe: {
            let newValue = propValue;
            if (!Array.isArray(newValue)) {
                if (propDesc.default) {
                    newValue = deepClone(propDesc.default);
                }
                else {
                    newValue = [];
                }
                // obj[propName] = newValue;
            }
            const length = newValue.length;
            for (let i = 0; i < length; i++) {
                const elementValue = newValue[i];
                newValue[i] = validFacetPropertyFromObject(newValue, propDesc.elementType, propName, elementValue, facetType, {
                    obj: newValue,
                    propDesc: propDesc
                }, onResolveProperty);
            }
            return newValue;
        }
        case PropertyType.FixedSizeArray: {
            let newValue = propValue;
            if (!Array.isArray(newValue)) {
                if (propDesc.default) {
                    newValue = deepClone(propDesc.default);
                }
                else {
                    newValue = [];
                }
                // obj[propName] = newValue;
            }
            const length = propDesc.length;
            for (let i = 0; i < length; i++) {
                const elementValue = newValue[i];
                newValue[i] = validFacetPropertyFromObject(newValue, propDesc.elementType, propName, elementValue, facetType, {
                    obj: newValue,
                    propDesc: propDesc
                }, onResolveProperty);
            }
            return newValue;
        }
        case PropertyType.Struct:
        case PropertyType.StructSafe: {
            let newValue = propValue;
            if (valueType !== "object") {
                if (propDesc.default) {
                    newValue = deepClone(propDesc.default);
                }
                else {
                    newValue = {};
                }
                // obj[propName] = newValue;
            }
            for (const key of Object.keys(newValue)) {
                const elementValue = newValue[key];
                const elementDesc = propDesc.properties[key];
                if (elementDesc === undefined) {
                    console.warn("[Common] Element description is undefined!");
                    continue;
                }
                newValue[key] = validFacetPropertyFromObject(newValue, elementDesc, key, elementValue, facetType, {
                    obj: newValue,
                    propDesc: propDesc
                }, onResolveProperty);
            }
            return newValue;
        }
        case PropertyType.Dictionary:
        case PropertyType.DictionarySafe: {
            let newValue = propValue;
            if (valueType !== "object") {
                if (propDesc.default) {
                    newValue = deepClone(propDesc.default);
                }
                else {
                    newValue = {};
                }
            }
            for (const [key, elementValue] of Object.entries(newValue)) {
                newValue[key] = validFacetPropertyFromObject(newValue, propDesc.elementType, key, elementValue, facetType, {
                    obj: newValue,
                    propDesc: propDesc
                }, onResolveProperty);
            }
            return newValue;
        }
        case PropertyType.If:
            return validFacetPropertyFromObject(_obj, propDesc.properties, propName, propValue, facetType, parentData, onResolveProperty);
        case PropertyType.EnumUint8:
        case PropertyType.EnumUint8Safe: {
            let newValue = propValue;
            if (valueType === "string") {
                const comp = newValue.toLowerCase();
                const found = Object.entries(propDesc.items).find(([{}, value]) => value.toLowerCase() === comp);
                if (found === undefined) {
                    if (propDesc.default) {
                        newValue = deepClone(propDesc.default);
                    }
                    else {
                        newValue = 0;
                    }
                }
                else {
                    newValue = Number(found[0]);
                }
            }
            return newValue;
        }
        // default:
        //   console.warn(`Unsupported type ${PropertyType[propDesc.type]} for property "${propName}" with value`, propValue)
    }
    return propValue;
}

/**
 * Registry for facet constructors, metadata, and property descriptors.
 */
class FacetFactory {
    static creatorCallbacks = new Map();
    static metaData = new Map();
    static propTypes = new Map();
    /**
     * Registers a facet type so it can be recreated from serialized data.
     * @param type Facet constructor together with its static metadata.
     */
    static register(type) {
        const typestatic = type.typestatic;
        if (typestatic === undefined) {
            throw new Error(`Facet with name ${type.name} has no typestatic`);
        }
        const hash = FacetTypeTable.hashCode(typestatic);
        if (this.creatorCallbacks.has(hash)) {
            throw new Error(`Collision ${hash} with ${typestatic} and ${this.creatorCallbacks.get(hash)?.typestatic ?? "unknown"}`);
        }
        if (type.propTypes !== undefined) {
            this.propTypes.set(hash, type.propTypes);
        }
        if (type.metaData !== undefined) {
            this.metaData.set(hash, type.metaData);
        }
        type.typeHashStatic = hash;
        this.creatorCallbacks.set(hash, type);
    }
    static canCreate(hash) {
        return this.creatorCallbacks.has(hash);
    }
    /**
     * Checks whether the given object is a registered facet instance.
     * @param facet Facet instance to validate against the registry.
     */
    static isValidObject(facet) {
        if (!(facet instanceof Facet))
            return false;
        return this.creatorCallbacks.has(facet.typeHash);
    }
    /**
     * Creates a facet instance from its registered type hash.
     * @param hash Registered numeric facet type hash.
     */
    static create(hash) {
        const creator = this.creatorCallbacks.get(hash);
        if (creator === undefined)
            throw new Error(`Facet with type: ${FacetTypeTable.toString(hash)} is not registered`);
        return this.new(creator);
    }
    /**
     * Recreates a facet from raw object data and an explicit type string.
     * @param obj Raw facet-shaped object data.
     * @param type Explicit facet type string.
     * @param validateProperties Whether incoming properties should be validated and defaulted.
     * @param onResolveProperty Optional hook for transforming property values during validation.
     */
    static createFromObjectByTypeString(obj, type, validateProperties = false, onResolveProperty) {
        const hash = FacetTypeTable.getHash(type);
        if (hash === undefined) {
            return undefined;
        }
        const facet = this.create(hash);
        if (validateProperties) {
            const propTypes = this.propTypes.get(hash);
            if (propTypes !== undefined) {
                for (const propName in obj) {
                    if (!propTypes.hasOwnProperty(propName)) {
                        console.warn(`Property ${propName} for facet ${facet.type} is not valid`);
                        continue;
                    }
                    const propDesc = propTypes[propName];
                    const propValue = obj[propName];
                    obj[propName] = validFacetPropertyFromObject(obj, propDesc, propName, propValue, facet.type, undefined, onResolveProperty);
                }
                for (const propName in propTypes) {
                    const propInfo = propTypes[propName];
                    if (!obj.hasOwnProperty(propName)) {
                        if ("default" in propInfo) {
                            obj[propName] = propInfo.default;
                        }
                        else if (propInfo.isRequired === true) {
                            throw new Error(`Property ${propName} for facet ${facet.type} is required`);
                        }
                    }
                }
            }
        }
        return Object.assign(facet, obj);
    }
    /**
     * Returns the registered constructor for a facet hash.
     * @param hash Registered numeric facet type hash.
     */
    static getCreator(hash) {
        const creator = this.creatorCallbacks.get(hash);
        if (creator === undefined)
            throw new Error(`Facet with type: ${FacetTypeTable.toString(hash)} is not registered`);
        return creator;
    }
    /**
     * Iterates over all registered facet types together with their optional tooling metadata.
     */
    static *iterator() {
        for (const [hash, type] of this.creatorCallbacks) {
            const metaData = this.metaData.get(hash);
            const propTypes = this.propTypes.get(hash);
            yield {
                type,
                metaData,
                propTypes
            };
        }
    }
    /// Private helper methods ///
    /**
     * Creates a facet instance without calling its constructor, for deserialization scenarios.
     * @param type Facet constructor whose prototype should be instantiated.
     */
    static new(type) {
        const newObj = Object.create(type.prototype);
        newObj.type = type.typestatic;
        return newObj;
    }
    /**
     * Recreates a facet from a raw object containing a `type` field.
     * @param obj Raw object that contains a `type` discriminator.
     */
    static createFromObject(obj) {
        if (!("type" in obj))
            throw new Error(`type is not defined in object: ${JSON.stringify(obj)}`);
        return this.createFromObjectByTypeString(obj, obj.type);
    }
}

/**
 * Bidirectional lookup table between message type names and their stable numeric hashes.
 */
class MessageTypeTable {
    static table = new Map();
    static typeToHash = new Map();
    /**
     * Registers a type/hash pair and guards against collisions.
     * @param type Stable message type string.
     * @param hash Stable numeric hash assigned to the message type.
     */
    static register(type, hash) {
        const existing = this.table.get(hash);
        if (existing !== undefined) {
            if (existing !== type) {
                throw new Error(`Collision with (type: ${type} hash: ${hash}) and ${this.table.get(hash)}`);
            }
            return;
        }
        this.table.set(hash, type);
        this.typeToHash.set(type, hash);
    }
    /**
     * Returns the stable hash for a message type, generating and caching it on first use.
     * @param type Stable message type string.
     */
    static hashCode(type) {
        let hash = this.typeToHash.get(type);
        if (hash === undefined) {
            hash = hashCode(type);
            this.register(type, hash);
        }
        return hash;
    }
    /**
     * Looks up the registered hash for a message type.
     * @param type Stable message type string.
     */
    static getHash(type) {
        return this.typeToHash.get(type);
    }
    /**
     * Resolves a registered message type name from its hash.
     * @param hash Stable numeric hash assigned to a message type.
     */
    static toString(hash) {
        const type = this.table.get(hash);
        if (type === undefined)
            throw new Error(`There is no message type registered for hash ${hash}!`);
        return type;
    }
}

/**
 * Interface for BusMessages.
 *
 * ### Registering your class:
 * When implementing BusMessage, remember to register your class at MessageFactory.register().
 * That way MessageFactory will know how to create your class.
 *
 * Best Practice: put a piece of code _after_ your class-definition that says:
 * ```javascript
 *  MessageFactory.register(MyClass.hashstatic, MyClass);
 * ```
 */
class BusMessage {
    /**
     * Runtime message type copied from the static `typestatic` field.
     */
    type;
    /**
     * Routing subject that determines which bus domain receives the message.
     */
    get subject() {
        return this.constructor.subjectstatic;
    }
    /**
     * Marker used by bulk transport helpers.
     */
    isBulkMessage;
    /**
     * Marker used by request/response helpers.
     */
    isAskMessage;
    /**
     * Stable numeric hash of the concrete message type, when registered.
     */
    get typeHash() {
        return this.constructor.typeHashStatic;
    }
    /**
     * The number of bytes this message needs when serialized, used to size the buffer before `serialization(stream)`
     * writes into it. The default implementation returns `0`; override it in any message that actually carries data
     * (a message with no constructor parameters has nothing to serialize). All constants and helpers below are
     * defined in `ByteStreamUtils.ts` and imported from `flavr-common`, for example:
     * ```ts
     * import { BusMessage, calcArrayBufferByteLength, calcStringByteLength, INT32_BYTES_PER_ELEMENT } from "flavr-common"
     * ```
     *
     * For fixed-size fields, add up the matching byte-length constant instead of hardcoding a number:
     * - `BOOLEAN_BYTES_PER_ELEMENT`
     * - `ENUM_BYTES_PER_ELEMENT` (enums with fewer than 256 members)
     * - `INT32_BYTES_PER_ELEMENT`
     * - `UINT8_BYTES_PER_ELEMENT`
     * - `UINT16_BYTES_PER_ELEMENT`
     * - `UINT32_BYTES_PER_ELEMENT`
     * - `FLOAT32_BYTES_PER_ELEMENT`
     * - `UID_BYTES_PER_ELEMENT`
     * - `UID_SAFE_BYTES_PER_ELEMENT` (a UID that can also encode `undefined`)
     * - `TYPE_HASH_BYTES_PER_ELEMENT`
     * - `NUMBER3_BYTES_PER_ELEMENT`
     * - `NUMBER4_BYTES_PER_ELEMENT`
     * - `NUMBER16_BYTES_PER_ELEMENT`
     *
     * For fields whose size depends on their current content, call the matching `calc*ByteLength` helper instead:
     * - `calcStringByteLength`
     * - `calcStringArrayByteLength`
     * - `calcFastFloat32ByteLength`
     * - `calcLocalizationStringByteLength`
     * - `calcArrayByteLength`
     * - `calcMapByteLength`
     * - `calcSetByteLength`
     * - `calcObjectByteLength`
     * - `calcArrayBufferByteLength`
     * - `calcBusMessageByteLength` / `calcBusMessagesByteLength` (for a nested message or message array)
     * - `calcTypeByteLength` / `calcKnownTypeByteLength` - lower-level helpers the functions above are built on; you
     *   normally won't need to call these directly
     *
     * A message combining a fixed-size field with dynamically-sized ones just adds them up, for example
     * {@link flavr-client!messages/file/FileMessages.AddFileDataMessage | AddFileDataMessage}:
     * ```ts
     * get byteLength(): number {
     *   return calcArrayBufferByteLength(this.data) + calcStringByteLength(this.fileName) + calcStringByteLength(this.fileTypeExtension)
     * }
     * ```
     *
     * Importantly, `byteLength` has to add up to exactly as many bytes as `serialization(stream)` actually reads or
     * writes - too few bytes corrupts whatever is serialized after this message, too many wastes space. Keep the terms
     * in `byteLength` in the same order as the constructor parameters and as they are read/written in
     * `serialization(stream)`, so a mismatch (a forgotten field, a wrong constant) is easy to spot at a glance.
     *
     * Be careful when calculating `byteLength` for safe values (see {@link BusMessage.serialization}). Some safe types
     * have an explicit `*_SAFE_BYTES_PER_ELEMENT` constant, for example `UID_SAFE_BYTES_PER_ELEMENT`. Other safe types
     * reuse the same byte width as the non-safe variant and encode `undefined` via sentinel values such as `NaN`,
     * `UINT32_MAX_VALUE` or a special boolean state.
     * @see {@link Facet.byteLength} for the equivalent getter on facets
     */
    get byteLength() {
        return 0;
    }
    /**
     * Initializes the runtime `type` from the message class metadata.
     */
    constructor() {
        this.type = this.constructor.typestatic || this.constructor.name;
    }
    /**
     * Copies enumerable properties from a plain object onto this message instance.
     * @param obj Plain object carrying serialized message fields.
     */
    fromObject(obj) {
        Object.assign(this, obj);
    }
}

/**
 * This message type allows to send a ask request to one module
 * This message is not spread through the workers, so it does not need to be registered.
 */
class AskMessage extends BusMessage {
    askId;
    message;
    static typestatic = "AskMessage";
    /**
     * Reuses the wrapped message subject so ask requests route like the original message.
     */
    get subject() {
        return this.message.subject;
    }
    /**
     * @param askId Correlation id used to match the later answer.
     * @param message Wrapped request message that should be handled by exactly one module.
     */
    constructor(
    /**
     * Correlation id used to match the later answer.
     */
    askId, 
    /**
     * Wrapped request message that should be handled by exactly one module.
     */
    message) {
        super();
        this.askId = askId;
        this.message = message;
        this.type = message.type;
    }
    /**
     * Marker used to identify ask wrappers during message unwrapping.
     */
    isAskMessage = true;
    /**
     * Ask messages are transport wrappers and do not serialize additional fields here.
     */
    serialization() { }
    static is(msg) {
        return !!msg.isAskMessage;
    }
}
/**
 * This is the answer message to an ask request
 * This message is not spread through the workers, so it does not need to be registered.
 */
class AnswerMessage extends BusMessage {
    askId;
    targetWorkerId;
    messages;
    static typestatic = "AnswerMessage";
    static subjectstatic = "system";
    /**
     * @param askId Correlation id of the ask request being answered.
     * @param targetWorkerId Worker that should receive the answer.
     * @param messages Payload messages returned by the handler.
     */
    constructor(
    /**
     * Correlation id of the ask request being answered.
     */
    askId, 
    /**
     * Worker that should receive the answer.
     */
    targetWorkerId, 
    /**
     * Payload messages returned by the handler.
     */
    messages) {
        super();
        this.askId = askId;
        this.targetWorkerId = targetWorkerId;
        this.messages = messages;
    }
    /**
     * Answer messages are transport wrappers and do not serialize additional fields here.
     */
    serialization() { }
    static is(msg) {
        return msg.type === AnswerMessage.typestatic;
    }
}
/**
 * This message type allows to send multiple messages of the same type at once
 * This message is not spread through the workers, so it does not need to be registered.
 */
class BulkMessage extends BusMessage {
    messages;
    static typestatic = "BulkMessage";
    _subject;
    /**
     * Shared subject used for all messages contained in this bulk payload.
     */
    get subject() {
        return this._subject;
    }
    /**
     * @param messages Messages batched together for transport.
     * @param subject Shared subject of all contained messages.
     * @param type Shared type prefix used for routing.
     */
    constructor(
    /**
     * Messages batched together for transport.
     */
    messages, subject, type) {
        super();
        this.messages = messages;
        if (subject === "system") {
            throw new Error("System Messages cannot be send as BulkMessages/via busService.sendMultiple!");
        }
        this._subject = subject;
        this.type = type;
    }
    /**
     * Marker used to identify bulk wrappers during transport handling.
     */
    isBulkMessage = true;
    /**
     * Bulk messages are transport wrappers and do not serialize additional fields here.
     */
    serialization() { }
    static is(msg) {
        return !!msg.isBulkMessage;
    }
}

/** Minimum representable signed 8-bit integer. */
const INT8_MIN_VALUE = -128;
/** Maximum representable unsigned 8-bit integer. */
const UINT8_MAX_VALUE = 255;
/** Maximum representable unsigned 16-bit integer. */
const UINT16_MAX_VALUE = 65535;
/** Maximum representable unsigned 32-bit integer. */
const UINT32_MAX_VALUE = 4294967295;
/** Number of bytes needed to store a 32-bit signed integer. */
const INT32_BYTES_PER_ELEMENT = Int32Array.BYTES_PER_ELEMENT;
/** Number of bytes needed to store an unsigned 8-bit integer. */
const UINT8_BYTES_PER_ELEMENT = Uint8Array.BYTES_PER_ELEMENT;
/** Number of bytes needed to store an unsigned 16-bit integer. */
const UINT16_BYTES_PER_ELEMENT = Uint16Array.BYTES_PER_ELEMENT;
/** Number of bytes needed to store an unsigned 32-bit integer. */
const UINT32_BYTES_PER_ELEMENT = Uint32Array.BYTES_PER_ELEMENT;
/** Number of bytes needed to store a 32-bit float. */
const FLOAT32_BYTES_PER_ELEMENT = Float32Array.BYTES_PER_ELEMENT;
/** Number of bytes used by serialized booleans. */
const BOOLEAN_BYTES_PER_ELEMENT = Uint8Array.BYTES_PER_ELEMENT;
/** Number of bytes used by compact enum values. */
const ENUM_BYTES_PER_ELEMENT = Uint8Array.BYTES_PER_ELEMENT; // Works for enums with less than 256 entries
/** Number of bytes used to store a type hash. */
const TYPE_HASH_BYTES_PER_ELEMENT = Int32Array.BYTES_PER_ELEMENT;
/** Number of bytes used to store a UID. */
const UID_BYTES_PER_ELEMENT = Int32Array.BYTES_PER_ELEMENT;
/** Number of bytes used to store an optional UID flag plus its value. */
const UID_SAFE_BYTES_PER_ELEMENT = BOOLEAN_BYTES_PER_ELEMENT + UID_BYTES_PER_ELEMENT;
/** Number of bytes used by serialized `Number3` tuples. */
const NUMBER3_BYTES_PER_ELEMENT = 3 * FLOAT32_BYTES_PER_ELEMENT;
/** Number of bytes used by serialized `Number4` tuples. */
const NUMBER4_BYTES_PER_ELEMENT = 4 * FLOAT32_BYTES_PER_ELEMENT;
/** Number of bytes used by serialized `Number16` tuples. */
const NUMBER16_BYTES_PER_ELEMENT = 16 * FLOAT32_BYTES_PER_ELEMENT;
/** Returns whether a value is binary data backed by an `ArrayBuffer` or Node `Buffer`. */
function isArrayOrNodeBuffer(obj) {
    if (obj instanceof ArrayBuffer)
        return true;
    return typeof Buffer !== "undefined" && obj instanceof Buffer;
}
/** Calculates the serialized byte size of a UTF-16 string value. */
function calcStringByteLength(str) {
    if (str === undefined) {
        return UINT32_BYTES_PER_ELEMENT;
    }
    return (UINT32_BYTES_PER_ELEMENT + // length
        str.length * UINT16_BYTES_PER_ELEMENT); // characters
}
/** Calculates the serialized byte size of a string array. */
function calcStringArrayByteLength(str) {
    if (str === undefined) {
        return Uint32Array.BYTES_PER_ELEMENT;
    }
    let byteLength = Uint32Array.BYTES_PER_ELEMENT;
    for (const s of str) {
        byteLength +=
            Uint32Array.BYTES_PER_ELEMENT + // length
                s.length * Uint16Array.BYTES_PER_ELEMENT; // characters
    }
    return byteLength;
}
/** Calculates the serialized byte size of an array-like float buffer. */
function calcFastFloat32ByteLength(ff32) {
    if (!ff32) {
        return UINT32_BYTES_PER_ELEMENT;
    }
    else {
        return (UINT32_BYTES_PER_ELEMENT + // length
            ff32.length * Float32Array.BYTES_PER_ELEMENT); // characters
    }
}
/** Calculates the serialized byte size of a localized string payload. */
function calcLocalizationStringByteLength(localization) {
    if (localization === undefined) {
        return Uint8Array.BYTES_PER_ELEMENT;
    }
    let byteLength = Uint8Array.BYTES_PER_ELEMENT + // count
        Uint32Array.BYTES_PER_ELEMENT; // string length
    if (typeof localization === "string") {
        return byteLength + localization.length * Uint16Array.BYTES_PER_ELEMENT;
    }
    byteLength += localization.key.length * Uint16Array.BYTES_PER_ELEMENT;
    if (localization.params) {
        for (const key in localization.params) {
            const value = localization.params[key];
            byteLength += Uint32Array.BYTES_PER_ELEMENT; // string length
            byteLength += key.length * Uint16Array.BYTES_PER_ELEMENT;
            byteLength += Uint32Array.BYTES_PER_ELEMENT; // string length
            byteLength += value.length * Uint16Array.BYTES_PER_ELEMENT;
        }
    }
    return byteLength;
}
/** Calculates the serialized byte size of a single bus message including its type hash. */
function calcBusMessageByteLength(msg) {
    if (msg.byteLength === undefined)
        throw new Error(`byteLength is not defined in msg ${msg.type}`);
    return TYPE_HASH_BYTES_PER_ELEMENT + msg.byteLength;
}
/** Calculates the serialized byte size of a message array including array bookkeeping. */
function calcBusMessagesByteLength(messages) {
    let byteLength = Uint32Array.BYTES_PER_ELEMENT; // length
    if (messages === undefined) {
        return byteLength;
    }
    for (const msg of messages) {
        const bL = msg.byteLength;
        if (bL === undefined || msg.typeHash === undefined)
            continue;
        byteLength += TYPE_HASH_BYTES_PER_ELEMENT; // typeHash
        byteLength += msg.byteLength;
    }
    return byteLength;
}
/** Calculates the serialized byte size of a generic array. */
function calcArrayByteLength(arr) {
    let byteLength = Uint32Array.BYTES_PER_ELEMENT; // length
    if (arr === undefined) {
        return byteLength;
    }
    for (const value of arr) {
        byteLength += calcTypeByteLength(value);
    }
    return byteLength;
}
/** Calculates the serialized byte size of a generic map. */
function calcMapByteLength(map) {
    let byteLength = Uint32Array.BYTES_PER_ELEMENT; // size
    if (map === undefined) {
        return byteLength;
    }
    for (const [key, value] of map) {
        byteLength += calcTypeByteLength(key);
        byteLength += calcTypeByteLength(value);
    }
    return byteLength;
}
/** Calculates the serialized byte size of a generic set. */
function calcSetByteLength(set) {
    let byteLength = Uint32Array.BYTES_PER_ELEMENT; // size
    if (set === undefined) {
        return byteLength;
    }
    for (const value of set) {
        byteLength += calcTypeByteLength(value);
    }
    return byteLength;
}
/** Calculates the serialized byte size of a runtime value using generic object encoding rules. */
function calcTypeByteLength(value) {
    let byteLength = Uint8Array.BYTES_PER_ELEMENT; // type
    switch (typeof value) {
        case "number":
            byteLength += Float64Array.BYTES_PER_ELEMENT;
            break;
        case "string":
            byteLength += calcStringByteLength(value);
            break;
        case "boolean":
            byteLength += BOOLEAN_BYTES_PER_ELEMENT;
            break;
        case "undefined":
            break;
        case "object":
            if (Array.isArray(value)) {
                byteLength += calcArrayByteLength(value);
            }
            else if (value instanceof BusMessage) {
                byteLength += calcBusMessageByteLength(value);
            }
            else if (value instanceof Map) {
                byteLength += calcMapByteLength(value);
            }
            else if (value instanceof Set) {
                byteLength += calcSetByteLength(value);
            }
            else if (isArrayOrNodeBuffer(value)) {
                byteLength += calcArrayBufferByteLength(value);
            }
            else if (value.constructor.name !== "Object") {
                byteLength += calcObjectByteLength(value);
            }
            else {
                byteLength += calcObjectByteLength(value);
            }
            break;
        default:
            console.error(`Type ${typeof value} is not supported`);
            break;
    }
    return byteLength;
}
/** Calculates the serialized byte size of a plain object by summing keys and values. */
function calcObjectByteLength(obj) {
    let byteLength = Uint32Array.BYTES_PER_ELEMENT; // property count
    if (obj != undefined) {
        for (const [key, value] of Object.entries(obj)) {
            byteLength += calcStringByteLength(key);
            byteLength += calcTypeByteLength(value);
        }
    }
    return byteLength;
}
/** Calculates the serialized byte size of an `ArrayBuffer` including its stored length. */
function calcArrayBufferByteLength(buffer) {
    return Uint32Array.BYTES_PER_ELEMENT + buffer.byteLength;
}
/** Calculates the serialized byte size of a value when its {@link TypeDescriptor} is already known. */
function calcKnownTypeByteLength(value, descriptor) {
    switch (descriptor.type) {
        case PropertyType.Uid:
            return UID_BYTES_PER_ELEMENT;
        case PropertyType.UidSafe:
            return UID_SAFE_BYTES_PER_ELEMENT;
        case PropertyType.Boolean:
        case PropertyType.BooleanSafe:
            return BOOLEAN_BYTES_PER_ELEMENT;
        case PropertyType.Int8:
        case PropertyType.Uint8:
        case PropertyType.Uint8Safe:
            return Int8Array.BYTES_PER_ELEMENT;
        case PropertyType.Int16:
        case PropertyType.Uint16:
        case PropertyType.Uint16Safe:
            return Int16Array.BYTES_PER_ELEMENT;
        case PropertyType.Int32:
        case PropertyType.Uint32:
        case PropertyType.Uint32Safe:
            return Int32Array.BYTES_PER_ELEMENT;
        case PropertyType.Float32:
        case PropertyType.Float32Safe:
            return Float32Array.BYTES_PER_ELEMENT;
        case PropertyType.Float64:
        case PropertyType.Float64Safe:
            return Float32Array.BYTES_PER_ELEMENT;
        case PropertyType.Utf16String:
            if (value === undefined)
                throw new Error(`Value is not a string`);
            if (typeof value !== "string")
                throw new Error(`Value is not a string`);
            return calcStringByteLength(value);
        case PropertyType.Utf16StringSafe:
            if (value != undefined && typeof value !== "string")
                throw new Error(`Value is not a string`);
            return calcStringByteLength(value);
        case PropertyType.BusMessage: {
            if (value === undefined || !(value instanceof BusMessage))
                throw new Error(`Value is not a BusMessage`);
            const byteLength = value.byteLength;
            if (byteLength === undefined)
                throw new Error(`byteLength is not defined in msg ${value.type}`);
            return byteLength;
        }
        case PropertyType.Array:
        case PropertyType.ArraySafe: {
            let byteLength = Uint32Array.BYTES_PER_ELEMENT; // array length;
            if (value === undefined)
                return byteLength;
            if (!Array.isArray(value))
                throw new Error(`Value is not an Array`);
            for (const element of value) {
                byteLength += calcKnownTypeByteLength(element, descriptor.elementType);
            }
            return byteLength;
        }
        case PropertyType.FixedSizeArray: {
            let byteLength = 0;
            if (value === undefined)
                return byteLength;
            if (!Array.isArray(value))
                throw new Error(`Value is not an Array`);
            if (value.length !== descriptor.length)
                throw new Error(`Value.length doesn't match the specified length of the FixedSizeArray`);
            for (const element of value) {
                byteLength += calcKnownTypeByteLength(element, descriptor.elementType);
            }
            return byteLength;
        }
        case PropertyType.Dictionary:
        case PropertyType.DictionarySafe: {
            let byteLength = Uint32Array.BYTES_PER_ELEMENT; // num entries;
            if (value === undefined)
                return byteLength;
            if (typeof value !== "object")
                throw new Error(`Value is not an Object`);
            for (const entryName in value) {
                byteLength += calcStringByteLength(entryName);
                byteLength += calcKnownTypeByteLength(value[entryName], descriptor.elementType);
            }
            return byteLength;
        }
        case PropertyType.Struct:
        case PropertyType.StructSafe: {
            let byteLength = 0;
            if (value === undefined)
                return byteLength;
            if (typeof value !== "object")
                throw new Error(`Value is not an Object`);
            for (const propertyName in descriptor.properties) {
                byteLength += calcKnownTypeByteLength(value[propertyName], descriptor.properties[propertyName]);
            }
            return byteLength;
        }
        case PropertyType.Number3:
        case PropertyType.Number3Safe: {
            return Float32Array.BYTES_PER_ELEMENT * 3;
        }
        case PropertyType.Number4:
        case PropertyType.Number4Safe: {
            return Float32Array.BYTES_PER_ELEMENT * 4;
        }
        case PropertyType.Number16:
        case PropertyType.Number16Safe: {
            return Float32Array.BYTES_PER_ELEMENT * 16;
        }
        case PropertyType.LocalizationString: {
            return calcLocalizationStringByteLength(value);
        }
        case PropertyType.Object:
        case PropertyType.ObjectSafe:
            return calcObjectByteLength(value);
        case PropertyType.Multiple:
            return 0;
        case PropertyType.If: {
            if ("type" in descriptor.properties) {
                return calcKnownTypeByteLength(value, descriptor.properties);
            }
            else {
                let byteLength = 0;
                for (const desc of Object.values(descriptor.properties)) {
                    byteLength += calcKnownTypeByteLength(value, desc);
                }
                return byteLength;
            }
        }
        case PropertyType.EnumUint8:
        case PropertyType.EnumUint8Safe: {
            return Uint8Array.BYTES_PER_ELEMENT;
        }
        case PropertyType.ColorHex:
        case PropertyType.ColorHexSafe: {
            return Uint32Array.BYTES_PER_ELEMENT;
        }
        case PropertyType.AssetPath:
            if (value === undefined)
                throw new Error(`Value is not a string`);
        case PropertyType.AssetPathSafe:
            if (value != undefined && typeof value !== "string")
                throw new Error(`Value is not a string`);
            return calcStringByteLength(value);
        case PropertyType.GroupUint32:
        case PropertyType.GroupUint32Safe:
            return Uint32Array.BYTES_PER_ELEMENT;
        case PropertyType.Ignore:
            return 0;
    }
    console.warn(`calcKnownTypeByteLength: type ${descriptor.type} isn't handled`);
    return 0;
}

/**
 * Carries a human-readable diagnostic string. Sent mostly server-to-client via
 * `ClientConnectionService.sendDebugMessageToClient` to report errors or fallback notices (e.g. missing
 * permissions, unhandled message types) when no dedicated message type applies; the client's
 * `BusServiceMain` subscribes to it and logs the text with `console.debug`.
 */
class DebugMessage extends BusMessage {
    message;
    severity;
    static typestatic = "DebugMessage";
    static subjectstatic = "general";
    get byteLength() {
        return calcStringByteLength(this.message) + calcStringByteLength(this.severity);
    }
    /**
     * @param message Human-readable debug payload forwarded through the bus.
     */
    constructor(
    /**
     * Human-readable debug payload forwarded through the bus.
     */
    message, severity) {
        super();
        this.message = message;
        this.severity = severity;
    }
    serialization(stream) {
        this.message = stream.utf16String(this.message);
        this.severity = stream.utf16StringSafe(this.severity);
    }
    static is(msg) {
        return msg.type === DebugMessage.typestatic;
    }
}

/**
 * Separator between a message subject and type in wrapped string messages.
 */
const SUBJECT_TYPE_SEPERATOR = "/";
/**
 * Separator between wrapped message metadata fields.
 */
const METADATA_SEPERATOR = "$";
/**
 * Separator between the header and payload portions of a wrapped message.
 */
const MESSAGE_SEPERATOR = ":";
/**
 * Separator used by answer messages to encode the target worker and ask id.
 */
const ANSWER_MESSAGE_SEPERATOR = "@";
/**
 * Builds a regular expression that matches wrapped messages for a subscription pattern.
 * @param pattern Subscription pattern that may include a full `subject/type` pair or only a type.
 */
function getRegExpForSubscriptionPattern(pattern) {
    return new RegExp(pattern.includes(SUBJECT_TYPE_SEPERATOR) ? pattern : `.*${SUBJECT_TYPE_SEPERATOR}${pattern}`);
}
/**
 * Wraps a bus message into the string transport format including worker metadata.
 * @param message Message instance to serialize for transport.
 * @param workerId Worker that originated the message.
 * @param moduleId Module that emitted the message.
 * @param timeStamp Emission timestamp that should travel with the wrapped payload.
 */
function wrapMessage(message, workerId, moduleId, timeStamp) {
    const check_subject_type = `${message.subject}${message.type}`;
    if (check_subject_type.includes(SUBJECT_TYPE_SEPERATOR) || check_subject_type.includes(MESSAGE_SEPERATOR)) {
        throw new Error(`Message Subject and Type may not contain any of these Seperators ${SUBJECT_TYPE_SEPERATOR} or ${MESSAGE_SEPERATOR}`);
    }
    if (AnswerMessage.is(message)) {
        // Answer message: <targetWorkerId>@<askId>:<type>:{object}
        return `${message.targetWorkerId}${ANSWER_MESSAGE_SEPERATOR}${message.askId}${MESSAGE_SEPERATOR}${message.type}${METADATA_SEPERATOR}${workerId}${METADATA_SEPERATOR}${moduleId}${METADATA_SEPERATOR}${timeStamp}${MESSAGE_SEPERATOR}${JSON.stringify(message.messages)}`;
    }
    else if (!BulkMessage.is(message)) {
        // we convert the message to string to avoid additional parsing in the bus
        // we also add message subject and type to allow easy filtering with regexes
        return `${message.subject}${SUBJECT_TYPE_SEPERATOR}${message.type}${METADATA_SEPERATOR}${workerId}${METADATA_SEPERATOR}${moduleId}${METADATA_SEPERATOR}${timeStamp}${MESSAGE_SEPERATOR}${JSON.stringify(message)}`;
    }
    else {
        // we have more than one message wrapped in a BulkMessage: stringify the containing messages instead
        return `${message.subject}${SUBJECT_TYPE_SEPERATOR}${message.type}${METADATA_SEPERATOR}${workerId}${METADATA_SEPERATOR}${moduleId}${METADATA_SEPERATOR}${timeStamp}${MESSAGE_SEPERATOR}${JSON.stringify(message.messages)}`;
    }
}
/**
 * Wraps a bus message into the string transport format without worker metadata.
 * @param message Message instance to serialize for local transport.
 */
function wrapMessageWithoutMetaData(message) {
    const check_subject_type = `${message.subject}${message.type}`;
    if (check_subject_type.includes(SUBJECT_TYPE_SEPERATOR) || check_subject_type.includes(MESSAGE_SEPERATOR)) {
        throw new Error(`Message Subject and Type may not contain any of these Seperators ${SUBJECT_TYPE_SEPERATOR} or ${MESSAGE_SEPERATOR}`);
    }
    if (AnswerMessage.is(message)) {
        // Answer message: <targetWorkerId>@<askId>:<type>:{object}
        return `${message.targetWorkerId}${ANSWER_MESSAGE_SEPERATOR}${message.askId}${MESSAGE_SEPERATOR}${message.type}${MESSAGE_SEPERATOR}${JSON.stringify(message.messages)}`;
    }
    else if (!BulkMessage.is(message)) {
        // we convert the message to string to avoid additional parsing in the bus
        // we also add message subject and type to allow easy filtering with regexes
        return `${message.subject}${SUBJECT_TYPE_SEPERATOR}${message.type}${MESSAGE_SEPERATOR}${JSON.stringify(message)}`;
    }
    else {
        // we have more than one message wrapped in a BulkMessage: stringify the containing messages instead
        return `${message.subject}${SUBJECT_TYPE_SEPERATOR}${message.type}${MESSAGE_SEPERATOR}${JSON.stringify(message.messages)}`;
    }
}
/**
 * Extracts the JSON payload portion of a wrapped message string and parses it into a plain bus-message-shaped object.
 * @param str Wrapped transport string whose payload should be decoded.
 */
function unwrapMessage(str) {
    const colon = str.indexOf(MESSAGE_SEPERATOR);
    if (colon === -1) {
        const error = `received invalid message, missing Message Seperator (${MESSAGE_SEPERATOR}) in ${str}`;
        console.error("[Common]", error);
        return new DebugMessage(error);
    }
    return JSON.parse(str.substring(colon + 1));
}

/**
 * Registers message classes and recreates typed bus messages from hashes or plain objects.
 */
class MessageFactory {
    static creatorCallbacks = new Map();
    /**
     * Registers a message class so plain objects and serialized payloads can be rehydrated into that type later.
     * Best practice is to call this once directly after the class definition.
     * @param type Message class to register. Its constructor must be callable without required parameters.
     */
    static register(type) {
        const typestatic = type.typestatic;
        if (typestatic === undefined) {
            throw new Error(`Message with name ${type.name} has no typestatic`);
        }
        const hash = MessageTypeTable.hashCode(typestatic);
        if (this.creatorCallbacks.has(hash)) {
            throw new Error(`Collission ${hash} with ${typestatic} and ${this.creatorCallbacks.get(hash).typestatic}`);
        }
        type.typeHashStatic = hash;
        this.creatorCallbacks.set(hash, type);
    }
    /**
     * Creates a new message instance from a registered type hash.
     * @param hash Stable numeric type hash of the message to instantiate.
     */
    static create(hash) {
        const creator = this.creatorCallbacks.get(hash);
        if (!creator)
            throw new Error(`Message with type: ${MessageTypeTable.toString(hash)} is not registered`);
        return this.new(creator);
    }
    /**
     * Creates a message instance without invoking its constructor arguments.
     * @param type Registered message constructor whose prototype should back the new instance.
     */
    static new(type) {
        const newObj = Object.create(type.prototype);
        newObj.type = type.typestatic;
        return newObj;
    }
    /**
     * Unwraps a serialized message string and recreates the corresponding message instance when possible.
     * @param msgString Wrapped message string in transport format.
     */
    static createFromString(msgString) {
        const msgObj = unwrapMessage(msgString);
        return this.createFromObject(msgObj);
    }
    /**
     * Rehydrates a typed message from a plain object carrying a `type` field.
     * @param msgObj Plain object that should be turned back into a registered message instance.
     */
    static createFromObject(msgObj) {
        if (!("type" in msgObj))
            throw new Error(`type is not defined in object: ${JSON.stringify(msgObj)}`);
        const hash = MessageTypeTable.getHash(msgObj.type);
        if (hash === undefined) {
            return msgObj;
        }
        const msg = this.create(hash);
        msg.fromObject(msgObj);
        return msg;
    }
    /**
     * Unwraps a serialized message string and rehydrates it as the expected message type.
     * @param msgString Wrapped message string in transport format.
     * @param type Expected message class used for typed reconstruction.
     */
    static createFromStringByType(msgString, type) {
        const msgObj = unwrapMessage(msgString);
        return this.createFromObjectByType(msgObj, type);
    }
    /**
     * Rehydrates a typed message from a plain object using an explicitly provided message class.
     * @param msgObj Plain object that should be copied into the target message type.
     * @param type Expected message class used for typed reconstruction.
     */
    static createFromObjectByType(msgObj, type) {
        if (type.typeHashStatic === undefined) {
            console.warn(`[Common] createFromObjectByType: typeHashStatic is not defined in BusMessage ${type.name}`);
            return msgObj;
        }
        const msg = this.create(type.typeHashStatic);
        msg.fromObject(msgObj);
        return msg;
    }
    /**
     * Returns the registered message constructor for a type hash.
     * @param hash Stable numeric type hash of the requested message class.
     */
    static getCreator(hash) {
        const creator = this.creatorCallbacks.get(hash);
        if (!creator)
            throw new Error(`Message with type: ${MessageTypeTable.toString(hash)} is not registered`);
        return creator;
    }
}
MessageFactory.register(DebugMessage);

/**
 * Tags the runtime value categories that can be written with the generic `type` serializer.
 */
var ObjectType;
(function (ObjectType) {
    /** UTF-16 string value. */
    ObjectType[ObjectType["String"] = 0] = "String";
    /** Numeric value serialized as a floating-point number. */
    ObjectType[ObjectType["Number"] = 1] = "Number";
    /** Boolean value. */
    ObjectType[ObjectType["Boolean"] = 2] = "Boolean";
    /** Runtime-typed array value. */
    ObjectType[ObjectType["Array"] = 3] = "Array";
    /** Plain object or structured record. */
    ObjectType[ObjectType["Object"] = 4] = "Object";
    /** Class instance serialized via object semantics. */
    ObjectType[ObjectType["Class"] = 5] = "Class";
    /** Nested bus message. */
    ObjectType[ObjectType["BusMessage"] = 6] = "BusMessage";
    /** Map collection. */
    ObjectType[ObjectType["Map"] = 7] = "Map";
    /** Set collection. */
    ObjectType[ObjectType["Set"] = 8] = "Set";
    /** Raw binary payload. */
    ObjectType[ObjectType["ArrayBuffer"] = 9] = "ArrayBuffer";
    /** Explicit undefined marker. */
    ObjectType[ObjectType["Undefined"] = 10] = "Undefined";
})(ObjectType || (ObjectType = {}));
/**
 * Shared base for binary readers and writers used throughout FlaVR serialization.
 */
class ByteStream {
    /**
     * Current read or write cursor within the backing buffer.
     */
    offset = 0;
    /**
     * Data view used for raw numeric access.
     */
    view;
    /**
     * @param buffer Backing buffer that will be read from or written to.
     */
    constructor(buffer) {
        this.view = new DataView(buffer);
    }
}

/**
 * Deserializes typed values from a binary byte stream.
 */
class ByteStreamReader extends ByteStream {
    isReading = true;
    getUsedByteLength() {
        return this.offset;
    }
    getUnreadBytes() {
        return this.view.byteLength - this.offset;
    }
    /**
     * Returns the unread remainder of the backing buffer as a sliced `ArrayBuffer`.
     */
    getUnreadArrayBuffer() {
        return this.view.buffer.slice(this.offset);
    }
    /**
     * Reads a raw signed 8-bit integer and advances the cursor.
     */
    getInt8() {
        const value = this.view.getInt8(this.offset);
        this.offset += Int8Array.BYTES_PER_ELEMENT;
        return value;
    }
    /**
     * Reads a raw unsigned 8-bit integer and advances the cursor.
     */
    getUint8() {
        const value = this.view.getUint8(this.offset);
        this.offset += Uint8Array.BYTES_PER_ELEMENT;
        return value;
    }
    /**
     * Reads a raw signed 16-bit integer and advances the cursor.
     */
    getInt16() {
        const value = this.view.getInt16(this.offset);
        this.offset += Int16Array.BYTES_PER_ELEMENT;
        return value;
    }
    /**
     * Reads a raw unsigned 16-bit integer and advances the cursor.
     */
    getUint16() {
        const value = this.view.getUint16(this.offset);
        this.offset += Uint16Array.BYTES_PER_ELEMENT;
        return value;
    }
    /**
     * Reads a raw signed 32-bit integer and advances the cursor.
     */
    getInt32() {
        const value = this.view.getInt32(this.offset);
        this.offset += Int32Array.BYTES_PER_ELEMENT;
        return value;
    }
    /**
     * Reads a raw unsigned 32-bit integer and advances the cursor.
     */
    getUint32() {
        const value = this.view.getUint32(this.offset);
        this.offset += Uint32Array.BYTES_PER_ELEMENT;
        return value;
    }
    /**
     * Reads a raw 32-bit float and advances the cursor.
     */
    getFloat32() {
        const value = this.view.getFloat32(this.offset);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        return value;
    }
    /**
     * Reads a raw 64-bit float and advances the cursor.
     */
    getFloat64() {
        const value = this.view.getFloat64(this.offset);
        this.offset += Float64Array.BYTES_PER_ELEMENT;
        return value;
    }
    /**
     * Reads a length-prefixed UTF-16 string.
     */
    getUtf16String() {
        const strLength = this.view.getUint32(this.offset);
        this.offset += Uint32Array.BYTES_PER_ELEMENT;
        let str = "";
        for (let i = 0; i < strLength; ++i) {
            str += String.fromCharCode(this.view.getUint16(this.offset, true));
            this.offset += Uint16Array.BYTES_PER_ELEMENT;
        }
        return str;
    }
    /**
     * Reads a registered message or facet type hash and advances the cursor.
     */
    getTypeHash() {
        const value = this.view.getInt32(this.offset);
        this.offset += TYPE_HASH_BYTES_PER_ELEMENT;
        return value;
    }
    uid(value) {
        value = this.view.getInt32(this.offset);
        this.offset += UID_BYTES_PER_ELEMENT;
        return value;
    }
    uidSafe(value) {
        if (!this.boolean(false)) {
            this.offset += UID_BYTES_PER_ELEMENT;
            return;
        }
        value = this.uid(0);
        return value;
    }
    typeHash(value) {
        value = this.view.getInt32(this.offset);
        this.offset += TYPE_HASH_BYTES_PER_ELEMENT;
        return value;
    }
    boolean(value) {
        value = !!this.view.getUint8(this.offset);
        this.offset += Uint8Array.BYTES_PER_ELEMENT;
        return value;
    }
    booleanSafe() {
        const state = this.getUint8();
        switch (state) {
            case 0:
                return false;
            case 1:
                return true;
            default:
                return undefined;
        }
    }
    int8(value) {
        value = this.view.getInt8(this.offset);
        this.offset += Int8Array.BYTES_PER_ELEMENT;
        return value;
    }
    int8Safe(value) {
        value = this.view.getInt8(this.offset);
        this.offset += Int8Array.BYTES_PER_ELEMENT;
        if (value !== INT8_MIN_VALUE)
            return value;
    }
    uint8(value) {
        value = this.view.getUint8(this.offset);
        this.offset += Uint8Array.BYTES_PER_ELEMENT;
        return value;
    }
    uint8Safe(value) {
        value = this.view.getUint8(this.offset);
        this.offset += Uint8Array.BYTES_PER_ELEMENT;
        if (value !== UINT8_MAX_VALUE)
            return value;
    }
    enum(value) {
        return this.uint8(value);
    }
    enumSafe(value) {
        return this.uint8Safe(value);
    }
    int16(value) {
        value = this.view.getInt16(this.offset);
        this.offset += Int16Array.BYTES_PER_ELEMENT;
        return value;
    }
    uint16(value) {
        value = this.view.getUint16(this.offset);
        this.offset += Uint16Array.BYTES_PER_ELEMENT;
        return value;
    }
    uint16Safe(value) {
        value = this.view.getUint16(this.offset);
        this.offset += Uint16Array.BYTES_PER_ELEMENT;
        if (value !== UINT16_MAX_VALUE)
            return value;
    }
    int32(value) {
        value = this.view.getInt32(this.offset);
        this.offset += Int32Array.BYTES_PER_ELEMENT;
        return value;
    }
    uint32(value) {
        value = this.view.getUint32(this.offset);
        this.offset += Uint32Array.BYTES_PER_ELEMENT;
        return value;
    }
    uint32Safe(value) {
        value = this.view.getUint32(this.offset);
        this.offset += Uint32Array.BYTES_PER_ELEMENT;
        if (value !== UINT32_MAX_VALUE)
            return value;
    }
    float32(value) {
        value = this.view.getFloat32(this.offset);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        return value;
    }
    float32Safe(value) {
        value = this.view.getFloat32(this.offset);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        if (!isNaN(value))
            return value;
    }
    float64(value) {
        value = this.view.getFloat64(this.offset);
        this.offset += Float64Array.BYTES_PER_ELEMENT;
        return value;
    }
    float64Safe(value) {
        value = this.view.getFloat64(this.offset);
        this.offset += Float64Array.BYTES_PER_ELEMENT;
        if (!isNaN(value))
            return value;
    }
    number2(value) {
        if (!value)
            value = [0, 0];
        value[0] = this.view.getFloat32(this.offset);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        value[1] = this.view.getFloat32(this.offset);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        return value;
    }
    number3(value) {
        if (!value)
            value = [0, 0, 0];
        value[0] = this.view.getFloat32(this.offset);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        value[1] = this.view.getFloat32(this.offset);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        value[2] = this.view.getFloat32(this.offset);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        return value;
    }
    number3Safe(value) {
        value = this.number3(value || [0, 0, 0]);
        if (!isNaN(value[0]))
            return value;
    }
    number4(value) {
        if (!value)
            value = [0, 0, 0, 0];
        value[0] = this.view.getFloat32(this.offset);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        value[1] = this.view.getFloat32(this.offset);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        value[2] = this.view.getFloat32(this.offset);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        value[3] = this.view.getFloat32(this.offset);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        return value;
    }
    number4Safe(value) {
        value = this.number4(value || [0, 0, 0, 1]);
        if (!isNaN(value[0]))
            return value;
    }
    number16(value) {
        if (!value)
            value = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        for (let i = 0; i < 16; ++i) {
            value[i] = this.view.getFloat32(this.offset);
            this.offset += Float32Array.BYTES_PER_ELEMENT;
        }
        return value;
    }
    number16Safe(value) {
        value = this.number16(value || [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
        return !isNaN(value[0]) ? value : undefined;
    }
    numberFloat32Array(value, length) {
        if (!value)
            value = [];
        value.length = length;
        for (let i = 0; i < length; ++i) {
            value[i] = this.float32(value[i]);
        }
        return value;
    }
    numberFloat32ArraySafe(length, value) {
        value = this.numberFloat32Array(value || [], length);
        if (!isNaN(value[0]))
            return value;
    }
    utf16String(str) {
        str = this.getUtf16String();
        return str;
    }
    utf16StringSafe(_str) {
        const strLength = this.view.getUint32(this.offset);
        if (strLength !== UINT32_MAX_VALUE)
            return this.getUtf16String();
        this.offset += Uint32Array.BYTES_PER_ELEMENT;
    }
    stringArray(array) {
        const arrLeng = this.getUint32();
        array = [];
        for (let i = 0; i < arrLeng; i++) {
            array.push(this.getUtf16String());
        }
        return array;
    }
    stringArraySafe(array) {
        const arrLeng = this.getUint32();
        if (isNaN(arrLeng)) {
            return undefined;
        }
        array = [];
        for (let i = 0; i < arrLeng; i++) {
            array.push(this.getUtf16String());
        }
        return array;
    }
    localizationString(localization) {
        const paramsLength = this.getUint8();
        if (paramsLength == UINT8_MAX_VALUE - 1) {
            // localization is from type string
            localization = this.getUtf16String();
        }
        else {
            const key = this.getUtf16String();
            let params;
            if (paramsLength !== 0) {
                params = {};
                for (let i = 0; i < paramsLength; ++i) {
                    const key = this.getUtf16String();
                    const value = this.getUtf16String();
                    params[key] = value;
                }
            }
            localization = { key, params };
        }
        return localization;
    }
    localizationStringSafe(localization) {
        const paramsLength = this.view.getUint8(this.offset);
        if (paramsLength === UINT8_MAX_VALUE) {
            this.offset += Uint8Array.BYTES_PER_ELEMENT;
            return undefined;
        }
        localization = this.localizationString(localization || "");
        return localization;
    }
    /**
     * Instantiates a new message via {@link MessageFactory} based on the stream's type hash and deserializes into it (the passed-in `msg` is not reused).
     */
    busMessage(msg) {
        const typeHash = this.getTypeHash();
        msg = MessageFactory.create(typeHash);
        if (!msg.serialization)
            throw new Error(`serialization is not defined in msg ${msg.type}`);
        msg.serialization(this);
        return msg;
    }
    busMessages(messages) {
        const length = this.getUint32();
        if (!messages)
            messages = [];
        messages.length = length;
        for (let i = 0; i < length; ++i) {
            const typeHash = this.getTypeHash();
            const msg = MessageFactory.create(typeHash);
            if (msg.serialization) {
                msg.serialization(this);
            }
            messages[i] = msg;
        }
        return messages;
    }
    busMessagesSafe(messages) {
        const length = this.view.getUint32(this.offset);
        if (length === UINT32_MAX_VALUE) {
            this.offset += Uint32Array.BYTES_PER_ELEMENT;
            return;
        }
        messages = this.busMessages(messages || []);
        return messages;
    }
    array(arr, typeCheck = false) {
        const length = this.getUint32();
        if (!arr)
            arr = [];
        arr.length = length;
        for (let i = 0; i < length; ++i) {
            let value = undefined;
            arr[i] = this.type(value, typeCheck);
        }
        return arr;
    }
    arraySafe(arr) {
        const propertyCount = this.view.getUint32(this.offset);
        if (propertyCount === UINT32_MAX_VALUE) {
            this.offset += Uint32Array.BYTES_PER_ELEMENT;
            return;
        }
        arr = this.array(arr || []);
        return arr;
    }
    /**
     * Reads a packed float array as a zero-copy `Float32Array` view over the backing buffer, rather than copying values into a new array.
     */
    fastFloat32Array(arr) {
        const byteLength = this.getUint32();
        // byteOffset must be a multiple of 4
        const arrayView = new Float32Array(this.view.buffer, this.offset, byteLength);
        arr = arrayView;
        this.offset += Float32Array.BYTES_PER_ELEMENT * byteLength;
        return arr;
    }
    map(map, typeCheck = false) {
        const size = this.getUint32();
        if (map)
            map.clear();
        else
            map = new Map();
        for (let i = 0; i < size; ++i) {
            let key = undefined;
            key = this.type(key, typeCheck);
            let value = undefined;
            value = this.type(value, typeCheck);
            map.set(key, value);
        }
        return map;
    }
    mapSafe(map) {
        const propertyCount = this.view.getUint32(this.offset);
        if (propertyCount === UINT32_MAX_VALUE) {
            this.offset += Uint32Array.BYTES_PER_ELEMENT;
            return;
        }
        map = this.map(map || new Map());
        return map;
    }
    set(set, typeCheck = false) {
        const size = this.getUint32();
        if (set)
            set.clear();
        else
            set = new Set();
        for (let i = 0; i < size; ++i) {
            let value = undefined;
            value = this.type(value, typeCheck);
            set.add(value);
        }
        return set;
    }
    setSafe(set) {
        const propertyCount = this.view.getUint32(this.offset);
        if (propertyCount === UINT32_MAX_VALUE) {
            this.offset += Uint32Array.BYTES_PER_ELEMENT;
            return;
        }
        set = this.set(set || new Set());
        return set;
    }
    class(c) {
        if (!c)
            c = {};
        // Class not yet supported
        c = this.object(c);
        return c;
    }
    object(obj) {
        const propertyCount = this.getUint32();
        if (!obj)
            obj = {};
        for (let i = 0; i < propertyCount; ++i) {
            const key = this.getUtf16String();
            let value = undefined;
            value = this.type(value);
            obj[key] = value;
        }
        return obj;
    }
    objectSafe(obj) {
        const propertyCount = this.view.getUint32(this.offset);
        if (propertyCount === UINT32_MAX_VALUE) {
            this.offset += Uint32Array.BYTES_PER_ELEMENT;
            return;
        }
        obj = this.object(obj ?? {});
        return obj;
    }
    arrayBuffer(buffer) {
        const byteLength = this.getUint32();
        const data = new Uint8Array(byteLength);
        for (let i = 0; i < byteLength; ++i) {
            data[i] = this.view.getUint8(this.offset);
            this.offset += Uint8Array.BYTES_PER_ELEMENT;
        }
        buffer = data.buffer;
        return buffer;
    }
    type(value, typeCheck = false) {
        const type = this.getUint8();
        switch (type) {
            case ObjectType.Number:
                if (typeCheck && typeof value !== "number") {
                    throw new Error("not number");
                }
                value = this.getFloat64();
                break;
            case ObjectType.String:
                if (typeCheck && typeof value !== "string") {
                    throw new Error("not string");
                }
                value = this.getUtf16String();
                break;
            case ObjectType.Boolean:
                if (typeCheck && typeof value !== "boolean") {
                    throw new Error("not boolean");
                }
                value = !!this.getUint8();
                break;
            case ObjectType.Undefined:
                value = undefined;
                break;
            case ObjectType.Array:
                value = this.array([], typeCheck);
                break;
            case ObjectType.BusMessage:
                value = this.busMessage(value);
                break;
            case ObjectType.Map:
                value = this.map(new Map(), typeCheck);
                break;
            case ObjectType.Set:
                value = this.set(new Set(), typeCheck);
                break;
            case ObjectType.ArrayBuffer:
                value = this.arrayBuffer(value);
                break;
            case ObjectType.Class:
                value = this.class({});
                break;
            case ObjectType.Object:
                value = this.object({});
                break;
            default:
                console.error(`[Common] ByteStreamReader: Type ${type} is not supported`);
                break;
        }
        return value;
    }
    knownType(descriptor, value) {
        switch (descriptor.type) {
            case PropertyType.Uid:
                return this.uid(value);
            case PropertyType.UidSafe:
                return this.uidSafe(value);
            case PropertyType.Boolean:
                return this.boolean(value);
            case PropertyType.Int8:
                return this.int8(value);
            case PropertyType.Uint8:
                return this.uint8(value);
            case PropertyType.Uint8Safe:
                return this.uint8Safe(value);
            case PropertyType.Int16:
                return this.int16(value);
            case PropertyType.Uint16:
                return this.uint16(value);
            case PropertyType.Uint16Safe:
                return this.uint16Safe(value);
            case PropertyType.Int32:
                return this.int32(value);
            case PropertyType.Uint32:
                return this.uint32(value);
            case PropertyType.Uint32Safe:
                return this.uint32Safe(value);
            case PropertyType.Float32:
                return this.float32(value);
            case PropertyType.Float32Safe:
                return this.float32Safe(value);
            case PropertyType.Float64:
                return this.float64(value);
            case PropertyType.Float64Safe:
                return this.float64Safe(value);
            case PropertyType.Utf16String:
                return this.utf16String(value);
            case PropertyType.Utf16StringSafe:
                return this.utf16StringSafe(value);
            case PropertyType.BusMessage:
                return this.busMessage(value);
            case PropertyType.Array:
            case PropertyType.ArraySafe:
            case PropertyType.FixedSizeArray: {
                let arrayLength;
                if (descriptor.type === PropertyType.FixedSizeArray) {
                    arrayLength = descriptor.length;
                }
                else {
                    arrayLength = this.getUint32();
                }
                if (arrayLength == 0)
                    return descriptor.type === PropertyType.ArraySafe ? undefined : [];
                if (value === undefined)
                    value = [];
                if (!Array.isArray(value))
                    throw new Error(`Value is not an Array`);
                for (let i = 0; i < arrayLength; ++i) {
                    value[i] = this.knownType(descriptor.elementType, value[i]);
                }
                return value;
            }
            case PropertyType.Dictionary: {
                const numEntries = this.getUint32();
                if (value === undefined)
                    value = {};
                if (typeof value !== "object")
                    throw new Error(`Value is not an Object`);
                for (let i = 0; i < numEntries; ++i) {
                    const entryName = this.getUtf16String();
                    value[entryName] = this.knownType(descriptor.elementType, value[entryName]);
                }
                return value;
            }
            case PropertyType.Struct: {
                if (value === undefined)
                    value = {};
                if (typeof value !== "object")
                    throw new Error(`Value is not an Object`);
                for (const propertyName in descriptor.properties) {
                    value[propertyName] = this.knownType(descriptor.properties[propertyName], value[propertyName]);
                }
                return value;
            }
        }
    }
}

/**
 * Persistent playback defaults for an object's animation clips.
 * Material primitives use the settings on their common parent model.
 */
class AnimationFacet extends Facet {
    loop;
    playOnStart;
    clipName;
    clipOccurrence;
    clipEnabled;
    static typestatic = "AnimationFacet";
    static metaData = {
        export: true,
        description: "Animation playback defaults",
        category: "General"
    };
    static propTypes = {
        loop: { type: PropertyType.Boolean, default: true },
        playOnStart: { type: PropertyType.Boolean, default: true },
        clipName: { type: PropertyType.Utf16StringSafe },
        clipOccurrence: { type: PropertyType.Uint32, default: 0 },
        clipEnabled: { type: PropertyType.Boolean, default: true }
    };
    constructor(loop = true, playOnStart = true, clipName, clipOccurrence = 0, clipEnabled = true) {
        super();
        this.loop = loop;
        this.playOnStart = playOnStart;
        this.clipName = clipName;
        this.clipOccurrence = clipOccurrence;
        this.clipEnabled = clipEnabled;
    }
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT * 3 + calcStringByteLength(this.clipName) + Uint32Array.BYTES_PER_ELEMENT;
    }
    serialization(stream) {
        this.loop = stream.boolean(this.loop);
        this.playOnStart = stream.boolean(this.playOnStart);
        // Older saved AnimationFacets contain only the two playback flags.
        if (stream instanceof ByteStreamReader && stream.getUnreadBytes() === 0) {
            this.clipName = undefined;
            this.clipOccurrence = 0;
            this.clipEnabled = true;
            return;
        }
        this.clipName = stream.utf16StringSafe(this.clipName);
        this.clipOccurrence = stream.uint32(this.clipOccurrence);
        this.clipEnabled = stream.boolean(this.clipEnabled);
    }
    static is(facet) {
        return facet.type === AnimationFacet.typestatic;
    }
}
FacetFactory.register(AnimationFacet);

/**
 * Attaches a positional audio source at the model's location. `AudioSourceRenderable` loads the given `audioUrl`
 * into a three.js `PositionalAudio` node parented to the model and applies volume, reference/max distance and
 * rolloff, playing on loop once visible and in the current room.
 * @see {@link flavr-client!modules/audio/AudioSourceRenderable.AudioSourceRenderable | AudioSourceRenderable}
 */
class AudioSourceFacet extends Facet {
    audioUrl;
    params;
    static typestatic = "AudioSourceFacet";
    static metaData = {
        export: true,
        description: "Playing audio from a specified url",
        category: "General"
    };
    static propTypes = {
        audioUrl: {
            type: PropertyType.Utf16String,
            isRequired: true
        },
        params: {
            type: PropertyType.Struct,
            properties: {
                volume: { type: PropertyType.Float32Safe },
                refDistance: { type: PropertyType.Float32Safe },
                maxDistance: { type: PropertyType.Float32Safe },
                rolloffFactor: { type: PropertyType.Float32Safe }
            }
        }
    };
    static BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT * 4;
    get byteLength() {
        return calcStringByteLength(this.audioUrl) + AudioSourceFacet.BYTE_LENGTH;
    }
    constructor(
    /** URL or asset path of the sound file to play. */
    audioUrl, 
    /** Optional positional-audio settings such as volume and falloff. */
    params = {}) {
        super();
        this.audioUrl = audioUrl;
        this.params = params;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.params = {};
        }
        this.audioUrl = stream.utf16String(this.audioUrl);
        this.params.volume = stream.float32Safe(this.params.volume);
        this.params.refDistance = stream.float32Safe(this.params.refDistance);
        this.params.maxDistance = stream.float32Safe(this.params.maxDistance);
        this.params.rolloffFactor = stream.float32Safe(this.params.rolloffFactor);
    }
    static is(facet) {
        return facet.type === AudioSourceFacet.typestatic;
    }
}
FacetFactory.register(AudioSourceFacet);

/**
 * Base class for facets whose data defines a model's own visual appearance, as opposed to auxiliary facets
 * that merely attach behaviour or metadata. Each concrete subclass is matched by a
 * {@link flavr-client!modules/rendering/renderable/Renderable.Renderable | Renderable} responsible for constructing
 * and updating the corresponding three.js object for a model.
 */
class RenderableFacet extends Facet {
    /** Marker used to recognize renderable facet subclasses at runtime. */
    static isRenderabel = true;
    /**
     * Checks whether `facet`'s class is a `RenderableFacet` subclass. Unlike the `static is` guard on concrete
     * facets, this can't compare against a single `typestatic` (there are many renderable facet types), so it
     * checks for the `isRenderabel` marker on the constructor instead.
     */
    static is(facet) {
        return "isRenderabel" in facet.constructor;
    }
    /**
     * Same check as {@link RenderableFacet.is}, but for a registered facet type string instead of an instance -
     * useful when only the type is known, for example before a facet has been deserialized.
     */
    static isDerivedType(facetTypeString) {
        const typeHash = FacetTypeTable.getHash(facetTypeString);
        if (typeHash === undefined)
            return false;
        const creator = FacetFactory.getCreator(typeHash);
        return "isRenderabel" in creator;
    }
    clone(cloneContext) {
        return super.clone(cloneContext);
    }
}

/**
 * Primitive geometry presets used by creation and modification actions.
 */
var PrimitiveShape;
(function (PrimitiveShape) {
    /** Axis-aligned box primitive. */
    PrimitiveShape[PrimitiveShape["Box"] = 0] = "Box";
    /** Circular cylinder primitive. */
    PrimitiveShape[PrimitiveShape["Cylinder"] = 1] = "Cylinder";
    /** UV sphere primitive. */
    PrimitiveShape[PrimitiveShape["Sphere"] = 2] = "Sphere";
    /** Circular cone primitive. */
    PrimitiveShape[PrimitiveShape["Cone"] = 3] = "Cone";
    /** Pyramid primitive with a polygonal base tapering to a point. */
    PrimitiveShape[PrimitiveShape["Pyramid"] = 4] = "Pyramid";
    /** Prism primitive with parallel top and bottom faces. */
    PrimitiveShape[PrimitiveShape["Prism"] = 5] = "Prism";
    /** Flat plane primitive. */
    PrimitiveShape[PrimitiveShape["Plane"] = 6] = "Plane";
})(PrimitiveShape || (PrimitiveShape = {}));

/**
 * Mesh-based collision generation strategies.
 */
var CollisionMeshShapeType;
(function (CollisionMeshShapeType) {
    /** Uses the mesh geometry directly for collision. */
    CollisionMeshShapeType[CollisionMeshShapeType["MESH"] = 0] = "MESH";
    /** Builds a convex hull from the mesh. */
    CollisionMeshShapeType[CollisionMeshShapeType["HULL"] = 1] = "HULL";
    /** Builds a convex decomposition with HACD. */
    CollisionMeshShapeType[CollisionMeshShapeType["HACD"] = 2] = "HACD";
    /** Builds a convex decomposition with VHACD. */
    CollisionMeshShapeType[CollisionMeshShapeType["VHACD"] = 3] = "VHACD";
    /** Uses the mesh as a heightfield. */
    CollisionMeshShapeType[CollisionMeshShapeType["HEIGHTFIELD"] = 4] = "HEIGHTFIELD";
})(CollisionMeshShapeType || (CollisionMeshShapeType = {}));
/**
 * Checks whether a collision shape is a compound multi-element shape.
 */
function isCompoundShape(shape) {
    return Array.isArray(shape);
}
/**
 * Checks whether a collision shape is mesh-based rather than primitive-based.
 */
function isCollisionMeshShape(shape) {
    return shape?.hasOwnProperty("meshUrl");
}
/**
 * Mirrors exactly what `collisionPrimitiveAndMeshShapeSerialization` writes: a leading "isMesh" boolean, then either
 * a single shape byte (primitive) or a shape byte plus the two variable-length mesh/hull strings. Shared between the
 * top-level shape and each compound element, since both go through that same serialization function.
 */
function calcPrimitiveOrMeshShapeByteLength(shape) {
    let byteLength = BOOLEAN_BYTES_PER_ELEMENT + // isMesh
        Uint8Array.BYTES_PER_ELEMENT; // shape
    if (isCollisionMeshShape(shape)) {
        byteLength +=
            calcStringByteLength(shape.meshUrl) +
                calcStringByteLength(shape.pieceName);
    }
    return byteLength;
}
function calcCompoundElementByteLength(element) {
    let byteLength = calcPrimitiveOrMeshShapeByteLength(element.shape) +
        Float32Array.BYTES_PER_ELEMENT * 3 + // trans.pos
        Float32Array.BYTES_PER_ELEMENT * 4 + // trans.ori
        Float32Array.BYTES_PER_ELEMENT * 3 + // trans.scale
        BOOLEAN_BYTES_PER_ELEMENT; // hasUid
    if (element.uid !== undefined) {
        byteLength += UID_BYTES_PER_ELEMENT;
    }
    return byteLength;
}
/**
 * Computes the serialized byte length of `shape`: for a compound shape, the sum of each element's size (see
 * `calcCompoundElementByteLength`); otherwise the size of the single primitive or mesh shape.
 */
function calcCollisionShapeByteLength(shape) {
    let byteLength = Uint8Array.BYTES_PER_ELEMENT; // isCompound
    if (isCompoundShape(shape)) {
        byteLength += Uint16Array.BYTES_PER_ELEMENT; // shapeLength
        for (const element of shape) {
            byteLength += calcCompoundElementByteLength(element);
        }
    }
    else {
        byteLength += calcPrimitiveOrMeshShapeByteLength(shape);
    }
    return byteLength;
}
function collisionPrimitiveAndMeshShapeSerialization(stream, shape) {
    const isMesh = stream.boolean(isCollisionMeshShape(shape));
    if (isMesh && stream.isReading) {
        shape = {
            shape: 0,
            meshUrl: ""
        };
    }
    if (isCollisionMeshShape(shape)) {
        shape.shape = stream.uint8(shape.shape);
        shape.meshUrl = stream.utf16String(shape.meshUrl);
        shape.pieceName = stream.utf16StringSafe(shape.pieceName);
    }
    else {
        shape = stream.uint8(shape);
    }
    return shape;
}
/**
 * Serializes or deserializes a collision shape, including primitives, mesh shapes, and compound shapes.
 */
function collisionShapeSerialization(stream, shape) {
    const isCompound = stream.boolean(Array.isArray(shape));
    if (isCompound && stream.isReading) {
        shape = [];
    }
    if (Array.isArray(shape)) {
        const shapeLength = stream.uint16(shape.length);
        for (let i = 0; i < shapeLength; ++i) {
            let newShape;
            if (stream.isReading) {
                newShape = {
                    shape: PrimitiveShape.Box,
                    trans: {
                        pos: [0, 0, 0],
                        ori: [0, 0, 0, 1],
                        scale: [1, 1, 1]
                    }
                };
                shape.push(newShape);
            }
            else {
                newShape = shape[i];
            }
            newShape.shape = collisionPrimitiveAndMeshShapeSerialization(stream, newShape.shape);
            newShape.trans.pos = stream.number3(newShape.trans.pos);
            newShape.trans.ori = stream.number4(newShape.trans.ori);
            newShape.trans.scale = stream.number3(newShape.trans.scale);
            if (stream.boolean(newShape.uid !== undefined)) {
                newShape.uid = stream.uid(newShape.uid);
            }
        }
    }
    else {
        shape = collisionPrimitiveAndMeshShapeSerialization(stream, shape);
    }
    return shape;
}

/**
 * Describes the physics collision shape of a model. `CollisionModule` reacts to this facet by creating or
 * updating the shape in the physics `collisionWorld`, which drives selection raycasts/proximity queries
 * (`isSelectable`) and enter/leave contact events (`isTrigger`), e.g. for `DropOffAreaFacet` areas. Several
 * interaction facets (`GrabbableFacet`, `PushableFacet`, `DropOffAreaFacet`) declare this facet as a dependency.
 */
class CollisionFacet extends Facet {
    shape;
    isSelectable;
    isTrigger;
    ignoreInvisibleChildren;
    static typestatic = "CollisionFacet";
    static propTypes = {
        shape: {
            type: PropertyType.EnumUint8,
            items: enumToPropertyItems(PrimitiveShape)
        },
        isSelectable: {
            type: PropertyType.Boolean,
            default: false
        },
        isTrigger: {
            type: PropertyType.Boolean,
            default: false
        },
        ignoreInvisibleChildren: {
            type: PropertyType.Boolean,
            default: false
        },
    };
    get byteLength() {
        return (calcCollisionShapeByteLength(this.shape) +
            BOOLEAN_BYTES_PER_ELEMENT + // isSelectable
            BOOLEAN_BYTES_PER_ELEMENT + // isTrigger
            BOOLEAN_BYTES_PER_ELEMENT // ignoreInvisibleChildren
        );
    }
    constructor(
    /** Collision shape description used to build the physics representation. */
    shape = PrimitiveShape.Box, 
    /** Whether this shape should participate in selection queries/raycasts. */
    isSelectable = false, 
    /** Whether this shape should emit contact enter/leave events instead of blocking. */
    isTrigger = false, 
    /** Whether invisible child geometry should be ignored while building the shape. */
    ignoreInvisibleChildren = false) {
        super();
        this.shape = shape;
        this.isSelectable = isSelectable;
        this.isTrigger = isTrigger;
        this.ignoreInvisibleChildren = ignoreInvisibleChildren;
    }
    serialization(stream) {
        this.shape = collisionShapeSerialization(stream, this.shape);
        this.isSelectable = stream.boolean(this.isSelectable);
        this.isTrigger = stream.boolean(this.isTrigger);
        this.ignoreInvisibleChildren = stream.boolean(this.ignoreInvisibleChildren);
    }
    static is(facet) {
        return facet.type === CollisionFacet.typestatic;
    }
    /**
     * True when `shape` is a plain `PrimitiveShape` enum value - determined by exclusion, since primitive shapes
     * have no discriminator of their own (unlike compound and mesh shapes).
     */
    static isPrimitiveShape(shape) {
        return !isCompoundShape(shape) && !isCollisionMeshShape(shape);
    }
    /**
     * True when `shape` is a compound shape, i.e. an array of `CompoundElement`s rather than a single primitive or
     * mesh shape.
     */
    static isCompoundShape(shape) {
        return isCompoundShape(shape);
    }
    /**
     * True when `shape` is a `CollisionMeshShape` object (identified by having a `meshUrl`) rather than a
     * primitive or compound shape.
     */
    static isMeshShape(shape) {
        return isCollisionMeshShape(shape);
    }
}
FacetFactory.register(CollisionFacet);

/**
 * Overrides the local transform (relative to the model) of an already created collision shape, e.g. so a
 * collision volume can be offset or resized independently of the model's own transform. `CollisionModule`
 * applies it to the physics shape in `collisionWorld`; if the shape hasn't been created yet, the facet is
 * queued and applied once the corresponding `CollisionFacet` has been resolved.
 */
class CollisionShapeLocalTransformFacet extends Facet {
    localTransform;
    static typestatic = "CollisionShapeLocalTransformFacet";
    static propTypes = {
        localTransform: {
            type: PropertyType.Number16
        },
    };
    get byteLength() {
        return NUMBER16_BYTES_PER_ELEMENT;
    }
    constructor(
    /** Local transform matrix applied to the collision shape relative to the model. */
    localTransform) {
        super();
        this.localTransform = localTransform;
    }
    serialization(stream) {
        this.localTransform = stream.number16(this.localTransform);
    }
    static is(facet) {
        return facet.type === CollisionShapeLocalTransformFacet.typestatic;
    }
}
FacetFactory.register(CollisionShapeLocalTransformFacet);

/**
 * This string can be used as the 'attrib' parameter for a ToggleButton in a ContextMenu,
 * to automatically determining the state of the ToggleButton based on whether the
 * TransformHandles are enabled (for the model associated with the ContextMenu).
 * @see EnableModelTransformHandlesAction
 */
const TRANSFORM_HANDLES_ENABLED_ATTRIBUTE = "TransformHandlesEnabled";
/**
 * This action is called when the user clicks any of the Transform Mode Controls
 * @see EnableModelTransformHandlesAction
 */
class SwitchTransformModeAction {
    mode;
    constructor(
    /** Transform-handle mode or coordinate-space toggle to activate. */
    mode) {
        this.mode = mode;
    }
    static typestatic = "SwitchTransformModeAction";
    action = SwitchTransformModeAction.typestatic;
    static is(params) {
        return params.action === SwitchTransformModeAction.typestatic;
    }
}
/**
 * Enables/Disables TransformHandles for the target model of this action.
 * TransformHandles allow users to move, rotate and scale a model.
 * There can only be one model with enabled TransformHandles at a time.
 * Enabling the TransformHandles for one model will disable the
 * TransformHandles for other models.
 */
class EnableModelTransformHandlesAction {
    enable;
    scale;
    scalable;
    rotatable;
    /**
     * @param enable (note: the code when set to "false" seems odd to me. double check if you plan to set this to false.)
     * @param scale Some meshes might be pre-scaled on the instance level. In that case any bounding box based on the original mesh-data will need to be scaled.
     * @param scalable if set to true, the user can scale the object
     * @param rotatable if set to true, the user can rotate the object
     */
    constructor(enable, 
    /** Extra scale factor applied when computing the handle bounds. */
    scale = 1, scalable = true, rotatable = true) {
        this.enable = enable;
        this.scale = scale;
        this.scalable = scalable;
        this.rotatable = rotatable;
    }
    static typestatic = "EnableModelTransformHandlesAction";
    action = EnableModelTransformHandlesAction.typestatic;
    static is(params) {
        return params.action === EnableModelTransformHandlesAction.typestatic;
    }
}
/**
 * This string can be used as the 'attrib' paramater for a ToggleButton in a ContextMenu,
 * to automatically determin the state of the ToggleButton based on whether the
 * SnapToGrid is a enabled for the model associated with the ContextMenu.
 * @see EnableSnapToGridAction
 */
const SNAP_TO_GRID_ENABLED_ATTRIBUTE = "SnapToGridEnabled";
/**
 * Enables/Disables SnapToGrid for TransformHandles of a model.
 * @see EnableModelTransformHandlesAction
 * When SnapToGrid is enabled models can only be moved/rotated/scaled via TransformHandles
 * in fixed steps.
 * @see SetSnapToGridParameterAction
 */
class EnableSnapToGridAction {
    enable;
    constructor(
    /** Whether snap-to-grid should be enabled for the active transform handles. */
    enable) {
        this.enable = enable;
    }
    static typestatic = "EnableSnapToGridAction";
    action = EnableSnapToGridAction.typestatic;
    static is(params) {
        return params.action === EnableSnapToGridAction.typestatic;
    }
}
/**
 * Identifies which snap-to-grid parameter should be changed.
 */
var SnapToGridParm;
(function (SnapToGridParm) {
    SnapToGridParm[SnapToGridParm["GridSize"] = 0] = "GridSize";
    SnapToGridParm[SnapToGridParm["SnapAngle"] = 1] = "SnapAngle";
    SnapToGridParm[SnapToGridParm["ScaleStepSize"] = 2] = "ScaleStepSize";
})(SnapToGridParm || (SnapToGridParm = {}));
/**
 * Sets the grid parameters for TransformHandles.
 * These parameters only affect the TransformHandles if SnapToGrid is enabled.
 * @see EnableSnapToGridAction
 * @param param which parameter should be set. Can be GridSize, SnapAngle, ScaleStepSize:
 *  - GridSize: model can only be move in fixed steps according to GridSize.
 *    Model can be moved freely if GridSize is set to 0;
 *  - SnapAngle: models can only be rotated by multiples of the SnapAngle.
 *    Model can be rotated freely if GridSize is set to 0;
 *  - ScaleStepSize: models can only be scaled to a multiple of ScaleStepSize.
 *    Model can be scaled freely if GridSize is set to 0;
 * @param value the value for the parameter.
 */
class SetSnapToGridParameterAction {
    param;
    value;
    constructor(param, value) {
        this.param = param;
        this.value = value;
    }
    static typestatic = "SetSnapToGridParameterAction";
    action = SetSnapToGridParameterAction.typestatic;
    static is(params) {
        return params.action === SetSnapToGridParameterAction.typestatic;
    }
}

/**
 * Action that changes which primitive shape newly created models should use.
 */
class SetShapeForNewModels {
    shape;
    constructor(shape) {
        this.shape = shape;
    }
    static typestatic = "SetShapeForNewModels";
    action = SetShapeForNewModels.typestatic;
    static is(params) {
        return params.action === SetShapeForNewModels.typestatic;
    }
}
/**
 * Action that changes which color newly created models should use.
 */
class SetColorForNewModels {
    color;
    constructor(color) {
        this.color = color;
    }
    static typestatic = "SetColorForNewModels";
    action = SetColorForNewModels.typestatic;
    static is(params) {
        return params.action === SetColorForNewModels.typestatic;
    }
}
/**
 * Metadata key used to store whether the create-model flow should create rooms instead of plain models.
 */
const NEW_MODELS_ARE_ROOMS_ATTRIBUTE = "NewModelsAreRooms";
/**
 * This Action can be used to set wether the CreateModelAction should
 * create normal models or rooms (and which layouter they should use)
 */
class SetNewModelsAreRooms {
    layouterParams;
    /**
     * @param layouterParams the layouter parametes that sould be used
     * by rooms created through the CreateModelAction or undefined if
     * CreateModelAction should create models that aren't rooms.
     */
    constructor(layouterParams) {
        this.layouterParams = layouterParams;
    }
    static typestatic = "SetNewModelsAreRooms";
    action = SetNewModelsAreRooms.typestatic;
    static is(params) {
        return params.action === SetNewModelsAreRooms.typestatic;
    }
}
/**
 * Action that creates a new model below a given parent and opens it in the edit flow.
 */
class CreateModelAction {
    prefixName;
    editModelMenuUid;
    parentUid;
    constructor(
    /** Base name used when generating the new model. */
    prefixName, 
    /** UID of the edit-model menu/context associated with the creation flow. */
    editModelMenuUid, 
    /** UID of the model that should receive the newly created child. */
    parentUid) {
        this.prefixName = prefixName;
        this.editModelMenuUid = editModelMenuUid;
        this.parentUid = parentUid;
    }
    static typestatic = "CreateModelAction";
    action = CreateModelAction.typestatic;
    static is(params) {
        return params.action === CreateModelAction.typestatic;
    }
}
/**
 * Action that applies the most recently recorded label text to the current model.
 */
class ApplyRecordedLabelTextToModelAction {
    constructor() { }
    static typestatic = "ApplyRecordeLabelTextToModelAction";
    action = ApplyRecordedLabelTextToModelAction.typestatic;
    static is(params) {
        return params.action === ApplyRecordedLabelTextToModelAction.typestatic;
    }
}

/**
 * Action that creates a new label at the current interaction target.
 */
class CreateLabelAction {
    constructor() { }
    static typestatic = "CreateLabelModifierInteraction";
    action = CreateLabelAction.typestatic;
    static is(params) {
        return params.action === CreateLabelAction.typestatic;
    }
}

/**
 * Scatterplot channels that can be remapped to another data column.
 */
var MappingTarget;
(function (MappingTarget) {
    /** Remaps the scatterplot's X axis. */
    MappingTarget[MappingTarget["XAxis"] = 0] = "XAxis";
    /** Remaps the scatterplot's Y axis. */
    MappingTarget[MappingTarget["YAxis"] = 1] = "YAxis";
    /** Remaps the scatterplot's Z axis. */
    MappingTarget[MappingTarget["ZAxis"] = 2] = "ZAxis";
    /** Remaps the scatterplot's scale channel. */
    MappingTarget[MappingTarget["Scale"] = 3] = "Scale";
    /** Remaps the scatterplot's color channel. */
    MappingTarget[MappingTarget["Color"] = 4] = "Color";
})(MappingTarget || (MappingTarget = {}));
/**
 * Action that remaps one visual channel of a scatterplot to a different data index.
 */
class ChangeScatterplotDataMappingAction {
    scatterplotRootUid;
    target;
    valueIndex;
    constructor(
    /** UID of the scatterplot root model to update. */
    scatterplotRootUid, 
    /** Visual channel that should be remapped. */
    target, 
    /** Optional source data column index for the selected channel. */
    valueIndex) {
        this.scatterplotRootUid = scatterplotRootUid;
        this.target = target;
        this.valueIndex = valueIndex;
    }
    static typestatic = "ChangeScatterplotDataMappingAction";
    action = ChangeScatterplotDataMappingAction.typestatic;
    static is(params) {
        return params.action === ChangeScatterplotDataMappingAction.typestatic;
    }
}

/**
 * Action that begins an interactive line-connection creation flow.
 */
class StartLineConnectionAction {
    color;
    constructor(
    /** Optional initial color for the line connection being created. */
    color) {
        this.color = color;
    }
    static typestatic = "StartLineConnectionAction";
    action = StartLineConnectionAction.typestatic;
    static is(action) {
        return action.action === StartLineConnectionAction.typestatic;
    }
}
/**
 * Action that finishes the currently active line-connection creation flow.
 */
class EndLineConnectionAction {
    constructor() { }
    static typestatic = "EndLineConnectionAction";
    action = EndLineConnectionAction.typestatic;
    static is(action) {
        return action.action === EndLineConnectionAction.typestatic;
    }
}
/**
 * Action that updates the default color used for subsequently created line connections.
 */
class SetDefaultLineColorAction {
    color;
    constructor(
    /** Default color to use for future line connections. */
    color) {
        this.color = color;
    }
    static typestatic = "SetDefaultLineColorAction";
    action = SetDefaultLineColorAction.typestatic;
    static is(action) {
        return action.action === SetDefaultLineColorAction.typestatic;
    }
}

/**
 * Action that moves the user toward the current interaction hit point.
 */
class FlyToHitpointAction {
    offset;
    velocity;
    constructor(
    /** Local-space offset applied relative to the hit point after moving. */
    offset, 
    /** Optional travel speed override for the fly-to animation. */
    velocity) {
        this.offset = offset;
        this.velocity = velocity;
    }
    static typestatic = "FlyToHitpoint";
    action = FlyToHitpointAction.typestatic;
    static is(parms) {
        return parms.action === FlyToHitpointAction.typestatic;
    }
}

/**
 * Action that changes the target model's color.
 */
class ChangeModelColorAction {
    color;
    constructor(color) {
        this.color = color;
    }
    static typestatic = "ChangeModelColor";
    action = ChangeModelColorAction.typestatic;
    static is(action) {
        return action.action === ChangeModelColorAction.typestatic;
    }
}
/**
 * Action that changes the target model's primitive shape.
 */
class ChangeModelShapeAction {
    shape;
    constructor(shape) {
        this.shape = shape;
    }
    static typestatic = "ChangeModelShapeAction";
    action = ChangeModelShapeAction.typestatic;
    static is(action) {
        return action.action === ChangeModelShapeAction.typestatic;
    }
}
/**
 * Action that changes the target model's material settings.
 */
class ChangeModelMaterialAction {
    material;
    constructor(material) {
        this.material = material;
    }
    static typestatic = "ChangeModelMaterialAction";
    action = ChangeModelMaterialAction.typestatic;
    static is(action) {
        return action.action === ChangeModelMaterialAction.typestatic;
    }
}
/**
 * Action that deletes the target model.
 */
class DeleteModelAction {
    static typestatic = "DeleteModelAction";
    action = DeleteModelAction.typestatic;
    static is(action) {
        return action.action === DeleteModelAction.typestatic;
    }
}

/**
 * Action that opens the context menu for the current interaction target.
 */
class OpenContextMenuAction {
    constructor() { }
    static typestatic = "OpenContextMenuAction";
    action = OpenContextMenuAction.typestatic;
    static is(parms) {
        return parms.action === OpenContextMenuAction.typestatic;
    }
}

/**
 * This action can be used to send messaged when it is trigger.
 * E.g. when a model is clicked.
 */
class SendMessagesAction {
    messages;
    description;
    constructor(
    /** Messages that should be published when the action fires. */
    messages, 
    /** Optional human-readable description for editors or UI. */
    description) {
        this.messages = messages;
        this.description = description;
    }
    static typestatic = "SendMessages";
    action = SendMessagesAction.typestatic;
    static is(params) {
        return params.action === SendMessagesAction.typestatic;
    }
}
/**
 * Conditions that gate whether a conditional message action may fire.
 */
var SendMessageCondition;
(function (SendMessageCondition) {
    SendMessageCondition[SendMessageCondition["IsOperator"] = 0] = "IsOperator";
})(SendMessageCondition || (SendMessageCondition = {}));
/**
 * This action can be used to send messaged when it is trigger and the specivied condition is met.
 * E.g. when a model is clicked.
 */
class ConditionalSendMessagesAction {
    condition;
    messages;
    constructor(
    /** Condition that must be satisfied before the messages are sent. */
    condition, 
    /** Messages that should be published when the condition is met. */
    messages) {
        this.condition = condition;
        this.messages = messages;
    }
    static typestatic = "ConditionalSendMessagesAction";
    action = ConditionalSendMessagesAction.typestatic;
    static is(params) {
        return params.action === ConditionalSendMessagesAction.typestatic;
    }
}
/**
 * This action will send the specivied messages after the specifed delay when it is trigger.
 * E.g. if a model with SendDelayedMessagesAction([foo], 1000) as its onClickAction is clicked
 * then the message 'foo' will be send 1000 millisecond after the model has been clicked.
 * @param messages the messages that should be send
 * @param delay the dely in milliseconds after which the messages should be send
 */
class SendDelayedMessagesAction {
    messages;
    delay;
    constructor(
    /** Messages that should be sent after the delay elapses. */
    messages, 
    /** Delay in milliseconds before the messages are published. */
    delay) {
        this.messages = messages;
        this.delay = delay;
    }
    static typestatic = "SendDelayedMessages";
    action = SendDelayedMessagesAction.typestatic;
    static is(params) {
        return params.action === SendDelayedMessagesAction.typestatic;
    }
}
/**
 * This action will send the specivied messages after asking the user for confirmation.
 * @param messages the messages that should be send
 * @param confirmationText The text that the message to the user will contain.
 */
class SendMessagesAfterConfirmationAction {
    messages;
    confirmationText;
    constructor(
    /** Messages that should be sent after the user confirms. */
    messages, 
    /** Confirmation prompt shown to the user before sending. */
    confirmationText) {
        this.messages = messages;
        this.confirmationText = confirmationText;
    }
    static typestatic = "SendMessagesAfterConfirmation";
    action = SendMessagesAfterConfirmationAction.typestatic;
    static is(params) {
        return params.action === SendMessagesAfterConfirmationAction.typestatic;
    }
}

/**
 * Action that starts a named fade-in animation on the target model.
 */
class StartFadeInAnimationAction {
    name;
    constructor(
    /** Name of the fade-in animation preset to start. */
    name) {
        this.name = name;
    }
    static typestatic = "StartFadeInAnimation";
    action = StartFadeInAnimationAction.typestatic;
    static is(parms) {
        return parms.action === StartFadeInAnimationAction.typestatic;
    }
}

/**
 * Action that starts a named fade-out animation on the target model.
 */
class StartFadeOutAnimationAction {
    name;
    constructor(
    /** Name of the fade-out animation preset to start. */
    name) {
        this.name = name;
    }
    static typestatic = "StartFadeOutAnimation";
    action = StartFadeOutAnimationAction.typestatic;
    static is(parms) {
        return parms.action === StartFadeOutAnimationAction.typestatic;
    }
}

/**
 * Action that toggles a reference plane used for spatial comparison along an axis.
 */
class ToggleReferencePlaneAction {
    orientation;
    size;
    minPlaneDistance;
    operatorOnly;
    constructor(
    /** Orientation/axis that the reference plane should align to. */
    orientation, 
    /** Width and height of the generated reference plane. */
    size, 
    /** Minimum spacing between planes with the same orientation before toggling removes an existing one. */
    minPlaneDistance = 0.1, 
    /** Whether only operator users may toggle this reference plane. */
    operatorOnly) {
        this.orientation = orientation;
        this.size = size;
        this.minPlaneDistance = minPlaneDistance;
        this.operatorOnly = operatorOnly;
    }
    static typestatic = "ToggleReferencePlane";
    action = ToggleReferencePlaneAction.typestatic;
    static is(params) {
        return params.action === ToggleReferencePlaneAction.typestatic;
    }
}

/**
 * Action that starts a named repeating timer which triggers nested actions.
 */
class StartIntervalAction {
    name;
    milliseconds;
    actions;
    messageOnStop;
    constructor(
    /** Stable interval identifier used to update or stop the timer later. */
    name, 
    /** Repeat interval duration in milliseconds. */
    milliseconds, 
    /** Actions executed each time the interval fires. */
    actions, 
    /** Optional bus messages emitted when the interval is stopped. */
    messageOnStop) {
        this.name = name;
        this.milliseconds = milliseconds;
        this.actions = actions;
        this.messageOnStop = messageOnStop;
    }
    static typestatic = "StartIntervalAction";
    action = StartIntervalAction.typestatic;
    static is(params) {
        return params.action === StartIntervalAction.typestatic;
    }
}
/**
 * Action that stops a previously started named interval timer.
 */
class StopIntervalAction {
    name;
    constructor(
    /** Stable interval identifier of the timer that should be stopped. */
    name) {
        this.name = name;
    }
    static typestatic = "StopIntervalAction";
    action = StopIntervalAction.typestatic;
    static is(params) {
        return params.action === StopIntervalAction.typestatic;
    }
}

/**
 * Declares an intent to attach a model to a VR controller (`params.hand`), with an optional `priority` and
 * offset matrix. No client or server module currently reads this facet (only `AttachToModelFacet` is wired
 * up for reparenting) - as of this writing it appears to be unused/dead code.
 */
class AttachToControllerFacet extends Facet {
    params;
    static typestatic = "AttachToControllerFacet";
    static metaData = {
        export: true,
        description: "",
        category: "Interaction"
    };
    static propTypes = {
        params: {
            type: PropertyType.Struct,
            properties: {
                hand: { type: PropertyType.Uint8Safe },
                priority: { type: PropertyType.Uint8Safe },
                offset: { type: PropertyType.Number16Safe }
            }
        }
    };
    get byteLength() {
        return Uint8Array.BYTES_PER_ELEMENT * 2 + Float32Array.BYTES_PER_ELEMENT * 16;
    }
    constructor(
    /** Optional controller attachment settings such as hand, priority, and offset. */
    params = {}) {
        super();
        this.params = params;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.params = {};
        }
        this.params.hand = stream.uint8Safe(this.params.hand);
        this.params.priority = stream.uint8Safe(this.params.priority);
        this.params.offset = stream.number16Safe(this.params.offset);
    }
    static is(facet) {
        return facet.type === AttachToControllerFacet.typestatic;
    }
}
FacetFactory.register(AttachToControllerFacet);

// AttachToSubMeshFacet
/**
 * Declares an intent to attach a model to a named sub-object (`params.subObjectName`) of `targetUid`, with
 * an optional offset. No client or server module currently reads this facet (`AttachToModelFacet` is the
 * one actually handled for model reparenting) - as of this writing it appears to be unused/dead code.
 */
class AttachToFacet extends Facet {
    targetUid;
    params;
    static typestatic = "AttachToFacet";
    static metaData = {
        export: true,
        description: "",
        category: "Interaction"
    };
    static propTypes = {
        targetUid: {
            type: PropertyType.Uid,
            isRequired: true
        },
        params: {
            type: PropertyType.Struct,
            properties: {
                subObjectName: {
                    type: PropertyType.Utf16StringSafe
                },
                offset: {
                    type: PropertyType.Number3Safe
                }
            }
        }
    };
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT +
            Float32Array.BYTES_PER_ELEMENT * 3 + // offset
            calcStringByteLength(this.params.subObjectName));
    }
    constructor(
    /** UID of the model this object should attach to. */
    targetUid, 
    /** Optional attachment settings such as sub-object name and offset. */
    params = {}) {
        super();
        this.targetUid = targetUid;
        this.params = params;
    }
    serialization(stream) {
        this.targetUid = stream.uid(this.targetUid);
        if (stream.isReading) {
            this.params = {};
        }
        this.params.subObjectName = stream.utf16StringSafe(this.params.subObjectName);
        this.params.offset = stream.number3Safe(this.params.offset);
    }
    static is(facet) {
        return facet.type === AttachToFacet.typestatic;
    }
}
FacetFactory.register(AttachToFacet);

/**
 * Declares an intent to attach a model to the local player with an offset matrix (`params.offset`), similar
 * in shape to `AttachToControllerFacet`. No client or server module currently reads this facet - as of this
 * writing it appears to be unused/dead code.
 */
class AttachToLocalPlayerFacet extends Facet {
    params;
    static typestatic = "AttachToLocalPlayerFacet";
    static metaData = {
        export: true,
        description: "",
        category: "Interaction"
    };
    static propTypes = {
        params: {
            type: PropertyType.Struct,
            properties: {
                offset: { type: PropertyType.Number16Safe }
            }
        }
    };
    get byteLength() {
        return Float32Array.BYTES_PER_ELEMENT * 16;
    }
    constructor(
    /** Optional local-player attachment settings such as the offset matrix. */
    params = {}) {
        super();
        this.params = params;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.params = {};
        }
        this.params.offset = stream.number16Safe(this.params.offset);
    }
    static is(facet) {
        return facet.type === AttachToLocalPlayerFacet.typestatic;
    }
}
FacetFactory.register(AttachToLocalPlayerFacet);

/**
 * Reparents this model to `targetUid` at runtime. `Model` handles this facet directly (not through a
 * `@FacetHandler`): it re-parents itself to the target and, if set, applies `params.offsetMatrix` as its
 * local transform relative to `params.subObjectName` or the target's origin. `ModelPickupModule` swaps this
 * facet for `LinkToModelFacet` when a held object is placed down, and `TrainingOrchestratorModule` uses it
 * to attach training-scenario objects to their parent.
 */
class AttachToModelFacet extends Facet {
    targetUid;
    params;
    static typestatic = "AttachToModelFacet";
    static metaData = {
        export: true,
        description: "",
        category: "Interaction"
    };
    static propTypes = {
        targetUid: {
            type: PropertyType.Uid,
            isRequired: true
        },
        params: {
            type: PropertyType.Struct,
            properties: {
                offsetMatrix: { type: PropertyType.Number16Safe },
                subObjectName: { type: PropertyType.Utf16StringSafe }
            }
        }
    };
    get byteLength() {
        return UID_SAFE_BYTES_PER_ELEMENT + Float32Array.BYTES_PER_ELEMENT * 16 + calcStringByteLength(this.params.subObjectName);
    }
    constructor(
    /** UID of the model this object should become attached to. */
    targetUid, 
    /** Optional attachment settings such as sub-object name and offset matrix. */
    params = {}) {
        super();
        this.targetUid = targetUid;
        this.params = params;
    }
    serialization(stream) {
        this.targetUid = stream.uidSafe(this.targetUid);
        if (stream.isReading) {
            this.params = {};
        }
        this.params.offsetMatrix = stream.number16Safe(this.params.offsetMatrix);
        this.params.subObjectName = stream.utf16StringSafe(this.params.subObjectName);
    }
    static is(facet) {
        return facet.type === AttachToModelFacet.typestatic;
    }
}
FacetFactory.register(AttachToModelFacet);

/**
 * Makes this model follow `targetUID` (plus an optional `offsetMatrix`) without becoming its scene-graph
 * child, unlike `AttachToModelFacet`. `LinkingModule` reacts to this facet by recomputing the linked
 * model's local transform whenever the target moves, and drops the link when the facet is removed or the
 * target is deleted. It is also used by `ModelPickupModule`, `ToolBelt` and `TeleporterModule` to keep
 * picked-up or tool-belt objects, and teleporter arrows, tracking a controller.
 */
class LinkToModelFacet extends Facet {
    targetUID;
    offsetMatrix;
    static typestatic = "LinkToModelFacet";
    static metaData = {
        export: true,
        description: "Allows to link this model to a target model without physically being a child of the target model.",
        category: "Interaction"
    };
    static propTypes = {
        targetUID: {
            type: PropertyType.Uid,
            isRequired: true
        },
        offsetMatrix: {
            type: PropertyType.Number16Safe
        },
    };
    get byteLength() {
        return (UID_SAFE_BYTES_PER_ELEMENT + // targetUID
            NUMBER16_BYTES_PER_ELEMENT // offsetMatrix
        );
    }
    constructor(
    /** UID of the model this object should follow. */
    targetUID, 
    /** Optional local transform offset applied on top of the target transform. */
    offsetMatrix) {
        super();
        this.targetUID = targetUID;
        this.offsetMatrix = offsetMatrix;
    }
    serialization(stream) {
        this.targetUID = stream.uidSafe(this.targetUID);
        this.offsetMatrix = stream.number16Safe(this.offsetMatrix);
    }
    static is(facet) {
        return facet.type === LinkToModelFacet.typestatic;
    }
}
FacetFactory.register(LinkToModelFacet);

/**
 * Forwards interactions on this model to `uid` instead. `ForwardInteractionHandler` re-triggers the same
 * select/click/hover/etc. event on the target model whenever one occurs on the model carrying this behavior
 * (skipped if the two are already the same model).
 */
class ForwardInteraction {
    uid;
    constructor(
    /** UID of the model that should receive forwarded interactions. */
    uid) {
        this.uid = uid;
    }
    static typestatic = "ForwardInteraction";
    behavior = ForwardInteraction.typestatic;
    static is(params) {
        return params.behavior === ForwardInteraction.typestatic;
    }
}

/**
 * Marks a model as grabbable. Pressing the select button while it is selected makes `GrabbableInteractionHandler`
 * send a `PickupModelMessage` that picks it up and drops it again on release.
 */
class Grabbable {
    constructor() { }
    static typestatic = "Grabbable";
    behavior = Grabbable.typestatic;
    static is(params) {
        return params.behavior === Grabbable.typestatic;
    }
}

/**
 * Highlights `uids` while this model is selected. `HighlightAdditionalModelsInteractionHandler` sends an
 * `AddHighlightForModelsMessage` for `uids` on select, and a `RemoveHighlightsFromModelsMessage` on deselect.
 */
class HighlightAdditionalModels {
    uids;
    constructor(
    /** UIDs of the additional models that should be highlighted. */
    uids) {
        this.uids = uids;
    }
    static typestatic = "HighlightAdditionalModels";
    behavior = HighlightAdditionalModels.typestatic;
    static is(params) {
        return params.behavior === HighlightAdditionalModels.typestatic;
    }
}

/**
 * Renders a `LineStrip` outline through `points`, switching between the `unselected`, `selected` and
 * `highlighted` styling as `HighlightLineStripInteractionHandler` reacts to this model being selected/deselected
 * and to a select-button click, which toggles a persistent "highlighted" state for it.
 */
class HighlightLineStrip {
    points;
    unselected;
    selected;
    highlighted;
    static typestatic = "HighlightLineStrip";
    behavior = HighlightLineStrip.typestatic;
    constructor(
    /** Ordered line-strip points in local space. */
    points, 
    /** Visual settings used while the model is idle. */
    unselected, 
    /** Visual settings used while the model is selected. */
    selected, 
    /** Visual settings used while the model is highlighted. */
    highlighted) {
        this.points = points;
        this.unselected = unselected;
        this.selected = selected;
        this.highlighted = highlighted;
    }
    static is(params) {
        return params.behavior === HighlightLineStrip.typestatic;
    }
}

/**
 * Turns this model into a door into another room. `RoomInteractionHandler` loads the room on select (or waits
 * for a click first when `autoload` is false), sends `EnterRoomMessage` on click once it has loaded, and
 * unloads it again on deselect. `entry_point` is used as the room-local entry position instead of the click's
 * hit point, if set.
 */
class RoomInteraction {
    autoload;
    entry_point;
    constructor(
    /** Whether the target room should load immediately on interaction. */
    autoload, 
    /** Optional room-local entry point override used after entering. */
    entry_point) {
        this.autoload = autoload;
        this.entry_point = entry_point;
    }
    static typestatic = "RoomInteraction";
    behavior = RoomInteraction.typestatic;
    static is(params) {
        return params.behavior === RoomInteraction.typestatic;
    }
}

/**
 * Shows a model's `TooltipInfoFacet` while it is selected. Models with this behavior but without a
 * `TooltipInfoFacet` are unaffected, since `ShowTooltipsInteractionHandler` has nothing to display.
 */
class ShowTooltipsBehavior {
    static typestatic = "ShowTooltipsBehavior";
    behavior = ShowTooltipsBehavior.typestatic;
    static is(params) {
        return params.behavior === ShowTooltipsBehavior.typestatic;
    }
}

/**
 * Marker behavior intended to make interactions apply transforms in world space rather than local space. No
 * client or server module currently reacts to this behavior - as of this writing it appears to be unused/dead
 * code.
 */
class TransformWorldModifierInteraction {
    constructor() { }
    static typestatic = "TransformWorldModifierInteraction";
    behavior = TransformWorldModifierInteraction.typestatic;
    static is(params) {
        return params.behavior === TransformWorldModifierInteraction.typestatic;
    }
}

/**
 * Used on UI3D/HTML-canvas renderables so `UI3DInteractionHandler` forwards controller hover/touch events on the
 * model as `ModelHoverMessage`/`ModelTouchMessage`, which drive the underlying HTML UI element.
 */
class InputEventTrigger {
    sendHover;
    sendTouch;
    disableOrbitRotation;
    constructor(
    /** Whether hover enter/leave events should be emitted. */
    sendHover, 
    /** Whether touch/contact events should be emitted. */
    sendTouch, 
    /** Whether orbit-camera rotation should be disabled while interacting. */
    disableOrbitRotation = true) {
        this.sendHover = sendHover;
        this.sendTouch = sendTouch;
        this.disableOrbitRotation = disableOrbitRotation;
    }
    static typestatic = "InputEventTrigger";
    behavior = InputEventTrigger.typestatic;
    static is(params) {
        return params.behavior === InputEventTrigger.typestatic;
    }
}

/**
 * Reports the pressed state of a single button (identified by `buttonIndex`, stored as the facet's `subType`)
 * on a VR controller model. `RendererModelInterface` forwards updates to `ControllerRenderable` to visualize
 * the button press on the rendered controller mesh, and `PeerModelMessageHandler` broadcasts updates to other
 * peers in multiplayer sessions.
 */
class ControllerButton extends Facet {
    buttonIndex;
    pressed;
    static typestatic = "ControllerButton";
    static metaData = {
        export: true,
        description: "",
        category: "Interaction"
    };
    static propTypes = {
        buttonIndex: { type: PropertyType.Uint8, isRequired: true },
        pressed: { type: PropertyType.Boolean, default: true }
    };
    static MAX_BYTE_LENGTH = Uint8Array.BYTES_PER_ELEMENT + // buttonIndex
        BOOLEAN_BYTES_PER_ELEMENT; // pressed
    get byteLength() {
        return ControllerButton.MAX_BYTE_LENGTH;
    }
    constructor(buttonIndex, pressed = true) {
        super(buttonIndex.toString());
        this.buttonIndex = buttonIndex;
        this.pressed = pressed;
    }
    serialization(stream) {
        this.buttonIndex = stream.uint8(this.buttonIndex);
        this.pressed = stream.boolean(this.pressed);
        if (stream.isReading) {
            this.subType = this.buttonIndex.toString();
        }
    }
    static is(facet) {
        return facet.type === ControllerButton.typestatic;
    }
}
FacetFactory.register(ControllerButton);

/**
 * Suppresses automatic selection highlighting for a model. `InteractionModule` tracks the presence of this
 * facet per model and skips creating/removes an auto-created `SelectionHighlightFacet` for it when the model
 * is selected, so custom or no highlight behaviour can be used instead.
 */
class DisableSelectionHighlight extends Facet {
    static typestatic = "DisableSelectionHighlight";
    static metaData = {
        export: true,
        description: "Models with this facet won't be highlighted when selected",
        category: "Interaction"
    };
    static propTypes = {};
    get byteLength() {
        return 0;
    }
    /**
     * Marker facet without serialized payload.
     */
    serialization() { }
    static is(facet) {
        return facet.type === DisableSelectionHighlight.typestatic;
    }
}
FacetFactory.register(DisableSelectionHighlight);

/**
 * Specifies an area where a picked up model can be dropped of. The `dropOffLayer` is a filter that specifies which
 * models with a `PickupFacet` can be dropped. Therefor the `dropOffLayer` has to be contained in the `dropOffLayers`
 * array of the model with a `PickupFacet`.
 *
 * The model with the DropOffAreaFacet acts only as a collision geometry to specify when we can drop a model off. In
 * most cases the model should snap to some geometry. In this case the `dropOffTargetId` has to be specified. If it is
 * set, then a vertical raycast between the controller with the picked up model and the drop-off target is performed and
 * the model is dropped at the point of intersection. The `offset` can be used to fix the position, orientation and scale
 * relative to the intersection point.
 *
 * NOTE: if `dropOffTargetId` is specified, then it also needs a CollisionFacet facet!
 *
 * If no `dropOffTargetUid` is specified, the same downward raycast is performed against the drop-off area itself.
 * A hit is required before the preview is shown. A matching `destinationTargets` entry places the preview on that
 * position model. Otherwise the preview is shown at the drop-off area's world pose.
 *
 * `DropOffAreaManager` registers the area on `@FacetHandler`/`@FacetRemoveHandler` and matches its
 * `dropOffLayer` against collision contact events (`OnEnterContactsMessage`/`OnLeaveContactsMessage`) to fire
 * enter/leave/move-inside delegates that drive the actual drop-off logic.
 */
class DropOffAreaFacet extends Facet {
    static typestatic = "DropOffAreaFacet";
    static metaData = {
        export: true,
        description: "Defines an area where a PickupFacet with a matching dropOffLayers entry can be dropped off",
        category: "PharmaXR",
        dependencies: ["CollisionFacet"]
    };
    static propTypes = {
        dropOffLayer: {
            type: PropertyType.Uint8,
            default: 0,
            description: "Layer filter for compatible PickupFacet models. A pickup can be dropped here only if this value exists in its dropOffLayers."
        },
        dropOffTargetUid: {
            type: PropertyType.UidSafe
            // not possible atm: description: "Layer filter for compatible PickupFacet models. A pickup can be dropped here only if this value exists in its dropOffLayers."
        },
        destinationTargets: {
            type: PropertyType.ArraySafe,
            // not possible atm: description: "Layer filter for compatible PickupFacet models. A pickup can be dropped here only if this value exists in its dropOffLayers.",
            elementType: {
                type: PropertyType.Struct,
                properties: {
                    pickupUid: { type: PropertyType.UidSafe },
                    positionUid: { type: PropertyType.UidSafe }
                }
            }
        },
        useOffset: { type: PropertyType.Boolean, default: true },
        offset: {
            type: PropertyType.Struct,
            properties: {
                position: { type: PropertyType.Number3Safe, default: [0, 0, 0] },
                orientation: { type: PropertyType.Number4Safe, default: [0, 0, 0, 0] },
                scale: { type: PropertyType.Number3Safe, default: [1, 1, 1] }
            }
        }
    };
    /** Layer tag that must match a pickup model's accepted drop-off layers. */
    dropOffLayer;
    /** Optional target model used for raycast-based placement when dropping. */
    dropOffTargetUid;
    /** Optional list of specific pickup–position pairs accepted at this drop-off area. */
    destinationTargets;
    /** Whether the configured offset should be applied during placement. */
    useOffset;
    /** Optional local placement offset relative to the resolved drop-off point. */
    offset;
    constructor(dropOffLayer = 0, dropOffTargetUid, useOffset = true, offset, destinationTargets) {
        super();
        this.dropOffLayer = dropOffLayer;
        this.dropOffTargetUid = dropOffTargetUid;
        this.destinationTargets = destinationTargets;
        this.useOffset = useOffset;
        this.offset = offset;
    }
    get byteLength() {
        return (UINT8_BYTES_PER_ELEMENT + // dropOffLayer
            UID_SAFE_BYTES_PER_ELEMENT + // dropOffTargetId
            BOOLEAN_BYTES_PER_ELEMENT + // useOffset
            BOOLEAN_BYTES_PER_ELEMENT + // hasOffset
            (this.offset !== undefined ? Float32Array.BYTES_PER_ELEMENT * 10 : 0) + // offset
            UINT8_BYTES_PER_ELEMENT + // destinationTargets count
            (this.destinationTargets?.length ?? 0) * 2 * UID_SAFE_BYTES_PER_ELEMENT // destinationTargets entries
        );
    }
    serialization(stream) {
        this.dropOffLayer = stream.uint8(this.dropOffLayer);
        this.dropOffTargetUid = stream.uidSafe(this.dropOffTargetUid);
        this.useOffset = stream.boolean(this.useOffset);
        const hasOffset = stream.boolean(this.offset !== undefined);
        if (this.useOffset) {
            this.offset = this.offset ?? {};
            if (hasOffset) {
                this.offset.position = stream.number3Safe(this.offset.position);
                this.offset.orientation = stream.number4Safe(this.offset.orientation);
                this.offset.scale = stream.number3Safe(this.offset.position);
            }
            else {
                this.offset.position = [0, 0, 0];
                this.offset.orientation = [0, 0, 0, 0];
                this.offset.scale = [1, 1, 1];
            }
        }
        const targetCount = stream.uint8(this.destinationTargets?.length ?? 0);
        if (targetCount > 0) {
            if (this.destinationTargets === undefined)
                this.destinationTargets = [];
            for (let i = 0; i < targetCount; i++) {
                const t = this.destinationTargets[i] ?? {};
                t.pickupUid = stream.uidSafe(t.pickupUid);
                t.positionUid = stream.uidSafe(t.positionUid);
                this.destinationTargets[i] = t;
            }
        }
    }
    static is(facet) {
        return facet.type === DropOffAreaFacet.typestatic;
    }
}
FacetFactory.register(DropOffAreaFacet);

/**
 * Lists the supported input device families that can emit controller-related messages.
 */
var InputDeviceType;
(function (InputDeviceType) {
    /** Mouse input device. */
    InputDeviceType[InputDeviceType["Mouse"] = 0] = "Mouse";
    /** Keyboard input device. */
    InputDeviceType[InputDeviceType["Keyboard"] = 1] = "Keyboard";
    /** HTC Vive controller. */
    InputDeviceType[InputDeviceType["HtcViveController"] = 2] = "HtcViveController";
    /** Valve Index controller. */
    InputDeviceType[InputDeviceType["ValveIndexController"] = 3] = "ValveIndexController";
    /** Oculus controller. */
    InputDeviceType[InputDeviceType["OculusController"] = 4] = "OculusController";
})(InputDeviceType || (InputDeviceType = {}));
/**
 * Names the logical button actions that can be mapped or emitted by controller inputs.
 */
var ButtonType;
(function (ButtonType) {
    /** Wildcard that addresses all buttons. */
    ButtonType[ButtonType["ALL"] = 0] = "ALL";
    /** Generic click or confirm action. */
    ButtonType[ButtonType["CLICK"] = 1] = "CLICK";
    /** Grab action. */
    ButtonType[ButtonType["GRAB"] = 2] = "GRAB";
    /** Discard or cancel action. */
    ButtonType[ButtonType["DISCARD"] = 3] = "DISCARD";
    /** Select action. */
    ButtonType[ButtonType["SELECT"] = 4] = "SELECT";
    /** Controller A button. */
    ButtonType[ButtonType["BUTTON_A"] = 5] = "BUTTON_A";
})(ButtonType || (ButtonType = {}));
/**
 * Describes the lifecycle state of a button interaction.
 */
var ButtonSignal;
(function (ButtonSignal) {
    /** No button signal. */
    ButtonSignal[ButtonSignal["NONE"] = 0] = "NONE";
    /** Button was pressed. */
    ButtonSignal[ButtonSignal["PRESSED"] = 1] = "PRESSED";
    /** Button was released. */
    ButtonSignal[ButtonSignal["RELEASED"] = 2] = "RELEASED";
    /** Button was clicked. */
    ButtonSignal[ButtonSignal["CLICKED"] = 3] = "CLICKED";
    /** Button was double clicked. */
    ButtonSignal[ButtonSignal["DOUBLE_CLICK"] = 4] = "DOUBLE_CLICK";
    /** Button is being held. */
    ButtonSignal[ButtonSignal["HOLD"] = 5] = "HOLD";
    /** Held button was released. */
    ButtonSignal[ButtonSignal["HOLD_RELEASED"] = 6] = "HOLD_RELEASED";
})(ButtonSignal || (ButtonSignal = {}));
/**
 * Names the analog axes that controllers can report.
 */
var AxisType;
(function (AxisType) {
    /** Touchpad horizontal axis. */
    AxisType[AxisType["TOUCHPAD_X"] = 0] = "TOUCHPAD_X";
    /** Touchpad vertical axis. */
    AxisType[AxisType["TOUCHPAD_Y"] = 1] = "TOUCHPAD_Y";
    /** Thumbstick horizontal axis. */
    AxisType[AxisType["THUMBSTICK_X"] = 2] = "THUMBSTICK_X";
    /** Thumbstick vertical axis. */
    AxisType[AxisType["THUMBSTICK_Y"] = 3] = "THUMBSTICK_Y";
    /** Trigger analog axis. */
    AxisType[AxisType["TRIGGER"] = 4] = "TRIGGER";
})(AxisType || (AxisType = {}));
/**
 * Rebinds a logical button action to a specific input device button identifier.
 */
class ChangeInputDeviceButtonMappingMessage extends BusMessage {
    device;
    buttonID;
    buttonType;
    static typestatic = "ChangeInputDeviceButtonMappingMessage";
    static subjectstatic = "controller";
    static BYTE_LENGTH = Uint8Array.BYTES_PER_ELEMENT + // device
        Uint8Array.BYTES_PER_ELEMENT; // buttonType
    get byteLength() {
        return ChangeInputDeviceButtonMappingMessage.BYTE_LENGTH + calcStringByteLength(this.buttonID);
    }
    /**
     * @param device Selects the device family whose mapping should change.
     * @param buttonID Identifies the concrete device button to bind.
     * @param buttonType Selects the logical button action that should be triggered.
     */
    constructor(
    /**
     * Selects the device family whose mapping should change.
     */
    device = InputDeviceType.Mouse, 
    /**
     * Identifies the concrete device button to bind.
     */
    buttonID = "", 
    /**
     * Selects the logical button action that should be triggered.
     */
    buttonType) {
        super();
        this.device = device;
        this.buttonID = buttonID;
        this.buttonType = buttonType;
    }
    serialization(stream) {
        this.device = stream.uint8(this.device);
        this.buttonID = stream.utf16String(this.buttonID);
        this.buttonType = stream.uint8Safe(this.buttonType);
    }
    static is(msg) {
        return msg.type === ChangeInputDeviceButtonMappingMessage.typestatic;
    }
}
MessageFactory.register(ChangeInputDeviceButtonMappingMessage);
/**
 * Enables or disables input devices globally.
 */
class EnableInputDevicesMessage extends BusMessage {
    enable;
    static typestatic = "EnableInputDevicesMessage";
    static subjectstatic = "controller";
    static MAX_BYTE_LENGTH = BOOLEAN_BYTES_PER_ELEMENT; // enable
    get byteLength() {
        return EnableInputDevicesMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param enable Enables inputs when `true`, or suppresses them when `false`.
     */
    constructor(
    /**
     * Enables inputs when `true`, or suppresses them when `false`.
     */
    enable = true) {
        super();
        this.enable = enable;
    }
    serialization(stream) {
        this.enable = stream.boolean(this.enable);
    }
    static is(msg) {
        return msg.type === EnableInputDevicesMessage.typestatic;
    }
}
MessageFactory.register(EnableInputDevicesMessage);
/**
 * Reports a controller button state change (press, release, click, hold, ...). Sent by `InputDevice`
 * whenever a bound button changes state; the constructor appends `buttonType` to `this.type` so
 * `subscribeByType(ControllerButtonMessage, handler, buttonType)` can filter by a specific button.
 * Consumed by many modules that react to controller input, including `InteractionModule`,
 * `ScriptModule`, `TeleporterModule`, `ModelPickupModule`, `InputService`, `HTMLCanvasCursorModule`,
 * `TrainingWearableService` and `TrainingUserEventService`.
 */
class ControllerButtonMessage extends BusMessage {
    uid;
    buttonType;
    input;
    timestamp;
    static typestatic = "ControllerButtonMessage";
    static subjectstatic = "controller";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        Uint8Array.BYTES_PER_ELEMENT + // buttonType
        Uint8Array.BYTES_PER_ELEMENT + // input
        Float64Array.BYTES_PER_ELEMENT; // timestamp
    get byteLength() {
        return ControllerButtonMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Identifies the controller-related model that emitted the action.
     * @param buttonType Names the logical button action that changed.
     * @param input Describes how the action changed, for example pressed or released.
     * @param timestamp Captures when the input event occurred.
     */
    constructor(
    /**
     * Identifies the controller-related model that emitted the action.
     */
    uid = 0, 
    /**
     * Names the logical button action that changed.
     */
    buttonType = ButtonType.CLICK, 
    /**
     * Describes how the action changed, for example pressed or released.
     */
    input = ButtonSignal.CLICKED, 
    /**
     * Captures when the input event occurred.
     */
    timestamp = 0) {
        super();
        this.uid = uid;
        this.buttonType = buttonType;
        this.input = input;
        this.timestamp = timestamp;
        this.type += buttonType;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.buttonType = stream.uint8(this.buttonType);
        this.input = stream.uint8(this.input);
        this.timestamp = stream.float64(this.timestamp);
    }
    static is(msg) {
        return msg.type.startsWith(ControllerButtonMessage.typestatic);
    }
}
MessageFactory.register(ControllerButtonMessage);
/**
 * Carries an analog controller axis input value.
 */
class ControllerAxisMessage extends BusMessage {
    uid;
    axisType;
    value;
    static typestatic = "ControllerAxisMessage";
    static subjectstatic = "controller";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        Uint8Array.BYTES_PER_ELEMENT + // axisType
        Float32Array.BYTES_PER_ELEMENT; // value
    get byteLength() {
        return ControllerButtonMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Identifies the controller-related model that emitted the axis value.
     * @param axisType Selects which analog axis the value belongs to.
     * @param value Provides the current analog axis value.
     */
    constructor(
    /**
     * Identifies the controller-related model that emitted the axis value.
     */
    uid = 0, 
    /**
     * Selects which analog axis the value belongs to.
     */
    axisType = AxisType.TRIGGER, 
    /**
     * Provides the current analog axis value.
     */
    value = 0) {
        super();
        this.uid = uid;
        this.axisType = axisType;
        this.value = value;
        this.type += axisType;
    }
    /**
     * On read, also rebuilds `this.type` with the `axisType` suffix, mirroring what the constructor
     * does when the message is created directly, so type-suffix subscriptions still match.
     */
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.axisType = stream.uint8(this.axisType);
        this.value = stream.float32(this.value);
        if (stream.isReading) {
            this.type = `${ControllerAxisMessage.typestatic}${this.axisType}`;
        }
    }
    static is(msg) {
        return msg.type.startsWith(ControllerAxisMessage.typestatic);
    }
}
MessageFactory.register(ControllerAxisMessage);
/**
 * Requests the current transforms of the active controllers.
 */
class RequestControllerTransformMessage extends BusMessage {
    static typestatic = "RequestControllerTransformMessage";
    static subjectstatic = "controller";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === RequestControllerTransformMessage.typestatic;
    }
}
MessageFactory.register(RequestControllerTransformMessage);
/**
 * Returns the current transforms of the active controllers.
 */
class ResponseControllerTransformMessage extends BusMessage {
    ctrs;
    scale;
    static typestatic = "ResponseControllerTransformMessage";
    static subjectstatic = "controller";
    static MAX_TRANSFORM_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        Float32Array.BYTES_PER_ELEMENT * (3 * 4); // pos ori
    get byteLength() {
        return (Uint16Array.BYTES_PER_ELEMENT + // ctrLength
            Float32Array.BYTES_PER_ELEMENT + // scale
            this.ctrs.length * ResponseControllerTransformMessage.MAX_TRANSFORM_BYTE_LENGTH);
    }
    /**
     * @param ctrs Lists the controller poses that were sampled.
     * @param scale Provides the world-to-controller scaling factor used for the poses.
     */
    constructor(
    /**
     * Lists the controller poses that were sampled.
     */
    ctrs = [], 
    /**
     * Provides the world-to-controller scaling factor used for the poses.
     */
    scale = 1) {
        super();
        this.ctrs = ctrs;
        this.scale = scale;
    }
    /**
     * On read, pushes a freshly constructed transform for each entry into `ctrs`, since a deserialized
     * instance starts with an empty array instead of running the constructor's default.
     */
    serialization(stream) {
        const ctrLength = stream.uint16(this.ctrs.length);
        for (let i = 0; i < ctrLength; ++i) {
            let ctr;
            if (stream.isReading) {
                ctr = {
                    uid: 0,
                    position: [0, 0, 0],
                    orientation: [0, 0, 0, 1]
                };
                this.ctrs.push(ctr);
            }
            else {
                ctr = this.ctrs[i];
            }
            ctr.uid = stream.uid(ctr.uid);
            ctr.position = stream.number3(ctr.position);
            ctr.orientation = stream.number4(ctr.orientation);
        }
        this.scale = stream.float32(this.scale);
    }
    static is(msg) {
        return msg.type === ResponseControllerTransformMessage.typestatic;
    }
}
MessageFactory.register(ResponseControllerTransformMessage);
/**
 * Enables or disables controller annotations.
 */
class EnableControllerAnnotationMessage extends BusMessage {
    enable;
    static typestatic = "EnableControllerAnnotationMessage";
    static subjectstatic = "controller";
    static MAX_BYTE_LENGTH = BOOLEAN_BYTES_PER_ELEMENT; // boolean
    get byteLength() {
        return EnableControllerAnnotationMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param enable Enables controller annotations when `true`.
     */
    constructor(
    /**
     * Enables controller annotations when `true`.
     */
    enable = true) {
        super();
        this.enable = enable;
    }
    serialization(stream) {
        this.enable = stream.boolean(this.enable);
    }
    static is(msg) {
        return msg.type === EnableControllerAnnotationMessage.typestatic;
    }
}
MessageFactory.register(EnableControllerAnnotationMessage);
/**
 * Enables or disables the joystick visualization for the specified controller hand.
 */
class EnableControllerJoystickMessage extends BusMessage {
    enable;
    hand;
    static typestatic = "EnableControllerJoystickMessage";
    static subjectstatic = "controller";
    static MAX_BYTE_LENGTH = (BOOLEAN_BYTES_PER_ELEMENT + // boolean
        ENUM_BYTES_PER_ELEMENT);
    get byteLength() {
        return EnableControllerJoystickMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param enable Enables joystick visualization when `true`.
     * @param hand Selects the controller hand whose joystick should be shown or hidden.
     */
    constructor(
    /**
     * Enables joystick visualization when `true`.
     */
    enable = true, 
    /**
     * Selects the controller hand whose joystick should be shown or hidden.
     */
    hand) {
        super();
        this.enable = enable;
        this.hand = hand;
    }
    serialization(stream) {
        this.enable = stream.boolean(this.enable);
        this.hand = stream.enum(this.hand);
    }
    static is(msg) {
        return msg.type === EnableControllerJoystickMessage.typestatic;
    }
}
MessageFactory.register(EnableControllerJoystickMessage);

/**
 * Defines how a picked up model is placed again when released.
 */
var DropOffMode;
(function (DropOffMode) {
    /** Allows dropping the model anywhere. */
    DropOffMode[DropOffMode["ALWAYS"] = 0] = "ALWAYS";
    /** Returns the model to its previous position when released. */
    DropOffMode[DropOffMode["PREVIOUS_POSITION"] = 1] = "PREVIOUS_POSITION";
    /** Only allows dropping inside matching drop-off areas. */
    DropOffMode[DropOffMode["DROP_OFF_AREA"] = 2] = "DROP_OFF_AREA";
})(DropOffMode || (DropOffMode = {}));
/**
 * Makes a model pickable up by a controller. `InteractionModule` reacts to this facet by generating an
 * `InteractionFacet` with a `PickupAction` (using `offset`, `dropOffMode`, `dropOffButton` etc. to configure
 * pickup/drop-off behaviour) - it is a shorthand so models can be made grabbable directly from Blender without
 * hand-authoring an `InteractionFacet`. `dropOffLayers` is matched against the `dropOffLayer` of any
 * `DropOffAreaFacet` the model can be dropped on. Requires a `CollisionFacet` on the model.
 */
class PickupFacet extends Facet {
    static typestatic = "PickupFacet";
    static metaData = {
        export: true,
        description: "A model with this facet can be picked up and dropped off if the layer of the DropOffAreaFacet matches any entry in dropOffLayers",
        category: "PharmaXR",
        dependencies: ["CollisionFacet"]
    };
    static propTypes = {
        cloneModel: {
            type: PropertyType.Boolean,
            default: false
        },
        useOffset: {
            type: PropertyType.Boolean,
            default: true
        },
        useOffsetPriorToPickup: {
            type: PropertyType.Boolean,
            default: false
        },
        keepSelectionActiveInVR: {
            type: PropertyType.Boolean,
            default: false
        },
        showDropOffIcon: {
            type: PropertyType.Boolean,
            default: true
        },
        offset: {
            type: PropertyType.Struct,
            properties: {
                position: { type: PropertyType.Number3Safe, default: [0, 0, 0] },
                orientation: { type: PropertyType.Number4Safe, default: [0, 0, 0, 0] },
                scale: { type: PropertyType.Number3Safe, default: [1, 1, 1] },
            }
        },
        dropOffMode: {
            type: PropertyType.EnumUint8,
            items: {
                0: "Always",
                1: "Previous position",
                2: "Drop off area",
            }
        },
        dropOffLayers: {
            type: PropertyType.ArraySafe,
            elementType: { type: PropertyType.Float64 }
        },
        dropOffButton: {
            type: PropertyType.EnumUint8,
            items: {
                [ButtonType.DISCARD]: "Discard",
                [ButtonType.SELECT]: "Select",
                [ButtonType.CLICK]: "Click",
                [ButtonType.BUTTON_A]: "Button A",
                [ButtonType.GRAB]: "Grab"
            },
            default: ButtonType.DISCARD
        }
    };
    /** Whether the configured offset should be applied while the model is held. */
    useOffset;
    /** Whether the offset should already affect the pre-pickup interaction pose. */
    useOffsetPriorToPickup;
    /** Optional local transform offset relative to the controller or drop-off point. */
    offset;
    /** Strategy used when the model is released. */
    dropOffMode;
    /** Accepted drop-off area layers for `DROP_OFF_AREA` mode. */
    dropOffLayers = [];
    /** Controller button used to trigger dropping. */
    dropOffButton;
    /** Whether pickup should clone the model instead of moving the original. */
    cloneModel;
    /** Whether selection should remain active while the model is held in VR. */
    keepSelectionActiveInVR;
    /** Whether compatible drop-off areas should show their indicator while holding the model. */
    showDropOffIcon;
    /**
     * @param useOffset Whether the configured offset should be applied while holding the model.
     * @param offset Optional local transform offset relative to the controller.
     * @param dropOffMode Strategy used when the model is released.
     * @param dropOffLayers Accepted drop-off area layers for `DROP_OFF_AREA` mode.
     * @param dropOffButtonOrCloneModel Backward-compatible drop-off button or legacy clone flag.
     * @param cloneModel Whether pickup should create a clone instead of moving the original.
     * @param keepSelectionActiveInVR Whether selection should remain active while the model is held in VR.
     * @param showDropOffIcon Whether compatible drop-off areas should show their indicator while holding the model.
     * @param useOffsetPriorToPickup Whether the offset should already affect the pre-pickup interaction pose.
     */
    constructor(useOffset = true, offset, dropOffMode = DropOffMode.PREVIOUS_POSITION, dropOffLayers = undefined, dropOffButtonOrCloneModel = ButtonType.DISCARD, cloneModel = false, keepSelectionActiveInVR = false, showDropOffIcon = true, useOffsetPriorToPickup = false) {
        super();
        const dropOffButton = typeof dropOffButtonOrCloneModel === "boolean"
            ? ButtonType.DISCARD
            : dropOffButtonOrCloneModel;
        const resolvedCloneModel = typeof dropOffButtonOrCloneModel === "boolean"
            ? dropOffButtonOrCloneModel
            : cloneModel;
        this.useOffset = useOffset;
        this.useOffsetPriorToPickup = useOffsetPriorToPickup;
        this.offset = offset;
        this.dropOffMode = dropOffMode;
        this.dropOffLayers = dropOffLayers;
        this.dropOffButton = dropOffButton;
        this.cloneModel = resolvedCloneModel;
        this.keepSelectionActiveInVR = keepSelectionActiveInVR;
        this.showDropOffIcon = showDropOffIcon;
    }
    /**
     * Returns the serialized size of the pickup, drop-off, and offset configuration.
     */
    get byteLength() {
        return (BOOLEAN_BYTES_PER_ELEMENT + // useOffset
            BOOLEAN_BYTES_PER_ELEMENT + // hasOffset
            ((this.offset !== undefined) ? Float32Array.BYTES_PER_ELEMENT * 10 : 0) + // offset
            ENUM_BYTES_PER_ELEMENT + // dropOffMode
            calcArrayByteLength(this.dropOffLayers) + // dropOffLayers
            ENUM_BYTES_PER_ELEMENT + // dropOffButton
            BOOLEAN_BYTES_PER_ELEMENT + // cloneModel
            BOOLEAN_BYTES_PER_ELEMENT + // keepSelectionActiveInVR
            BOOLEAN_BYTES_PER_ELEMENT + // showDropOffIcon
            BOOLEAN_BYTES_PER_ELEMENT // useOffsetPriorToPickup
        );
    }
    /**
     * Serializes the pickup placement, drop-off behavior, and VR interaction flags.
     */
    serialization(stream) {
        this.useOffset = stream.boolean(this.useOffset);
        const hasOffset = stream.boolean(this.offset !== undefined);
        if (hasOffset) {
            this.offset = this.offset ?? { position: [0, 0, 0], orientation: [0, 0, 0, 0], scale: [1, 1, 1] };
            this.offset.position = stream.number3Safe(this.offset.position);
            this.offset.orientation = stream.number4Safe(this.offset.orientation);
            this.offset.scale = stream.number3Safe(this.offset.scale);
        }
        this.dropOffMode = stream.enum(this.dropOffMode);
        this.dropOffLayers = stream.arraySafe(this.dropOffLayers);
        this.dropOffButton = stream.enumSafe(this.dropOffButton) ?? ButtonType.DISCARD;
        this.cloneModel = stream.boolean(this.cloneModel);
        this.keepSelectionActiveInVR = stream.boolean(this.keepSelectionActiveInVR);
        this.showDropOffIcon = stream.boolean(this.showDropOffIcon);
        this.useOffsetPriorToPickup = stream.boolean(this.useOffsetPriorToPickup);
    }
    /**
     * Narrows a generic facet to `PickupFacet`.
     */
    static is(facet) {
        return facet.type === PickupFacet.typestatic;
    }
}
FacetFactory.register(PickupFacet);

/**
 * Identifies which axes a movement or rotation constraint applies to.
 */
var ConstraintsAxis;
(function (ConstraintsAxis) {
    /** X axis only. */
    ConstraintsAxis[ConstraintsAxis["X"] = 0] = "X";
    /** Y axis only. */
    ConstraintsAxis[ConstraintsAxis["Y"] = 1] = "Y";
    /** Z axis only. */
    ConstraintsAxis[ConstraintsAxis["Z"] = 2] = "Z";
    /** X and Y axes together. */
    ConstraintsAxis[ConstraintsAxis["XY"] = 3] = "XY";
    /** X and Z axes together. */
    ConstraintsAxis[ConstraintsAxis["XZ"] = 4] = "XZ";
    /** Y and X axes together. */
    ConstraintsAxis[ConstraintsAxis["YX"] = 5] = "YX";
    /** Y and Z axes together. */
    ConstraintsAxis[ConstraintsAxis["YZ"] = 6] = "YZ";
    /** Z and X axes together. */
    ConstraintsAxis[ConstraintsAxis["ZX"] = 7] = "ZX";
    /** Z and Y axes together. */
    ConstraintsAxis[ConstraintsAxis["ZY"] = 8] = "ZY";
})(ConstraintsAxis || (ConstraintsAxis = {}));
/**
 * Identifies which kind of movement constraint entry is stored.
 */
var ConstraintType;
(function (ConstraintType) {
    /** Restricts rotation. */
    ConstraintType[ConstraintType["ROTATION"] = 0] = "ROTATION";
    /** Locks or constrains movement. */
    ConstraintType[ConstraintType["MOVEMENT"] = 1] = "MOVEMENT";
    /** Clamps translation to a range. */
    ConstraintType[ConstraintType["RANGE"] = 2] = "RANGE";
    /** Snaps translation to a grid. */
    ConstraintType[ConstraintType["CONSTRAIN_TO_GRID"] = 3] = "CONSTRAIN_TO_GRID";
    /** Constrains movement to a navigation mesh. */
    ConstraintType[ConstraintType["CONSTRAIN_TO_NAVMESH"] = 4] = "CONSTRAIN_TO_NAVMESH";
})(ConstraintType || (ConstraintType = {}));
/**
 * When set on a model, the MovementConstraintModule will enforce the given constraints in world space
 * on every transform update, regardless of what system moved the model.
 */
class MovementConstraintFacet extends Facet {
    static typestatic = "MovementConstraintFacet";
    static metaData = {
        export: true,
        description: "Constrains movement and rotation of a model in world space",
        category: "PharmaXR",
    };
    static propTypes = {
        enabled: {
            type: PropertyType.Boolean,
            default: true,
            description: "When turned on, the model's current world transform is recorded as the anchor for all constraints. Turning it off releases all constraints.",
        },
        constraints: {
            type: PropertyType.ArraySafe,
            description: "List of constraints applied to this model in world space. Each entry is evaluated independently every time the model moves.",
            elementType: {
                type: PropertyType.Struct,
                properties: {
                    type: {
                        type: PropertyType.EnumUint8,
                        description: "Movement: locks position to the value when the constraint was enabled. Range: clamps position on an axis to a min/max range. Rotation: restricts rotation to one axis. Constrain to grid: snaps position to a grid.",
                        items: {
                            [ConstraintType.ROTATION]: "Rotation",
                            [ConstraintType.MOVEMENT]: "Movement",
                            [ConstraintType.RANGE]: "Range",
                            [ConstraintType.CONSTRAIN_TO_GRID]: "Constrain to grid",
                            [ConstraintType.CONSTRAIN_TO_NAVMESH]: "Constrain to NavMesh",
                        },
                    },
                    axis: {
                        type: PropertyType.If,
                        condition: { property: "type", expected: [ConstraintType.ROTATION, ConstraintType.MOVEMENT, ConstraintType.RANGE, ConstraintType.CONSTRAIN_TO_GRID] },
                        properties: {
                            type: PropertyType.EnumUint8Safe,
                            description: "Which axis the constraint applies to. For Movement and Rotation, leave empty to lock all axes. For Range, an axis is required.",
                            items: {
                                [ConstraintsAxis.X]: "X",
                                [ConstraintsAxis.Y]: "Y",
                                [ConstraintsAxis.Z]: "Z",
                                [ConstraintsAxis.XY]: "XY",
                                [ConstraintsAxis.XZ]: "XZ",
                                [ConstraintsAxis.YX]: "YX",
                                [ConstraintsAxis.YZ]: "YZ",
                                [ConstraintsAxis.ZX]: "ZX",
                                [ConstraintsAxis.ZY]: "ZY",
                            },
                        },
                    },
                    snapAngle: {
                        type: PropertyType.If,
                        condition: { property: "type", expected: ConstraintType.ROTATION },
                        properties: {
                            type: PropertyType.Float32Safe,
                            description: "Optional snap angle in radians. The rotation snaps to multiples of this value (e.g. 1.5708 = 90°). Leave empty for smooth free rotation around the axis.",
                        },
                    },
                    min: {
                        type: PropertyType.If,
                        condition: { property: "type", expected: ConstraintType.RANGE },
                        properties: {
                            type: PropertyType.Float32Safe,
                            description: "Minimum allowed world-space position on the selected axis. Leave empty for no lower bound.",
                        },
                    },
                    max: {
                        type: PropertyType.If,
                        condition: { property: "type", expected: ConstraintType.RANGE },
                        properties: {
                            type: PropertyType.Float32Safe,
                            description: "Maximum allowed world-space position on the selected axis. Leave empty for no upper bound.",
                        },
                    },
                    gridSize: {
                        type: PropertyType.If,
                        condition: { property: "type", expected: ConstraintType.CONSTRAIN_TO_GRID },
                        properties: {
                            type: PropertyType.Float32Safe,
                            description: "Grid cell size in world units. The position snaps to the nearest multiple of this value (e.g. 0.5 snaps to every half unit).",
                        },
                    },
                },
            },
        },
    };
    /** Whether the constraint set is currently active. */
    enabled;
    /** World-space constraints that should be enforced for this model. */
    constraints;
    constructor(enabled = true, constraints = []) {
        super();
        this.enabled = enabled;
        this.constraints = constraints;
    }
    /**
     * Every constraint entry uses the same fixed size regardless of its `type`: `serialization` packs each
     * constraint's type-specific fields (e.g. `min`/`max`, `snapAngle`, `gridSize`) into two shared enum and
     * two shared float32 slots.
     */
    get byteLength() {
        return (BOOLEAN_BYTES_PER_ELEMENT +
            UINT8_BYTES_PER_ELEMENT +
            this.constraints.length * (2 * ENUM_BYTES_PER_ELEMENT +
                2 * FLOAT32_BYTES_PER_ELEMENT));
    }
    serialization(stream) {
        this.enabled = stream.boolean(this.enabled);
        const count = stream.uint8(this.constraints?.length ?? 0);
        if (count > 0) {
            if (this.constraints === undefined)
                this.constraints = [];
            for (let i = 0; i < count; i++) {
                const c = this.constraints[i];
                const type = stream.enum(c?.type);
                const axis = stream.enumSafe(c !== undefined && 'axis' in c ? c.axis : undefined);
                // Pack semantic fields into the two optional float slots
                let field1;
                let field2;
                if (c !== undefined) {
                    switch (c.type) {
                        case ConstraintType.ROTATION:
                            field1 = c.snapAngle;
                            break;
                        case ConstraintType.RANGE:
                            field1 = c.min;
                            field2 = c.max;
                            break;
                        case ConstraintType.CONSTRAIN_TO_GRID:
                            field1 = c.gridSize;
                            break;
                    }
                }
                const f1 = stream.float32Safe(field1);
                const f2 = stream.float32Safe(field2);
                switch (type) {
                    case ConstraintType.MOVEMENT:
                        this.constraints[i] = { type, axis };
                        break;
                    case ConstraintType.ROTATION:
                        this.constraints[i] = { type, axis, snapAngle: f1 };
                        break;
                    case ConstraintType.RANGE:
                        this.constraints[i] = { type, axis: axis ?? ConstraintsAxis.Y, min: f1, max: f2 };
                        break;
                    case ConstraintType.CONSTRAIN_TO_GRID:
                        this.constraints[i] = { type, axis, gridSize: f1 ?? 1 };
                        break;
                    case ConstraintType.CONSTRAIN_TO_NAVMESH:
                        this.constraints[i] = { type };
                        break;
                }
            }
        }
    }
    static is(facet) {
        return facet.type === MovementConstraintFacet.typestatic;
    }
}
FacetFactory.register(MovementConstraintFacet);

/**
 * Overrides the default input bindings (key/button actions and analog axes) for the room's local player,
 * typically set on the room model from server-parsed room settings (`RoomInputSettingsParser`). Client-side
 * `InputService` and `InputActionService` subscribe to this facet to replace their default action/axis
 * mappings with the ones defined here.
 */
class InputMappingFacet extends Facet {
    params;
    static typestatic = "InputMappingFacet";
    static metaData = {
        export: true,
        description: "",
        category: "Interaction"
    };
    get byteLength() {
        let byteLength = Uint32Array.BYTES_PER_ELEMENT + // actionsLength
            Uint32Array.BYTES_PER_ELEMENT + // axesLength
            Uint32Array.BYTES_PER_ELEMENT; // axisConfigsLength
        for (const action of this.params.actions) {
            byteLength += calcStringByteLength(action.name);
            byteLength += calcLocalizationStringByteLength(action.descriptive);
            byteLength +=
                Uint32Array.BYTES_PER_ELEMENT + // keysLength
                    Uint32Array.BYTES_PER_ELEMENT; // axesLength
            for (const key of action.keys) {
                byteLength +=
                    Uint32Array.BYTES_PER_ELEMENT + // key
                        Uint8Array.BYTES_PER_ELEMENT + // state
                        BOOLEAN_BYTES_PER_ELEMENT * 4;
            }
            for (const axis of action.axes) {
                byteLength +=
                    Uint32Array.BYTES_PER_ELEMENT + // key
                        Float32Array.BYTES_PER_ELEMENT; // state
            }
        }
        for (const axis of this.params.axes) {
            byteLength += calcStringByteLength(axis.name);
            byteLength += calcLocalizationStringByteLength(axis.descriptivePositiveName);
            byteLength += calcLocalizationStringByteLength(axis.descriptiveNegativeName);
            byteLength += Uint32Array.BYTES_PER_ELEMENT; // keysLength
            for (const key of axis.keys) {
                byteLength +=
                    Uint32Array.BYTES_PER_ELEMENT + // key
                        Float32Array.BYTES_PER_ELEMENT; // threshold
            }
        }
        for (const config of this.params.axisConfigs) {
            byteLength += Uint32Array.BYTES_PER_ELEMENT; // keyIdx
            byteLength += Float32Array.BYTES_PER_ELEMENT; // deadZone
            byteLength += Float32Array.BYTES_PER_ELEMENT; // sensitivity
            byteLength += BOOLEAN_BYTES_PER_ELEMENT; // invert
        }
        return byteLength;
    }
    constructor(
    /** Carries the complete custom action, axis, and axis-processing configuration. */
    params) {
        super();
        this.params = params;
    }
    serialization(stream) {
        if (!this.params) {
            this.params = {
                actions: [],
                axes: [],
                axisConfigs: []
            };
        }
        if (!this.params.actions) {
            this.params.actions = [];
        }
        if (!this.params.axes) {
            this.params.axes = [];
        }
        if (!this.params.axisConfigs) {
            this.params.axisConfigs = [];
        }
        const actionsLength = stream.uint32(this.params.actions.length);
        for (let i = 0; i < actionsLength; ++i) {
            let action = this.params.actions[i];
            if (!action) {
                action = {
                    name: "",
                    keys: [],
                    axes: []
                };
            }
            action.name = stream.utf16String(action.name);
            action.descriptive = stream.localizationStringSafe(action.descriptive);
            const keysLength = stream.uint32(action.keys.length);
            for (let j = 0; j < keysLength; j++) {
                if (stream.isReading) {
                    action.keys[j] = {};
                }
                action.keys[j].key = stream.uint32(action.keys[j].key);
                action.keys[j].state = stream.uint8(action.keys[j].state);
                action.keys[j].shift = stream.boolean(action.keys[j].shift);
                action.keys[j].ctrl = stream.boolean(action.keys[j].ctrl);
                action.keys[j].alt = stream.boolean(action.keys[j].alt);
                action.keys[j].cmd = stream.boolean(action.keys[j].cmd);
            }
            const axesLength = stream.uint32(action.axes.length);
            for (let j = 0; j < axesLength; j++) {
                if (stream.isReading) {
                    action.axes[j] = {};
                }
                action.axes[j].key = stream.uint32(action.axes[j].key);
                action.axes[j].threshold = stream.float32(action.axes[j].threshold);
            }
            this.params.actions[i] = action;
        }
        const axesLength = stream.uint32(this.params.axes.length);
        for (let i = 0; i < axesLength; ++i) {
            let axis = this.params.axes[i];
            if (!axis) {
                axis = {
                    name: "",
                    keys: []
                };
            }
            axis.name = stream.utf16String(axis.name);
            axis.descriptivePositiveName = stream.localizationStringSafe(axis.descriptivePositiveName);
            axis.descriptiveNegativeName = stream.localizationStringSafe(axis.descriptiveNegativeName);
            const keysLength = stream.uint32(axis.keys.length);
            for (let j = 0; j < keysLength; j++) {
                if (stream.isReading) {
                    axis.keys[j] = {};
                }
                axis.keys[j].key = stream.uint32(axis.keys[j].key);
                axis.keys[j].scale = stream.float32(axis.keys[j].scale);
            }
            this.params.axes[i] = axis;
        }
        const axisConfigsLength = stream.uint32(this.params.axisConfigs.length);
        for (let i = 0; i < axisConfigsLength; i++) {
            let config = this.params.axisConfigs[i];
            if (!config) {
                config = {};
                this.params.axisConfigs[i] = config;
            }
            this.params.axisConfigs[i].keyIdx = stream.uint32(config.keyIdx);
            this.params.axisConfigs[i].deadZone = stream.float32(config.deadZone);
            this.params.axisConfigs[i].sensitivity = stream.float32(config.sensitivity);
            this.params.axisConfigs[i].invert = stream.boolean(config.invert);
        }
    }
    static is(facet) {
        return facet.type === InputMappingFacet.typestatic;
    }
}
FacetFactory.register(InputMappingFacet);

/**
 * Base class for typed metadata payloads stored inside {@link MetaDataFacet}.
 */
class MetaDataBase {
    /** Stores the runtime metadata type identifier. */
    type;
    /** Initializes the runtime metadata type identifier from the subclass. */
    constructor() {
        this.type = this.constructor.typestatic;
    }
    /**
     * Checks whether this metadata payload belongs to the given runtime type.
     * @param type Metadata runtime type to compare against.
     */
    is(type) {
        return this.type === type.typestatic;
    }
}
/**
 * Selects which project-tree aggregation view should be shown.
 */
var ProjectTreeProjectView;
(function (ProjectTreeProjectView) {
    /** Aggregates projects by their overall size. */
    ProjectTreeProjectView[ProjectTreeProjectView["PROJECT_SIZE"] = 0] = "PROJECT_SIZE";
    /** Aggregates projects by work per month. */
    ProjectTreeProjectView[ProjectTreeProjectView["WORK_PER_MONTH"] = 1] = "WORK_PER_MONTH";
    /** Aggregates projects by person and month. */
    ProjectTreeProjectView[ProjectTreeProjectView["WORK_PER_PERSON_PER_MONTH"] = 2] = "WORK_PER_PERSON_PER_MONTH";
})(ProjectTreeProjectView || (ProjectTreeProjectView = {}));
/**
 * Associates a project-tree project label with a concrete project.
 */
class ProjTreeProjectLabelMetaData extends MetaDataBase {
    projectId;
    static typestatic = "ProjTreeProject";
    constructor(projectId) {
        super();
        this.projectId = projectId;
    }
}
/**
 * Stores tooltip text for a project-tree user entry.
 */
class ProjTreeTooltipMetaData extends MetaDataBase {
    userId;
    text;
    static typestatic = "ProjTreeTooltip";
    constructor(userId, text) {
        super();
        this.userId = userId;
        this.text = text;
    }
}
/**
 * Stores the sub-layouters affected by the demo layouter interaction UI.
 */
class DemoLayouterInteractionMetaData extends MetaDataBase {
    subLayouterUids;
    static typestatic = "DemoInteraction";
    /**
     * @param subLayouterUids Lists the layouter models controlled by the interaction.
     */
    constructor(subLayouterUids) {
        super();
        this.subLayouterUids = subLayouterUids;
    }
}
/**
 * Stores interactive layout-configuration state for a configurable layouter.
 */
class ConfigureLayoutMetaData extends MetaDataBase {
    scale;
    throwDownActions;
    xAxis;
    zAxis;
    static typestatic = "ConfigureLayout";
    /**
     * @param scale Scales the interactive configuration widget.
     * @param throwDownActions Lists actions that should run on throw-down interaction.
     */
    constructor(scale, throwDownActions, xAxis, zAxis) {
        super();
        this.scale = scale;
        this.throwDownActions = throwDownActions;
        this.xAxis = xAxis;
        this.zAxis = zAxis;
    }
}
/**
 * Describes named fade animation timing.
 */
class FadeAnimationMetaData extends MetaDataBase {
    name;
    fadeInDelay;
    fadeOutDelay;
    static typestatic = "FadeAnim";
    constructor(name, fadeInDelay = 0, fadeOutDelay = 0) {
        super();
        this.name = name;
        this.fadeInDelay = fadeInDelay;
        this.fadeOutDelay = fadeOutDelay;
    }
}
/**
 * Carries editor-wide project, user, and worklog data for the editor visualization.
 */
class EditorMetaData extends MetaDataBase {
    rootUid;
    contextMenu;
    projects;
    users;
    worklogs;
    static typestatic = "EditorData";
    constructor(rootUid, contextMenu, projects, users, worklogs) {
        super();
        this.rootUid = rootUid;
        this.contextMenu = contextMenu;
        this.projects = projects;
        this.users = users;
        this.worklogs = worklogs;
    }
}
/**
 * Stores per-element editor graph data for one project or user node.
 */
class EditorElementMetaData extends MetaDataBase {
    id;
    isProject;
    x;
    z;
    min;
    max;
    connections;
    monthModels;
    static typestatic = "EditorElement";
    /**
     * @param id Identifies the represented project or user.
     * @param isProject Distinguishes project nodes from user nodes.
     * @param x Stores the X position in the editor layout.
     * @param z Stores the Z position in the editor layout.
     * @param min Stores the minimum aggregated value for the element.
     * @param max Stores the maximum aggregated value for the element.
     * @param connections Lists connected element model UIDs.
     * @param monthModels Maps month indices to month-model UIDs.
     */
    constructor(id, isProject, x, z, min, max, connections, monthModels) {
        super();
        this.id = id;
        this.isProject = isProject;
        this.x = x;
        this.z = z;
        this.min = min;
        this.max = max;
        this.connections = connections;
        this.monthModels = monthModels;
    }
}
/**
 * Selects which world axis a reference plane belongs to.
 */
var ReferencePlaneAxis;
(function (ReferencePlaneAxis) {
    /** Plane normal points along the x-axis. */
    ReferencePlaneAxis[ReferencePlaneAxis["X"] = 0] = "X";
    /** Plane normal points along the y-axis. */
    ReferencePlaneAxis[ReferencePlaneAxis["Y"] = 1] = "Y";
    /** Plane normal points along the z-axis. */
    ReferencePlaneAxis[ReferencePlaneAxis["Z"] = 2] = "Z";
})(ReferencePlaneAxis || (ReferencePlaneAxis = {}));
/**
 * Stores the placement of one reference plane in a multi-plane setup.
 */
class ReferencePlaneMetaData extends MetaDataBase {
    index;
    axis;
    position;
    static typestatic = "ReferencePlane";
    /**
     * @param index Identifies the plane within the reference-plane set.
     * @param axis Selects the axis the plane is aligned to.
     * @param position Stores the plane position along that axis.
     */
    constructor(index, axis, position) {
        super();
        this.index = index;
        this.axis = axis;
        this.position = position;
    }
}
/**
 * Stores the axis labels and helper-model references for a scatterplot.
 */
class ScatterplotMetaData extends MetaDataBase {
    valueNames;
    yAxisUid;
    xAxisUid;
    zAxisUid;
    yAxisMinLableUid;
    xAxisMinLableUid;
    zAxisMinLableUid;
    yAxisMaxLableUid;
    xAxisMaxLableUid;
    zAxisMaxLableUid;
    static typestatic = "Scatterplot";
    /**
     * @param valueNames Lists the value names available for mapping.
     */
    constructor(valueNames, yAxisUid, xAxisUid, zAxisUid, yAxisMinLableUid, xAxisMinLableUid, zAxisMinLableUid, yAxisMaxLableUid, xAxisMaxLableUid, zAxisMaxLableUid) {
        super();
        this.valueNames = valueNames;
        this.yAxisUid = yAxisUid;
        this.xAxisUid = xAxisUid;
        this.zAxisUid = zAxisUid;
        this.yAxisMinLableUid = yAxisMinLableUid;
        this.xAxisMinLableUid = xAxisMinLableUid;
        this.zAxisMinLableUid = zAxisMinLableUid;
        this.yAxisMaxLableUid = yAxisMaxLableUid;
        this.xAxisMaxLableUid = xAxisMaxLableUid;
        this.zAxisMaxLableUid = zAxisMaxLableUid;
    }
}
/**
 * Stores which data dimensions are mapped to a scatterplot element.
 */
class ScatterplotMappingMetaData extends MetaDataBase {
    x;
    y;
    z;
    scale;
    color;
    static typestatic = "ScatterplotMapping";
    constructor(x, y, z, scale, color) {
        super();
        this.x = x;
        this.y = y;
        this.z = z;
        this.scale = scale;
        this.color = color;
    }
}
/**
 * Stores the raw numeric values represented by one scatterplot element.
 */
class ScatterplotElementMetaData extends MetaDataBase {
    values;
    static typestatic = "ScatterplotElement";
    /**
     * @param values Lists the element's numeric values in value-name order.
     */
    constructor(values) {
        super();
        this.values = values;
    }
}

/**
 * Makes a model interactive by attaching behaviors and click/double-click actions to it. `InteractionModule`
 * reacts to this facet by registering the model for interaction handling (e.g. auto-creating a selection
 * highlight) and runs the configured `onClick`/`onDoubleClick` actions and `behaviors` in response to
 * controller/speech input. `GrabbableFacet` and `PushableFacet` are both implemented as shorthands that get
 * turned into an `InteractionFacet` under the hood.
 */
class InteractionFacet extends Facet {
    static typestatic = "Interaction";
    static propTypes = {
        behaviors: {
            type: PropertyType.ArraySafe,
            elementType: {
                type: PropertyType.Struct,
                properties: {
                    behavior: { type: PropertyType.Utf16String }
                }
            }
        },
        onClick: {
            type: PropertyType.ArraySafe,
            elementType: {
                type: PropertyType.Struct,
                properties: {
                    action: { type: PropertyType.Utf16String }
                }
            }
        },
        onDoubleClick: {
            type: PropertyType.ArraySafe,
            elementType: {
                type: PropertyType.Struct,
                properties: {
                    action: { type: PropertyType.Utf16String }
                }
            }
        },
        description: {
            type: PropertyType.Utf16StringSafe
        }
    };
    get byteLength() {
        return (calcArrayByteLength(this.behaviors) +
            calcArrayByteLength(this.onClick) +
            calcArrayByteLength(this.onDoubleClick) +
            calcStringByteLength(this.description));
    }
    /** Lists the behaviors that augment runtime interaction handling. */
    behaviors;
    onClick;
    onDoubleClick;
    /** Provides optional descriptive text for tooling. */
    description;
    /**
     * @param params Groups the interaction behaviors and actions that should be attached to the model.
     */
    constructor(params = {}) {
        super();
        this.behaviors = params.behaviors ?? [];
        this.onClick = params.onClick ?? [];
        this.onDoubleClick = params.onDoubleClick ?? [];
        this.description = params.description;
    }
    serialization(stream) {
        this.behaviors = stream.array(this.behaviors);
        this.onClick = stream.array(this.onClick);
        this.onDoubleClick = stream.array(this.onDoubleClick);
        this.description = stream.utf16StringSafe(this.description);
    }
    static is(facet) {
        return facet.type === InteractionFacet.typestatic;
    }
}
FacetFactory.register(InteractionFacet);

/**
 * Generic container for arbitrary application/interaction metadata on a model. Each `MetaDataBase` subtype
 * (e.g. `ProjTreeProjectLabelMetaData`, `ConfigureLayoutMetaData`, `EditorMetaData`, `ScatterplotMetaData`) is
 * keyed by its own `type` string as the facet's `subType`, so a model can hold several `MetaDataFacet`
 * instances at once. `ModelUtils.setMetaData`/`getMetaData` read and write it, `InteractionMetaDataManager`
 * distributes updates to registered listeners, and `ScriptModule` notifies scripts via
 * `onUpdatePersistentVariables` when it is removed.
 */
class MetaDataFacet extends Facet {
    data;
    static typestatic = "MetaDataFacet";
    get byteLength() {
        return calcObjectByteLength(this.data);
    }
    constructor(
    /** Carries one typed metadata payload stored on the model. */
    data) {
        super(data.type);
        this.data = data;
    }
    /**
     * Serializes the typed metadata payload and restores its subtype while reading.
     */
    serialization(stream) {
        if (stream.isReading && !this.data) {
            // We need the is function from MetaDataBase
            this.data = Object.create(MetaDataBase.prototype);
        }
        this.data = stream.object(this.data);
        if (stream.isReading) {
            this.subType = this.data.type;
        }
    }
    static is(facet) {
        return facet.type === MetaDataFacet.typestatic;
    }
}
FacetFactory.register(MetaDataFacet);

/**
 * Customizes the local player's movement controls: movement/look/height speed, whether vertical look is
 * enabled, and which input axes drive forward/right/up movement and looking. `InputService` subscribes to
 * this facet and applies the given `params` to override its default movement axis mapping.
 */
class MovementInputMappingFacet extends Facet {
    params;
    static typestatic = "MovementInputMappingFacet";
    static metaData = {
        export: true,
        description: "Customizes the users movement settings",
        category: "Interaction"
    };
    get byteLength() {
        return calcObjectByteLength(this.params);
    }
    constructor(
    /** Defines the movement speeds and axis names that should override the defaults. */
    params = {}) {
        super();
        this.params = params;
    }
    serialization(stream) {
        this.params = stream.object(this.params);
    }
    static is(facet) {
        return facet.type === MovementInputMappingFacet.typestatic;
    }
}
FacetFactory.register(MovementInputMappingFacet);

/**
 * Visualizes the selection radius used by proximity-based selection. `ProximitySelect` attaches this facet
 * (with `radius` set to its `maxLength`) to the controller model when `showSphere` is enabled, and
 * `ProximitySphereRenderable` renders it as a sphere mesh and periodically queries nearby collision shapes to
 * update which models are highlighted as selectable.
 * @see {@link flavr-client!modules/rendering/line/ProximitySphereRenderable.ProximitySphereRenderable | ProximitySphereRenderable}
 */
class ProximitySphere extends Facet {
    radius;
    static typestatic = "ProximitySphere";
    static metaData = {
        export: false,
        description: "Displays the proximity selection radius as a sphere",
        category: "Rendering"
    };
    static MAX_BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT; // radius
    get byteLength() {
        return ProximitySphere.MAX_BYTE_LENGTH;
    }
    constructor(
    /** Radius of the proximity-selection sphere in world units. */
    radius = 1) {
        super();
        this.radius = radius;
    }
    serialization(stream) {
        this.radius = stream.float32(this.radius);
    }
    static is(facet) {
        return facet.type === ProximitySphere.typestatic;
    }
}
FacetFactory.register(ProximitySphere);

/**
 * Visualizes a controller's selection ray: origin, direction, length and an optional hit-point normal.
 * `RaySelect` updates this facet on the controller model every time the ray is cast, changing `colorHex`
 * when it hits a model with a `SelectionHighlightFacet`, and `ControllerView`/`ControllerPeer` toggle its
 * visibility while a ray is active.
 * @see {@link flavr-client!modules/rendering/line/RaycastLineRenderable.RaycastLineRenderable | RaycastLineRenderable}
 */
class RaycastLine extends Facet {
    origin;
    direction;
    maxLength;
    colorHex;
    autoLength;
    hitNorm;
    static typestatic = "RaycastLine";
    static metaData = {
        export: true,
        description: "Displays the raycast as a line with a hitpoint indicator",
        category: "Rendering"
    };
    static MAX_BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT * (3 + 3 + 1 + 3) + // origin, direction, maxLength, hitNorm
        Uint32Array.BYTES_PER_ELEMENT + // colorHex
        BOOLEAN_BYTES_PER_ELEMENT; // autoLength
    get byteLength() {
        return RaycastLine.MAX_BYTE_LENGTH;
    }
    constructor(
    /** World-space start point of the ray. */
    origin = [0, 0, 0], 
    /** Normalized world-space direction of the ray. */
    direction = [0, 1, 0], 
    /** Maximum ray length before any hit shortening is applied. */
    maxLength = 100, 
    /** Color used to render the line and hit indicator. */
    colorHex = 0, 
    /** Whether the visible ray should stop at the first hit automatically. */
    autoLength = false, 
    /** Optional surface normal at the current hit point. */
    hitNorm) {
        super();
        this.origin = origin;
        this.direction = direction;
        this.maxLength = maxLength;
        this.colorHex = colorHex;
        this.autoLength = autoLength;
        this.hitNorm = hitNorm;
    }
    serialization(stream) {
        this.origin = stream.number3(this.origin);
        this.direction = stream.number3(this.direction);
        this.maxLength = stream.float32(this.maxLength);
        this.colorHex = stream.uint32(this.colorHex);
        this.autoLength = stream.boolean(this.autoLength);
        this.hitNorm = stream.number3Safe(this.hitNorm);
    }
    static is(facet) {
        return facet.type === RaycastLine.typestatic;
    }
}
FacetFactory.register(RaycastLine);

/**
 * Marks a model as a ray-curve teleporter and binds it to a controller. `TeleporterModule` reacts to this
 * facet by creating a `Teleporter` for the model, aiming its target arrow using `params.directionYaw`, and
 * continuously updating the reachable teleport target as the owning controller moves. Removing the facet
 * (or the controller disconnecting) tears down the associated teleporter state.
 */
class RayCurveTeleporterFacet extends Facet {
    static typestatic = "RayCurveTeleporterFacet";
    static metaData = {
        export: true,
        description: "Displays a ray parabola that shows the teleport position",
        category: "Rendering"
    };
    static propTypes = {
        controllerUid: {
            type: PropertyType.UidSafe
        },
        params: {
            type: PropertyType.StructSafe,
            properties: {
                directionYaw: {
                    type: PropertyType.Float32Safe
                }
            }
        }
    };
    get byteLength() {
        return FLOAT32_BYTES_PER_ELEMENT + UID_SAFE_BYTES_PER_ELEMENT;
    }
    controllerUid;
    params = {};
    /**
     * @param controllerUidOrParams Controller model UID, or just params for legacy call sites.
     * @param params Optional teleporter tuning values when the first argument is a controller UID.
     */
    constructor(controllerUidOrParams, params = {}) {
        super();
        if (typeof controllerUidOrParams === "number") {
            this.controllerUid = controllerUidOrParams;
            this.params = params;
        }
        else {
            // Backward compatibility for older call sites that passed only params.
            this.controllerUid = undefined;
            this.params = controllerUidOrParams ?? {};
        }
    }
    serialization(stream) {
        if (stream.isReading) {
            this.params = {};
        }
        this.params.directionYaw = stream.float32Safe(this.params.directionYaw);
        this.controllerUid = stream.uidSafe(this.controllerUid);
    }
    static is(facet) {
        return facet.type === RayCurveTeleporterFacet.typestatic;
    }
}
FacetFactory.register(RayCurveTeleporterFacet);

/**
 * Controls whether `InteractionModule` shows a selection-highlight overlay in `color` when a controller
 * selects this model, and lets that color be overridden per model. `InteractionModule` auto-adds this facet
 * (with default settings) whenever a model receives an `InteractionFacet` and no highlight facet exists yet.
 */
class SelectionHighlightFacet extends Facet {
    enabled;
    color;
    static typestatic = "SelectionHighlightFacet";
    static metaData = {
        export: true,
        description: "Controls whether a model can show a selection highlight",
        category: "Interaction"
    };
    static propTypes = {
        enabled: {
            type: PropertyType.Boolean,
            isRequired: true,
            default: true,
        },
        color: {
            type: PropertyType.ColorHex,
            isRequired: true,
            default: 0x000000,
        }
    };
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT + UINT32_BYTES_PER_ELEMENT;
    }
    constructor(
    /** Whether automatic selection highlighting is allowed for this model. */
    enabled = true, 
    /** Override color used for the selection highlight overlay. */
    color = 0x000000) {
        super();
        this.enabled = enabled;
        this.color = color;
    }
    serialization(stream) {
        this.enabled = stream.boolean(this.enabled);
        this.color = stream.uint32(this.color);
    }
    static is(facet) {
        return facet.type === SelectionHighlightFacet.typestatic;
    }
}
FacetFactory.register(SelectionHighlightFacet);

/**
 * Intended to create a new child model named after `prefixName` below `parentUid`, analogous to
 * `CreateLabelSpeechCommand`. No client or server module currently reacts to this speech command - as of this
 * writing it appears to be unused/dead code.
 */
class CreateModelSpeechCommand {
    prefixName;
    parentUid;
    constructor(
    /** Base name used when generating the new model. */
    prefixName, 
    /** UID of the model that should receive the created child model. */
    parentUid) {
        this.prefixName = prefixName;
        this.parentUid = parentUid;
    }
    static typestatic = "CreateModelSpeechCommand";
    command = CreateModelSpeechCommand.typestatic;
    static is(params) {
        return params.command === CreateModelSpeechCommand.typestatic;
    }
}

/**
 * Recognized by `CreateLabelInteractionHandler.onSpeechCommand` for the "label" voice command: it creates a new
 * child model named `` `${prefixName}_${uuid}` `` under `parentUid` and attaches a `LabelFacet` with the
 * dictated text.
 */
class CreateLabelSpeechCommand {
    prefixName;
    parentUid;
    constructor(
    /** Base name used when generating the new label model. */
    prefixName, 
    /** UID of the model that should receive the created label. */
    parentUid) {
        this.prefixName = prefixName;
        this.parentUid = parentUid;
    }
    static typestatic = "CreateLabelSpeechCommand";
    command = CreateLabelSpeechCommand.typestatic;
    static is(param) {
        return param.command === CreateLabelSpeechCommand.typestatic;
    }
}

/**
 * Server-only facet: when the client identified by `clientIdx` disconnects, `SessionService` traverses the
 * session's root model looking for this facet and, on a match, sends a `DeleteModelMessage` that removes
 * this model and all of its descendants. Remove the facet if the object shouldn't be deleted on disconnect
 * anymore. `PeerManager`/`PeerModule` attach it (often alongside `RemoveFacetsOnDisconnectFacet`) to models
 * that should be cleaned up when their owning client leaves, e.g. picked-up tool-belt objects.
 */
class DeleteModelOnDisconnectFacet extends Facet {
    clientIdx;
    static typestatic = "DeleteModelOnDisconnectFacet";
    static metaData = {
        export: false,
        description: "Deletes this model and all descendants if the client that created this model leaves the session",
        category: "Networking"
    };
    static propTypes = {
        clientIdx: {
            type: PropertyType.Float32,
            isRequired: true
        }
    };
    /**
     * @param clientIdx Numeric client index whose disconnect should trigger deletion of this model subtree.
     */
    constructor(
    /** Numeric client index whose disconnect should trigger deletion of this model subtree. */
    clientIdx) {
        super();
        this.clientIdx = clientIdx;
    }
    get byteLength() {
        return FLOAT32_BYTES_PER_ELEMENT;
    }
    serialization(stream) {
        this.clientIdx = stream.float32(this.clientIdx);
    }
    static is(facet) {
        return facet.type === DeleteModelOnDisconnectFacet.typestatic;
    }
}
FacetFactory.register(DeleteModelOnDisconnectFacet);

// ToDo: Remove this facet and the associated code in favor of DeleteModelOnDisconnectedFacet which is handled on the
//  server
/**
 * Marks a model to be destroyed when the client identified by `clientId` disconnects. `PeerModule`
 * registers the model with `PeerManager.addDestroyModelOnDisconnect` while the facet is present and
 * cancels the registration when it is removed. Legacy mechanism being superseded by
 * `DeleteModelOnDisconnectFacet`, whose cleanup is handled server-side.
 */
class DestroyOnDisconnectFacet extends Facet {
    clientId;
    static typestatic = "DestroyOnDisconnectFacet";
    static metaData = {
        export: true,
        description: "",
        category: "Misc"
    };
    static propTypes = {
        clientId: { type: PropertyType.Utf16String, isRequired: true }
    };
    get byteLength() {
        return calcStringByteLength(this.clientId);
    }
    /**
     * @param clientId Socket/client identifier whose disconnect should destroy this model.
     */
    constructor(
    /** Socket/client identifier whose disconnect should destroy this model. */
    clientId) {
        super();
        this.clientId = clientId;
    }
    serialization(stream) {
        this.clientId = stream.utf16String(this.clientId);
    }
    static is(facet) {
        return facet.type === DestroyOnDisconnectFacet.typestatic;
    }
}
FacetFactory.register(DestroyOnDisconnectFacet);

/**
 * Marks a model as currently being manipulated by the client `clientIdx` through the transform gizmo,
 * preventing other clients from selecting or grabbing it while the lock is held. `GizmoModule` reacts
 * to it being added or removed by calling `GizmoService.handleGizmoLockChanged`, and the server's
 * `SessionService` removes a client's lock facets when that client disconnects.
 */
class GizmoLockFacet extends Facet {
    clientIdx;
    static typestatic = "GizmoLockFacet";
    static metaData = {
        export: false,
        description: "Marks a model as currently locked for gizmo editing by a specific client.",
        category: "General"
    };
    /**
     * @param clientIdx Numeric client index currently holding the gizmo lock for this model.
     */
    constructor(
    /** Numeric client index currently holding the gizmo lock for this model. */
    clientIdx) {
        super(GizmoLockFacet.typestatic);
        this.clientIdx = clientIdx;
    }
    get byteLength() {
        return FLOAT32_BYTES_PER_ELEMENT;
    }
    serialization(stream) {
        this.clientIdx = stream.float32(this.clientIdx);
    }
    static is(facet) {
        return facet.type === GizmoLockFacet.typestatic;
    }
}
FacetFactory.register(GizmoLockFacet);

/**
 * Tracks ownership of a networked model: `networkId`, the owning and creating client, and when the
 * ownership last changed. `NetworkTransformService` and `NetworkEntitiesService` on the client read it
 * to decide which peer drives the model's transform and to raise owner-changed callbacks; when
 * `destroyWhenOwnerDisconnect` is set, the owning peer's disconnect causes the model to be deleted.
 */
class NetworkFacet extends Facet {
    networkId;
    creatorId;
    ownerId;
    lastOwnerTime;
    destroyWhenOwnerDisconnect;
    static typestatic = "NetworkFacet";
    static metaData = {
        export: true,
        description: "",
        category: "Misc"
    };
    get byteLength() {
        return (Uint32Array.BYTES_PER_ELEMENT + // networkId
            Uint32Array.BYTES_PER_ELEMENT + // ownerId
            Uint32Array.BYTES_PER_ELEMENT + // creatorId
            Uint32Array.BYTES_PER_ELEMENT + // lastOwnerTime
            BOOLEAN_BYTES_PER_ELEMENT // destroyWhenOwnerDisconnect
        );
    }
    /**
     * @param networkId Identifies the networked entity across peers.
     * @param creatorId Identifies the client that originally created the model.
     * @param ownerId Identifies the client that currently owns the model.
     * @param lastOwnerTime Stores when ownership last changed.
     * @param destroyWhenOwnerDisconnect Removes the model automatically when the owner disconnects.
     */
    constructor(
    /** Identifies the networked entity across peers. */
    networkId, 
    /** Identifies the client that originally created the model. */
    creatorId, 
    /** Identifies the client that currently owns the model. */
    ownerId, 
    /** Stores when ownership last changed. */
    lastOwnerTime, 
    /** Removes the model automatically when the owner disconnects. */
    destroyWhenOwnerDisconnect) {
        super();
        this.networkId = networkId;
        this.creatorId = creatorId;
        this.ownerId = ownerId;
        this.lastOwnerTime = lastOwnerTime;
        this.destroyWhenOwnerDisconnect = destroyWhenOwnerDisconnect;
    }
    serialization(stream) {
        this.networkId = stream.uid(this.networkId);
        this.ownerId = stream.uint32Safe(this.ownerId);
        this.creatorId = stream.uint32Safe(this.creatorId);
        this.lastOwnerTime = stream.uint32Safe(this.lastOwnerTime);
        this.destroyWhenOwnerDisconnect = stream.booleanSafe(this.destroyWhenOwnerDisconnect);
    }
    static is(facet) {
        return facet.type === NetworkFacet.typestatic;
    }
}
FacetFactory.register(NetworkFacet);

/**
 * Lists facets (`facetsToBeRemoved`) that should be stripped from the model once the client
 * `clientIdx` disconnects, then removes itself. Handled entirely server-side by `SessionService`,
 * which removes each listed facet from the model on disconnect. Used e.g. by `ModelPickupModule` to
 * undo attachment facets left by a disconnected user and by `NetworkEntitiesService` to clean up
 * `NetworkFacet`.
 */
class RemoveFacetsOnDisconnectFacet extends Facet {
    facetsToBeRemoved;
    clientIdx;
    static typestatic = "RemoveFacetsOnDisconnectFacet";
    static metaData = {
        export: true,
        description: "Solution for removing facets when a user disconnects. Needs a model to put onto, a list of Facets to be removed from the model" +
            "and a clientIdx to work. The facet will remove itself after the disconnect happened. The magic happens on the server",
        category: "General"
    };
    get byteLength() {
        return Float32Array.BYTES_PER_ELEMENT + // clientIdx
            calcArrayByteLength(this.facetsToBeRemoved); // facetsToBeRemoved
    }
    /**
     * @param facetsToBeRemoved Facets that should be stripped from the model when the client disconnects.
     * @param clientIdx Numeric client index whose disconnect triggers the cleanup.
     */
    constructor(
    /** Facets that should be stripped from the model when the client disconnects. */
    facetsToBeRemoved, 
    /** Numeric client index whose disconnect triggers the cleanup. */
    clientIdx) {
        super(RemoveFacetsOnDisconnectFacet.typestatic);
        this.facetsToBeRemoved = facetsToBeRemoved;
        this.clientIdx = clientIdx;
    }
    serialization(stream) {
        this.clientIdx = stream.float32(this.clientIdx);
        this.facetsToBeRemoved = stream.array(this.facetsToBeRemoved);
    }
    static is(facet) {
        return facet.type === RemoveFacetsOnDisconnectFacet.typestatic;
    }
}
FacetFactory.register(RemoveFacetsOnDisconnectFacet);

/**
 * Replaces a model's normal appearance with an animated loading indicator (a grid of small boxes that
 * fill in as `progress` rises towards 1), rendered by `BusyIndicatorRenderable`. Used for example by
 * `ToolBeltLoadingSpinner`, which creates a dedicated child model carrying just this facet while an asset
 * loads. If `destroyOnFinished` is set, the model is removed once the indicator finishes animating out.
 * @see {@link flavr-client!modules/rendering/busyIndicator/BusyIndicatorRenderable.BusyIndicatorRenderable | BusyIndicatorRenderable}
 */
class BusyIndicator extends Facet {
    progress;
    destroyOnFinished;
    static typestatic = "BusyIndicator";
    static metaData = {
        export: true,
        description: "Shows a busy indicator instead of the mesh",
        category: "Misc"
    };
    static propTypes = {
        progress: { type: PropertyType.Float32Safe },
        destroyOnFinished: { type: PropertyType.Boolean, default: false }
    };
    get byteLength() {
        return Float32Array.BYTES_PER_ELEMENT + BOOLEAN_BYTES_PER_ELEMENT;
    }
    constructor(
    /** Optional normalized progress value used to animate the indicator fill state. */
    progress, 
    /** Whether the model should be removed automatically once the indicator completes. */
    destroyOnFinished) {
        super();
        this.progress = progress;
        this.destroyOnFinished = destroyOnFinished;
    }
    serialization(stream) {
        this.progress = stream.float32Safe(this.progress);
        this.destroyOnFinished = stream.boolean(this.destroyOnFinished || false);
    }
    static is(facet) {
        return facet.type === BusyIndicator.typestatic;
    }
}
FacetFactory.register(BusyIndicator);

/**
 * Carries an `id` intended to mark a model as cloneable/identify its clone source. No client or server
 * module reads or writes this facet anywhere in the codebase - as of this writing it appears to be
 * unused/dead code.
 */
class CloneableFacet extends Facet {
    id;
    static typestatic = "CloneableFacet";
    static metaData = {
        export: true,
        description: "",
        category: "Misc"
    };
    static propTypes = {
        id: { type: PropertyType.Utf16String, isRequired: true }
    };
    get byteLength() {
        return calcStringByteLength();
    }
    constructor(
    /** Identifier describing the clone source or clone group. */
    id) {
        super();
        this.id = id;
    }
    serialization(stream) {
        this.id = stream.utf16String(this.id);
    }
    static is(facet) {
        return facet.type === CloneableFacet.typestatic;
    }
}
FacetFactory.register(CloneableFacet);

// This class is for loading ROS bags -> google Ros bag
/**
 * Point-cloud source/streaming modes supported by {@link PointCloudFacet}.
 */
var PointCloudType;
(function (PointCloudType) {
    /** Builds a point cloud from a static mesh source. */
    PointCloudType[PointCloudType["MeshBased"] = 0] = "MeshBased";
    /** Builds or updates a point cloud from image-based input. */
    PointCloudType[PointCloudType["ImageBased"] = 1] = "ImageBased";
    /** Streams a point cloud from ROS bag data. */
    PointCloudType[PointCloudType["RosBagBased"] = 2] = "RosBagBased";
})(PointCloudType || (PointCloudType = {}));
/**
 * Configures a 3D point-cloud visualization, rendered by `PointCloudRenderable`, in one of three modes:
 * a static mesh-derived cloud, an image-based cloud, or a live ROS-bag-streamed cloud (with topics,
 * timing and transform options). `RosBagUIService` creates models carrying this facet to play back
 * recorded or shared ROS bag data.
 * @see {@link flavr-client!modules/rosbag/PointCloudRenderable.PointCloudRenderable | PointCloudRenderable}
 */
class PointCloudFacet extends Facet {
    params;
    static typestatic = "PointCloudFacet";
    static metaData = {
        export: true,
        description: "Displays a point cloud",
        category: "Rendering"
    };
    get byteLength() {
        const byteLength = Uint8Array.BYTES_PER_ELEMENT + // pointCloudType
            calcStringByteLength(this.params.url) +
            calcFastFloat32ByteLength(this.params.color) +
            BOOLEAN_BYTES_PER_ELEMENT + // use vertex colors
            Float32Array.BYTES_PER_ELEMENT; // point Size
        switch (this.params.type) {
            case PointCloudType.MeshBased:
                return byteLength;
            case PointCloudType.ImageBased:
                return byteLength + Uint32Array.BYTES_PER_ELEMENT; // time out interval
            case PointCloudType.RosBagBased:
                return (byteLength +
                    Uint32Array.BYTES_PER_ELEMENT + // time out interval
                    calcArrayByteLength(this.params.topics) +
                    calcStringByteLength(this.params.identifier) +
                    BOOLEAN_BYTES_PER_ELEMENT + // UseCustomMaterial
                    BOOLEAN_BYTES_PER_ELEMENT + // ShareWithOthers
                    BOOLEAN_BYTES_PER_ELEMENT + // UseTransform
                    BOOLEAN_BYTES_PER_ELEMENT + // UseOnlyLocalTime
                    3 * Float32Array.BYTES_PER_ELEMENT + // position
                    4 * Float32Array.BYTES_PER_ELEMENT); // rotation
        }
    }
    constructor(
    /** Full point-cloud configuration for the selected source mode. */
    params) {
        super();
        this.params = params;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.params = {};
        }
        this.params.type = stream.uint8(this.params.type);
        this.params.url = stream.utf16String(this.params.url);
        const byteLength = stream.uint32(this.params.color === undefined ? NaN : this.params.color.length);
        if (!isNaN(byteLength)) {
            this.params.color = stream.numberFloat32Array(this.params.color, byteLength);
        }
        else {
            this.params.color = undefined;
        }
        this.params.useVertexColor = stream.booleanSafe(this.params.useVertexColor);
        this.params.pointSize = stream.float32Safe(this.params.pointSize);
        switch (this.params.type) {
            case PointCloudType.MeshBased:
                break;
            case PointCloudType.ImageBased:
                this.params.timeOutInterval = stream.uint32(this.params.timeOutInterval);
                break;
            case PointCloudType.RosBagBased:
                this.params.timeOutInterval = stream.uint32(this.params.timeOutInterval);
                this.params.topics = stream.array(this.params.topics);
                this.params.identifier = stream.utf16String(this.params.identifier);
                this.params.useCustomMaterial = stream.boolean(this.params.useCustomMaterial);
                this.params.shareWithOthers = stream.boolean(this.params.shareWithOthers);
                this.params.useTransform = stream.boolean(this.params.useTransform);
                this.params.useOnlyLocalTime = stream.boolean(this.params.useOnlyLocalTime);
                this.params.position = stream.number3(this.params.position);
                this.params.rotation = stream.number4(this.params.rotation);
                break;
            default:
                throw new Error(`PointCloudType type ${this.params.type} is unsupported`);
        }
    }
    static is(facet) {
        return facet.type === PointCloudFacet.typestatic;
    }
    static isMeshBasedParams(params) {
        return params.type === PointCloudType.MeshBased;
    }
    static isImageBasedParams(params) {
        return params.type === PointCloudType.ImageBased;
    }
    static isRosBagBasedParams(params) {
        return params.type === PointCloudType.RosBagBased;
    }
}
FacetFactory.register(PointCloudFacet);

/**
 * Aligns tooltip text within the tooltip bubble.
 */
var TooltipTextAlign;
(function (TooltipTextAlign) {
    TooltipTextAlign[TooltipTextAlign["Left"] = 0] = "Left";
    TooltipTextAlign[TooltipTextAlign["Center"] = 1] = "Center";
    TooltipTextAlign[TooltipTextAlign["Right"] = 2] = "Right";
})(TooltipTextAlign || (TooltipTextAlign = {}));
/**
 * Selects where a tooltip should be displayed relative to the model or controller.
 */
var TooltipPlacement;
(function (TooltipPlacement) {
    TooltipPlacement[TooltipPlacement["CONTROLLER"] = 0] = "CONTROLLER";
    TooltipPlacement[TooltipPlacement["LEFT_OF_MODEL"] = 1] = "LEFT_OF_MODEL";
    TooltipPlacement[TooltipPlacement["RIGHT_OF_MODEL"] = 2] = "RIGHT_OF_MODEL";
    TooltipPlacement[TooltipPlacement["ABOVE_MODEL"] = 3] = "ABOVE_MODEL";
    TooltipPlacement[TooltipPlacement["BELOW_MODEL"] = 4] = "BELOW_MODEL";
})(TooltipPlacement || (TooltipPlacement = {}));
// A model can have information that is displayed in the tooltip.
/**
 * Attaches speech-bubble tooltip content (text, avatar, placement, colors) to a model. `TooltipInfoRenderer`
 * shows the bubble anchored to the model for any `placement` other than `CONTROLLER`; for `CONTROLLER`
 * placement, `ShowTooltipsInteractionHandler` instead copies the text/align/avatar onto the selecting
 * controller's `Tooltip` facet so it renders near the controller rather than the model.
 * @see {@link flavr-client!modules/rendering/tooltip/TooltipInfoRenderer.TooltipInfoRenderer | TooltipInfoRenderer}
 */
class TooltipInfoFacet extends Facet {
    text;
    align;
    avatar;
    placement;
    color;
    borderColor;
    isLockable;
    static typestatic = "TooltipInfoFacet";
    static metaData = {
        export: true,
        description: "Specifies the information that is used by a Tooltip facet",
        category: "Rendering",
        dependencies: ["Tooltip"]
    };
    static propTypes = {
        text: { type: PropertyType.LocalizationString },
        align: {
            type: PropertyType.EnumUint8,
            items: {
                0: "Left",
                1: "Center",
                2: "Right"
            },
            default: TooltipTextAlign.Center
        },
        avatar: { type: PropertyType.Utf16StringSafe },
        placement: {
            type: PropertyType.EnumUint8,
            items: {
                0: "CONTROLLER",
                1: "LEFT_OF_MODEL",
                2: "RIGHT_OF_MODEL",
                3: "ABOVE_MODEL",
                4: "BELOW_MODEL"
            }
        },
        color: { type: PropertyType.ColorHexSafe },
        borderColor: { type: PropertyType.ColorHexSafe },
        isLockable: { type: PropertyType.BooleanSafe }
    };
    static BYTE_LENGTH = Uint8Array.BYTES_PER_ELEMENT + // align
        Uint8Array.BYTES_PER_ELEMENT + // placement
        Uint32Array.BYTES_PER_ELEMENT * 2 + // color
        BOOLEAN_BYTES_PER_ELEMENT; // isLockable
    get byteLength() {
        return calcLocalizationStringByteLength(this.text) + calcStringByteLength(this.avatar) + TooltipInfoFacet.BYTE_LENGTH;
    }
    constructor(
    /** Provides the tooltip text or localization key. */
    text = "", align = TooltipTextAlign.Center, 
    /** Optionally shows an avatar image alongside the tooltip. */
    avatar, placement = TooltipPlacement.CONTROLLER, 
    /** Overrides the tooltip background color. */
    color, 
    /** Overrides the tooltip border color. */
    borderColor, 
    /** Indicates whether the tooltip may stay pinned. */
    isLockable) {
        super();
        this.text = text;
        this.align = align;
        this.avatar = avatar;
        this.placement = placement;
        this.color = color;
        this.borderColor = borderColor;
        this.isLockable = isLockable;
    }
    serialization(stream) {
        this.text = stream.localizationString(this.text);
        this.align = stream.uint8(this.align);
        this.avatar = stream.utf16StringSafe(this.avatar);
        this.placement = stream.uint8(this.placement);
        this.color = stream.uint32Safe(this.color);
        this.borderColor = stream.uint32Safe(this.borderColor);
        this.isLockable = stream.booleanSafe(this.isLockable);
    }
    static is(facet) {
        return facet.type === TooltipInfoFacet.typestatic;
    }
}
FacetFactory.register(TooltipInfoFacet);
/**
 * Renders tooltip text/avatar attached directly to a controller model, via `TooltipRenderable`.
 * `ShowTooltipsInteractionHandler` sets this facet on a controller when a selected model's
 * `TooltipInfoFacet` has `placement === TooltipPlacement.CONTROLLER`. This placement has a known
 * drawback: the user may not see the tooltip if the controller is outside their field of view.
 * @see {@link flavr-client!modules/rendering/tooltip/TooltipRenderable.TooltipRenderable | TooltipRenderable}
 */
class Tooltip extends Facet {
    label;
    align;
    avatar;
    static typestatic = "Tooltip";
    static metaData = {
        export: true,
        description: "Displays the info from a TooltipInfoFacet at a controller",
        category: "Rendering",
        dependencies: ["TooltipInfoFacet"],
    };
    get byteLength() {
        return (calcLocalizationStringByteLength(this.label) +
            Uint8Array.BYTES_PER_ELEMENT + // align
            calcStringByteLength(this.avatar));
    }
    constructor(
    /** Provides the tooltip label shown near the controller. */
    label, align = TooltipTextAlign.Center, 
    /** Optionally shows an avatar image alongside the tooltip. */
    avatar) {
        super();
        this.label = label;
        this.align = align;
        this.avatar = avatar;
    }
    serialization(stream) {
        this.label = stream.localizationString(this.label);
        this.align = stream.uint8(this.align);
        this.avatar = stream.utf16StringSafe(this.avatar);
    }
    static is(facet) {
        return facet.type === Tooltip.typestatic;
    }
}
FacetFactory.register(Tooltip);

/**
 * Selects which shading model should be used for a primitive material.
 */
var ShadingMode;
(function (ShadingMode) {
    /** Lit phong shading without shadow casting. */
    ShadingMode[ShadingMode["Phong"] = 0] = "Phong";
    /** Lit phong shading with shadow support. */
    ShadingMode[ShadingMode["PhongWithShadows"] = 1] = "PhongWithShadows";
    ShadingMode[ShadingMode["Simple"] = 2] = "Simple";
    /** Unlit flat shading. */
    ShadingMode[ShadingMode["Unlit"] = 3] = "Unlit";
    ShadingMode[ShadingMode["TransparentGrid"] = 4] = "TransparentGrid";
})(ShadingMode || (ShadingMode = {}));
/**
 * Selects how outlines should be rendered.
 */
var OutlineMode;
(function (OutlineMode) {
    /** Disables outlines. */
    OutlineMode[OutlineMode["None"] = 0] = "None";
    OutlineMode[OutlineMode["Straight"] = 1] = "Straight";
    OutlineMode[OutlineMode["Wavy"] = 2] = "Wavy";
})(OutlineMode || (OutlineMode = {}));
/**
 * Selects which triangle faces should be culled during rendering.
 */
var CullingMode;
(function (CullingMode) {
    /** Disables face culling. */
    CullingMode[CullingMode["None"] = 1] = "None";
    CullingMode[CullingMode["Back"] = 0] = "Back";
    CullingMode[CullingMode["Front"] = 2] = "Front";
})(CullingMode || (CullingMode = {}));
/**
 * Builds a stable cache key for a set of material properties.
 */
const hashMaterialProperties = (mat) => {
    return `${mat.shading}_${mat.outline ?? 0}_${mat.cull ?? 0}_${(mat.castShadow === true) ? 0 : 1}`;
};
/**
 * Byte width used when serializing {@link MaterialProperties}.
 */
const MATERIAL_PROPERTIES_BYTE_LENGTH = Uint8Array.BYTES_PER_ELEMENT + // shading
    Uint8Array.BYTES_PER_ELEMENT + // outline
    BOOLEAN_BYTES_PER_ELEMENT + // castShadow
    Uint8Array.BYTES_PER_ELEMENT; // cull
/**
 * Serializes or deserializes {@link MaterialProperties} using the shared byte stream format.
 */
const serializationMaterialProperties = (stream, mat) => {
    if (stream.isReading && mat === undefined)
        mat = {};
    mat.shading = stream.uint8(mat.shading);
    mat.outline = stream.uint8Safe(mat.outline);
    mat.castShadow = stream.boolean(mat.castShadow ?? false);
    mat.cull = stream.uint8Safe(mat.cull);
    return mat;
};

/**
 * Reusable primitive mesh material presets.
 */
const PrimitiveMeshMaterial = {
    /** Default lit material with straight outlines and shadow casting. */
    DEFAULT: {
        shading: ShadingMode.PhongWithShadows,
        outline: OutlineMode.Straight,
        castShadow: true,
        cull: CullingMode.None
    },
    /** Lit material with straight outlines and shadow casting. */
    STRAIGHT_OUTLINES: {
        shading: ShadingMode.PhongWithShadows,
        outline: OutlineMode.Straight,
        castShadow: true
    },
    /** Lit material without outlines. */
    NO_OUTLINES: {
        shading: ShadingMode.PhongWithShadows,
        castShadow: true
    },
    /** Lit material without outlines or shadow casting. */
    NO_OUTLINES_NO_SHADOW: {
        shading: ShadingMode.Phong
    },
    /** Unlit material with wavy outlines. */
    UNLIT: {
        shading: ShadingMode.Unlit,
        outline: OutlineMode.Wavy
    },
    /** Unlit material without outlines. */
    UNLIT_NO_OUTLINES: {
        shading: ShadingMode.Unlit
    },
    /** Backface-only material useful for shell-like effects. */
    BACKFACES_ONLY: {
        shading: ShadingMode.Phong,
        outline: OutlineMode.Wavy,
        cull: CullingMode.Front
    },
    SIMPLE_SHADING: {
        shading: ShadingMode.Simple
    },
    TRANSPARENT_GRID: {
        shading: ShadingMode.TransparentGrid,
        cull: CullingMode.None
    }
};

/**
 * Renders a partial ring/arc primitive with the given arc length, color and material. Scripts can
 * recolor it at runtime, e.g. `ModelScriptFunctions.setMaterialColor` updates `color` on the model's
 * `ArcFacet`.
 * @see {@link flavr-client!modules/rendering/primitives/ArcRenderable.ArcRenderable | ArcRenderable}
 */
class ArcFacet extends RenderableFacet {
    arcLength;
    color;
    material;
    static typestatic = "ArcFacet";
    static metaData = {
        export: true,
        description: "",
        category: "Rendering"
    };
    static propTypes = {
        arcLength: {
            type: PropertyType.Float32,
            isRequired: true
        },
        color: {
            type: PropertyType.Uint32,
            isRequired: true
        },
        material: {
            type: PropertyType.Struct,
            properties: {
                shading: {
                    type: PropertyType.Uint8
                },
                outline: { type: PropertyType.Uint8Safe },
                castShadow: {
                    type: PropertyType.Boolean,
                    default: false
                },
                cull: {
                    type: PropertyType.Uint8Safe
                }
            },
            default: { ...PrimitiveMeshMaterial.DEFAULT }
        }
    };
    static MAX_BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT + // arcLength
        Uint32Array.BYTES_PER_ELEMENT + // color
        MATERIAL_PROPERTIES_BYTE_LENGTH;
    get byteLength() {
        return ArcFacet.MAX_BYTE_LENGTH;
    }
    /**
     * @param material Groups the shading, outline, shadow, and culling settings.
     */
    constructor(arcLength, color, 
    /** Groups the shading, outline, shadow, and culling settings. */
    material = { ...PrimitiveMeshMaterial.DEFAULT }) {
        super();
        this.arcLength = arcLength;
        this.color = color;
        this.material = material;
    }
    serialization(stream) {
        this.arcLength = stream.float32(this.arcLength);
        this.color = stream.uint32(this.color);
        this.material = serializationMaterialProperties(stream, this.material);
    }
    static is(facet) {
        return facet.type === ArcFacet.typestatic;
    }
}
FacetFactory.register(ArcFacet);

/**
 * Displays a three-axis debug gizmo at the model's origin, sized by `size` and optionally offset by
 * `offset`. `ControllerModule` attaches it to controller grip-space models and the mouse model when
 * "show controller axis helpers" is enabled, and removes it again when disabled.
 * @see {@link flavr-client!modules/rendering/axisHelper/AxisHelperRenderable.AxisHelperRenderable | AxisHelperRenderable}
 */
class AxisHelperFacet extends Facet {
    size;
    isScalable;
    offset;
    static typestatic = "AxisHelperFacet";
    static metaData = {
        export: true,
        description: "Displays a three-axis helper",
        category: "Rendering"
    };
    static propTypes = {
        size: {
            type: PropertyType.Float32,
            isRequired: true,
            default: 1
        },
        isScalable: {
            type: PropertyType.Boolean,
            isRequired: true,
            default: true
        },
        offset: {
            type: PropertyType.Number16Safe
        }
    };
    static BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT +
        Uint8Array.BYTES_PER_ELEMENT +
        NUMBER16_BYTES_PER_ELEMENT;
    get byteLength() {
        return AxisHelperFacet.BYTE_LENGTH;
    }
    /**
     * @param size Size of the rendered axis helper in world units.
     * @param isScalable Whether the helper should scale together with the model.
     */
    constructor(
    /** Size of the rendered axis helper in world units. */
    size = 1, 
    /** Whether the helper should scale together with the model. */
    isScalable = true, offset) {
        super();
        this.size = size;
        this.isScalable = isScalable;
        this.offset = offset;
    }
    serialization(stream) {
        this.size = stream.float32(this.size);
        this.isScalable = stream.boolean(this.isScalable);
        const offset = stream.number16Safe(this.offset !== undefined ? convertToNumber16(this.offset) : undefined);
        this.offset = offset !== undefined ? new Matrix4().fromArray(offset) : undefined;
    }
    static is(facet) {
        return facet.type === AxisHelperFacet.typestatic;
    }
}
FacetFactory.register(AxisHelperFacet);

/**
 * Selects the visual style used for an avatar representation.
 */
var AvatarStyle;
(function (AvatarStyle) {
    AvatarStyle[AvatarStyle["Monitor"] = 0] = "Monitor";
    AvatarStyle[AvatarStyle["Head"] = 1] = "Head";
})(AvatarStyle || (AvatarStyle = {}));
/**
 * Carries the visual representation of a connected user: display name, avatar style (monitor or head),
 * base color, optional video stream URL, mute state and accessories. `AvatarPeer` creates and updates
 * this facet on a peer's avatar model whenever the user's avatar settings change.
 * @see {@link flavr-client!modules/avatar/AvatarRenderable.AvatarRenderable | AvatarRenderable}
 */
class AvatarFacet extends RenderableFacet {
    username;
    avatarStyle;
    baseColor;
    ownerId;
    displayName;
    videoUrl;
    params;
    static typestatic = "AvatarFacet";
    static metaData = {
        export: true,
        description: "Displays the avatar of a user",
        category: "Rendering"
    };
    static propTypes = {
        username: {
            type: PropertyType.Utf16String,
            isRequired: true
        },
        avatarStyle: {
            type: PropertyType.Uint8,
            isRequired: true
        },
        baseColor: {
            type: PropertyType.Uint32,
            isRequired: true
        },
        ownerId: {
            type: PropertyType.Utf16String,
            isRequired: true
        },
        displayName: {
            type: PropertyType.Utf16StringSafe
        },
        videoUrl: {
            type: PropertyType.Utf16StringSafe
        },
        params: {
            type: PropertyType.Struct,
            properties: {
                isMuted: {
                    type: PropertyType.BooleanSafe
                },
                accessoires: {
                    type: PropertyType.Array,
                    elementType: {
                        type: PropertyType.Struct,
                        properties: {
                            id: { type: PropertyType.Uint32 },
                            color: { type: PropertyType.Uint32 }
                        }
                    }
                }
            }
        }
    };
    get byteLength() {
        let byteLength = calcStringByteLength(this.username) +
            Int8Array.BYTES_PER_ELEMENT +
            Uint32Array.BYTES_PER_ELEMENT + // baseColor
            calcStringByteLength(this.ownerId) +
            calcStringByteLength(this.displayName) +
            calcStringByteLength(this.videoUrl) +
            BOOLEAN_BYTES_PER_ELEMENT; // isMuted
        byteLength += Uint32Array.BYTES_PER_ELEMENT; // accessoiresLength
        if (this.params && this.params.accessoires) {
            byteLength +=
                this.params.accessoires.length *
                    (Uint32Array.BYTES_PER_ELEMENT + // id
                        Uint32Array.BYTES_PER_ELEMENT); // color
        }
        return byteLength;
    }
    /**
     * @param username Stable username associated with the avatar.
     * @param baseColor Packed base color applied to the avatar.
     * @param videoUrl Optional video stream URL used by some avatar variants.
     */
    constructor(
    /** Stable username associated with the avatar. */
    username, avatarStyle, 
    /** Packed base color applied to the avatar. */
    baseColor, ownerId, displayName, 
    /** Optional video stream URL used by some avatar variants. */
    videoUrl, params) {
        super();
        this.username = username;
        this.avatarStyle = avatarStyle;
        this.baseColor = baseColor;
        this.ownerId = ownerId;
        this.displayName = displayName;
        this.videoUrl = videoUrl;
        this.params = params;
    }
    serialization(stream) {
        this.username = stream.utf16String(this.username);
        this.avatarStyle = stream.uint8(this.avatarStyle);
        this.baseColor = stream.uint32(this.baseColor);
        this.ownerId = stream.utf16String(this.ownerId);
        this.displayName = stream.utf16StringSafe(this.displayName);
        this.videoUrl = stream.utf16StringSafe(this.videoUrl);
        if (stream.isReading || !this.params) {
            this.params = {};
        }
        this.params.isMuted = stream.booleanSafe(this.params.isMuted);
        const accessoiresLength = stream.uint32(this.params.accessoires ? this.params.accessoires.length : 0);
        if (accessoiresLength > 0) {
            if (!this.params.accessoires) {
                this.params.accessoires = [];
            }
            for (let i = 0; i < accessoiresLength; i++) {
                let accessoire;
                if (stream.isReading) {
                    accessoire = {};
                    this.params.accessoires.push(accessoire);
                }
                else {
                    accessoire = this.params.accessoires[i];
                }
                accessoire.id = stream.uint32(accessoire.id);
                accessoire.color = stream.uint32(accessoire.color);
            }
        }
    }
    static is(facet) {
        return facet.type === AvatarFacet.typestatic;
    }
}
FacetFactory.register(AvatarFacet);

/**
 * Marks a model to continuously rotate towards the local camera, keeping planes such as labels or
 * icons readable from the current viewpoint. `BillboardModule` tracks models carrying this facet and
 * updates their orientation every frame; `snapAngle`, `yawOffset` and `applyParentYaw` constrain how
 * the rotation is applied.
 */
class BillboardFacet extends Facet {
    snapAngle;
    yawOffset;
    applyParentYaw;
    static typestatic = "BillboardFacet";
    static metaData = {
        export: true,
        description: "A billboard tries to face to the camera at all times. Works best with planes",
        category: "Rendering"
    };
    static propTypes = {
        snapAngle: { type: PropertyType.Float32Safe },
        yawOffset: { type: PropertyType.Float32Safe },
        applyParentYaw: { type: PropertyType.Boolean, default: false }
    };
    get byteLength() {
        return Float32Array.BYTES_PER_ELEMENT * 2 + BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param snapAngle Optional angular snap increment used while facing the camera.
     * @param yawOffset Optional yaw offset applied after billboarding.
     */
    constructor(
    /** Optional angular snap increment used while facing the camera. */
    snapAngle, 
    /** Optional yaw offset applied after billboarding. */
    yawOffset, applyParentYaw = false) {
        super();
        this.snapAngle = snapAngle;
        this.yawOffset = yawOffset;
        this.applyParentYaw = applyParentYaw;
    }
    serialization(stream) {
        this.snapAngle = stream.float32Safe(this.snapAngle);
        this.yawOffset = stream.float32Safe(this.yawOffset);
        this.applyParentYaw = stream.boolean(this.applyParentYaw);
    }
    static is(facet) {
        return facet.type === BillboardFacet.typestatic;
    }
}
FacetFactory.register(BillboardFacet);

/**
 * Clips the model's mesh against one or more planes using the renderer's material clipping support,
 * optionally animating the clip plane over time via `animator.speed`. Exposed to users through the
 * model settings appearance tab.
 * @see {@link flavr-client!modules/clipping/ClippingMeshRenderable.ClippingMeshRenderable | ClippingMeshRenderable}
 */
class ClippingMeshFacet extends Facet {
    planes;
    animator;
    static typestatic = "ClippingMeshFacet";
    static metaData = {
        export: true,
        description: "Clips a mesh at along a plane",
        category: "Rendering"
    };
    static propTypes = {
        planes: {
            type: PropertyType.Array,
            elementType: { type: PropertyType.Number4 }
        },
        animator: {
            type: PropertyType.Struct,
            properties: {
                speed: { type: PropertyType.Float32Safe }
            }
        }
    };
    static ANIMATOR_PARAMS_BYTELENGTH = Float32Array.BYTES_PER_ELEMENT;
    get byteLength() {
        return (calcArrayByteLength(this.planes) +
            BOOLEAN_BYTES_PER_ELEMENT + // hasAnimator
            (this.animator != undefined ? ClippingMeshFacet.ANIMATOR_PARAMS_BYTELENGTH : 0));
    }
    constructor(planes, animator) {
        super();
        this.planes = planes;
        this.animator = animator;
    }
    serialization(stream) {
        this.planes = stream.array(this.planes);
        if (stream.boolean(this.animator != undefined)) {
            if (stream.isReading) {
                this.animator = {};
            }
            this.animator.speed = stream.float32Safe(this.animator.speed);
        }
    }
    static is(facet) {
        return facet.type === ClippingMeshFacet.typestatic;
    }
}
FacetFactory.register(ClippingMeshFacet);

/**
 * Selects which visual style a connection line should use.
 */
var ConnectionLineTypes;
(function (ConnectionLineTypes) {
    ConnectionLineTypes["Basic"] = "basic";
    ConnectionLineTypes["Arrow"] = "arrow";
    ConnectionLineTypes["Flow"] = "flow";
})(ConnectionLineTypes || (ConnectionLineTypes = {}));
/**
 * Holds, per target model uid, the set of visible connection lines (basic, arrow or animated flow)
 * drawn from this model. The renderer's `Connections` facet renderer reads `connections` to create and
 * update the line meshes; `LineConnectionInteractionHandler` mutates it while a user is interactively
 * drawing or removing a connection between two models.
 */
class ConnectionLines extends Facet {
    connections;
    static typestatic = "ConnectionLines";
    static metaData = {
        export: true,
        description: "",
        category: "Rendering"
    };
    static propTypes = {
        connections: { type: PropertyType.Object }
    };
    get byteLength() {
        return calcObjectByteLength(this.connections);
    }
    /**
     * @param connections Maps target model UIDs to the named connections that should be rendered.
     */
    constructor(
    /** Maps target model UIDs to the named connections that should be rendered. */
    connections = {}) {
        super();
        this.connections = connections;
    }
    serialization(stream) {
        this.connections = stream.object(this.connections);
    }
    static is(facet) {
        return facet.type === ConnectionLines.typestatic;
    }
}
FacetFactory.register(ConnectionLines);

/**
 * Selects which hand a controller model belongs to.
 */
var ControllerHand;
(function (ControllerHand) {
    /** Hand could not be determined. */
    ControllerHand[ControllerHand["Unknown"] = 0] = "Unknown";
    ControllerHand[ControllerHand["LeftHand"] = 1] = "LeftHand";
    ControllerHand[ControllerHand["RightHand"] = 2] = "RightHand";
})(ControllerHand || (ControllerHand = {}));
/**
 * Displays the built-in controller mesh for a tracked hand, including whether the VR controller model
 * or the hand-tracking model should be shown. `ControllerModule` updates this facet on each controller
 * model as input devices connect, disconnect or switch between controller and hand tracking.
 * @see {@link flavr-client!modules/rendering/controller/ControllerRenderable.ControllerRenderable | ControllerRenderable}
 */
class ControllerFacet extends RenderableFacet {
    controllerType;
    hand;
    isShowingVRController;
    hasTrackedHand;
    baseColor;
    static typestatic = "ControllerFacet";
    static metaData = {
        export: true,
        description: "Displays a predefined controller mesh for a specific hand",
        category: "Interaction"
    };
    static propTypes = {
        controllerType: {
            type: PropertyType.Uint8,
            isRequired: true
        },
        hand: {
            type: PropertyType.Uint8,
            isRequired: true
        },
        isShowingVRController: {
            type: PropertyType.Boolean,
            isRequired: true
        },
        hasTrackedHand: {
            type: PropertyType.Boolean,
            isRequired: true
        },
        baseColor: {
            type: PropertyType.Uint32,
            isRequired: true
        }
    };
    static MAX_BYTE_LENGTH = Uint8Array.BYTES_PER_ELEMENT + // controllerType
        Uint8Array.BYTES_PER_ELEMENT + // hand
        BOOLEAN_BYTES_PER_ELEMENT + // isShowingVRController
        BOOLEAN_BYTES_PER_ELEMENT + // hasTrackedHand
        Uint32Array.BYTES_PER_ELEMENT; // baseColor
    get byteLength() {
        return ControllerFacet.MAX_BYTE_LENGTH;
    }
    /**
     * @param controllerType Identifies the connected controller/input device family.
     * @param hand Selects whether the controller belongs to the left or right hand.
     * @param isShowingVRController Set true to show the VR-Controller mesh in addition to the Hand mesh.
     * @param hasTrackedHand Set true when the user is using hand-tracking (suppresses all legacy meshes).
     * @param baseColor Sets the base tint color of the controller renderable.
     */
    constructor(
    /** Identifies the connected controller/input device family. */
    controllerType, 
    /** Selects whether the controller belongs to the left or right hand. */
    hand, 
    /** Set true to show the VR-Controller mesh in addition to the Hand mesh. */
    isShowingVRController, 
    /** Set true when the user is using hand-tracking (suppresses all legacy meshes). */
    hasTrackedHand, 
    /** Sets the base tint color of the controller renderable. */
    baseColor) {
        super();
        this.controllerType = controllerType;
        this.hand = hand;
        this.isShowingVRController = isShowingVRController;
        this.hasTrackedHand = hasTrackedHand;
        this.baseColor = baseColor;
    }
    serialization(stream) {
        this.controllerType = stream.uint8(this.controllerType);
        this.hand = stream.uint8(this.hand);
        this.isShowingVRController = stream.boolean(this.isShowingVRController);
        this.hasTrackedHand = stream.boolean(this.hasTrackedHand);
        this.baseColor = stream.uint32(this.baseColor);
    }
    static is(facet) {
        return facet.type === ControllerFacet.typestatic;
    }
}
FacetFactory.register(ControllerFacet);
/**
 * Marks a model as an externally supplied controller mesh (identified by `id`) that should replace the
 * built-in controller model for a hand. `ControllerModule` reacts to it via
 * `handleCustomControllerFacet`, tracking and updating the referenced model like a regular local
 * controller.
 */
class CustomControllerFacet extends Facet {
    id;
    static typestatic = "CustomControllerFacet";
    static metaData = {
        export: true,
        description: "Displays a custom controller mesh for a specific hand",
        category: "Rendering"
    };
    static propTypes = {
        id: {
            type: PropertyType.Utf16String,
            isRequired: true
        }
    };
    get byteLength() {
        return calcStringByteLength(this.id);
    }
    constructor(id) {
        super();
        this.id = id;
    }
    serialization(stream) {
        this.id = stream.utf16String(this.id);
    }
    static is(facet) {
        return facet.type === CustomControllerFacet.typestatic;
    }
}
FacetFactory.register(CustomControllerFacet);

// Change the visibility of a facet
/**
 * Toggles the visibility of another facet's rendered output on the same model, without touching the
 * model's overall visibility. `facetType` identifies the target facet type and `visible` the desired
 * state; `RendererModelInterface.updateFacet` reacts to it by calling `setFacetVisible` for that facet
 * type. Used e.g. by `ProximitySelect` to show/hide a controller's `ProximitySphere` and by
 * `ModelScriptFunctions.setFacetVisibility` to expose the toggle to scripts.
 */
class FacetVisibility extends Facet {
    visible;
    static typestatic = "FacetVisibility";
    static metaData = {
        export: true,
        description: "Allows to change the visibility of a facet",
        category: "Rendering"
    };
    static propTypes = {};
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + BOOLEAN_BYTES_PER_ELEMENT;
    get byteLength() {
        return FacetVisibility.MAX_BYTE_LENGTH;
    }
    /** Identifies which facet type on the model should have its visibility overridden. */
    facetType = "";
    constructor(type, visible = true) {
        super(type.typestatic);
        this.visible = visible;
        this.facetType = type.typestatic;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.facetType = FacetTypeTable.toString(stream.uid(0));
            this.subType = this.facetType;
        }
        else {
            stream.uid(FacetTypeTable.hashCode(this.facetType));
        }
        this.visible = stream.boolean(this.visible);
    }
    static is(facet) {
        return facet.type === FacetVisibility.typestatic;
    }
}
FacetFactory.register(FacetVisibility);

/**
 * Graphics quality levels used by {@link HideWhenQualityFacet}.
 */
var MaterialQuality;
(function (MaterialQuality) {
    MaterialQuality[MaterialQuality["LOW"] = 0] = "LOW";
    MaterialQuality[MaterialQuality["MIDDLE"] = 1] = "MIDDLE";
    MaterialQuality[MaterialQuality["HIGH"] = 2] = "HIGH";
})(MaterialQuality || (MaterialQuality = {}));
/**
 * Hides the model's renderable whenever the active graphics quality setting is at or below `quality`.
 * `HideWhenQualityRenderable` registers with the `MaterialQualitySystem` and shows/hides the model as
 * the global quality level changes, letting scenes drop expensive content on lower-end hardware.
 * @see {@link flavr-client!modules/rendering/material/HideWhenQualityRenderable.HideWhenQualityRenderable | HideWhenQualityRenderable}
 */
class HideWhenQualityFacet extends Facet {
    quality;
    static typestatic = "HideWhenQualityFacet";
    static metaData = {
        export: true,
        description: "Hides a model when the quality setting is set to the same or lower setting as `quality`",
        category: "Misc"
    };
    static propTypes = {
        quality: { type: PropertyType.Uint8, isRequired: true, description: "Specifies the quality threshold: LOW=0, MEDIUM=1, HIGH=2" }
    };
    get byteLength() {
        return Uint8Array.BYTES_PER_ELEMENT;
    }
    /**
     * @param quality Quality threshold at or below which the model should be hidden.
     */
    constructor(
    /** Quality threshold at or below which the model should be hidden. */
    quality) {
        super();
        this.quality = quality;
    }
    serialization(stream) {
        this.quality = stream.uint8(this.quality);
    }
    static is(facet) {
        return facet.type === HideWhenQualityFacet.typestatic;
    }
}
FacetFactory.register(HideWhenQualityFacet);

/**
 * Embeds an interactive HTML element (with optional external script packages, inline HTML/CSS,
 * dimensions and speech-to-text wiring) onto the model's surface. Used by GridVR assets scripts (e.g.
 * `ChargingCluster.js`, `ChargingFarm.js`) to render custom UI panels in the scene.
 * @see {@link flavr-client!modules/htmlCanvasRendering/HTMLCanvasRenderable.HTMLCanvasRenderable | HTMLCanvasRenderable}
 */
class HTMLCanvasFacet extends RenderableFacet {
    elementId;
    params;
    static typestatic = "HTMLCanvas";
    static metaData = {
        export: true,
        description: "Creates a HTML canvas and runs a script inside it",
        category: "Rendering"
    };
    static propTypes = {
        elementId: {
            type: PropertyType.Utf16String,
            isRequired: true
        },
        params: {
            type: PropertyType.Struct,
            properties: {
                externalPackages: {
                    type: PropertyType.ArraySafe,
                    elementType: {
                        type: PropertyType.Struct,
                        properties: {
                            moduleName: { type: PropertyType.Utf16String },
                            link: { type: PropertyType.Utf16String },
                            version: { type: PropertyType.Utf16StringSafe }
                        }
                    }
                },
                html: { type: PropertyType.Utf16StringSafe },
                height: { type: PropertyType.Uint32Safe },
                width: { type: PropertyType.Uint32Safe },
                scriptId: { type: PropertyType.Utf16StringSafe },
                parameters: { type: PropertyType.ObjectSafe },
                speechToTextId: { type: PropertyType.Utf16StringSafe },
                selectable: { type: PropertyType.Boolean, default: false }
            }
        }
    };
    get byteLength() {
        return (calcStringByteLength(this.elementId) +
            calcArrayByteLength(this.params.externalPackages) +
            calcStringByteLength(this.params.html) +
            Uint32Array.BYTES_PER_ELEMENT * 2 + // height, width
            calcStringByteLength(this.params.scriptId) +
            calcObjectByteLength(this.params.parameters) +
            calcStringByteLength(this.params.speechToTextId) +
            BOOLEAN_BYTES_PER_ELEMENT); // selectable
    }
    constructor(elementId, params = {}) {
        super();
        this.elementId = elementId;
        this.params = params;
    }
    serialization(stream) {
        this.elementId = stream.utf16String(this.elementId);
        if (stream.isReading)
            this.params = {};
        this.params.externalPackages = stream.arraySafe(this.params.externalPackages);
        this.params.html = stream.utf16StringSafe(this.params.html);
        this.params.height = stream.uint32Safe(this.params.height);
        this.params.width = stream.uint32Safe(this.params.width);
        this.params.scriptId = stream.utf16StringSafe(this.params.scriptId);
        this.params.parameters = stream.objectSafe(this.params.parameters);
        this.params.speechToTextId = stream.utf16StringSafe(this.params.speechToTextId);
        this.params.selectable = stream.boolean(this.params.selectable || false);
    }
    static is(facet) {
        return facet.type === HTMLCanvasFacet.typestatic;
    }
}
FacetFactory.register(HTMLCanvasFacet);

/**
 * Renders a textured plane showing an image loaded from `url` (including peer-shared files and offscreen
 * canvases). `ImageRenderable` resizes/crops the image per `param.width`/`height`/`maxWidth`/`maxHeight`,
 * tints it with `param.color` and can restrict its yaw/pitch billboard-snapping.
 * @see {@link flavr-client!modules/rendering/image/ImageRenderable.ImageRenderable | ImageRenderable}
 */
class ImageFacet extends RenderableFacet {
    url;
    param;
    static typestatic = "Image";
    static metaData = {
        export: true,
        description: "Loads an image from an url and displays it",
        category: "Rendering"
    };
    static propTypes = {
        url: {
            type: PropertyType.Utf16String,
            isRequired: true
        },
        param: {
            type: PropertyType.Struct,
            properties: {
                height: { type: PropertyType.Uint32Safe },
                width: { type: PropertyType.Uint32Safe },
                maxHeight: { type: PropertyType.Uint32Safe },
                maxWidth: { type: PropertyType.Uint32Safe },
                snapYaw: { type: PropertyType.Float32Safe },
                snapPitch: { type: PropertyType.Float32Safe },
                depthOffset: { type: PropertyType.Float32Safe },
                color: { type: PropertyType.Uint32Safe }
            }
        }
    };
    static BYTE_LENGTH = Uint32Array.BYTES_PER_ELEMENT * 2 + // height width
        Uint32Array.BYTES_PER_ELEMENT * 2 + // maxHeight maxWidth
        Float32Array.BYTES_PER_ELEMENT * 2 + // snapYaw snapPitch
        Float32Array.BYTES_PER_ELEMENT + // depthOffset
        Uint32Array.BYTES_PER_ELEMENT; // color
    get byteLength() {
        return calcStringByteLength(this.url) + ImageFacet.BYTE_LENGTH;
    }
    /**
     * @param param Groups optional sizing, tinting, and snapping settings.
     */
    constructor(url, 
    /** Groups optional sizing, tinting, and snapping settings. */
    param = {}) {
        super();
        this.url = url;
        this.param = param;
    }
    serialization(stream) {
        this.url = stream.utf16String(this.url);
        if (stream.isReading)
            this.param = {};
        this.param.height = stream.uint32Safe(this.param.height);
        this.param.width = stream.uint32Safe(this.param.width);
        this.param.maxHeight = stream.uint32Safe(this.param.maxHeight);
        this.param.maxWidth = stream.uint32Safe(this.param.maxWidth);
        this.param.snapYaw = stream.float32Safe(this.param.snapYaw);
        this.param.snapPitch = stream.float32Safe(this.param.snapPitch);
        this.param.depthOffset = stream.float32Safe(this.param.depthOffset);
        this.param.color = stream.uint32Safe(this.param.color);
    }
    static is(facet) {
        return facet.type === ImageFacet.typestatic;
    }
}
FacetFactory.register(ImageFacet);

/**
 * Loads an image from `url` and, via `ImageToTextureRenderable`, applies it as a texture map onto the
 * materials of the model's own mesh (see `MeshFacet`) instead of drawing a separate image plane.
 * @see {@link flavr-client!modules/rendering/texture/ImageToTextureRenderable.ImageToTextureRenderable | ImageToTextureRenderable}
 */
class ImageToTextureFacet extends Facet {
    url;
    static typestatic = "ImageToTextureFacet";
    static metaData = {
        export: true,
        description: "Loads a texture from an image",
        category: "Rendering"
    };
    static propTypes = {
        url: { type: PropertyType.Utf16String, isRequired: true }
    };
    get byteLength() {
        return calcStringByteLength(this.url);
    }
    constructor(url) {
        super();
        this.url = url;
    }
    serialization(stream) {
        this.url = stream.utf16String(this.url);
    }
    static is(facet) {
        return facet.type === ImageToTextureFacet.typestatic;
    }
}
FacetFactory.register(ImageToTextureFacet);

/**
 * Selects how the label is positioned around the model.
 */
var LabelAlign;
(function (LabelAlign) {
    LabelAlign[LabelAlign["Left"] = 0] = "Left";
    LabelAlign[LabelAlign["Right"] = 1] = "Right";
    LabelAlign[LabelAlign["Bottom"] = 2] = "Bottom";
    LabelAlign[LabelAlign["Top"] = 3] = "Top";
    LabelAlign[LabelAlign["Center"] = 4] = "Center";
})(LabelAlign || (LabelAlign = {}));
/**
 * Selects whether label sizing and alignment use the model's bounding box or local transform space.
 */
var LabelSpace;
(function (LabelSpace) {
    /** Uses the model's axis-aligned bounding box for placement and scaling. */
    LabelSpace[LabelSpace["AABB"] = 0] = "AABB";
    /** Uses the model's local transform space for placement and scaling. */
    LabelSpace[LabelSpace["Local"] = 1] = "Local";
})(LabelSpace || (LabelSpace = {}));
/**
 * Renders a floating text billboard above or around the model via `LabelRenderable` (troika-three-text),
 * aligned and scaled per `align`/`space`/`relativeSize` and optionally fading out and yaw-snapping.
 * Created interactively, for example by voice ("label" speech command) or menu actions that attach a label
 * to a selected model.
 * @see {@link flavr-client!modules/label/LabelRenderable.LabelRenderable | LabelRenderable}
 */
class LabelFacet extends Facet {
    text;
    static typestatic = "LabelFacet";
    static metaData = {
        export: true,
        description: "Shows a label when hovering over a model",
        category: "Rendering"
    };
    static propTypes = {
        text: {
            type: PropertyType.LocalizationString,
            isRequired: true
        },
        align: {
            type: PropertyType.Uint8,
            default: LabelAlign.Center
        },
        relativeSize: {
            type: PropertyType.Float32,
            default: 1
        },
        background: {
            type: PropertyType.Boolean,
            default: true
        },
        snapYaw: {
            type: PropertyType.Float32,
            default: Math.PI / 2
        },
        space: {
            type: PropertyType.Uint8,
            default: LabelSpace.AABB
        },
        heightInfluenceOnScaling: {
            type: PropertyType.Float32,
            default: 1
        },
        fadeOut: {
            type: PropertyType.Boolean,
            default: true
        }
    };
    static BYTE_LENGTH = Uint8Array.BYTES_PER_ELEMENT + // align
        Float32Array.BYTES_PER_ELEMENT + // relativeSize
        BOOLEAN_BYTES_PER_ELEMENT + // background
        Float32Array.BYTES_PER_ELEMENT + // snapYaw
        Uint8Array.BYTES_PER_ELEMENT + // space
        Float32Array.BYTES_PER_ELEMENT + // heightInfluenceOnScaling
        BOOLEAN_BYTES_PER_ELEMENT; // fadeOut
    get byteLength() {
        return LabelFacet.BYTE_LENGTH + calcLocalizationStringByteLength(this.text);
    }
    align = LabelAlign.Center;
    space = LabelSpace.AABB;
    /** Stores the yaw snapping step in radians. */
    snapYaw = Math.PI / 2;
    /** Controls whether the label should fade out when not in view. */
    fadeOut = true;
    /** Controls whether a background plate should be displayed behind the text. */
    background = true;
    /** Stores the label size relative to the model's bounding box or local scale. */
    relativeSize = 1;
    heightInfluenceOnScaling = 1;
    /**
     * @param text Provides the label text or localization key.
     * @param params Groups optional placement and sizing settings for the label.
     */
    constructor(
    /** Provides the label text or localization key. */
    text = "", params = {}) {
        super();
        this.text = text;
        if (params.align != undefined)
            this.align = params.align;
        if (params.space != undefined)
            this.space = params.space;
        if (params.snapYaw != undefined)
            this.snapYaw = params.snapYaw;
        if (params.fadeOut != undefined)
            this.fadeOut = params.fadeOut;
        if (params.background != undefined)
            this.background = params.background;
        if (params.relativeSize != undefined)
            this.relativeSize = params.relativeSize;
        if (params.heightInfluenceOnScaling != undefined)
            this.heightInfluenceOnScaling = params.heightInfluenceOnScaling;
    }
    serialization(stream) {
        this.text = stream.localizationString(this.text);
        this.align = stream.uint8(this.align);
        this.relativeSize = stream.float32(this.relativeSize);
        this.background = stream.boolean(this.background);
        this.snapYaw = stream.float32(this.snapYaw);
        this.space = stream.uint8(this.space);
        this.heightInfluenceOnScaling = stream.float32(this.heightInfluenceOnScaling);
        this.fadeOut = stream.boolean(this.fadeOut);
    }
    static is(facet) {
        return facet.type === LabelFacet.typestatic;
    }
}
FacetFactory.register(LabelFacet);

/**
 * Marker facet attached by the server's glTF scene parser to models found on a "lightProbe" node while
 * importing world layouts. No client-side renderer or handler currently reads this facet, so it has no
 * observable effect in the running app beyond marking the model's origin in the scene file.
 */
class LightProbeFacet extends Facet {
    static typestatic = "LightProbeFacet";
    static propTypes = {};
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(facet) {
        return facet.type === LightProbeFacet.typestatic;
    }
}
FacetFactory.register(LightProbeFacet);

/**
 * Selects which concrete light type should be created.
 */
var LightSourceType;
(function (LightSourceType) {
    /** Ambient light without direction. */
    LightSourceType[LightSourceType["Ambient"] = 0] = "Ambient";
    /** Directional light with a target direction. */
    LightSourceType[LightSourceType["Directional"] = 1] = "Directional";
    /** Hemisphere light with sky and ground colors. */
    LightSourceType[LightSourceType["Hemisphere"] = 2] = "Hemisphere";
    /** Point light emitting in all directions from one point. */
    LightSourceType[LightSourceType["Point"] = 3] = "Point";
    /** Spot light with a cone angle and penumbra. */
    LightSourceType[LightSourceType["Spot"] = 4] = "Spot";
    /** Rectangular area light. */
    LightSourceType[LightSourceType["Area"] = 5] = "Area";
})(LightSourceType || (LightSourceType = {}));
/**
 * Selects a preset shadow map resolution.
 */
var ShadowMapResolution;
(function (ShadowMapResolution) {
    ShadowMapResolution[ShadowMapResolution["LOW"] = 0] = "LOW";
    ShadowMapResolution[ShadowMapResolution["MEDIUM"] = 1] = "MEDIUM";
    ShadowMapResolution[ShadowMapResolution["HIGH"] = 2] = "HIGH";
})(ShadowMapResolution || (ShadowMapResolution = {}));
const shadowPropertyDescriptor = {
    type: PropertyType.Struct,
    properties: {
        bias: { type: PropertyType.Float32Safe },
        normalBias: { type: PropertyType.Float32Safe },
        blurSamples: { type: PropertyType.Uint16Safe },
        radius: { type: PropertyType.Float32Safe },
        resolution: {
            type: PropertyType.EnumUint8,
            items: {
                0: "LOW",
                1: "MEDIUM",
                2: "HIGH"
            },
            default: 0
        },
        near: { type: PropertyType.Float32Safe },
        far: { type: PropertyType.Float32Safe },
        frustumRadius: { type: PropertyType.Uint8Safe }
    }
};
/**
 * Turns the model into a real THREE.js light (Ambient, Directional, Hemisphere, Point, Spot or Area).
 * `LightSourceRenderable` creates and configures the light from `params`, including shadow-map settings,
 * animated transitions between parameter changes, an optional lensflare sprite, and tracking of a
 * `targetUid` model for directional/spot lights.
 * @see {@link flavr-client!modules/rendering/light/LightSourceRenderable.LightSourceRenderable | LightSourceRenderable}
 */
class LightSource extends Facet {
    params;
    static typestatic = "LightSource";
    static propTypes = {
        params: {
            type: PropertyType.Struct,
            properties: {
                type: {
                    type: PropertyType.EnumUint8,
                    items: {
                        0: "Ambient",
                        1: "Directional",
                        2: "Hemisphere",
                        3: "Point",
                        4: "Spot",
                        5: "Area"
                    }
                },
                color: {
                    type: PropertyType.ColorHex,
                    default: 0xffffff
                },
                intensity: {
                    type: PropertyType.Float32,
                    default: 1
                },
                groundColor: { type: PropertyType.Uint32Safe },
                distance: { type: PropertyType.Float32Safe },
                decay: { type: PropertyType.Float32Safe },
                angle: { type: PropertyType.Float32Safe },
                penumbra: { type: PropertyType.Float32Safe },
                animationDuration: { type: PropertyType.Float32Safe },
                targetUid: { type: PropertyType.UidSafe },
                lensflare: {
                    type: PropertyType.Boolean,
                    default: false
                },
                width: {
                    type: PropertyType.If,
                    condition: {
                        property: "type",
                        expected: 0
                    },
                    properties: { type: PropertyType.Float32Safe }
                },
                height: {
                    type: PropertyType.If,
                    condition: {
                        property: "type",
                        expected: 0
                    },
                    properties: { type: PropertyType.Float32Safe }
                },
                shadow: shadowPropertyDescriptor,
                lightSourceShadowParams: shadowPropertyDescriptor
            }
        }
    };
    constructor(params = {
        type: LightSourceType.Ambient,
        color: 0xffffff,
        intensity: 1
    }) {
        super();
        this.params = params;
        // Migrate legacy shadow data to the new field.
        if (this.params.lightSourceShadowParams === undefined && this.params.shadow !== undefined) {
            this.params.lightSourceShadowParams = this.params.shadow;
        }
        delete this.params.shadow;
    }
    static MAX_BYTE_LENGTH = (
    // LightSourceParamsBase
    UINT8_BYTES_PER_ELEMENT + // type
        UINT32_BYTES_PER_ELEMENT + // color
        FLOAT32_BYTES_PER_ELEMENT + // intensity
        FLOAT32_BYTES_PER_ELEMENT + // shadow.bias
        FLOAT32_BYTES_PER_ELEMENT + // shadow.normalBias
        FLOAT32_BYTES_PER_ELEMENT + // shadow.radius
        UINT8_BYTES_PER_ELEMENT + // shadow.resolution
        FLOAT32_BYTES_PER_ELEMENT + // shadow.near
        FLOAT32_BYTES_PER_ELEMENT + // shadow.far
        FLOAT32_BYTES_PER_ELEMENT + // shadow.frustumRadius
        UINT8_BYTES_PER_ELEMENT + // shadow.blurSamples
        UINT32_BYTES_PER_ELEMENT + // groundColor
        FLOAT32_BYTES_PER_ELEMENT + // distance
        FLOAT32_BYTES_PER_ELEMENT + // decay
        FLOAT32_BYTES_PER_ELEMENT + // angle
        FLOAT32_BYTES_PER_ELEMENT + // penumbra
        FLOAT32_BYTES_PER_ELEMENT + // animationDuration
        UID_SAFE_BYTES_PER_ELEMENT + // targetUid
        BOOLEAN_BYTES_PER_ELEMENT // lensflare
    );
    get byteLength() {
        let bL = LightSource.MAX_BYTE_LENGTH;
        switch (this.params.type) {
            case LightSourceType.Area:
                bL += Float32Array.BYTES_PER_ELEMENT * 2; // width, height
                break;
        }
        return bL;
    }
    serialization(stream) {
        if (stream.isReading)
            this.params = {};
        this.params.type = stream.uint8(this.params.type);
        this.params.color = stream.uint32(this.params.color);
        this.params.intensity = stream.float32(this.params.intensity);
        // Light source shadow params
        const shadowParams = this.params.lightSourceShadowParams ?? this.params.shadow;
        const bias = stream.float32Safe(shadowParams?.bias);
        const normalBias = stream.float32Safe(shadowParams?.normalBias);
        const radius = stream.float32Safe(shadowParams?.radius);
        const resolution = stream.uint8Safe(shadowParams?.resolution);
        const near = stream.float32Safe(shadowParams?.near);
        const far = stream.float32Safe(shadowParams?.far);
        const frustumRadius = stream.float32Safe(shadowParams?.frustumRadius);
        const blurSamples = stream.uint8Safe(shadowParams?.blurSamples);
        if (stream.isReading && (bias !== undefined ||
            normalBias !== undefined ||
            blurSamples !== undefined ||
            radius !== undefined ||
            resolution !== undefined ||
            near !== undefined ||
            far !== undefined ||
            frustumRadius !== undefined)) {
            const shadow = {
                bias,
                normalBias,
                radius,
                resolution,
                near,
                far,
                frustumRadius,
                blurSamples
            };
            this.params.lightSourceShadowParams = shadow;
        }
        if (stream.isReading) {
            delete this.params.shadow;
        }
        this.params.groundColor = stream.uint32Safe(this.params.groundColor);
        this.params.distance = stream.float32Safe(this.params.distance);
        this.params.decay = stream.float32Safe(this.params.decay);
        this.params.angle = stream.float32Safe(this.params.angle);
        this.params.penumbra = stream.float32Safe(this.params.penumbra);
        this.params.animationDuration = stream.float32Safe(this.params.animationDuration);
        this.params.targetUid = stream.uidSafe(this.params.targetUid);
        this.params.lensflare = stream.boolean(this.params.lensflare ?? false);
        // Light type specific parameters
        switch (this.params.type) {
            case LightSourceType.Area:
                this.params.width = stream.float32Safe(this.params.width);
                this.params.height = stream.float32Safe(this.params.height);
                break;
        }
    }
    static is(facet) {
        return facet.type === LightSource.typestatic;
    }
}
FacetFactory.register(LightSource);

/**
 * Draws a single straight instanced line of `length`, `thickness` and `colorHex`, centered on and
 * following the model's transform. `LineRenderable` allocates the line from the renderer's shared
 * line-batch system.
 * @see {@link flavr-client!modules/rendering/line/LineRenderable.LineRenderable | LineRenderable}
 */
class LineFacet extends RenderableFacet {
    length;
    colorHex;
    thickness;
    static typestatic = "LineFacet";
    static metaData = {
        export: true,
        description: "Draws a single line",
        category: "Rendering"
    };
    static propTypes = {
        length: {
            type: PropertyType.Float32,
            isRequired: true,
            default: 1
        },
        colorHex: {
            type: PropertyType.Uint32,
            isRequired: true,
            default: 0
        },
        thickness: {
            type: PropertyType.Float32,
            isRequired: true,
            default: 0.01
        }
    };
    static MAX_BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT +
        Uint32Array.BYTES_PER_ELEMENT + // colorHex
        Float32Array.BYTES_PER_ELEMENT; // thickness
    get byteLength() {
        return LineFacet.MAX_BYTE_LENGTH;
    }
    constructor(length = 1, colorHex = 0, thickness = 0.01) {
        super();
        this.length = length;
        this.colorHex = colorHex;
        this.thickness = thickness;
    }
    serialization(stream) {
        this.length = stream.float32(this.length);
        this.colorHex = stream.uint32(this.colorHex);
        this.thickness = stream.float32(this.thickness);
    }
    static is(facet) {
        return facet.type === LineFacet.typestatic;
    }
}
FacetFactory.register(LineFacet);

/**
 * Draws one or more connected polylines through world-space `points` (separate strips are split by an
 * empty `[]` entry). `LineStripRenderable` renders each segment as an instanced line with optional
 * randomized thickness ("fussyness") and animated noise; `HighlightLineStripInteractionHandler` swaps
 * the color/thickness between selected/highlighted/unselected variants when a user interacts with it.
 * @see {@link flavr-client!modules/rendering/line/LineStripRenderable.LineStripRenderable | LineStripRenderable}
 */
class LineStrip extends Facet {
    points;
    color;
    thickness;
    fussyness;
    noiseFrequency;
    noiseVelocity;
    noShading;
    static typestatic = "LineStrip";
    static metaData = {
        export: true,
        description: "Draws a list of lines",
        category: "Interaction"
    };
    /**
     * @param points Lists the polyline points, with empty `[]` entries separating different strips.
     * @param fussyness Controls how strongly animated noise varies the thickness.
     * @param noShading Disables the edge-darkening gradient when set.
     */
    constructor(
    /** Lists the polyline points, with empty `[]` entries separating different strips. */
    points, color, thickness = 1, 
    /** Controls how strongly animated noise varies the thickness. */
    fussyness, noiseFrequency, noiseVelocity, 
    /** Disables the edge-darkening gradient when set. */
    noShading) {
        super();
        this.points = points;
        this.color = color;
        this.thickness = thickness;
        this.fussyness = fussyness;
        this.noiseFrequency = noiseFrequency;
        this.noiseVelocity = noiseVelocity;
        this.noShading = noShading;
    }
    static BYTE_LENGTH = Uint32Array.BYTES_PER_ELEMENT + // length
        Uint32Array.BYTES_PER_ELEMENT + // color
        Float32Array.BYTES_PER_ELEMENT + // thickness
        Float32Array.BYTES_PER_ELEMENT + // fussyness
        Float32Array.BYTES_PER_ELEMENT + // noiseFrequency
        Float32Array.BYTES_PER_ELEMENT + // noiseVelocity
        BOOLEAN_BYTES_PER_ELEMENT; // noShading
    get byteLength() {
        let byteLength = LineStrip.BYTE_LENGTH;
        for (let i = 0, length = this.points.length; i < length; ++i) {
            byteLength += Float32Array.BYTES_PER_ELEMENT * 3;
        }
        return byteLength;
    }
    serialization(stream) {
        if (stream.isReading)
            this.points = [];
        const length = stream.uint32(this.points.length);
        for (let i = 0; i < length; ++i) {
            const point = this.points[i];
            this.points[i] = stream.number3Safe(point && point.length === 3 ? point : undefined) || [];
        }
        this.color = stream.uint32(this.color);
        this.thickness = stream.float32(this.thickness);
        this.fussyness = stream.float32Safe(this.fussyness);
        this.noiseFrequency = stream.float32Safe(this.noiseFrequency);
        this.noiseVelocity = stream.float32Safe(this.noiseVelocity);
        this.noShading = stream.boolean(this.noShading || false);
    }
    static is(facet) {
        return facet.type === LineStrip.typestatic;
    }
}
FacetFactory.register(LineStrip);

/**
 * Displays an interactive-style map centered on `latitude`/`longitude` at the given `zoom`. `MapRenderable`
 * drives a hidden Leaflet map (OpenStreetMap tiles) and rasterizes it into a canvas texture of `width`x
 * `height` shown on a plane.
 * @see {@link flavr-client!modules/map/MapRenderable.MapRenderable | MapRenderable}
 */
class MapFacet extends RenderableFacet {
    latitude;
    longitude;
    zoom;
    width;
    height;
    static typestatic = "MapFacet";
    static metaData = {
        export: true,
        description: "Draws a map centered at the specified latitude and longitude",
        category: "Rendering"
    };
    static propTypes = {
        latitude: {
            type: PropertyType.Float32,
            isRequired: true
        },
        longitude: {
            type: PropertyType.Float32,
            isRequired: true
        },
        zoom: {
            type: PropertyType.Uint16,
            default: 1
        },
        width: {
            type: PropertyType.Uint16,
            default: 4096
        },
        height: {
            type: PropertyType.Uint16,
            default: 4096
        }
    };
    static BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT + // latitude
        Float32Array.BYTES_PER_ELEMENT + // longitude
        Uint16Array.BYTES_PER_ELEMENT + // zoom
        Uint16Array.BYTES_PER_ELEMENT + // width
        Uint16Array.BYTES_PER_ELEMENT; // height
    get byteLength() {
        return MapFacet.BYTE_LENGTH;
    }
    constructor(latitude, longitude, zoom = 1, width = 4096, height = 4096) {
        super();
        this.latitude = latitude;
        this.longitude = longitude;
        this.zoom = zoom;
        this.width = width;
        this.height = height;
    }
    serialization(stream) {
        this.latitude = stream.float32(this.latitude);
        this.longitude = stream.float32(this.longitude);
        this.zoom = stream.uint16(this.zoom);
        this.width = stream.uint16(this.width);
        this.height = stream.uint16(this.height);
    }
    static is(facet) {
        return facet.type === MapFacet.typestatic;
    }
}
FacetFactory.register(MapFacet);

/**
 * Selects which live media source should be shown on a media screen.
 */
var MediaScreenFacetType;
(function (MediaScreenFacetType) {
    MediaScreenFacetType[MediaScreenFacetType["screenShare"] = 0] = "screenShare";
    MediaScreenFacetType[MediaScreenFacetType["player3dViewShare"] = 1] = "player3dViewShare";
})(MediaScreenFacetType || (MediaScreenFacetType = {}));
/**
 * Turns the model into a screen that shows a live video stream - either this user's own screen/3D-view
 * share (`params.type`) or a remote peer's - broadcast to all connected peers. `MediaScreenRenderable`
 * plays the stream into a video texture and applies it to the model's mesh (or a dedicated plane if
 * `disableMesh`/`applyOnlyTexture` are set); requires a `CollisionFacet` on the model.
 * @see {@link flavr-client!modules/mediaScreen/MediaScreenRenderable.MediaScreenRenderable | MediaScreenRenderable}
 */
class MediaScreenFacet extends Facet {
    params;
    static typestatic = "MediaScreenFacet";
    static metaData = {
        export: true,
        description: "Displays the users shared screen and broadcasts it to all peers",
        category: "General",
        dependencies: ["CollisionFacet"]
    };
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT * 4 + Uint8Array.BYTES_PER_ELEMENT;
    }
    constructor(params = {
        applyOnlyTexture: false,
        autoScale: false,
        disableMesh: false,
        doubleSided: false,
        type: MediaScreenFacetType.screenShare
    }) {
        super();
        this.params = params;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.params = {
                applyOnlyTexture: false,
                autoScale: false,
                disableMesh: false,
                doubleSided: false,
                type: MediaScreenFacetType.screenShare
            };
        }
        this.params.autoScale = stream.boolean(this.params.autoScale) ?? false;
        this.params.disableMesh = stream.boolean(this.params.disableMesh) ?? false;
        this.params.doubleSided = stream.boolean(this.params.doubleSided) ?? false;
        this.params.applyOnlyTexture = stream.boolean(this.params.applyOnlyTexture) ?? false;
        this.params.type = stream.uint8(this.params.type) ?? MediaScreenFacetType.screenShare;
    }
    static is(facet) {
        return facet.type === MediaScreenFacet.typestatic;
    }
}
FacetFactory.register(MediaScreenFacet);

/**
 * Selects which camera/rendering layers a mesh can participate in.
 */
var CameraLayers;
(function (CameraLayers) {
    // ThreeJs layers
    CameraLayers[CameraLayers["Default"] = 0] = "Default";
    CameraLayers[CameraLayers["XRLeftEye"] = 1] = "XRLeftEye";
    CameraLayers[CameraLayers["XRRightEye"] = 2] = "XRRightEye";
    // Custom layers
    CameraLayers[CameraLayers["Static"] = 3] = "Static";
    CameraLayers[CameraLayers["Dynamic"] = 4] = "Dynamic";
    CameraLayers[CameraLayers["FirstPerson"] = 5] = "FirstPerson";
    CameraLayers[CameraLayers["Reflection"] = 6] = "Reflection";
    CameraLayers[CameraLayers["UI"] = 7] = "UI";
    CameraLayers[CameraLayers["Utility"] = 8] = "Utility";
})(CameraLayers || (CameraLayers = {}));
/**
 * Loads and displays the named mesh asset (`meshName`, optionally a specific `params.pieceName`) via
 * `MeshRenderable`. Controls sub-mesh visibility, shadow casting/receiving, per-material color overrides,
 * material cloning/outlining, frustum culling, reflection-probe association and the busy indicator shown
 * while the mesh streams in.
 * @see {@link flavr-client!modules/rendering/mesh/MeshRenderable.MeshRenderable | MeshRenderable}
 */
class MeshFacet extends RenderableFacet {
    meshName;
    subMeshVisibilities;
    castShadow;
    receiveShadow;
    params;
    static typestatic = "MeshFacet";
    static propTypes = {
        meshName: {
            type: PropertyType.Utf16String,
            isRequired: true
        },
        subMeshVisibilities: {
            type: PropertyType.ObjectSafe
        },
        castShadow: {
            type: PropertyType.Boolean,
            default: false
        },
        receiveShadow: {
            type: PropertyType.Boolean,
            default: false
        },
        params: {
            type: PropertyType.Struct,
            properties: {
                pieceName: { type: PropertyType.Utf16StringSafe },
                needsRescale: { type: PropertyType.Boolean },
                colors: {
                    type: PropertyType.Dictionary,
                    elementType: { type: PropertyType.ColorHex }
                },
                cloneMaterialsForMaterialName: {
                    type: PropertyType.ArraySafe,
                    elementType: { type: PropertyType.Utf16String }
                },
                cloneMaterialsForSubObjectName: {
                    type: PropertyType.ArraySafe,
                    elementType: { type: PropertyType.Utf16String }
                },
                reflectionProbeUid: { type: PropertyType.UidSafe },
                ignoreCubeMapRendering: {
                    type: PropertyType.Boolean,
                    default: false
                },
                frustumCulled: {
                    type: PropertyType.Boolean,
                    default: false
                },
                isStatic: {
                    type: PropertyType.BooleanSafe
                },
                vertexScaleFactor: {
                    type: PropertyType.Float32Safe
                },
                showBusyIndicator: {
                    type: PropertyType.BooleanSafe
                },
                applyOutline: {
                    type: PropertyType.BooleanSafe
                },
                outlineColor: {
                    type: PropertyType.Utf16StringSafe
                },
                cloneMaterials: {
                    type: PropertyType.BooleanSafe
                }
            }
        }
    };
    get byteLength() {
        return (
        // MeshFacet
        calcStringByteLength(this.meshName) +
            calcObjectByteLength(this.subMeshVisibilities) +
            BOOLEAN_BYTES_PER_ELEMENT + // castShadow
            BOOLEAN_BYTES_PER_ELEMENT + // receiveShadow
            // MeshFacetParams
            calcStringByteLength(this.params.pieceName) +
            BOOLEAN_BYTES_PER_ELEMENT + // needsRescale
            calcObjectByteLength(this.params.colors) +
            calcArrayByteLength(this.params.cloneMaterialsForMaterialName) +
            calcArrayByteLength(this.params.cloneMaterialsForSubObjectName) +
            UID_SAFE_BYTES_PER_ELEMENT + // reflectionProbeUid
            BOOLEAN_BYTES_PER_ELEMENT + // ignoreCubeMapRendering
            BOOLEAN_BYTES_PER_ELEMENT + // frustumCulled
            BOOLEAN_BYTES_PER_ELEMENT + // showBusyIndicator
            Float32Array.BYTES_PER_ELEMENT + // vertexScaleFactor
            BOOLEAN_BYTES_PER_ELEMENT + // applyOutline
            calcStringByteLength(this.params.outlineColor) +
            BOOLEAN_BYTES_PER_ELEMENT + // cloneMaterials
            BOOLEAN_BYTES_PER_ELEMENT // isStatic
        );
    }
    /**
     * @param subMeshVisibilities Optionally toggles visibility per named sub mesh.
     */
    constructor(meshName, 
    /** Optionally toggles visibility per named sub mesh. */
    subMeshVisibilities, castShadow, receiveShadow, params = {}) {
        super();
        this.meshName = meshName;
        this.subMeshVisibilities = subMeshVisibilities;
        this.castShadow = castShadow;
        this.receiveShadow = receiveShadow;
        this.params = params;
    }
    serialization(stream) {
        this.params = this.params ?? {};
        this.meshName = stream.utf16String(this.meshName);
        this.subMeshVisibilities = stream.objectSafe(this.subMeshVisibilities);
        this.castShadow = stream.boolean(this.castShadow ?? false);
        this.receiveShadow = stream.boolean(this.receiveShadow ?? false);
        this.params.needsRescale = stream.boolean(this.params.needsRescale ?? false);
        this.params.pieceName = stream.utf16StringSafe(this.params.pieceName);
        this.params.colors = stream.objectSafe(this.params.colors);
        this.params.cloneMaterialsForMaterialName = stream.arraySafe(this.params.cloneMaterialsForMaterialName);
        this.params.cloneMaterialsForSubObjectName = stream.arraySafe(this.params.cloneMaterialsForSubObjectName);
        this.params.reflectionProbeUid = stream.uidSafe(this.params.reflectionProbeUid);
        this.params.ignoreCubeMapRendering = stream.boolean(this.params.ignoreCubeMapRendering ?? false);
        this.params.frustumCulled = stream.boolean(this.params.frustumCulled ?? false);
        this.params.isStatic = stream.booleanSafe(this.params.isStatic);
        this.params.vertexScaleFactor = stream.float32Safe(this.params.vertexScaleFactor);
        this.params.showBusyIndicator = stream.booleanSafe(this.params.showBusyIndicator);
        this.params.applyOutline = stream.booleanSafe(this.params.applyOutline);
        this.params.cloneMaterials = stream.booleanSafe(this.params.cloneMaterials);
        this.params.outlineColor = stream.utf16StringSafe(this.params.outlineColor);
    }
    /**
     * Clones the mesh facet and forces material cloning to avoid shared-material side effects.
     */
    clone(cloneContext) {
        const clonedFacet = super.clone(cloneContext);
        clonedFacet.params.cloneMaterials = true; // to avoid unexpected behaviour when changing material properties of the original model
        return clonedFacet;
    }
    static is(facet) {
        return facet.type === MeshFacet.typestatic;
    }
}
FacetFactory.register(MeshFacet);

/**
 * Creates an empty THREE.js `Group` as a plain scene-graph anchor with no visual content of its own, via
 * `MeshGroupRenderable`. Used for models that only need a transform to attach children/colliders to, e.g.
 * the local player's head model and the mouse-cursor input-device model.
 * @see {@link flavr-client!modules/rendering/mesh/MeshGroupRenderable.MeshGroupRenderable | MeshGroupRenderable}
 */
class MeshGroupFacet extends RenderableFacet {
    static typestatic = "MeshGroupFacet";
    static metaData = {
        export: false, // Probably we don't want to expose this?
        description: "Creates an empty THREE JS group",
        category: "Rendering"
    };
    static BYTE_LENGTH = 0;
    get byteLength() {
        return MeshGroupFacet.BYTE_LENGTH;
    }
    serialization(_stream) { }
    static is(facet) {
        return facet.type === MeshGroupFacet.typestatic;
    }
}
FacetFactory.register(MeshGroupFacet);

/**
 * Turns the model into a real-time mirror. `MirrorRenderable` creates a THREE.js `Reflector` plane sized
 * to the model's mesh bounds (or a 2x2 default), tinted by `color`. `textureWidth`/`textureHeight` and
 * `multisample` trade reflection quality for performance, and `clipBias` avoids self-clipping artifacts
 * near the mirror plane. Works best on flat, plane-shaped models.
 * @see {@link flavr-client!modules/mirror/MirrorRenderable.MirrorRenderable | MirrorRenderable}
 */
class MirrorFacet extends RenderableFacet {
    color;
    textureWidth;
    textureHeight;
    clipBias;
    multisample;
    static typestatic = "MirrorFacet";
    static metaData = {
        export: true,
        description: "Turns a model into a mirror. Works only well with planes",
        category: "Rendering"
    };
    static propTypes = {
        color: { type: PropertyType.ColorHex, default: 0xffffff, description: "Tint color of the mirror" },
        textureWidth: { type: PropertyType.Uint32, default: 512, description: "Resolution quality of the mirror" },
        textureHeight: { type: PropertyType.Uint32, default: 512, description: "Resolution quality of the mirror" },
        clipBias: { type: PropertyType.Float32, min: 0.0, max: 1.0, default: 0.03, description: "Specifies how close objects have to be to still appear in the mirror" },
        multisample: { type: PropertyType.Uint32, min: 0, max: 4, default: 0, description: "Makes the reflection smoother at the cost of performance" }
    };
    static MAX_BYTE_LENGTH = Uint32Array.BYTES_PER_ELEMENT + // color
        Uint32Array.BYTES_PER_ELEMENT + // textureWidth
        Uint32Array.BYTES_PER_ELEMENT + // textureHeight
        Float32Array.BYTES_PER_ELEMENT + // clipBias
        Uint32Array.BYTES_PER_ELEMENT; // multisample
    get byteLength() {
        return MirrorFacet.MAX_BYTE_LENGTH;
    }
    constructor(color = 0xffffff, textureWidth = 512, textureHeight = 512, clipBias = 0.01, multisample = 0) {
        super();
        this.color = color;
        this.textureWidth = textureWidth;
        this.textureHeight = textureHeight;
        this.clipBias = clipBias;
        this.multisample = multisample;
    }
    serialization(stream) {
        this.color = stream.uint32(this.color);
        this.textureWidth = stream.uint32(this.textureWidth);
        this.textureHeight = stream.uint32(this.textureHeight);
        this.clipBias = stream.float32(this.clipBias);
        this.multisample = stream.uint32(this.multisample);
    }
    static is(facet) {
        return facet.type === MirrorFacet.typestatic;
    }
}
FacetFactory.register(MirrorFacet);

/**
 * Continuously scrolls (`linearVelocity`) and/or spins (`angularVelocity`) the texture of one or more
 * named materials on the model's mesh, driven per-frame by `MovingTextureRenderable`. Used e.g. to
 * animate flow-direction textures on cable/pipe models.
 * @see {@link flavr-client!modules/rendering/texture/MovingTextureRenderable.MovingTextureRenderable | MovingTextureRenderable}
 */
class MovingTextureFacet extends Facet {
    params;
    static typestatic = "MovingTextureFacet";
    static metaData = {
        export: true,
        description: "Moves and rotates a texture",
        category: "Rendering"
    };
    get byteLength() {
        let byteLength = Uint32Array.BYTES_PER_ELEMENT;
        for (const param of this.params) {
            byteLength += calcStringByteLength(param.materialName) + Float32Array.BYTES_PER_ELEMENT * (2 + 1); // linearVelocity, angularVelocity
        }
        return byteLength;
    }
    constructor(params) {
        super();
        this.params = params;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.params = [];
        }
        const length = stream.uint32(this.params.length);
        for (let i = 0; i < length; ++i) {
            if (stream.isReading) {
                this.params[i] = {};
            }
            const param = this.params[i];
            param.materialName = stream.utf16String(param.materialName);
            param.linearVelocity = stream.number2(param.linearVelocity);
            param.angularVelocity = stream.float32(param.angularVelocity);
        }
    }
    static is(facet) {
        return facet.type === MovingTextureFacet.typestatic;
    }
}
FacetFactory.register(MovingTextureFacet);

/**
 * Renders one page (`page`, 1-based, default first page) of the PDF at `url` onto a canvas texture and
 * applies it to the model's mesh materials via `PdfToTextureRenderable`, effectively displaying a PDF
 * page as the model's surface.
 * @see {@link flavr-client!modules/rendering/texture/PdfToTextureRenderable.PdfToTextureRenderable | PdfToTextureRenderable}
 */
class PdfToTextureFacet extends Facet {
    url;
    page;
    static typestatic = "PdfToTextureFacet";
    static metaData = {
        export: true,
        description: "Loading a texture from a pdf page",
        category: "Rendering"
    };
    get byteLength() {
        return calcStringByteLength(this.url) + Uint32Array.BYTES_PER_ELEMENT;
    }
    /**
     * @param page Selects which 1-based PDF page should be rendered.
     */
    constructor(url, 
    /** Selects which 1-based PDF page should be rendered. */
    page) {
        super();
        this.url = url;
        this.page = page;
    }
    serialization(stream) {
        this.url = stream.utf16String(this.url);
        this.page = stream.uint32Safe(this.page);
    }
    static is(facet) {
        return facet.type === PdfToTextureFacet.typestatic;
    }
}
FacetFactory.register(PdfToTextureFacet);

/**
 * Displays the model as one of the predefined primitive shapes (box, sphere, etc., see `PrimitiveShape`)
 * instead of a loaded mesh asset, colored by `color` with shading/outline/shadow-casting/culling options
 * in `material`. `PrimitiveMeshRenderable` instances the shape cheaply without loading a mesh file; used
 * e.g. as the default render shape when no other renderable is set, and for reference-plane/modifier
 * gizmos in interaction handlers.
 * @see {@link flavr-client!modules/rendering/primitives/PrimitiveMeshRenderable.PrimitiveMeshRenderable | PrimitiveMeshRenderable}
 */
class PrimitiveMeshFacet extends RenderableFacet {
    shape;
    color;
    material;
    static typestatic = "PrimitiveMesh";
    static metaData = {
        export: true,
        description: "Displays a mesh based on predefined shapes",
        category: "Rendering"
    };
    static propTypes = {
        shape: {
            type: PropertyType.Uint8,
            isRequired: true
        },
        color: {
            type: PropertyType.Uint32,
            isRequired: true
        },
        material: {
            type: PropertyType.Struct,
            properties: {
                shading: {
                    type: PropertyType.Uint8
                },
                outline: { type: PropertyType.Uint8Safe },
                castShadow: {
                    type: PropertyType.Boolean,
                    default: false
                },
                cull: {
                    type: PropertyType.Uint8Safe
                }
            },
            default: { ...PrimitiveMeshMaterial.DEFAULT }
        }
    };
    static MAX_BYTE_LENGTH = Uint8Array.BYTES_PER_ELEMENT + // shape
        Uint32Array.BYTES_PER_ELEMENT + // color
        MATERIAL_PROPERTIES_BYTE_LENGTH;
    get byteLength() {
        return PrimitiveMeshFacet.MAX_BYTE_LENGTH;
    }
    /**
     * @param material Groups the shading, outline, shadow, and culling settings.
     */
    constructor(shape, color, 
    /** Groups the shading, outline, shadow, and culling settings. */
    material = { ...PrimitiveMeshMaterial.DEFAULT }) {
        super();
        this.shape = shape;
        this.color = color;
        this.material = material;
    }
    serialization(stream) {
        this.shape = stream.uint8(this.shape);
        this.color = stream.uint32(this.color);
        this.material = serializationMaterialProperties(stream, this.material);
    }
    static is(facet) {
        return facet.type === PrimitiveMeshFacet.typestatic;
    }
}
FacetFactory.register(PrimitiveMeshFacet);

/**
 * Selects whether a reflection probe is baked once or refreshed continuously.
 */
var ReflectionProbeRefreshMode;
(function (ReflectionProbeRefreshMode) {
    /** Capture the probe once and reuse the baked result. */
    ReflectionProbeRefreshMode[ReflectionProbeRefreshMode["Baked"] = 0] = "Baked";
    /** Continuously recapture the probe at runtime. */
    ReflectionProbeRefreshMode[ReflectionProbeRefreshMode["Realtime"] = 1] = "Realtime";
})(ReflectionProbeRefreshMode || (ReflectionProbeRefreshMode = {}));
/**
 * Turns a model into a reflection probe: `ReflectionProbeRenderable` renders the surrounding scene into a cube map (baked once, or continuously if `refreshMode` is `Realtime`) and applies it as the environment map on the models listed in `affectedUids`. Populated server-side by `ReflectionProbeParser` when parsing scene content.
 * @see {@link flavr-client!modules/rendering/reflectionProbe/ReflectionProbeRenderable.ReflectionProbeRenderable | ReflectionProbeRenderable}
 */
class ReflectionProbeFacet extends Facet {
    params;
    static typestatic = "ReflectionProbeFacet";
    static propTypes = {
        params: {
            type: PropertyType.Struct,
            properties: {
                resolution: { type: PropertyType.Uint16Safe },
                near: { type: PropertyType.Float32Safe },
                far: { type: PropertyType.Float32Safe },
                refreshMode: {
                    type: PropertyType.EnumUint8Safe,
                    items: {
                        0: "Baked",
                        1: "Realtime"
                    }
                },
                affectedUids: {
                    type: PropertyType.Array,
                    elementType: {
                        type: PropertyType.Uid
                    }
                }
            }
        }
    };
    get byteLength() {
        return (Uint16Array.BYTES_PER_ELEMENT + // resolution
            Float32Array.BYTES_PER_ELEMENT + // near
            Float32Array.BYTES_PER_ELEMENT + // far
            Uint8Array.BYTES_PER_ELEMENT + // refreshMode
            Uint32Array.BYTES_PER_ELEMENT + // affectedLength
            (this.params.affectedUids ? this.params.affectedUids.length * UID_BYTES_PER_ELEMENT : 0));
    }
    constructor(params = {}) {
        super();
        this.params = params;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.params = {};
        }
        this.params.resolution = stream.uint16Safe(this.params.resolution);
        this.params.near = stream.float32Safe(this.params.near);
        this.params.far = stream.float32Safe(this.params.far);
        this.params.refreshMode = stream.uint8Safe(this.params.refreshMode);
        if (!this.params.affectedUids) {
            this.params.affectedUids = [];
        }
        const affectedLength = stream.uint32(this.params.affectedUids.length);
        for (let i = 0; i < affectedLength; i++) {
            this.params.affectedUids[i] = stream.uid(this.params.affectedUids[i]);
        }
    }
    static is(facet) {
        return facet.type === ReflectionProbeFacet.typestatic;
    }
}
FacetFactory.register(ReflectionProbeFacet);

/**
 * Renders a physically simulated rope stretched between `startTarget` and `endTarget` (either fixed points or other models, via `UID`). `RopeRenderable` runs a Verlet particle simulation (segments, damping, gravityScale, solveIteration) and draws the resulting curve as an instanced line.
 * @see {@link flavr-client!modules/rendering/line/RopeRenderable.RopeRenderable | RopeRenderable}
 */
class RopeFacet extends RenderableFacet {
    startTarget;
    startOffset;
    endTarget;
    endOffset;
    length;
    thickness;
    color;
    segments;
    damping;
    gravityScale;
    solveIteration;
    floorHeight;
    static typestatic = "RopeFacet";
    static metaData = {
        export: true,
        description: "Displays a physically simulated rope",
        category: "Physics"
    };
    static propTypes = {
        startTarget: {
            type: PropertyType.Multiple,
            types: [{ type: PropertyType.Uid }, { type: PropertyType.Number3 }],
            isRequired: true
        },
        startOffset: {
            type: PropertyType.Number3,
            isRequired: true
        },
        endTarget: {
            type: PropertyType.Multiple,
            types: [{ type: PropertyType.Uid }, { type: PropertyType.Number3 }],
            isRequired: true
        },
        endOffset: {
            type: PropertyType.Number3,
            isRequired: true
        },
        length: {
            type: PropertyType.Float32,
            isRequired: true
        },
        thickness: {
            type: PropertyType.Float32,
            isRequired: true
        },
        color: {
            type: PropertyType.Uint32,
            isRequired: true
        },
        segments: {
            type: PropertyType.Uint16,
            default: 6
        },
        damping: {
            type: PropertyType.Float32,
            default: 1.1
        },
        gravityScale: {
            type: PropertyType.Float32,
            default: 1
        },
        solveIteration: {
            type: PropertyType.Uint8,
            default: 1
        },
        floorHeight: {
            type: PropertyType.Float32Safe
        }
    };
    get byteLength() {
        return (BOOLEAN_BYTES_PER_ELEMENT * 2 +
            (Array.isArray(this.startTarget) ? Float32Array.BYTES_PER_ELEMENT * 3 : UID_BYTES_PER_ELEMENT) +
            Float32Array.BYTES_PER_ELEMENT * 3 + // startOffset
            (Array.isArray(this.endTarget) ? Float32Array.BYTES_PER_ELEMENT * 3 : UID_BYTES_PER_ELEMENT) +
            Float32Array.BYTES_PER_ELEMENT * 3 + // endOffset
            Float32Array.BYTES_PER_ELEMENT + // length
            Float32Array.BYTES_PER_ELEMENT + // thickness
            Uint32Array.BYTES_PER_ELEMENT + // color
            Uint16Array.BYTES_PER_ELEMENT + // segments
            Float32Array.BYTES_PER_ELEMENT + // damping
            Float32Array.BYTES_PER_ELEMENT + // gravityScale
            Uint8Array.BYTES_PER_ELEMENT + // solveIteration
            Float32Array.BYTES_PER_ELEMENT); // floorHeight
    }
    /**
     * @param startTarget Identifies the rope start as either a model UID or a fixed world-space point.
     * @param startOffset Offsets the rope start relative to the resolved target.
     * @param endTarget Identifies the rope end as either a model UID or a fixed world-space point.
     * @param endOffset Offsets the rope end relative to the resolved target.
     * @param length Sets the simulated rope length.
     * @param segments Sets how many rope segments the simulation uses.
     * @param solveIteration Sets how often constraints are solved each frame.
     * @param floorHeight Optionally adds a collision floor for the rope.
     */
    constructor(
    /** Identifies the rope start as either a model UID or a fixed world-space point. */
    startTarget, 
    /** Offsets the rope start relative to the resolved target. */
    startOffset, 
    /** Identifies the rope end as either a model UID or a fixed world-space point. */
    endTarget, 
    /** Offsets the rope end relative to the resolved target. */
    endOffset, 
    /** Sets the simulated rope length. */
    length, thickness, color, 
    /** Sets how many rope segments the simulation uses. */
    segments = 6, damping = 1.1, gravityScale = 1, 
    /** Sets how often constraints are solved each frame. */
    solveIteration = 1, 
    /** Optionally adds a collision floor for the rope. */
    floorHeight) {
        super();
        this.startTarget = startTarget;
        this.startOffset = startOffset;
        this.endTarget = endTarget;
        this.endOffset = endOffset;
        this.length = length;
        this.thickness = thickness;
        this.color = color;
        this.segments = segments;
        this.damping = damping;
        this.gravityScale = gravityScale;
        this.solveIteration = solveIteration;
        this.floorHeight = floorHeight;
    }
    serialization(stream) {
        if (stream.boolean(Array.isArray(this.startTarget))) {
            this.startTarget = stream.number3(this.startTarget);
        }
        else {
            this.startTarget = stream.uid(this.startTarget);
        }
        this.startOffset = stream.number3(this.startOffset);
        if (stream.boolean(Array.isArray(this.endTarget))) {
            this.endTarget = stream.number3(this.endTarget);
        }
        else {
            this.endTarget = stream.uid(this.endTarget);
        }
        this.endOffset = stream.number3(this.endOffset);
        this.length = stream.float32(this.length);
        this.thickness = stream.float32(this.thickness);
        this.color = stream.uint32(this.color);
        this.segments = stream.uint16(this.segments);
        this.damping = stream.float32(this.damping);
        this.gravityScale = stream.float32(this.gravityScale);
        this.solveIteration = stream.uint8(this.solveIteration);
        this.floorHeight = stream.float32Safe(this.floorHeight);
    }
    static is(facet) {
        return facet.type === RopeFacet.typestatic;
    }
}
FacetFactory.register(RopeFacet);

/**
 * Horizontal alignment options for text layout.
 */
var TextHorizontalAlign;
(function (TextHorizontalAlign) {
    TextHorizontalAlign[TextHorizontalAlign["Left"] = 0] = "Left";
    TextHorizontalAlign[TextHorizontalAlign["Right"] = 1] = "Right";
    TextHorizontalAlign[TextHorizontalAlign["Center"] = 2] = "Center";
})(TextHorizontalAlign || (TextHorizontalAlign = {}));
/**
 * Vertical alignment options for text layout.
 */
var TextVerticalAlign;
(function (TextVerticalAlign) {
    TextVerticalAlign[TextVerticalAlign["Top"] = 0] = "Top";
    TextVerticalAlign[TextVerticalAlign["Bottom"] = 1] = "Bottom";
    TextVerticalAlign[TextVerticalAlign["Center"] = 2] = "Center";
    /** Align text to its typographic baseline. */
    TextVerticalAlign[TextVerticalAlign["BaseLine"] = 3] = "BaseLine";
})(TextVerticalAlign || (TextVerticalAlign = {}));
/**
 * Horizontal anchor positions used by the text renderer.
 */
var TextAnchorX;
(function (TextAnchorX) {
    TextAnchorX[TextAnchorX["Left"] = 0] = "Left";
    TextAnchorX[TextAnchorX["Center"] = 1] = "Center";
    TextAnchorX[TextAnchorX["Right"] = 2] = "Right";
})(TextAnchorX || (TextAnchorX = {}));
/**
 * Vertical anchor positions used by the text renderer.
 */
var TextAnchorY;
(function (TextAnchorY) {
    TextAnchorY[TextAnchorY["Top"] = 0] = "Top";
    TextAnchorY[TextAnchorY["TopBaseline"] = 1] = "TopBaseline";
    /** Anchor text at the top cap height. */
    TextAnchorY[TextAnchorY["TopCap"] = 2] = "TopCap";
    /** Anchor text at the top x-height. */
    TextAnchorY[TextAnchorY["TopEx"] = 3] = "TopEx";
    TextAnchorY[TextAnchorY["Middle"] = 4] = "Middle";
    TextAnchorY[TextAnchorY["BottomBaseline"] = 5] = "BottomBaseline";
    TextAnchorY[TextAnchorY["Bottom"] = 6] = "Bottom";
})(TextAnchorY || (TextAnchorY = {}));
/**
 * Multi-line text alignment modes.
 */
var TextAlign;
(function (TextAlign) {
    TextAlign[TextAlign["Left"] = 0] = "Left";
    TextAlign[TextAlign["Right"] = 1] = "Right";
    TextAlign[TextAlign["Center"] = 2] = "Center";
    /** Justify wrapped lines across the width. */
    TextAlign[TextAlign["Justify"] = 3] = "Justify";
})(TextAlign || (TextAlign = {}));
/**
 * Font style options used by the text renderer.
 */
var FontStyle;
(function (FontStyle) {
    FontStyle[FontStyle["Italic"] = 0] = "Italic";
    FontStyle[FontStyle["Normal"] = 1] = "Normal";
})(FontStyle || (FontStyle = {}));
/**
 * Font weight options used by the text renderer.
 */
var FontWeight;
(function (FontWeight) {
    FontWeight[FontWeight["Bold"] = 0] = "Bold";
    FontWeight[FontWeight["Normal"] = 1] = "Normal";
})(FontWeight || (FontWeight = {}));
/**
 * Renders `text` as 3D text geometry on the model. `TextRenderable` builds a `troika-three-text` `Text` object from `text` and `params` (font, alignment, color, outline/stroke, curveRadius, clipRect, etc.) and keeps it in sync as the facet changes.
 * @see {@link flavr-client!modules/rendering/primitives/TextRenderable.TextRenderable | TextRenderable}
 */
class TextFacet extends RenderableFacet {
    text;
    params;
    static typestatic = "Text";
    static metaData = {
        export: true,
        description: "Displays text as geometry",
        category: "Rendering"
    };
    static propTypes = {
        text: {
            type: PropertyType.LocalizationString,
            isRequired: true
        },
        params: {
            type: PropertyType.Struct,
            properties: {
                anchorX: { type: PropertyType.Uint8Safe },
                anchorY: { type: PropertyType.Uint8Safe },
                fontStyle: { type: PropertyType.Uint8Safe },
                fontWeight: { type: PropertyType.Uint8Safe },
                textAlign: { type: PropertyType.Uint8Safe },
                color: { type: PropertyType.ColorHexSafe },
                outlineColor: { type: PropertyType.ColorHexSafe },
                strokeColor: { type: PropertyType.ColorHexSafe },
                curveRadius: { type: PropertyType.Float32Safe },
                depthOffset: { type: PropertyType.Float32Safe },
                fillOpacity: { type: PropertyType.Float32Safe },
                glyphGeometryDetail: { type: PropertyType.Float32Safe },
                letterSpacing: { type: PropertyType.Float32Safe },
                maxWidth: { type: PropertyType.Float32Safe },
                outlineBlur: { type: PropertyType.Float32Safe },
                outlineOpacity: { type: PropertyType.Float32Safe },
                outlineWidth: { type: PropertyType.Float32Safe },
                strokeOpacity: { type: PropertyType.Float32Safe },
                strokeWidth: { type: PropertyType.Float32Safe },
                fontSize: { type: PropertyType.Float32Safe },
                clipRect: { type: PropertyType.ArraySafe, elementType: { type: PropertyType.Float32Safe } },
                font: { type: PropertyType.Utf16StringSafe }
            }
        }
    };
    static BYTE_LENGTH = Uint8Array.BYTES_PER_ELEMENT * 5 + // anchorX, anchorY, fontStyle, fontWeight, textAlign
        Uint32Array.BYTES_PER_ELEMENT * 3 + // color, outlineColor, strokeColor
        Float32Array.BYTES_PER_ELEMENT * 12; // curveRadius, depthOffset, fillOpacity, fontSize, letterSpacing, maxWidth, outlineBlur, outlineOpacity, outlineWidth, strokeOpacity, strokeWidth, glyphGeometryDetail
    get byteLength() {
        return (calcLocalizationStringByteLength(this.text) + calcStringByteLength(this.params.font) + calcArrayByteLength(this.params.clipRect) + TextFacet.BYTE_LENGTH);
    }
    /**
     * @param text Provides the text content or localization key to render.
     * @param params Groups optional font, layout, color, and effect settings.
     */
    constructor(
    /** Provides the text content or localization key to render. */
    text, 
    /** Groups optional font, layout, color, and effect settings. */
    params = {}) {
        super();
        this.text = text;
        this.params = params;
    }
    serialization(stream) {
        this.text = stream.localizationString(this.text);
        if (stream.isReading)
            this.params = {};
        this.params.anchorX = stream.uint8Safe(this.params.anchorX);
        this.params.anchorY = stream.uint8Safe(this.params.anchorY);
        this.params.fontStyle = stream.uint8Safe(this.params.fontStyle);
        this.params.fontWeight = stream.uint8Safe(this.params.fontWeight);
        this.params.textAlign = stream.uint8Safe(this.params.textAlign);
        this.params.color = stream.uint32Safe(this.params.color);
        this.params.outlineColor = stream.uint32Safe(this.params.outlineColor);
        this.params.strokeColor = stream.uint32Safe(this.params.strokeColor);
        this.params.curveRadius = stream.float32Safe(this.params.curveRadius);
        this.params.depthOffset = stream.float32Safe(this.params.depthOffset);
        this.params.fillOpacity = stream.float32Safe(this.params.fillOpacity);
        this.params.glyphGeometryDetail = stream.float32Safe(this.params.glyphGeometryDetail);
        this.params.letterSpacing = stream.float32Safe(this.params.letterSpacing);
        this.params.maxWidth = stream.float32Safe(this.params.maxWidth);
        this.params.outlineBlur = stream.float32Safe(this.params.outlineBlur);
        this.params.outlineOpacity = stream.float32Safe(this.params.outlineOpacity);
        this.params.outlineWidth = stream.float32Safe(this.params.outlineWidth);
        this.params.strokeOpacity = stream.float32Safe(this.params.strokeOpacity);
        this.params.strokeWidth = stream.float32Safe(this.params.strokeWidth);
        this.params.fontSize = stream.float32Safe(this.params.fontSize);
        this.params.clipRect = stream.arraySafe(this.params.clipRect);
        this.params.font = stream.utf16StringSafe(this.params.font);
    }
    static is(facet) {
        return facet.type === TextFacet.typestatic;
    }
}
FacetFactory.register(TextFacet);

/**
 * Horizontal text alignment options for text-to-texture rendering.
 */
var TextToTextureTextAlign;
(function (TextToTextureTextAlign) {
    /** Centers text around the provided X position. */
    TextToTextureTextAlign[TextToTextureTextAlign["Center"] = 0] = "Center";
    /** Aligns text to the logical end edge. */
    TextToTextureTextAlign[TextToTextureTextAlign["End"] = 1] = "End";
    TextToTextureTextAlign[TextToTextureTextAlign["Left"] = 2] = "Left";
    TextToTextureTextAlign[TextToTextureTextAlign["Right"] = 3] = "Right";
    /** Aligns text to the logical start edge. */
    TextToTextureTextAlign[TextToTextureTextAlign["Start"] = 4] = "Start";
})(TextToTextureTextAlign || (TextToTextureTextAlign = {}));
/**
 * Text-baseline alignment options for text-to-texture rendering.
 */
var TextToTextureTextBaseline;
(function (TextToTextureTextBaseline) {
    TextToTextureTextBaseline[TextToTextureTextBaseline["Alphabetic"] = 0] = "Alphabetic";
    TextToTextureTextBaseline[TextToTextureTextBaseline["Bottom"] = 1] = "Bottom";
    TextToTextureTextBaseline[TextToTextureTextBaseline["Hanging"] = 2] = "Hanging";
    TextToTextureTextBaseline[TextToTextureTextBaseline["Ideographic"] = 3] = "Ideographic";
    TextToTextureTextBaseline[TextToTextureTextBaseline["Middle"] = 4] = "Middle";
    TextToTextureTextBaseline[TextToTextureTextBaseline["Top"] = 5] = "Top";
})(TextToTextureTextBaseline || (TextToTextureTextBaseline = {}));
/**
 * Renders `params.text` (and any additional `params.elements`) onto a 2D canvas that `TextToTextureRenderable` turns into a `CanvasTexture` applied to the model's material, letting text be drawn onto a mesh's surface instead of as 3D geometry.
 * @see {@link flavr-client!modules/rendering/texture/TextToTextureRenderable.TextToTextureRenderable | TextToTextureRenderable}
 */
class TextToTextureFacet extends Facet {
    params;
    static typestatic = "TextToTextureFacet";
    static metaData = {
        export: true,
        description: "Renders text into a texture",
        category: "Interaction"
    };
    static propTypes = {
        params: {
            type: PropertyType.Struct,
            properties: {
                text: { type: PropertyType.Utf16StringSafe },
                textAlign: {
                    type: PropertyType.EnumUint8,
                    items: {
                        0: "Center",
                        1: "End",
                        2: "Left",
                        3: "Right",
                        4: "Start"
                    }
                },
                textBaseline: {
                    type: PropertyType.EnumUint8,
                    items: {
                        0: "Alphabetic",
                        1: "Bottom",
                        2: "Hanging",
                        3: "Ideographic",
                        4: "Middle"
                    }
                },
                fontSize: {
                    type: PropertyType.Uint16Safe
                },
                textColor: { type: PropertyType.ColorHexSafe },
                backgroundColor: { type: PropertyType.ColorHexSafe }
            }
        }
    };
    get byteLength() {
        let byteLength = calcStringByteLength(this.params.text) +
            Uint8Array.BYTES_PER_ELEMENT + // textAlign
            Uint8Array.BYTES_PER_ELEMENT + // textBaseline
            Uint16Array.BYTES_PER_ELEMENT + // fontSize
            Uint32Array.BYTES_PER_ELEMENT + // textColor
            Uint32Array.BYTES_PER_ELEMENT + // backgroundColor
            Uint32Array.BYTES_PER_ELEMENT + // width
            Uint32Array.BYTES_PER_ELEMENT; // height
        byteLength += Uint32Array.BYTES_PER_ELEMENT; // elementsLength
        if (this.params.elements) {
            for (const element of this.params.elements) {
                byteLength +=
                    calcStringByteLength(element.text) +
                        Uint32Array.BYTES_PER_ELEMENT + // x
                        Uint32Array.BYTES_PER_ELEMENT + // y
                        Uint8Array.BYTES_PER_ELEMENT + // textAlign
                        Uint8Array.BYTES_PER_ELEMENT + // textBaseline
                        Uint16Array.BYTES_PER_ELEMENT + // fontSize
                        Uint32Array.BYTES_PER_ELEMENT; // textColor
            }
        }
        return byteLength;
    }
    constructor(params = {}) {
        super();
        this.params = params;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.params = {};
            this.params.elements = [];
        }
        this.params.text = stream.utf16StringSafe(this.params.text);
        this.params.textAlign = stream.uint8Safe(this.params.textAlign);
        this.params.textBaseline = stream.uint8Safe(this.params.textBaseline);
        this.params.fontSize = stream.uint16Safe(this.params.fontSize);
        this.params.textColor = stream.uint32Safe(this.params.textColor);
        this.params.backgroundColor = stream.uint32Safe(this.params.backgroundColor);
        this.params.width = stream.uint32Safe(this.params.width);
        this.params.height = stream.uint32Safe(this.params.height);
        const elementsLength = stream.uint32(this.params.elements ? this.params.elements.length : 0);
        for (let i = 0; i < elementsLength; ++i) {
            const element = this.params.elements[i] || {};
            element.text = stream.utf16String(element.text);
            element.x = stream.uint32(element.x);
            element.y = stream.uint32(element.y);
            element.textAlign = stream.uint8Safe(element.textAlign);
            element.textBaseline = stream.uint8Safe(element.textBaseline);
            element.fontSize = stream.uint16Safe(element.fontSize);
            element.textColor = stream.uint32Safe(element.textColor);
            this.params.elements[i] = element;
        }
    }
    static is(facet) {
        return facet.type === TextToTextureFacet.typestatic;
    }
}
FacetFactory.register(TextToTextureFacet);

/**
 * Renders the model as a truncated cone (a cone with the tip cut off). `TruncatedConeRenderable`
 * builds the geometry from `ratio` (top-to-bottom radius ratio, 0 to 1) and `pointUp`
 * (whether the smaller radius faces up), and shades it using `material` and `color`.
 * @see {@link flavr-client!modules/rendering/primitives/TruncatedConeRenderable.TruncatedConeRenderable | TruncatedConeRenderable}
 */
class TruncatedConeFacet extends RenderableFacet {
    ratio;
    pointUp;
    color;
    material;
    static typestatic = "TruncatedCone";
    static metaData = {
        export: true,
        description: "Displays a truncated cone",
        category: "Rendering"
    };
    static propTypes = {
        ratio: {
            type: PropertyType.Float32,
            default: 1
        },
        pointUp: {
            type: PropertyType.Boolean,
            default: false
        },
        color: {
            type: PropertyType.Uint32,
            default: 0
        },
        material: {
            type: PropertyType.Struct,
            properties: {
                shading: {
                    type: PropertyType.Uint8
                },
                outline: { type: PropertyType.Uint8Safe },
                castShadow: {
                    type: PropertyType.Boolean,
                    default: false
                },
                cull: {
                    type: PropertyType.Uint8Safe
                }
            },
            default: { ...PrimitiveMeshMaterial.DEFAULT }
        }
    };
    get byteLength() {
        return (Float32Array.BYTES_PER_ELEMENT + // ratio
            BOOLEAN_BYTES_PER_ELEMENT + // pointUp
            Uint32Array.BYTES_PER_ELEMENT + // color
            MATERIAL_PROPERTIES_BYTE_LENGTH);
    }
    /**
     * @param material Groups the shading, outline, shadow, and culling settings.
     */
    constructor(ratio = 1, pointUp = false, color = 0, 
    /** Groups the shading, outline, shadow, and culling settings. */
    material = { ...PrimitiveMeshMaterial.DEFAULT }) {
        super();
        this.ratio = ratio;
        this.pointUp = pointUp;
        this.color = color;
        this.material = material;
        if (ratio < 0 || ratio > 1) {
            throw new Error(`ratio shoud be between 0 and 1 but is ${ratio}`);
        }
    }
    serialization(stream) {
        this.ratio = stream.float32(this.ratio);
        this.pointUp = stream.boolean(this.pointUp);
        this.color = stream.uint32(this.color);
        this.material = serializationMaterialProperties(stream, this.material);
    }
    static is(facet) {
        return facet.type === TruncatedConeFacet.typestatic;
    }
}
FacetFactory.register(TruncatedConeFacet);

/**
 * Controls whether a model is shown or hidden. Set via `Model.setVisibility`/read via `Model.getVisibility` on both client and server; `RendererModule` reacts to it by calling `setVisible` on the model's renderables to actually show/hide the corresponding scene objects.
 */
class VisibilityFacet extends Facet {
    visible;
    test;
    arr;
    dict;
    static typestatic = "VisibilityFacet";
    static metaData = {
        export: true,
        description: "Toggles the visibility of a model",
        category: "Rendering"
    };
    static propTypes = {
        visible: {
            type: PropertyType.Boolean,
            isRequired: true
        }
    };
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param test Legacy placeholder field kept for compatibility.
     * @param arr Legacy placeholder field kept for compatibility.
     * @param dict Legacy placeholder field kept for compatibility.
     */
    constructor(visible = true, 
    /** Legacy placeholder field kept for compatibility. */
    test = 0, 
    /** Legacy placeholder field kept for compatibility. */
    arr = [], 
    /** Legacy placeholder field kept for compatibility. */
    dict = {}) {
        super();
        this.visible = visible;
        this.test = test;
        this.arr = arr;
        this.dict = dict;
    }
    serialization(stream) {
        this.visible = stream.boolean(this.visible);
    }
    static is(facet) {
        return facet.type === VisibilityFacet.typestatic;
    }
}
FacetFactory.register(VisibilityFacet);

/**
 * Placed on a room model to have that room's content auto-scaled and re-centered to fit within the room's own bounding box, using `relativeMargin` and `relativeCenter`. Set by `RoomHelper`/`LayoutContext` when a room is created (e.g. `ModifierModelInteractionHandler` applies per-shape margins when a room's primitive shape changes), and read wherever the room's content-fitting transform is computed.
 * @param relativeMargin: defines the margin between the model and the content of the room (@see AutoScaleMargin).
 * @param relativeCenter: you can adjust the center, with each axis a fraction of the bounding box size [-0.5, 0.5]
 * @param boundingBoxMayShrink whether the autoscaleChildren transformation should be updated when combined
 * bounding box for the models within the room shrinkes
 */
class AutoScaleChildrenFacet extends Facet {
    relativeMargin;
    relativeCenter;
    boundingBoxMayShrink;
    static typestatic = "AutoScaleChildrenFacet";
    static metaData = {
        export: true,
        description: "",
        category: "Misc"
    };
    static propTypes = {
        relativeMargin: {
            type: PropertyType.Struct,
            properties: {
                margin: { type: PropertyType.Float32Safe },
                top_margin: { type: PropertyType.Float32Safe },
                bottom_margin: { type: PropertyType.Float32Safe },
                left_margin: { type: PropertyType.Float32Safe },
                right_margin: { type: PropertyType.Float32Safe },
                front_margin: { type: PropertyType.Float32Safe },
                back_margin: { type: PropertyType.Float32Safe }
            },
            default: { margin: 0.1 }
        },
        relativeCenter: {
            type: PropertyType.Number3,
            default: [0, 0, 0]
        },
        boundingBoxMayShrink: {
            type: PropertyType.Boolean,
            default: false
        }
    };
    static MAX_BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT * (7 + 3) + BOOLEAN_BYTES_PER_ELEMENT;
    get byteLength() {
        return AutoScaleChildrenFacet.MAX_BYTE_LENGTH;
    }
    /**
     * @param relativeMargin Margins that define how much empty space remains around the fitted child content.
     * @param relativeCenter Center offset for the fitted content, expressed relative to room bounds.
     * @param boundingBoxMayShrink Whether shrinking child bounds should also recompute the autoscale transform.
     */
    constructor(
    /** Margins that define how much empty space remains around the fitted child content. */
    relativeMargin = { margin: 0.1 }, 
    /** Center offset for the fitted content, expressed relative to room bounds. */
    relativeCenter = [0, 0, 0], 
    /** Whether shrinking child bounds should also recompute the autoscale transform. */
    boundingBoxMayShrink = false) {
        super();
        this.relativeMargin = relativeMargin;
        this.relativeCenter = relativeCenter;
        this.boundingBoxMayShrink = boundingBoxMayShrink;
    }
    serialization(stream) {
        if (this.relativeMargin === undefined) {
            this.relativeMargin = {};
        }
        this.relativeMargin.margin = stream.float32Safe(this.relativeMargin.margin);
        this.relativeMargin.top_margin = stream.float32Safe(this.relativeMargin.top_margin);
        this.relativeMargin.bottom_margin = stream.float32Safe(this.relativeMargin.bottom_margin);
        this.relativeMargin.left_margin = stream.float32Safe(this.relativeMargin.left_margin);
        this.relativeMargin.right_margin = stream.float32Safe(this.relativeMargin.right_margin);
        this.relativeMargin.front_margin = stream.float32Safe(this.relativeMargin.front_margin);
        this.relativeMargin.back_margin = stream.float32Safe(this.relativeMargin.back_margin);
        this.relativeCenter = stream.number3(this.relativeCenter);
        this.boundingBoxMayShrink = stream.boolean(this.boundingBoxMayShrink);
    }
    static is(facet) {
        return facet.type === AutoScaleChildrenFacet.typestatic;
    }
}
FacetFactory.register(AutoScaleChildrenFacet);

/**
 * Records the `layouterId` a room was built from when its scene is parsed from a GLTF asset (set by `GltfSceneParser`). No code was found reading this facet anywhere else in the repo (the actual layouter selection used by `LayoutManager` comes from `LayoutFacet.params.layouterId`), so this currently appears to be write-only/unused.
 */
class LayouterAssetFacet extends Facet {
    layouterId;
    static typestatic = "LayouterAssetFacet";
    get byteLength() {
        return calcStringByteLength(this.layouterId);
    }
    /**
     * @param layouterId Identifier of the layouter asset associated with this room/model.
     */
    constructor(
    /** Identifier of the layouter asset associated with this room/model. */
    layouterId) {
        super();
        this.layouterId = layouterId;
    }
    serialization(stream) {
        this.layouterId = stream.utf16String(this.layouterId);
    }
    static is(facet) {
        return facet.type === LayouterAssetFacet.typestatic;
    }
}
FacetFactory.register(LayouterAssetFacet);

/**
 * Stores the `LayouterParameters` (which layouter, and its config) used to populate a room's content. The server's `LayoutManager` reads this to pick and run the layouter when creating/re-creating a room, and the client's `LayouterModule` caches updates to `params` sent as `UpdateFacetOnModelMessage`.
 */
class LayoutFacet extends Facet {
    params;
    static typestatic = "LayoutFacet";
    get byteLength() {
        return calcObjectByteLength(this.params);
    }
    /**
     * @param params Stores which layouter should populate the room and with which configuration.
     */
    constructor(
    /** Stores which layouter should populate the room and with which configuration. */
    params) {
        super();
        this.params = params;
    }
    serialization(stream) {
        this.params = stream.object(this.params);
    }
    static is(facet) {
        return facet.type === LayoutFacet.typestatic;
    }
}
FacetFactory.register(LayoutFacet);

// a transformation of the model in local coordinates
/**
 * Holds a model's position, orientation and scale relative to its parent. This is the core transform facet: `Model`/`ModelImplementation` read and write it to move/rotate/scale models on both client and server, and the renderer applies it (combined with the parent chain) to compute each model's world matrix.
 */
class LocalTransformFacet extends Facet {
    position;
    orientation;
    scale;
    static typestatic = "Transform";
    static propTypes = {
        position: {
            type: PropertyType.Number3,
            default: [0, 0, 0]
        },
        orientation: {
            type: PropertyType.Number4,
            default: [0, 0, 0, 1]
        },
        scale: {
            type: PropertyType.Number3,
            default: [1, 1, 1]
        }
    };
    get byteLength() {
        return Float32Array.BYTES_PER_ELEMENT * (3 + 4 + 3);
    }
    /**
     * @param position Stores the model position relative to its parent.
     * @param orientation Stores the model rotation relative to its parent as a quaternion.
     * @param scale Stores the model scale relative to its parent.
     */
    constructor(
    /** Stores the model position relative to its parent. */
    position = [0, 0, 0], 
    /** Stores the model rotation relative to its parent as a quaternion. */
    orientation = [0, 0, 0, 1], 
    /** Stores the model scale relative to its parent. */
    scale = [1, 1, 1]) {
        super();
        this.position = position;
        this.orientation = orientation;
        this.scale = scale;
        if (scale[0] === 0 || scale[1] === 0 || scale[2] === 0) {
            console.warn(`[Common] Creating invalid transform with a scale axis 0: ${JSON.stringify(scale)}`);
        }
    }
    serialization(stream) {
        this.position = stream.number3(this.position);
        this.orientation = stream.number4(this.orientation);
        this.scale = stream.number3(this.scale);
    }
    static is(facet) {
        return facet.type === LocalTransformFacet.typestatic;
    }
}
FacetFactory.register(LocalTransformFacet);

/**
 * Animates the model towards the model identified by `targetUid`. `MovingToRenderable` moves and rotates the model each frame at `linearSpeed`/`rotatingSpeed`, sends the resulting transform once the target is reached, and optionally destroys the model (`destroyOnReached`); the facet is removed if a fresh transform update arrives first.
 * @see {@link flavr-client!modules/moveTo/MovingToRenderable.MovingToRenderable | MovingToRenderable}
 */
class MovingToFacet extends Facet {
    targetUid;
    linearSpeed;
    rotatingSpeed;
    destroyOnReached;
    static typestatic = "MovingToFacet";
    static metaData = {
        export: true,
        description: "Moves the user to this model",
        category: "Interaction"
    };
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT +
            Float32Array.BYTES_PER_ELEMENT * 2 + // linearSpeed, rotatingSpeed
            BOOLEAN_BYTES_PER_ELEMENT); // destroyOnReached
    }
    /**
     * @param targetUid UID of the target model this model should move toward.
     * @param linearSpeed Translation speed used while moving toward the target.
     * @param rotatingSpeed Rotation speed used while aligning to the target.
     * @param destroyOnReached Whether the moving model should be destroyed after reaching the target.
     */
    constructor(
    /** UID of the target model this model should move toward. */
    targetUid, 
    /** Translation speed used while moving toward the target. */
    linearSpeed = 1, 
    /** Rotation speed used while aligning to the target. */
    rotatingSpeed = 1, 
    /** Whether the moving model should be destroyed after reaching the target. */
    destroyOnReached = false) {
        super();
        this.targetUid = targetUid;
        this.linearSpeed = linearSpeed;
        this.rotatingSpeed = rotatingSpeed;
        this.destroyOnReached = destroyOnReached;
    }
    serialization(stream) {
        this.targetUid = stream.uid(this.targetUid);
        this.linearSpeed = stream.float32(this.linearSpeed);
        this.rotatingSpeed = stream.float32(this.rotatingSpeed);
        this.destroyOnReached = stream.boolean(this.destroyOnReached);
    }
    static is(facet) {
        return facet.type === MovingToFacet.typestatic;
    }
}
FacetFactory.register(MovingToFacet);

/**
 * Marks the model's geometry as a navigation mesh that restricts user movement/locomotion. `NavMeshRenderable`/`NavMeshService` build the actual walkable mesh from it, scoped to `layerId` (only active while that layer is the session's current layer), and `hideMesh` controls whether the source geometry itself stays visible.
 * @see {@link flavr-client!modules/navMesh/NavMeshRenderable.NavMeshRenderable | NavMeshRenderable}
 */
class NavMeshFacet extends Facet {
    layerId;
    hideMesh;
    static typestatic = "NavMeshFacet";
    static metaData = {
        export: true,
        description: "Restricts the user movement to this model",
        category: "General"
    };
    static propTypes = {
        layerId: {
            type: PropertyType.Int32,
            default: 0,
            description: "NavMesh is only used if its layerId matches the current active layerId of the session. The initial layerId is 0"
        },
        hideMesh: {
            type: PropertyType.Boolean,
            default: true,
            description: "Toggle visibility of geometry that is used for the navigation mesh"
        }
    };
    get byteLength() {
        return (Int32Array.BYTES_PER_ELEMENT + // layerId
            BOOLEAN_BYTES_PER_ELEMENT); // hideMesh
    }
    /**
     * @param layerId Session layer ID on which this nav mesh should be active.
     * @param hideMesh Whether the source mesh should be hidden while still serving as nav mesh geometry.
     */
    constructor(
    /** Session layer ID on which this nav mesh should be active. */
    layerId = 0, 
    /** Whether the source mesh should be hidden while still serving as nav mesh geometry. */
    hideMesh = true) {
        super();
        this.layerId = layerId;
        this.hideMesh = hideMesh;
    }
    serialization(stream) {
        this.layerId = stream.int32(this.layerId);
        this.hideMesh = stream.boolean(this.hideMesh);
    }
    static is(facet) {
        return facet.type === NavMeshFacet.typestatic;
    }
}
FacetFactory.register(NavMeshFacet);

const BLOOM_RESOLUTION_SETTING = { default: 100, min: 10, max: 100, step: 5 };
const BLOOM_NUMBER_SETTINGS = {
    bloomStrength: { default: 0.1, min: 0, max: 1, step: 0.01 },
    bloomRadius: { default: 0.4, min: 0, max: 1, step: 0.01 },
    bloomThreshold: { default: 1.25, min: 0, max: 3, step: 0.01 }
};
const GTAO_NUMBER_SETTINGS = {
    gtaoBlendIntensity: { default: 1, min: 0, max: 1, step: 0.05 },
    gtaoRadius: { default: 0.25, min: 0.01, max: 5, step: 0.01 },
    gtaoThickness: { default: 1, min: 0.01, max: 10, step: 0.01 },
    gtaoDistanceExponent: { default: 1, min: 0.1, max: 5, step: 0.1 },
    gtaoDistanceFallOff: { default: 1, min: 0.01, max: 5, step: 0.01 },
    gtaoScale: { default: 1, min: 0, max: 5, step: 0.1 },
    gtaoSamples: { default: 16, min: 4, max: 64, step: 1 },
    gtaoDenoiseRadius: { default: 8, min: 0, max: 32, step: 1 },
    gtaoDenoiseSamples: { default: 16, min: 4, max: 64, step: 1 },
    gtaoDenoiseRings: { default: 2, min: 1, max: 8, step: 1 },
    gtaoDenoiseRadiusExponent: { default: 2, min: 0.1, max: 5, step: 0.1 },
    gtaoDenoiseLumaPhi: { default: 10, min: 0.1, max: 50, step: 0.1 },
    gtaoDenoiseDepthPhi: { default: 2, min: 0.1, max: 20, step: 0.1 },
    gtaoDenoiseNormalPhi: { default: 3, min: 0.1, max: 20, step: 0.1 }
};
const POST_EFFECT_NUMBER_SETTINGS = {
    bloomResolution: BLOOM_RESOLUTION_SETTING,
    ...BLOOM_NUMBER_SETTINGS,
    ...GTAO_NUMBER_SETTINGS
};
const POST_EFFECT_NUMBER_KEYS = Object.keys(POST_EFFECT_NUMBER_SETTINGS);
function normalizePostEffectNumber(key, value) {
    const config = POST_EFFECT_NUMBER_SETTINGS[key];
    if (value === undefined || !Number.isFinite(value))
        return config.default;
    const bounded = Math.max(config.min, Math.min(config.max, value));
    return config.step === 1 ? Math.round(bounded) : bounded;
}

/**
 * Placed on a room to configure its background/skybox/infinite-plane appearance (see `EnvironmentParams` above). The client's `BackgroundModule` subscribes to this facet, caches the params per room, and applies them to the scene background/environment map whenever that room becomes the active room.
 */
class RoomEnvironmentFacet extends Facet {
    params;
    static typestatic = "RoomEnvironment";
    static BYTE_LENGTH = BOOLEAN_BYTES_PER_ELEMENT + // showSkybox
        Uint32Array.BYTES_PER_ELEMENT + // skyboxIndex
        BOOLEAN_BYTES_PER_ELEMENT + // useSkyboxAsEnvironmentMap
        BOOLEAN_BYTES_PER_ELEMENT + // usePostEffects
        BOOLEAN_BYTES_PER_ELEMENT + // useBloom
        BOOLEAN_BYTES_PER_ELEMENT + // useGtao
        Uint32Array.BYTES_PER_ELEMENT + // backgroundColor
        BOOLEAN_BYTES_PER_ELEMENT + // showInfinitePlane
        Uint32Array.BYTES_PER_ELEMENT + // infinitePlaneColor
        Float32Array.BYTES_PER_ELEMENT + // infinitePlaneElevation
        BOOLEAN_BYTES_PER_ELEMENT + // gtaoScreenSpaceRadius
        POST_EFFECT_NUMBER_KEYS.length * Float32Array.BYTES_PER_ELEMENT +
        BOOLEAN_BYTES_PER_ELEMENT + // showValuesInFlaVR
        Float32Array.BYTES_PER_ELEMENT; // environmentMapRotation
    get byteLength() {
        return RoomEnvironmentFacet.BYTE_LENGTH;
    }
    /**
     * @param params Carries the environment settings that should be applied to the room.
     */
    constructor(
    /** Carries the environment settings that should be applied to the room. */
    params) {
        super();
        this.params = params;
    }
    serialization(stream) {
        if (stream.isReading)
            this.params = {};
        const params = this.params;
        params.showSkybox = stream.booleanSafe(params.showSkybox);
        params.skyboxIndex = stream.uint32Safe(params.skyboxIndex);
        params.useSkyboxAsEnvironmentMap = stream.booleanSafe(params.useSkyboxAsEnvironmentMap);
        params.usePostEffects = stream.booleanSafe(params.usePostEffects);
        params.useBloom = stream.booleanSafe(params.useBloom);
        params.useGtao = stream.booleanSafe(params.useGtao);
        params.backgroundColor = stream.uint32Safe(params.backgroundColor);
        params.showInfinitePlane = stream.booleanSafe(params.showInfinitePlane);
        params.infinitePlaneColor = stream.uint32Safe(params.infinitePlaneColor);
        params.infinitePlaneElevation = stream.float32Safe(params.infinitePlaneElevation);
        params.gtaoScreenSpaceRadius = stream.booleanSafe(params.gtaoScreenSpaceRadius);
        for (const key of POST_EFFECT_NUMBER_KEYS) {
            params[key] = stream.float32Safe(params[key]);
        }
        params.showValuesInFlaVR = stream.booleanSafe(params.showValuesInFlaVR);
        params.environmentMapRotation = stream.float32Safe(params.environmentMapRotation);
    }
    static is(facet) {
        return facet.type === RoomEnvironmentFacet.typestatic;
    }
}
FacetFactory.register(RoomEnvironmentFacet);

/**
 * Loads a blueprint asset so it can be instantiated at runtime.
 */
class LoadBlueprintMessage extends BusMessage {
    parentUid;
    fileId;
    static typestatic = "LoadBlueprintMessage";
    static subjectstatic = "general";
    get byteLength() {
        return calcStringByteLength(this.fileId);
    }
    /**
     * @param parentUid Identifies the parent model that should host the loaded blueprint.
     * @param fileId Identifies the blueprint asset to load.
     */
    constructor(
    /**
     * Identifies the parent model that should host the loaded blueprint.
     */
    parentUid, 
    /**
     * Identifies the blueprint asset to load.
     */
    fileId) {
        super();
        this.parentUid = parentUid;
        this.fileId = fileId;
    }
    serialization(stream) {
        this.fileId = stream.utf16String(this.fileId);
    }
    static is(msg) {
        return msg.type === LoadBlueprintMessage.typestatic;
    }
}
MessageFactory.register(LoadBlueprintMessage);
/**
 * Requests creation of runtime models from a blueprint asset.
 */
class CreateModelFromBlueprintMessage extends BusMessage {
    parentUid;
    fileId;
    static typestatic = "CreateModelFromBlueprintMessage";
    static subjectstatic = "general";
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + calcStringByteLength(this.fileId);
    }
    /**
     * @param parentUid Identifies the parent model under which the blueprint models should be created.
     * @param fileId Identifies the blueprint asset to instantiate.
     */
    constructor(
    /**
     * Identifies the parent model under which the blueprint models should be created.
     */
    parentUid, 
    /**
     * Identifies the blueprint asset to instantiate.
     */
    fileId) {
        super();
        this.parentUid = parentUid;
        this.fileId = fileId;
    }
    serialization(stream) {
        this.parentUid = stream.uid(this.parentUid);
        this.fileId = stream.utf16String(this.fileId);
    }
    static is(msg) {
        return msg.type === CreateModelFromBlueprintMessage.typestatic;
    }
}
MessageFactory.register(CreateModelFromBlueprintMessage);
/**
 * Returns the result of creating models from a blueprint asset.
 */
class ResponseCreateModelFromBlueprint extends BusMessage {
    uids;
    static typestatic = "ResponseCreateModelFromBlueprint";
    static subjectstatic = "general";
    get byteLength() {
        return (Uint32Array.BYTES_PER_ELEMENT + // length
            UID_BYTES_PER_ELEMENT * this.uids.length);
    }
    /**
     * @param uids Lists the model identifiers created from the requested blueprint.
     */
    constructor(
    /**
     * Lists the model identifiers created from the requested blueprint.
     */
    uids) {
        super();
        this.uids = uids;
    }
    /**
     * On read, resets `uids` to `[]` first, since a deserialized instance skips the constructor and
     * would otherwise still hold whatever was passed in.
     */
    serialization(stream) {
        if (stream.isReading) {
            this.uids = [];
        }
        const length = stream.uint32(this.uids.length);
        for (let i = 0; i < length; i++) {
            this.uids[i] = stream.uid(this.uids[i]);
        }
    }
    static is(msg) {
        return msg.type === ResponseCreateModelFromBlueprint.typestatic;
    }
}
MessageFactory.register(ResponseCreateModelFromBlueprint);
/**
 * Adds a blueprint-defined tool to the tool viewer.
 */
class AddToolFromBlueprintMessage extends BusMessage {
    name;
    description;
    fileId;
    static typestatic = "AddToolFromBlueprintMessage";
    static subjectstatic = "general";
    get byteLength() {
        return calcStringByteLength(this.name) + calcStringByteLength(this.description) + calcStringByteLength(this.fileId);
    }
    /**
     * @param name Provides the tool name shown in the viewer.
     * @param description Describes what the tool does.
     * @param fileId Identifies the blueprint asset behind the tool entry.
     */
    constructor(
    /**
     * Provides the tool name shown in the viewer.
     */
    name, 
    /**
     * Describes what the tool does.
     */
    description, 
    /**
     * Identifies the blueprint asset behind the tool entry.
     */
    fileId) {
        super();
        this.name = name;
        this.description = description;
        this.fileId = fileId;
    }
    serialization(stream) {
        this.name = stream.utf16String(this.name);
        this.description = stream.utf16String(this.description);
        this.fileId = stream.utf16String(this.fileId);
    }
    static is(msg) {
        return msg.type === AddToolFromBlueprintMessage.typestatic;
    }
}
MessageFactory.register(AddToolFromBlueprintMessage);
/**
 * Removes a blueprint-defined tool from the tool viewer.
 */
class RemoveToolFromBlueprintMessage extends BusMessage {
    name;
    fileId;
    static typestatic = "RemoveToolFromBlueprintMessage";
    static subjectstatic = "general";
    get byteLength() {
        return calcStringByteLength(this.name) + calcStringByteLength(this.fileId);
    }
    /**
     * @param name Identifies the tool entry to remove.
     * @param fileId Identifies the blueprint asset the tool entry came from.
     */
    constructor(
    /**
     * Identifies the tool entry to remove.
     */
    name, 
    /**
     * Identifies the blueprint asset the tool entry came from.
     */
    fileId) {
        super();
        this.name = name;
        this.fileId = fileId;
    }
    serialization(stream) {
        this.name = stream.utf16StringSafe(this.name);
        this.fileId = stream.utf16StringSafe(this.fileId);
    }
    static is(msg) {
        return msg.type === RemoveToolFromBlueprintMessage.typestatic;
    }
}
MessageFactory.register(RemoveToolFromBlueprintMessage);

/**
 * Toggles whether the tool viewer is visible.
 */
class SetToolViewerVisibilityMessage extends BusMessage {
    visibility;
    static typestatic = "SetToolViewerVisibilityMessage";
    static subjectstatic = "general";
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param visibility Controls whether the tool viewer should be visible.
     */
    constructor(
    /**
     * Controls whether the tool viewer should be visible.
     */
    visibility) {
        super();
        this.visibility = visibility;
    }
    serialization(stream) {
        this.visibility = stream.boolean(this.visibility);
    }
    static is(msg) {
        return msg.type === SetToolViewerVisibilityMessage.typestatic;
    }
}
MessageFactory.register(SetToolViewerVisibilityMessage);
/**
 * Reports viewport changes of the tool viewer.
 */
class ToolViewerViewportChangedMessage extends BusMessage {
    aspectRatio;
    static typestatic = "ToolViewerViewportChangedMessage";
    static subjectstatic = "general";
    get byteLength() {
        return Float32Array.BYTES_PER_ELEMENT;
    }
    /**
     * @param aspectRatio Reports the updated viewport aspect ratio.
     */
    constructor(
    /**
     * Reports the updated viewport aspect ratio.
     */
    aspectRatio) {
        super();
        this.aspectRatio = aspectRatio;
    }
    serialization(stream) {
        this.aspectRatio = stream.float32(this.aspectRatio);
    }
    static is(msg) {
        return msg.type === ToolViewerViewportChangedMessage.typestatic;
    }
}
MessageFactory.register(ToolViewerViewportChangedMessage);
/**
 * Enables or disables the tool viewer feature.
 */
class ToolViewerActivationMessage extends BusMessage {
    active;
    static typestatic = "ToolViewerActivationMessage";
    static subjectstatic = "general";
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param active Enables or disables the tool viewer feature.
     */
    constructor(
    /**
     * Enables or disables the tool viewer feature.
     */
    active) {
        super();
        this.active = active;
    }
    serialization(stream) {
        this.active = stream.boolean(this.active);
    }
    static is(msg) {
        return msg.type === ToolViewerActivationMessage.typestatic;
    }
}
MessageFactory.register(ToolViewerActivationMessage);

/**
 * Requests playback of a sound.
 */
class PlaySoundMessage extends BusMessage {
    uid;
    url;
    params;
    static typestatic = "PlaySoundMessage";
    static subjectstatic = "record";
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + calcStringByteLength(this.url) + calcObjectByteLength(this.params);
    }
    /**
     * @param uid Model uid at which the sound should play.
     * @param url Asset url of the sound file.
     * @param params Optional playback configuration.
     */
    constructor(
    /**
     * Model uid at which the sound should play.
     */
    uid, 
    /**
     * Asset url of the sound file.
     */
    url, 
    /**
     * Optional playback configuration.
     */
    params) {
        super();
        this.uid = uid;
        this.url = url;
        this.params = params;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.url = stream.utf16String(this.url);
        this.params = stream.objectSafe(this.params);
    }
    static is(msg) {
        return msg.type === PlaySoundMessage.typestatic;
    }
}
MessageFactory.register(PlaySoundMessage);

/** Selects a clip or controls the current playback on the server. Does not change saved defaults. */
class ControlAnimationMessage extends BusMessage {
    uid;
    command;
    clip;
    pauseAt;
    static typestatic = "ControlAnimationMessage";
    static subjectstatic = "server";
    constructor(uid, command, clip, pauseAt) {
        super();
        this.uid = uid;
        this.command = command;
        this.clip = clip;
        this.pauseAt = pauseAt;
    }
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + calcStringByteLength(this.command) + BOOLEAN_BYTES_PER_ELEMENT +
            calcObjectByteLength(this.clip ?? undefined) + calcObjectByteLength(this.pauseAt);
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.command = stream.utf16String(this.command);
        const cleared = stream.boolean(this.clip === null);
        const selection = stream.objectSafe(this.clip ?? undefined);
        this.clip = cleared ? null : selection;
        this.pauseAt = stream.objectSafe(this.pauseAt);
    }
    static is(msg) {
        return msg.type === ControlAnimationMessage.typestatic;
    }
}
MessageFactory.register(ControlAnimationMessage);
/** Registers the loaded object's clip metadata and requests its authoritative playback state. */
class RequestAnimationStateMessage extends BusMessage {
    uid;
    clips;
    static typestatic = "RequestAnimationStateMessage";
    static subjectstatic = "server";
    constructor(uid, clips) {
        super();
        this.uid = uid;
        this.clips = clips;
    }
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + calcArrayByteLength(this.clips);
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.clips = stream.array(this.clips);
    }
    static is(msg) {
        return msg.type === RequestAnimationStateMessage.typestatic;
    }
}
MessageFactory.register(RequestAnimationStateMessage);
/** Authoritative state; clients must not originate this message. */
class AnimationStateMessage extends BusMessage {
    uid;
    state;
    static typestatic = "AnimationStateMessage";
    static subjectstatic = "renderer";
    constructor(uid, state) {
        super();
        this.uid = uid;
        this.state = state;
    }
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + calcObjectByteLength({ ...this.state, clipKey: this.state.clipKey ?? undefined });
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        const state = stream.object({ ...this.state, clipKey: this.state?.clipKey ?? undefined });
        this.state = { ...state, clipKey: state.clipKey ?? null };
    }
    static is(msg) {
        return msg.type === AnimationStateMessage.typestatic;
    }
}
MessageFactory.register(AnimationStateMessage);
/** Emitted once by the server when a non-looping playback reaches its end. */
class AnimationClipFinishedMessage extends BusMessage {
    uid;
    clipName;
    occurrence;
    playbackId;
    static typestatic = "AnimationClipFinishedMessage";
    static subjectstatic = "general";
    constructor(uid, clipName, occurrence, playbackId) {
        super();
        this.uid = uid;
        this.clipName = clipName;
        this.occurrence = occurrence;
        this.playbackId = playbackId;
    }
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + calcStringByteLength(this.clipName) + Float64Array.BYTES_PER_ELEMENT + calcStringByteLength(this.playbackId);
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.clipName = stream.utf16String(this.clipName);
        this.occurrence = stream.float64(this.occurrence);
        this.playbackId = stream.utf16String(this.playbackId);
    }
    static is(msg) {
        return msg.type === AnimationClipFinishedMessage.typestatic;
    }
}
MessageFactory.register(AnimationClipFinishedMessage);

/**
 * Carries an error answer for a failed ask request.
 */
class AskErrorMessage extends BusMessage {
    error;
    static typestatic = "AskErrorMessage";
    static subjectstatic = "system";
    get byteLength() {
        return calcLocalizationStringByteLength(this.error);
    }
    /**
     * @param error Error description returned when handling the ask request fails.
     */
    constructor(
    /**
     * Error description returned when handling the ask request fails.
     */
    error) {
        super();
        this.error = error;
        if (typeof error === "object" && !("key" in error)) {
            try {
                this.error = JSON.stringify(error);
            }
            catch {
                this.error = undefined;
            }
            console.error("[Common] AskErrorMessage: error: ", error);
        }
    }
    serialization(stream) {
        this.error = stream.localizationStringSafe(this.error);
    }
    static is(msg) {
        return msg.type === AskErrorMessage.typestatic;
    }
}
MessageFactory.register(AskErrorMessage);

/**
 * Carries a successful answer for an ask request.
 */
class AskOkMessage extends BusMessage {
    result;
    static typestatic = "AskOkMessage";
    static subjectstatic = "general";
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param result Optional success payload returned by the answered request.
     */
    constructor(
    /**
     * Optional success payload returned by the answered request.
     */
    result) {
        super();
        this.result = result;
    }
    serialization(stream) {
        this.result = stream.booleanSafe(this.result);
    }
    static is(msg) {
        return msg.type === AskOkMessage.typestatic;
    }
}
MessageFactory.register(AskOkMessage);

/**
 * Carries a rejected answer for an ask request.
 */
class AskRejectMessage extends BusMessage {
    message;
    static typestatic = "AskRejectMessage";
    static subjectstatic = "general";
    get byteLength() {
        return calcLocalizationStringByteLength(this.message);
    }
    /**
     * @param message User-facing explanation for why the ask request was rejected.
     */
    constructor(
    /**
     * User-facing explanation for why the ask request was rejected.
     */
    message) {
        super();
        this.message = message;
    }
    serialization(stream) {
        this.message = stream.localizationStringSafe(this.message);
    }
    static is(msg) {
        return msg.type === AskRejectMessage.typestatic;
    }
}
MessageFactory.register(AskRejectMessage);

/**
 * Requests the list of available database schemes.
 */
class RequestDatabaseSchemesListMessage extends BusMessage {
    static typestatic = "RequestDatabaseSchemesListMessage";
    static subjectstatic = "server";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === RequestDatabaseSchemesListMessage.typestatic;
    }
}
MessageFactory.register(RequestDatabaseSchemesListMessage);
/**
 * Returns the list of available database schemes.
 */
class DatabaseSchemesListMessage extends BusMessage {
    databases;
    static typestatic = "DatabaseSchemesListMessage";
    static subjectstatic = "database";
    get byteLength() {
        return calcObjectByteLength(this.databases);
    }
    /**
     * @param databases Available database entries with internal name and display label.
     */
    constructor(
    /**
     * Available database entries with internal name and display label.
     */
    databases) {
        super();
        this.databases = databases;
    }
    serialization(stream) {
        this.databases = stream.object(this.databases);
    }
    static is(msg) {
        return msg.type === DatabaseSchemesListMessage.typestatic;
    }
}
MessageFactory.register(DatabaseSchemesListMessage);
/**
 * Requests the schema for a specific database.
 */
class RequestDatabaseSchemaMessage extends BusMessage {
    name;
    static typestatic = "RequestDatabaseSchemaMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcStringByteLength(this.name);
    }
    /**
     * @param name Database whose schema is requested.
     */
    constructor(
    /**
     * Database whose schema is requested.
     */
    name) {
        super();
        this.name = name;
    }
    serialization(stream) {
        this.name = stream.utf16String(this.name);
    }
    static is(msg) {
        return msg.type === RequestDatabaseSchemaMessage.typestatic;
    }
}
MessageFactory.register(RequestDatabaseSchemaMessage);
/**
 * Returns the schema for a specific database.
 */
class DatabaseSchemaMessage extends BusMessage {
    tables;
    static typestatic = "DatabaseSchemaMessage";
    static subjectstatic = "database";
    get byteLength() {
        return calcObjectByteLength(this.tables);
    }
    /**
     * @param tables Tables keyed by table name.
     */
    constructor(
    /**
     * Tables keyed by table name.
     */
    tables) {
        super();
        this.tables = tables;
    }
    serialization(stream) {
        this.tables = stream.object(this.tables);
    }
    static is(msg) {
        return msg.type === DatabaseSchemaMessage.typestatic;
    }
}
MessageFactory.register(DatabaseSchemaMessage);
/**
 * Strategy used when testing multiple database connections.
 */
var TestDatabaseConnectionsMode;
(function (TestDatabaseConnectionsMode) {
    /** Test the selected databases in their given order. */
    TestDatabaseConnectionsMode[TestDatabaseConnectionsMode["ORDERED"] = 0] = "ORDERED";
    /** Test the selected databases in a random order each pass. */
    TestDatabaseConnectionsMode[TestDatabaseConnectionsMode["RANDOM"] = 1] = "RANDOM";
    /** Test the selected databases in a modular round-robin pattern. */
    TestDatabaseConnectionsMode[TestDatabaseConnectionsMode["MODULAR"] = 2] = "MODULAR";
})(TestDatabaseConnectionsMode || (TestDatabaseConnectionsMode = {}));
/**
 * Triggers a test of the configured database connections.
 */
class TestDatabaseConnectionsMessage extends BusMessage {
    dbNames;
    mode;
    iterations;
    delay;
    static typestatic = "TestDatabaseConnectionsMessage";
    static subjectstatic = "server";
    get byteLength() {
        return (calcArrayByteLength(this.dbNames) +
            Uint8Array.BYTES_PER_ELEMENT + // mode
            Uint32Array.BYTES_PER_ELEMENT + // iterations
            Uint32Array.BYTES_PER_ELEMENT); // delay
    }
    /**
     * @param dbNames Databases that should be tested.
     * @param mode Strategy used to iterate over the selected databases.
     * @param iterations Number of test iterations to run.
     * @param delay Delay between iterations in milliseconds.
     */
    constructor(
    /**
     * Databases that should be tested.
     */
    dbNames, 
    /**
     * Strategy used to iterate over the selected databases.
     */
    mode, 
    /**
     * Number of test iterations to run.
     */
    iterations = 100, 
    /**
     * Delay between iterations in milliseconds.
     */
    delay = 0) {
        super();
        this.dbNames = dbNames;
        this.mode = mode;
        this.iterations = iterations;
        this.delay = delay;
    }
    serialization(stream) {
        this.dbNames = stream.array(this.dbNames);
        this.mode = stream.uint8(this.mode);
        this.iterations = stream.uint32(this.iterations);
        this.delay = stream.uint32(this.delay);
    }
    static is(msg) {
        return msg.type === TestDatabaseConnectionsMessage.typestatic;
    }
}
MessageFactory.register(TestDatabaseConnectionsMessage);
/**
 * Returns the result of testing database connections.
 */
class ResponseTestDatabaseConnectionsMessage extends BusMessage {
    dbs;
    static typestatic = "ResponseTestDatabaseConnectionsMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcObjectByteLength(this.dbs);
    }
    /**
     * @param dbs Per-database connection test results.
     */
    constructor(
    /**
     * Per-database connection test results.
     */
    dbs) {
        super();
        this.dbs = dbs;
    }
    serialization(stream) {
        this.dbs = stream.object(this.dbs);
    }
    static is(msg) {
        return msg.type === ResponseTestDatabaseConnectionsMessage.typestatic;
    }
}
MessageFactory.register(ResponseTestDatabaseConnectionsMessage);
/**
 * Requests whether the session database is connected.
 */
class RequestSessionDatbaseConnected extends BusMessage {
    static typestatic = "RequestSessionDatbaseConnected";
    static subjectstatic = "server";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === RequestSessionDatbaseConnected.typestatic;
    }
}
MessageFactory.register(RequestSessionDatbaseConnected);
/**
 * Returns whether the session database is connected.
 */
class ResponseSessionDatbaseConnected extends BusMessage {
    connected;
    static typestatic = "ResponseSessionDatbaseConnected";
    static subjectstatic = "server";
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param connected Whether the session database is currently connected.
     */
    constructor(
    /**
     * Whether the session database is currently connected.
     */
    connected) {
        super();
        this.connected = connected;
    }
    serialization(stream) {
        this.connected = stream.boolean(this.connected);
    }
    static is(msg) {
        return msg.type === ResponseSessionDatbaseConnected.typestatic;
    }
}
MessageFactory.register(ResponseSessionDatbaseConnected);

/**
 * Enumerates the gestures that can be enabled, disabled, or configured.
 */
var GestureType;
(function (GestureType) {
    /** Two-dimensional grab movement gesture. */
    GestureType[GestureType["GrabMove2D"] = 0] = "GrabMove2D";
    /** Three-dimensional grab movement gesture. */
    GestureType[GestureType["GrabMove3D"] = 1] = "GrabMove3D";
    /** Uniform or non-uniform grab scaling gesture. */
    GestureType[GestureType["GrabScale"] = 2] = "GrabScale";
    /** Grab-based rotation gesture. */
    GestureType[GestureType["GrabRotate"] = 3] = "GrabRotate";
    /** Boundary push movement gesture. */
    GestureType[GestureType["PushBondaryMovement"] = 4] = "PushBondaryMovement";
    /** First scroll gesture variant. */
    GestureType[GestureType["ScrollVariantA"] = 5] = "ScrollVariantA";
    /** Second scroll gesture variant. */
    GestureType[GestureType["ScrollVariantB"] = 6] = "ScrollVariantB";
    /** Third scroll gesture variant. */
    GestureType[GestureType["ScrollVariantC"] = 7] = "ScrollVariantC";
    /** Fourth scroll gesture variant. */
    GestureType[GestureType["ScrollVariantD"] = 8] = "ScrollVariantD";
    /** Fifth scroll gesture variant. */
    GestureType[GestureType["ScrollVariantE"] = 9] = "ScrollVariantE";
    /** Throw-down interaction gesture. */
    GestureType[GestureType["ThrowDown"] = 10] = "ThrowDown";
    /** Gesture that reveals contextual information. */
    GestureType[GestureType["DisplayInfo"] = 11] = "DisplayInfo";
    /** View rotation gesture used while editing. */
    GestureType[GestureType["RotateViewWhenEditing"] = 12] = "RotateViewWhenEditing";
})(GestureType || (GestureType = {}));
/**
 * Disables a specific gesture.
 */
class DisableGestureMessage extends BusMessage {
    gesture;
    disable;
    static typestatic = "DisableGestureMessage";
    static subjectstatic = "interaction";
    static MAX_BYTE_LENGTH = Uint8Array.BYTES_PER_ELEMENT + // gesture
        BOOLEAN_BYTES_PER_ELEMENT; // disable
    get byteLength() {
        return DisableGestureMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param gesture Selects the gesture to update.
     * @param disable Disables the gesture when set to `true`.
     */
    constructor(
    /**
     * Selects the gesture to update.
     */
    gesture = GestureType.GrabMove2D, 
    /**
     * Disables the gesture when set to `true`.
     */
    disable = false) {
        super();
        this.gesture = gesture;
        this.disable = disable;
    }
    serialization(stream) {
        this.gesture = stream.uint8(this.gesture);
        this.disable = stream.boolean(this.disable);
    }
    static is(msg) {
        return msg.type === DisableGestureMessage.typestatic;
    }
}
MessageFactory.register(DisableGestureMessage);
/**
 * Disables all gestures at once.
 */
class DisableAllGesturesMessage extends BusMessage {
    disable;
    static typestatic = "DisableAllGesturesMessage";
    static subjectstatic = "interaction";
    static MAX_BYTE_LENGTH = BOOLEAN_BYTES_PER_ELEMENT; // disable
    get byteLength() {
        return DisableAllGesturesMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param disable Disables every gesture when set to `true`.
     */
    constructor(
    /**
     * Disables every gesture when set to `true`.
     */
    disable = false) {
        super();
        this.disable = disable;
    }
    serialization(stream) {
        this.disable = stream.boolean(this.disable);
    }
    static is(msg) {
        return msg.type === DisableAllGesturesMessage.typestatic;
    }
}
MessageFactory.register(DisableAllGesturesMessage);
/**
 * Sets which gestures are currently enabled.
 */
class SetEnabledGesturesMessage extends BusMessage {
    enabledGestures;
    static typestatic = "SetEnabledGesturesMessage";
    static subjectstatic = "interaction";
    get byteLength() {
        return calcArrayByteLength(this.enabledGestures);
    }
    /**
     * @param enabledGestures Lists the gestures that should remain enabled.
     */
    constructor(
    /**
     * Lists the gestures that should remain enabled.
     */
    enabledGestures) {
        super();
        this.enabledGestures = enabledGestures;
    }
    serialization(stream) {
        this.enabledGestures = stream.array(this.enabledGestures);
    }
    static is(msg) {
        return msg.type === SetEnabledGesturesMessage.typestatic;
    }
}
MessageFactory.register(SetEnabledGesturesMessage);
/**
 * Reports movement that was produced by a gesture.
 */
class MoveGestureMessage extends BusMessage {
    delta;
    static typestatic = "MoveGestureMessage";
    static subjectstatic = "interaction";
    static MAX_BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT * 3; // delta
    get byteLength() {
        return MoveGestureMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param delta Carries the movement delta produced by the gesture.
     */
    constructor(
    /**
     * Carries the movement delta produced by the gesture.
     */
    delta = [0, 0, 0]) {
        super();
        this.delta = delta;
    }
    serialization(stream) {
        this.delta = stream.number3(this.delta);
    }
    static is(msg) {
        return msg.type === MoveGestureMessage.typestatic;
    }
}
MessageFactory.register(MoveGestureMessage);
/**
 * Reports that a throw-down gesture was recognized.
 */
class ThrowDownGestureMessage extends BusMessage {
    position;
    orientation;
    static typestatic = "ThrowDownGestureMessage";
    static subjectstatic = "interaction";
    static MAX_BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT * (3 + 4); // position orientation
    get byteLength() {
        return ThrowDownGestureMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param position Reports where the throw-down gesture was recognized.
     * @param orientation Reports the gesture orientation.
     */
    constructor(
    /**
     * Reports where the throw-down gesture was recognized.
     */
    position = [0, 0, 0], 
    /**
     * Reports the gesture orientation.
     */
    orientation = [0, 0, 0, 1]) {
        super();
        this.position = position;
        this.orientation = orientation;
    }
    serialization(stream) {
        this.position = stream.number3(this.position);
        this.orientation = stream.number4(this.orientation);
    }
    static is(msg) {
        return msg.type === ThrowDownGestureMessage.typestatic;
    }
}
MessageFactory.register(ThrowDownGestureMessage);
/**
 * Updates the configuration of a scroll gesture.
 */
class ConfigureScrollGestureMessage extends BusMessage {
    config;
    static typestatic = "ConfigureScrollGestureMessage";
    static subjectstatic = "interaction";
    get byteLength() {
        return calcArrayByteLength(this.config);
    }
    /**
     * @param config Defines the selectable scroll gesture entries.
     */
    constructor(
    /**
     * Defines the selectable scroll gesture entries.
     */
    config) {
        super();
        this.config = config;
    }
    serialization(stream) {
        this.config = stream.array(this.config);
    }
    static is(msg) {
        return msg.type === ConfigureScrollGestureMessage.typestatic;
    }
}
MessageFactory.register(ConfigureScrollGestureMessage);
/**
 * Sets the informational text shown for gesture guidance.
 */
class SetInfoGestureTextMessage extends BusMessage {
    text;
    showWatch;
    static typestatic = "SetInfoGestureTextMessage";
    static subjectstatic = "interaction";
    static BYTE_LENGTH = BOOLEAN_BYTES_PER_ELEMENT; // showWatch
    get byteLength() {
        return SetInfoGestureTextMessage.BYTE_LENGTH + calcStringByteLength(this.text);
    }
    /**
     * @param text Provides the instructional text that should be shown.
     * @param showWatch Controls whether the watch hint should be visible.
     */
    constructor(
    /**
     * Provides the instructional text that should be shown.
     */
    text = "", 
    /**
     * Controls whether the watch hint should be visible.
     */
    showWatch = false) {
        super();
        this.text = text;
        this.showWatch = showWatch;
    }
    serialization(stream) {
        this.text = stream.utf16String(this.text);
        this.showWatch = stream.boolean(this.showWatch);
    }
    static is(msg) {
        return msg.type === SetInfoGestureTextMessage.typestatic;
    }
}
MessageFactory.register(SetInfoGestureTextMessage);

/**
 * Temporarily disables gizmo interaction for peer-controlled scenarios.
 */
class DisablePeerGizmoMessage extends BusMessage {
    static typestatic = "DisablePeerGizmoMessage";
    static subjectstatic = "general";
    /** Identifies the model whose peer gizmo should be disabled. */
    modelUid = 0;
    get byteLength() {
        return UID_SAFE_BYTES_PER_ELEMENT;
    }
    /**
     * @param modelUid Identifies the model whose peer-controlled gizmo should be disabled.
     */
    constructor(modelUid) {
        super();
        this.modelUid = modelUid;
    }
    serialization(stream) {
        this.modelUid = stream.uidSafe(this.modelUid);
    }
    static is(msg) {
        return msg.type === DisablePeerGizmoMessage.typestatic;
    }
}
MessageFactory.register(DisablePeerGizmoMessage);
/**
 * Updates the overall gizmo state.
 */
class UpdateGizmoMessage extends BusMessage {
    static typestatic = "UpdateGizmoMessage";
    static subjectstatic = "general";
    /** Identifies the model targeted by the gizmo. */
    targetUid;
    enable;
    /** Places the gizmo in world space. */
    position;
    /** Orients the gizmo in world space. */
    rotation;
    /** Scales the gizmo visuals. */
    scale;
    /** Controls whether scale handles should be available. */
    scalable;
    get byteLength() {
        return (UID_SAFE_BYTES_PER_ELEMENT +
            BOOLEAN_BYTES_PER_ELEMENT +
            Float32Array.BYTES_PER_ELEMENT * 3 +
            Float32Array.BYTES_PER_ELEMENT * 4 +
            Float32Array.BYTES_PER_ELEMENT * 3 +
            BOOLEAN_BYTES_PER_ELEMENT);
    }
    /**
     * @param targetUid Identifies the model targeted by the gizmo.
     * @param enable Enables or hides the gizmo.
     * @param position Places the gizmo in world space.
     * @param rotation Orients the gizmo in world space.
     * @param scale Scales the gizmo visuals.
     * @param scalable Indicates whether scale handles should be available.
     */
    constructor(targetUid, enable = true, position = [0, 0, 0], rotation = [0, 0, 0, 0], scale = [0, 0, 0], scalable = true) {
        super();
        this.targetUid = targetUid;
        this.enable = enable;
        this.position = position;
        this.rotation = rotation;
        this.scale = scale;
        this.scalable = scalable;
    }
    serialization(stream) {
        this.targetUid = stream.uidSafe(this.targetUid);
        this.enable = stream.booleanSafe(this.enable);
        this.position = stream.number3Safe(this.position);
        this.rotation = stream.number4Safe(this.rotation);
        this.scale = stream.number3Safe(this.scale);
        this.scalable = stream.booleanSafe(this.scalable);
    }
    static is(msg) {
        return msg.type === UpdateGizmoMessage.typestatic;
    }
}
MessageFactory.register(UpdateGizmoMessage);
/**
 * Updates the gizmo transform.
 */
class UpdateGizmoTransformMessage extends BusMessage {
    static typestatic = "UpdateGizmoTransformMessage";
    static subjectstatic = "general";
    /** Identifies the model targeted by the gizmo. */
    targetUid;
    /** Places the gizmo in world space. */
    position;
    /** Orients the gizmo in world space. */
    rotation;
    /** Scales the gizmo visuals. */
    scale;
    get byteLength() {
        return UID_SAFE_BYTES_PER_ELEMENT + Float32Array.BYTES_PER_ELEMENT * 3 + Float32Array.BYTES_PER_ELEMENT * 4 + Float32Array.BYTES_PER_ELEMENT * 3;
    }
    /**
     * @param targetUid Identifies the model targeted by the gizmo.
     * @param position Places the gizmo in world space.
     * @param rotation Orients the gizmo in world space.
     * @param scale Scales the gizmo visuals.
     */
    constructor(targetUid, position = [0, 0, 0], rotation = [0, 0, 0, 0], scale = [0, 0, 0]) {
        super();
        this.targetUid = targetUid;
        this.position = position;
        this.rotation = rotation;
        this.scale = scale;
    }
    serialization(stream) {
        this.targetUid = stream.uidSafe(this.targetUid);
        this.position = stream.number3Safe(this.position);
        this.rotation = stream.number4Safe(this.rotation);
        this.scale = stream.number3Safe(this.scale);
    }
    static is(msg) {
        return msg.type === UpdateGizmoTransformMessage.typestatic;
    }
}
MessageFactory.register(UpdateGizmoTransformMessage);
/**
 * Updates the current gizmo mode.
 */
class UpdateGizmoModeMessage extends BusMessage {
    static typestatic = "UpdateGizmoModeMessage";
    static subjectstatic = "general";
    /** Identifies the model targeted by the gizmo. */
    targetUid;
    /** Selects the active transform mode. */
    transformMode;
    /** Chooses whether the gizmo operates in local space. */
    isLocal;
    get byteLength() {
        return UID_SAFE_BYTES_PER_ELEMENT + UID_SAFE_BYTES_PER_ELEMENT + BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param targetUid Identifies the model targeted by the gizmo.
     * @param transformMode Selects the active transform mode.
     * @param isLocal Chooses whether the gizmo should operate in local space.
     */
    constructor(targetUid, transformMode = -1, isLocal = undefined) {
        super();
        this.targetUid = targetUid;
        this.transformMode = transformMode;
        this.isLocal = isLocal;
    }
    serialization(stream) {
        this.targetUid = stream.uidSafe(this.targetUid);
        this.transformMode = stream.uidSafe(this.transformMode);
        this.isLocal = stream.booleanSafe(this.isLocal);
    }
    static is(msg) {
        return msg.type === UpdateGizmoModeMessage.typestatic;
    }
}
MessageFactory.register(UpdateGizmoModeMessage);

/**
 * Requests execution of one or more interaction actions.
 */
class PerformActionsMessage extends BusMessage {
    actions;
    targetUid;
    targetPosition;
    controllerUid;
    static typestatic = "PerformActionsMessage";
    static subjectstatic = "interaction";
    /**
     * Base serialized size before the action payload array is appended.
     */
    static BYTE_LENGTH = Uint8Array.BYTES_PER_ELEMENT * 2 + // hasTargetUid hascontrollerUid
        UID_BYTES_PER_ELEMENT * 2 + // targetUid controllerUid
        Float32Array.BYTES_PER_ELEMENT * 3; // targetPosition
    get byteLength() {
        return PerformActionsMessage.BYTE_LENGTH + calcArrayByteLength(this.actions);
    }
    /**
     * @param actions Interaction actions that should be executed.
     * @param targetUid Optional target model uid for the action execution.
     * @param targetPosition Optional world-space target position.
     * @param controllerUid Optional controller that triggered the action.
     */
    constructor(
    /**
     * Interaction actions that should be executed.
     */
    actions = [], 
    /**
     * Optional target model uid for the action execution.
     */
    targetUid, 
    /**
     * Optional world-space target position.
     */
    targetPosition, 
    /**
     * Optional controller that triggered the action.
     */
    controllerUid) {
        super();
        this.actions = actions;
        this.targetUid = targetUid;
        this.targetPosition = targetPosition;
        this.controllerUid = controllerUid;
    }
    serialization(stream) {
        this.actions = stream.array(this.actions);
        if (stream.boolean(this.targetUid !== undefined)) {
            this.targetUid = stream.uid(this.targetUid ?? 0);
        }
        this.targetPosition = stream.number3Safe(this.targetPosition);
        if (stream.boolean(this.controllerUid !== undefined)) {
            this.controllerUid = stream.uid(this.controllerUid ?? 0);
        }
    }
    static is(msg) {
        return msg.type === PerformActionsMessage.typestatic;
    }
}
MessageFactory.register(PerformActionsMessage);
/**
 * Enables or disables interaction facets at runtime.
 */
class InteractionFacetActivationMessage extends BusMessage {
    targetUid;
    enabled;
    static typestatic = "InteractionFacetActivationMessage";
    static subjectstatic = "interaction";
    /**
     * Base serialized size of the target uid and enable flag.
     */
    static BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // targetUid
        BOOLEAN_BYTES_PER_ELEMENT; // enabled
    get byteLength() {
        return PerformActionsMessage.BYTE_LENGTH;
    }
    /**
     * @param targetUid Model whose interaction facet should change.
     * @param enabled Whether the interaction facet should be enabled.
     */
    constructor(
    /**
     * Model whose interaction facet should change.
     */
    targetUid, 
    /**
     * Whether the interaction facet should be enabled.
     */
    enabled = true) {
        super();
        this.targetUid = targetUid;
        this.enabled = enabled;
    }
    serialization(stream) {
        this.targetUid = stream.uid(this.targetUid);
        this.enabled = stream.boolean(this.enabled);
    }
    static is(msg) {
        return msg.type === InteractionFacetActivationMessage.typestatic;
    }
}
MessageFactory.register(InteractionFacetActivationMessage);
/**
 * Requests execution of an interaction action on the server.
 */
class PerformServerActionMessage extends BusMessage {
    action;
    targetUid;
    targetPosition;
    static typestatic = "PerformServerActionMessage";
    static subjectstatic = "server";
    /**
     * Base serialized size before the action payload object is appended.
     */
    static BYTE_LENGTH = UID_SAFE_BYTES_PER_ELEMENT + // hasTargetUid
        Float32Array.BYTES_PER_ELEMENT * 3; // targetPosition
    get byteLength() {
        return PerformActionsMessage.BYTE_LENGTH + calcObjectByteLength(this.action);
    }
    /**
     * @param action Interaction action that should be executed on the server.
     * @param targetUid Optional target model uid.
     * @param targetPosition Optional world-space target position.
     */
    constructor(
    /**
     * Interaction action that should be executed on the server.
     */
    action = { action: "" }, 
    /**
     * Optional target model uid.
     */
    targetUid, 
    /**
     * Optional world-space target position.
     */
    targetPosition) {
        super();
        this.action = action;
        this.targetUid = targetUid;
        this.targetPosition = targetPosition;
    }
    serialization(stream) {
        this.action = stream.object(this.action);
        this.targetUid = stream.uidSafe(this.targetUid);
        this.targetPosition = stream.number3Safe(this.targetPosition);
    }
    static is(msg) {
        return msg.type === PerformServerActionMessage.typestatic;
    }
}
MessageFactory.register(PerformServerActionMessage);

/**
 * Initializes layout creation for a room.
 */
class InitLayoutMessage extends BusMessage {
    uid;
    messages;
    static typestatic = "InitLayoutMessage";
    static subjectstatic = "layout";
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + calcBusMessagesByteLength(this.messages);
    }
    /**
     * @param uid Identifies the room whose layout should be initialized.
     * @param messages Carries the setup messages that should run before layout creation starts.
     */
    constructor(
    /**
     * Identifies the room whose layout should be initialized.
     */
    uid, 
    /**
     * Carries the setup messages that should run before layout creation starts.
     */
    messages) {
        super();
        this.uid = uid;
        this.messages = messages;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.messages = stream.busMessages(this.messages);
    }
    static is(msg) {
        return msg.type == InitLayoutMessage.typestatic;
    }
}
MessageFactory.register(InitLayoutMessage);
/**
 * Message to tell layouter to produce contents (may be additional content for the given model).
 * The layouterIdentifier (from HasDynamicChildren) gets appended to the message type, so you can only subscribe
 * to your requests, using "layouters/CreateLayoutMessage<layouterIdentifier>" as pattern
 */
class CreateLayoutMessage extends BusMessage {
    uid;
    static typestatic = "CreateLayoutMessage";
    static subjectstatic = "layout";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // uid
    get byteLength() {
        return CreateLayoutMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Identifies the room or model context for which content should be created.
     */
    constructor(
    /**
     * Identifies the room or model context for which content should be created.
     */
    uid = 0) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === CreateLayoutMessage.typestatic;
    }
}
MessageFactory.register(CreateLayoutMessage);
/**
 * Announces that layout creation started.
 */
class LayoutCreationStartedMessage extends BusMessage {
    uid;
    clientId;
    static typestatic = "LayoutCreationStartedMessage";
    static subjectstatic = "layout";
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + calcStringByteLength(this.clientId);
    }
    /**
     * @param uid the uid of the room for which the layout is genarated
     * @param clientId the id of the client that generates the layout for the room or undefined if the layoute is generatred on the server
     */
    constructor(
    /**
     * Identifies the room whose layout generation started.
     */
    uid, 
    /**
     * Client generating the layout, or `undefined` when generation runs on the server.
     */
    clientId) {
        super();
        this.uid = uid;
        this.clientId = clientId;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.clientId = stream.utf16StringSafe(this.clientId);
    }
    static is(msg) {
        return msg.type === LayoutCreationStartedMessage.typestatic;
    }
}
MessageFactory.register(LayoutCreationStartedMessage);
// This message should be sent by the layouter once it has finished creating a layout
/**
 * Announces that layout creation finished successfully.
 */
class LayoutCreationFinishedMessage extends BusMessage {
    layouterIdentifier;
    uid;
    isFirstCreation;
    static typestatic = "LayoutCreationFinishedMessage";
    static subjectstatic = "layout";
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // layouterIdentifier
            calcStringByteLength(this.layouterIdentifier) +
            BOOLEAN_BYTES_PER_ELEMENT); // isFirstCreation
    }
    /**
     * @param layouterIdentifier Names the layouter instance that finished.
     * @param uid Identifies the room whose layout generation finished.
     * @param isFirstCreation Indicates whether this was the initial layout build for the room.
     */
    constructor(
    /**
     * Names the layouter instance that finished.
     */
    layouterIdentifier = "", 
    /**
     * Identifies the room whose layout generation finished.
     */
    uid = 0, 
    /**
     * Indicates whether this was the initial layout build for the room.
     */
    isFirstCreation = false) {
        super();
        this.layouterIdentifier = layouterIdentifier;
        this.uid = uid;
        this.isFirstCreation = isFirstCreation;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.layouterIdentifier = stream.utf16String(this.layouterIdentifier);
        this.isFirstCreation = stream.boolean(this.isFirstCreation);
    }
    static is(msg) {
        return msg.type === LayoutCreationFinishedMessage.typestatic;
    }
}
MessageFactory.register(LayoutCreationFinishedMessage);
/**
 * Announces that layout creation failed.
 */
class LayoutCreationFailedMessage extends BusMessage {
    uid;
    error;
    static typestatic = "LayoutCreationFailedMessage";
    static subjectstatic = "layout";
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + calcStringByteLength(this.error);
    }
    /**
     * @param uid Identifies the room whose layout generation failed.
     * @param error Describes the failure so the caller can surface or log it.
     */
    constructor(
    /**
     * Identifies the room whose layout generation failed.
     */
    uid = 0, 
    /**
     * Describes the failure so the caller can surface or log it.
     */
    error = "") {
        super();
        this.uid = uid;
        this.error = error;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.error = stream.utf16String(this.error);
    }
    static is(msg) {
        return msg.type === LayoutCreationFailedMessage.typestatic;
    }
}
MessageFactory.register(LayoutCreationFailedMessage);
// Message to tell layouter to remove contents
/**
 * Requests destruction of a room layout.
 */
class DestroyLayoutMessage extends BusMessage {
    uid;
    static typestatic = "DestroyLayoutMessage";
    static subjectstatic = "layout";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // uid
    get byteLength() {
        return DestroyLayoutMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Identifies the room whose layout should be removed.
     */
    constructor(
    /**
     * Identifies the room whose layout should be removed.
     */
    uid = 0) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === DestroyLayoutMessage.typestatic;
    }
}
MessageFactory.register(DestroyLayoutMessage);
/**
 * Announces that a layout was updated.
 */
class LayoutUpdatedMessage extends BusMessage {
    uid;
    static typestatic = "LayoutUpdatedMessage";
    static subjectstatic = "layout";
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    /**
     * @param uid Identifies the room whose layout content changed.
     */
    constructor(
    /**
     * Identifies the room whose layout content changed.
     */
    uid = 0) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === LayoutUpdatedMessage.typestatic;
    }
}
MessageFactory.register(LayoutUpdatedMessage);
/**
 * This message is sent when the model pool finished processing all model creation messages
 * that were sent since layout creation started
 */
class LayoutCompleteMessage extends BusMessage {
    layouterIdentifier;
    uid;
    isFirstCreation;
    static typestatic = "LayoutCompleteMessage";
    static subjectstatic = "layout";
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // layouterIdentifier
            calcStringByteLength(this.layouterIdentifier) + // uid
            BOOLEAN_BYTES_PER_ELEMENT // isFirstCreation
        );
    }
    /**
     * @param layouterIdentifier Names the layouter instance that completed processing.
     * @param uid Identifies the room whose queued layout work is fully applied.
     * @param isFirstCreation Indicates whether this completion belongs to the initial room creation.
     */
    constructor(
    /**
     * Names the layouter instance that completed processing.
     */
    layouterIdentifier = "", 
    /**
     * Identifies the room whose queued layout work is fully applied.
     */
    uid = 0, 
    /**
     * Indicates whether this completion belongs to the initial room creation.
     */
    isFirstCreation = false) {
        super();
        this.layouterIdentifier = layouterIdentifier;
        this.uid = uid;
        this.isFirstCreation = isFirstCreation;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.layouterIdentifier = stream.utf16String(this.layouterIdentifier);
        this.isFirstCreation = stream.boolean(this.isFirstCreation);
    }
    static is(msg) {
        return msg.type === LayoutCompleteMessage.typestatic;
    }
}
MessageFactory.register(LayoutCompleteMessage);
/**
 * Remove a previously created model. Any children get recursively removed too
 * @param modelId the model to remove
 * @param childrenOnly don't remove the model itself, but only its children
 */
class RemoveModelMessage extends BusMessage {
    uid;
    childrenOnly;
    static typestatic = "RemoveModelMessage";
    static subjectstatic = "layout";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        BOOLEAN_BYTES_PER_ELEMENT; // childrenOnly
    get byteLength() {
        return RemoveModelMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Identifies the root model that should be removed.
     * @param childrenOnly Removes only the descendants when set to `true`.
     */
    constructor(
    /**
     * Identifies the root model that should be removed.
     */
    uid = 0, 
    /**
     * Removes only the descendants when set to `true`.
     */
    childrenOnly = false) {
        super();
        this.uid = uid;
        this.childrenOnly = childrenOnly;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.childrenOnly = stream.boolean(this.childrenOnly);
    }
    static is(msg) {
        return msg.type === RemoveModelMessage.typestatic;
    }
}
MessageFactory.register(RemoveModelMessage);
/**
 * Requests the layouters that are currently available.
 */
class RequestAvailableLayoutersMessage extends BusMessage {
    static typestatic = "RequestAvailableLayoutersMessage";
    static subjectstatic = "server";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === RequestAvailableLayoutersMessage.typestatic;
    }
}
MessageFactory.register(RequestAvailableLayoutersMessage);
/**
 * Returns the layouters that are currently available.
 */
class AvailableLayoutersMessage extends BusMessage {
    layouters;
    static typestatic = "AvailableLayoutersMessage";
    static subjectstatic = "layout";
    get byteLength() {
        return calcObjectByteLength(this.layouters);
    }
    /**
     * @param layouters Maps layouter identifiers to their descriptive metadata.
     */
    constructor(
    /**
     * Maps layouter identifiers to their descriptive metadata.
     */
    layouters = {}) {
        super();
        this.layouters = layouters;
    }
    serialization(stream) {
        this.layouters = stream.object(this.layouters);
    }
    static is(msg) {
        return msg.type === AvailableLayoutersMessage.typestatic;
    }
}
MessageFactory.register(AvailableLayoutersMessage);
/**
 * Requests changing the active world definition.
 */
class ChangeWorldMessage extends BusMessage {
    layouterId;
    static typestatic = "ChangeWorldMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcStringByteLength(this.layouterId);
    }
    /**
     * @param layouterId Identifies the world definition that should become active.
     */
    constructor(
    /**
     * Identifies the world definition that should become active.
     */
    layouterId) {
        super();
        this.layouterId = layouterId;
    }
    serialization(stream) {
        this.layouterId = stream.utf16String(this.layouterId);
    }
    static is(msg) {
        return msg.type === ChangeWorldMessage.typestatic;
    }
}
MessageFactory.register(ChangeWorldMessage);

/**
 * Most ModelMessage will be forwarded to all peers.
 * See PeerModelMessageHandler to learn which ones.
 */
class ModelMessage extends BusMessage {
    uid;
    static subjectstatic = "model";
    /**
     * @param uid Uid of the model this message targets.
     */
    constructor(uid) {
        super();
        this.uid = uid;
    }
    static isSubject(msg) {
        return msg.subject === ModelMessage.subjectstatic;
    }
}
/**
 * Creates a new submodel entry and announces its path.
 */
class CreateSubModelMessage extends ModelMessage {
    uid;
    static typestatic = "CreateSubModelMessage";
    /**
     * Absolute path of the created submodel.
     */
    path;
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + calcStringByteLength(this.path);
    }
    /**
     * @param uid Uid of the submodel to create.
     */
    constructor(
    /**
     * Uid of the submodel to create.
     */
    uid) {
        super(uid);
        this.uid = uid;
        this.path = ModelId.getAbsolutePath(uid);
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.path = stream.utf16String(this.path);
    }
    static is(msg) {
        return msg.type === CreateSubModelMessage.typestatic;
    }
}
MessageFactory.register(CreateSubModelMessage);
/**
 * Remove a given model. You should not normally create this event, but RemoveModelMessage instead. The WorldTransformerModule
 * then creates these events recursively for all the children
 */
class RemoveSubModelMessage extends ModelMessage {
    uid;
    static typestatic = "RemoveSubModelMessage";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // uid
    get byteLength() {
        return RemoveSubModelMessage.MAX_BYTE_LENGTH;
    }
    constructor(
    /**
     * Uid of the submodel to remove.
     */
    uid) {
        super(uid);
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === RemoveSubModelMessage.typestatic;
    }
}
MessageFactory.register(RemoveSubModelMessage);
/**
 * Message that handles deleting a model permanently by its id
 */
class DeleteModelMessage extends ModelMessage {
    uid;
    static typestatic = "DeleteModelMessage";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // uid
    get byteLength() {
        return DeleteModelMessage.MAX_BYTE_LENGTH;
    }
    constructor(
    /**
     * Uid of the model to delete permanently.
     */
    uid) {
        super(uid);
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === DeleteModelMessage.typestatic;
    }
}
MessageFactory.register(DeleteModelMessage);
/**
 * Message that informs that a model changed its room
 */
class ModelRoomChangedMessage extends ModelMessage {
    uid;
    roomUID;
    static typestatic = "ModelRoomChangedMessage";
    static MAX_BYTE_LENGTH = (UID_BYTES_PER_ELEMENT + // uid
        UID_BYTES_PER_ELEMENT // roomUID
    );
    get byteLength() {
        return ModelRoomChangedMessage.MAX_BYTE_LENGTH;
    }
    constructor(
    /**
     * Uid of the model that changed rooms.
     */
    uid, 
    /**
     * Uid of the room the model now belongs to.
     */
    roomUID) {
        super(uid);
        this.uid = uid;
        this.roomUID = roomUID;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.roomUID = stream.uid(this.roomUID);
    }
    static is(msg) {
        return msg.type === ModelRoomChangedMessage.typestatic;
    }
}
MessageFactory.register(ModelRoomChangedMessage);
/**
 * Message that informs that a model changed its parent
 */
class ModelParentChangedMessage extends ModelMessage {
    uid;
    parentUID;
    static typestatic = "ModelParentChangedMessage";
    static MAX_BYTE_LENGTH = (UID_BYTES_PER_ELEMENT + // uid
        UID_BYTES_PER_ELEMENT // parentUID
    );
    get byteLength() {
        return ModelParentChangedMessage.MAX_BYTE_LENGTH;
    }
    constructor(
    /**
     * Uid of the model that changed parent.
     */
    uid, 
    /**
     * Uid of the model's new parent.
     */
    parentUID) {
        super(uid);
        this.uid = uid;
        this.parentUID = parentUID;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.parentUID = stream.uid(this.parentUID);
    }
    static is(msg) {
        return msg.type === ModelParentChangedMessage.typestatic;
    }
}
MessageFactory.register(ModelParentChangedMessage);
/**
 * Describes whether a child was added or removed from a model.
 */
var ModelChildChangedType;
(function (ModelChildChangedType) {
    /** A child model was added. */
    ModelChildChangedType[ModelChildChangedType["ADDED"] = 0] = "ADDED";
    /** A child model was removed. */
    ModelChildChangedType[ModelChildChangedType["REMOVED"] = 1] = "REMOVED";
})(ModelChildChangedType || (ModelChildChangedType = {}));
/**
 * Message that informs that a model either added or removed a child model
 */
class ModelChildChangedMessage extends ModelMessage {
    uid;
    childUID;
    changedType;
    static typestatic = "ModelChildChangedMessage";
    static MAX_BYTE_LENGTH = (UID_BYTES_PER_ELEMENT + // uid
        UID_BYTES_PER_ELEMENT + // childUID
        Uint8Array.BYTES_PER_ELEMENT // changedType
    );
    get byteLength() {
        return ModelChildChangedMessage.MAX_BYTE_LENGTH;
    }
    constructor(
    /**
     * Uid of the parent model whose child list changed.
     */
    uid, 
    /**
     * Uid of the child model that was added or removed.
     */
    childUID, 
    /**
     * Whether the child was added or removed.
     */
    changedType) {
        super(uid);
        this.uid = uid;
        this.childUID = childUID;
        this.changedType = changedType;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.childUID = stream.uid(this.childUID);
        this.changedType = stream.uint8(this.changedType);
    }
    static is(msg) {
        return msg.type === ModelChildChangedMessage.typestatic;
    }
}
MessageFactory.register(ModelChildChangedMessage);
/**
 * Detach a model and attach it to the root
 */
class DetachModelMessage extends ModelMessage {
    static typestatic = "DetachModelMessage";
    static MAX_BYTE_LENGTH = (UID_BYTES_PER_ELEMENT // uid
    );
    /**
     * Target model uid.
     */
    uid;
    /**
     * @param uid Uid of the model that should be detached from its current parent.
     */
    constructor(uid) {
        super(uid);
        this.uid = uid;
    }
    get byteLength() {
        return DetachModelMessage.MAX_BYTE_LENGTH;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === DetachModelMessage.typestatic;
    }
}
MessageFactory.register(DetachModelMessage);
/**
 * Set a certain facet of a model, or update it
 * The type of the facet gets appended to the message type, so you can only subscribe to specific facets, using
 * "model/UpdateFacetOnModelMessage<FacetType>" as pattern
 */
class UpdateFacetOnModelMessage extends ModelMessage {
    uid;
    facet;
    static typestatic = "UpdateFacetOnModelMessage";
    /**
     * Base serialized size before the facet payload is appended.
     */
    static BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        UID_BYTES_PER_ELEMENT; // facetTypeHash
    get byteLength() {
        return UpdateFacetOnModelMessage.BYTE_LENGTH + this.facet.byteLength;
    }
    /**
     * @param uid Target model uid.
     * @param facet Facet instance that should be added or updated.
     */
    constructor(
    /**
     * Target model uid.
     */
    uid, 
    /**
     * Facet instance that should be added or updated.
     */
    facet) {
        super(uid);
        this.uid = uid;
        this.facet = facet;
        this.type += facet.pattern;
    }
    /**
     * Rehydrates the typed facet instance after plain-object deserialization.
     */
    fromObject(obj) {
        super.fromObject(obj);
        const facet = FacetFactory.createFromObjectByTypeString(this.facet, this.facet.type);
        if (facet === undefined) {
            console.error(`[model] Facet with type ${this.facet.type} not registered or faulty`, obj);
            return;
        }
        this.facet = facet;
    }
    /**
     * On write, writes the facet's type hash so the reader knows which facet class to instantiate; on
     * read, uses that hash to create the concrete facet via `FacetFactory.create` before delegating to
     * `facet.serialization`, and rebuilds `this.type` with the facet's pattern suffix, mirroring the
     * constructor.
     */
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        if (stream.isReading) {
            const facetTypeHash = stream.uid(0);
            this.facet = FacetFactory.create(facetTypeHash);
        }
        else {
            stream.typeHash(this.facet.typeHash);
        }
        this.facet.serialization(stream);
        if (stream.isReading) {
            this.type = `${UpdateFacetOnModelMessage.typestatic}${this.facet.pattern}`;
        }
    }
    static is(msg) {
        return msg.type.startsWith(UpdateFacetOnModelMessage.typestatic);
    }
}
MessageFactory.register(UpdateFacetOnModelMessage);
/**
 * Remove a facet by FacetType.
 * FacetType gets appended to message type, like in UpdateFacetOnModelMessage
 */
class RemoveFacetFromModelMessage extends ModelMessage {
    uid;
    subtype;
    static typestatic = "RemoveFacetFromModelMessage";
    /**
     * Maximum serialized size before the optional subtype suffix is added.
     */
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        UID_BYTES_PER_ELEMENT; // facetTypeHash
    get byteLength() {
        return RemoveFacetFromModelMessage.MAX_BYTE_LENGTH + calcStringByteLength(this.subtype);
    }
    /**
     * Static facet type name used to rebuild the type-specific message suffix.
     */
    facetType = "";
    /**
     * @param uid Target model uid.
     * @param type Facet type that should be removed.
     * @param subtype Optional facet subtype qualifier.
     */
    constructor(
    /**
     * Target model uid.
     */
    uid, type, 
    /**
     * Optional facet subtype qualifier.
     */
    subtype) {
        super(uid);
        this.uid = uid;
        this.subtype = subtype;
        this.facetType = type.typestatic;
        this.type += this.facetType;
    }
    /**
     * On write, writes the hash of `facetType`; on read, resolves that hash back to the type name via
     * `FacetTypeTable.toString` and rebuilds `this.type` with it, mirroring the constructor.
     */
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        if (stream.isReading) {
            this.facetType = FacetTypeTable.toString(stream.uid(0));
            this.type = `${RemoveFacetFromModelMessage.typestatic}${this.facetType}`;
        }
        else {
            stream.uid(FacetTypeTable.hashCode(this.facetType));
        }
        this.subtype = stream.utf16StringSafe(this.subtype);
    }
    static is(msg) {
        return msg.type.startsWith(RemoveFacetFromModelMessage.typestatic);
    }
}
MessageFactory.register(RemoveFacetFromModelMessage);
/**
 * The children of this model are not shared over the network
 */
class NetworkLocalOnly extends ModelMessage {
    uid;
    static typestatic = "NetworkLocalOnly";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // uid
    get byteLength() {
        return NetworkLocalOnly.MAX_BYTE_LENGTH;
    }
    constructor(
    /**
     * Uid of the model whose children should stay local-only.
     */
    uid) {
        super(uid);
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === NetworkLocalOnly.typestatic;
    }
}
MessageFactory.register(NetworkLocalOnly);
class SelectEventMessage extends ModelMessage {
    controllerUid;
    uid;
    isSelected;
    color;
    static typestatic = "SelectEventMessage";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // controllerUid
        UID_BYTES_PER_ELEMENT + // uid
        BOOLEAN_BYTES_PER_ELEMENT + // isSelected
        Uint32Array.BYTES_PER_ELEMENT; // color
    get byteLength() {
        return SelectEventMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param controllerUid Controller that triggered the selection event.
     * @param uid Selected model uid.
     * @param isSelected Whether the model became selected or deselected.
     * @param color Optional highlight color.
     */
    constructor(
    /**
     * Controller that triggered the selection event.
     */
    controllerUid, 
    /**
     * Selected model uid.
     */
    uid, 
    /**
     * Whether the model became selected or deselected.
     */
    isSelected = true, 
    /**
     * Optional highlight color.
     */
    color) {
        super(uid);
        this.controllerUid = controllerUid;
        this.uid = uid;
        this.isSelected = isSelected;
        this.color = color;
    }
    serialization(stream) {
        this.controllerUid = stream.uid(this.controllerUid);
        this.uid = stream.uid(this.uid);
        this.isSelected = stream.boolean(this.isSelected);
        this.color = stream.uint32Safe(this.color);
    }
    static is(msg) {
        return msg.type === SelectEventMessage.typestatic;
    }
}
MessageFactory.register(SelectEventMessage);
/**
 * Updates the rendered visibility state of a model.
 */
class RenderVisibilityMessage extends ModelMessage {
    uid;
    visible;
    static typestatic = "RenderVisibilityMessage";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        BOOLEAN_BYTES_PER_ELEMENT; // visible
    get byteLength() {
        return RenderVisibilityMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Target model uid.
     * @param visible Whether the model should render visibly.
     */
    constructor(
    /**
     * Target model uid.
     */
    uid, 
    /**
     * Whether the model should render visibly.
     */
    visible = true) {
        super(uid);
        this.uid = uid;
        this.visible = visible;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.visible = stream.boolean(this.visible);
    }
    static is(msg) {
        return msg.type === RenderVisibilityMessage.typestatic;
    }
}
MessageFactory.register(RenderVisibilityMessage);
/**
 * Requests the current rendered visibility state of a model.
 */
class RequestVisibilityMessage extends ModelMessage {
    uid;
    static typestatic = "RequestVisibilityMessage";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // uid
    get byteLength() {
        return RequestVisibilityMessage.MAX_BYTE_LENGTH;
    }
    constructor(
    /**
     * Target model uid.
     */
    uid) {
        super(uid);
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === RequestVisibilityMessage.typestatic;
    }
}
MessageFactory.register(RequestVisibilityMessage);
/**
 * Returns the current rendered visibility state of a model.
 */
class RequestVisibilityAnswerMessage extends ModelMessage {
    uid;
    visible;
    static typestatic = "RequestVisibilityAnswerMessage";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        BOOLEAN_BYTES_PER_ELEMENT; // visible
    get byteLength() {
        return RequestVisibilityAnswerMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Target model uid.
     * @param visible Current visibility state.
     */
    constructor(
    /**
     * Target model uid.
     */
    uid, 
    /**
     * Current visibility state.
     */
    visible) {
        super(uid);
        this.uid = uid;
        this.visible = visible;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.visible = stream.boolean(this.visible);
    }
    static is(msg) {
        return msg.type === RequestVisibilityAnswerMessage.typestatic;
    }
}
MessageFactory.register(RequestVisibilityAnswerMessage);
/**
 * Updates the rendered transparency value of a model.
 */
class RenderTransparencyMessage extends ModelMessage {
    uid;
    alpha;
    static typestatic = "RenderTransparencyMessage";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        Float32Array.BYTES_PER_ELEMENT; // alpha
    get byteLength() {
        return RenderTransparencyMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Target model uid.
     * @param alpha Desired transparency value.
     */
    constructor(
    /**
     * Target model uid.
     */
    uid, 
    /**
     * Desired transparency value.
     */
    alpha) {
        super(uid);
        this.uid = uid;
        this.alpha = alpha;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.alpha = stream.float32(this.alpha);
    }
    static is(msg) {
        return msg.type === RenderTransparencyMessage.typestatic;
    }
}
MessageFactory.register(RenderTransparencyMessage);
/**
 * Updates the rendered tint color of a model.
 */
class RenderColorMessage extends ModelMessage {
    uid;
    color;
    static typestatic = "RenderColorMessage";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        Int32Array.BYTES_PER_ELEMENT; // color
    get byteLength() {
        return RenderColorMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Target model uid.
     * @param color Packed color value to apply.
     */
    constructor(
    /**
     * Target model uid.
     */
    uid, 
    /**
     * Packed color value to apply.
     */
    color) {
        super(uid);
        this.uid = uid;
        this.color = color;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.color = stream.int32(this.color);
    }
    static is(msg) {
        return msg.type === RenderColorMessage.typestatic;
    }
}
MessageFactory.register(RenderColorMessage);
/**
 * Requests the current rendered transparency value of a model.
 */
class RequestTransparencyMessage extends ModelMessage {
    uid;
    static typestatic = "RequestTransparencyMessage";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // uid
    get byteLength() {
        return RequestTransparencyMessage.MAX_BYTE_LENGTH;
    }
    constructor(
    /**
     * Target model uid.
     */
    uid) {
        super(uid);
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === RequestTransparencyMessage.typestatic;
    }
}
MessageFactory.register(RequestTransparencyMessage);
/**
 * Returns the current rendered transparency value of a model.
 */
class RequestTransparencyAnswerMessage extends ModelMessage {
    uid;
    alpha;
    static typestatic = "RequestTransparencyAnswerMessage";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        Float32Array.BYTES_PER_ELEMENT; // alpha
    get byteLength() {
        return RequestTransparencyAnswerMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Target model uid.
     * @param alpha Current transparency value.
     */
    constructor(
    /**
     * Target model uid.
     */
    uid, 
    /**
     * Current transparency value.
     */
    alpha) {
        super(uid);
        this.uid = uid;
        this.alpha = alpha;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.alpha = stream.float32(this.alpha);
    }
    static is(msg) {
        return msg.type === RequestTransparencyAnswerMessage.typestatic;
    }
}
MessageFactory.register(RequestTransparencyAnswerMessage);

/**
 * Starts moving a data selection.
 */
class StartMoveDataSelectionMessage extends BusMessage {
    segments;
    static typestatic = "StartMoveDataSelectionMessage";
    static subjectstatic = "dataselection";
    get byteLength() {
        return calcArrayByteLength(this.segments);
    }
    /**
     * @param segments Selection segments that should be moved.
     */
    constructor(
    /**
     * Selection segments that should be moved.
     */
    segments) {
        super();
        this.segments = segments;
    }
    serialization(stream) {
        this.segments = stream.array(this.segments);
    }
    static is(msg) {
        return msg.type === StartMoveDataSelectionMessage.typestatic;
    }
}
MessageFactory.register(StartMoveDataSelectionMessage);
/**
 * Ends moving a data selection.
 */
class EndMoveDataSelectionMessage extends BusMessage {
    static typestatic = "EndMoveDataSelectionMessage";
    static subjectstatic = "dataselection";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === EndMoveDataSelectionMessage.typestatic;
    }
}
MessageFactory.register(EndMoveDataSelectionMessage);

/**
 * Broadcasts wrapped messages to peers.
 */
class BroadcastToPeerMessage extends BusMessage {
    msgs;
    static typestatic = "BroadcastToPeerMessage";
    static subjectstatic = "peer";
    get byteLength() {
        return calcBusMessagesByteLength(this.msgs);
    }
    /**
     * @param msgs Wraps the messages that should be broadcast to connected peers.
     */
    constructor(
    /**
     * Wraps the messages that should be broadcast to connected peers.
     */
    msgs) {
        super();
        this.msgs = msgs;
    }
    serialization(stream) {
        this.msgs = stream.busMessages(this.msgs);
    }
    static is(msg) {
        return msg.type === BroadcastToPeerMessage.typestatic;
    }
}
MessageFactory.register(BroadcastToPeerMessage);
/**
 * Wraps messages that should be forwarded explicitly to the server.
 */
class SendToServerMessage extends BusMessage {
    messages;
    static typestatic = "SendToServerMessage";
    static subjectstatic = "peer";
    get byteLength() {
        return calcBusMessagesByteLength(this.messages);
    }
    /**
     * @param messages Wraps the messages that should be forwarded to the server connection.
     */
    constructor(
    /**
     * Wraps the messages that should be forwarded to the server connection.
     */
    messages) {
        super();
        this.messages = messages;
    }
    serialization(stream) {
        this.messages = stream.busMessages(this.messages);
    }
    static is(msg) {
        return msg.type === SendToServerMessage.typestatic;
    }
}
MessageFactory.register(SendToServerMessage);
/**
 * Sends all wrapped messages only within the same worker
 */
class WorkerLocalMessage extends BusMessage {
    messages;
    static typestatic = "WorkerLocalMessage";
    static subjectstatic = "local";
    get byteLength() {
        return calcBusMessagesByteLength(this.messages);
    }
    /**
     * @param messages Wraps the messages that should stay within the current worker.
     */
    constructor(
    /**
     * Wraps the messages that should stay within the current worker.
     */
    messages) {
        super();
        this.messages = messages;
    }
    serialization(stream) {
        this.messages = stream.busMessages(this.messages);
    }
    static is(msg) {
        return msg.type === WorkerLocalMessage.typestatic;
    }
}
MessageFactory.register(WorkerLocalMessage);
/**
 * Requests the avatar model UID for a peer.
 */
class RequestAvatarModelUIDMessage extends BusMessage {
    static typestatic = "RequestAvatarModelUIDMessage";
    static subjectstatic = "peer";
    serialization() { }
    static is(msg) {
        return msg.type === RequestAvatarModelUIDMessage.typestatic;
    }
}
MessageFactory.register(RequestAvatarModelUIDMessage);
/**
 * Returns the avatar model UID for a peer.
 */
class ResponseAvatarModelUIDMessage extends BusMessage {
    uid;
    static typestatic = "ResponseAvatarModelUIDMessage";
    static subjectstatic = "peer";
    static MAX_BYTE_LENGTH = UID_SAFE_BYTES_PER_ELEMENT;
    get byteLength() {
        return ResponseAvatarModelUIDMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Avatar model uid for the requested peer, when available.
     */
    constructor(
    /**
     * Avatar model uid for the requested peer, when available.
     */
    uid) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uidSafe(this.uid);
    }
    static is(msg) {
        return msg.type === ResponseAvatarModelUIDMessage.typestatic;
    }
}
MessageFactory.register(ResponseAvatarModelUIDMessage);
/**
 * Changes whether peer selection visuals should be visible.
 */
class PeersSelectionVisibilityMessage extends BusMessage {
    visible;
    controllerUid;
    static typestatic = "PeersSelectionVisibilityMessage";
    static subjectstatic = "peer";
    static MAX_BYTE_LENGTH = Uint8Array.BYTES_PER_ELEMENT + // visible
        UID_SAFE_BYTES_PER_ELEMENT; // uid
    get byteLength() {
        return PeersSelectionVisibilityMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param visible Controls whether peer selection visuals should be shown.
     * @param controllerUid Optionally limits the change to a specific controller selection source.
     */
    constructor(
    /**
     * Controls whether peer selection visuals should be shown.
     */
    visible = true, 
    /**
     * Optionally limits the change to a specific controller selection source.
     */
    controllerUid) {
        super();
        this.visible = visible;
        this.controllerUid = controllerUid;
    }
    serialization(stream) {
        this.visible = stream.boolean(this.visible);
        this.controllerUid = stream.uidSafe(this.controllerUid);
    }
    static is(msg) {
        return msg.type === PeersSelectionVisibilityMessage.typestatic;
    }
}
MessageFactory.register(PeersSelectionVisibilityMessage);

/**
 * Starts performance logging.
 */
class StartPerformanceLoggingMessage extends BusMessage {
    static typestatic = "StartPerformanceLoggingMessage";
    static subjectstatic = "performance";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === StartPerformanceLoggingMessage.typestatic;
    }
}
MessageFactory.register(StartPerformanceLoggingMessage);
/**
 * Stops performance logging.
 */
class StopPerformanceLoggingMessage extends BusMessage {
    static typestatic = "StopPerformanceLoggingMessage";
    static subjectstatic = "performance";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === StopPerformanceLoggingMessage.typestatic;
    }
}
MessageFactory.register(StopPerformanceLoggingMessage);

/**
 * Enables scene rendering again after a room or loading transition blocked it.
 */
class EnableRenderingMessage extends BusMessage {
    static typestatic = "EnableRenderingMessage";
    static subjectstatic = "renderer";
    get byteLength() {
        return 0;
    }
    constructor() {
        super();
    }
    serialization() { }
    static is(msg) {
        return msg.type === EnableRenderingMessage.typestatic;
    }
}
MessageFactory.register(EnableRenderingMessage);
/**
 * Disables scene rendering during operations that should temporarily hide updates.
 */
class DisableRenderingMessage extends BusMessage {
    static typestatic = "DisableRenderingMessage";
    static subjectstatic = "renderer";
    serialization() { }
    static is(msg) {
        return msg.type === DisableRenderingMessage.typestatic;
    }
}
MessageFactory.register(DisableRenderingMessage);
/**
 * Announces that a transition into the given room has started.
 */
class StartRoomTransitionMessage extends BusMessage {
    uid;
    static typestatic = "StartRoomTransitionMessage";
    static subjectstatic = "room";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // uid
    get byteLength() {
        return StartRoomTransitionMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Target room uid whose transition started.
     */
    constructor(
    /**
     * Target room uid whose transition started.
     */
    uid = 0) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === StartRoomTransitionMessage.typestatic;
    }
}
MessageFactory.register(StartRoomTransitionMessage);
/**
 * Announces that the active room changed and optionally includes the previous room.
 */
class RoomChangedMessage extends BusMessage {
    uid;
    prevUid;
    ignoreRespawn;
    static typestatic = "RoomChangedMessage";
    static subjectstatic = "room";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        BOOLEAN_BYTES_PER_ELEMENT + // hasPrevUid
        UID_BYTES_PER_ELEMENT + // prevUid
        BOOLEAN_BYTES_PER_ELEMENT; // ignoreRespawn
    get byteLength() {
        return RoomChangedMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Newly active room uid.
     * @param prevUid Previously active room uid, if any.
     * @param ignoreRespawn Whether room entry should skip respawn behavior.
     */
    constructor(
    /**
     * Newly active room uid.
     */
    uid = 0, 
    /**
     * Previously active room uid, if any.
     */
    prevUid, 
    /**
     * Whether room entry should skip respawn behavior.
     */
    ignoreRespawn) {
        super();
        this.uid = uid;
        this.prevUid = prevUid;
        this.ignoreRespawn = ignoreRespawn;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        const hasPrevUid = stream.boolean(this.prevUid !== undefined);
        if (hasPrevUid) {
            this.prevUid = stream.uid(this.prevUid ?? 0);
        }
        this.ignoreRespawn = stream.booleanSafe(this.ignoreRespawn);
    }
    static is(msg) {
        return msg.type === RoomChangedMessage.typestatic;
    }
}
MessageFactory.register(RoomChangedMessage);
/**
 * Requests that room supervision should be changed through the server.
 */
class ClientChangeRoomSupervisionMessage extends BusMessage {
    static typestatic = "ClientChangeRoomSupervisionMessage";
    static subjectstatic = "server";
    serialization() { }
    static is(msg) {
        return msg.type === ClientChangeRoomSupervisionMessage.typestatic;
    }
}
MessageFactory.register(ClientChangeRoomSupervisionMessage);
/**
 * Enables or disables room supervision for the specified host client.
 */
class ChangeRoomSupervisionMessage extends BusMessage {
    hostClientId;
    enable;
    static typestatic = "ChangeRoomSupervisionMessage";
    static subjectstatic = "room";
    get byteLength() {
        return calcStringByteLength(this.hostClientId) + BOOLEAN_BYTES_PER_ELEMENT; // enable
    }
    /**
     * @param hostClientId Host client whose supervision state should change.
     * @param enable Whether room supervision should be enabled.
     */
    constructor(
    /**
     * Host client whose supervision state should change.
     */
    hostClientId, 
    /**
     * Whether room supervision should be enabled.
     */
    enable = true) {
        super();
        this.hostClientId = hostClientId;
        this.enable = enable;
    }
    serialization(stream) {
        this.hostClientId = stream.utf16String(this.hostClientId);
        this.enable = stream.boolean(this.enable);
    }
    static is(msg) {
        return msg.type === ChangeRoomSupervisionMessage.typestatic;
    }
}
MessageFactory.register(ChangeRoomSupervisionMessage);
/**
 * Announces that a new client became the host for room supervision.
 */
class NewRoomHostMessage extends BusMessage {
    hostClientId;
    static typestatic = "NewRoomHostMessage";
    static subjectstatic = "room";
    get byteLength() {
        return calcStringByteLength(this.hostClientId);
    }
    /**
     * @param hostClientId Client id that became the new room host.
     */
    constructor(
    /**
     * Client id that became the new room host.
     */
    hostClientId) {
        super();
        this.hostClientId = hostClientId;
    }
    serialization(stream) {
        this.hostClientId = stream.utf16String(this.hostClientId);
    }
    static is(msg) {
        return msg.type === NewRoomHostMessage.typestatic;
    }
}
MessageFactory.register(NewRoomHostMessage);
/**
 * Requests loading of the specified room.
 */
class LoadRoomMessage extends BusMessage {
    uid;
    controllerUid;
    immediately;
    static typestatic = "LoadRoomMessage";
    static subjectstatic = "room";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        UID_SAFE_BYTES_PER_ELEMENT + // controllerUid
        BOOLEAN_BYTES_PER_ELEMENT; // immediately
    get byteLength() {
        return LoadRoomMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Room uid to load.
     * @param controllerUid Optional controller that initiated the load.
     * @param immediately Whether loading should bypass delay or staging behavior.
     */
    constructor(
    /**
     * Room uid to load.
     */
    uid, 
    /**
     * Optional controller that initiated the load.
     */
    controllerUid, 
    /**
     * Whether loading should bypass delay or staging behavior.
     */
    immediately = false) {
        super();
        this.uid = uid;
        this.controllerUid = controllerUid;
        this.immediately = immediately;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.controllerUid = stream.uidSafe(this.controllerUid);
        this.immediately = stream.boolean(this.immediately);
    }
    static is(msg) {
        return msg.type === LoadRoomMessage.typestatic;
    }
}
MessageFactory.register(LoadRoomMessage);
/**
 * Requests unloading of the specified room.
 */
class UnloadRoomMessage extends BusMessage {
    uid;
    controllerUid;
    immediately;
    static typestatic = "UnloadRoomMessage";
    static subjectstatic = "room";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        UID_SAFE_BYTES_PER_ELEMENT + // controllerUid
        BOOLEAN_BYTES_PER_ELEMENT; // immediately
    get byteLength() {
        return UnloadRoomMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Room uid to unload.
     * @param controllerUid Optional controller that initiated the unload.
     * @param immediately Whether unloading should happen immediately.
     */
    constructor(
    /**
     * Room uid to unload.
     */
    uid, 
    /**
     * Optional controller that initiated the unload.
     */
    controllerUid, 
    /**
     * Whether unloading should happen immediately.
     */
    immediately = false) {
        super();
        this.uid = uid;
        this.controllerUid = controllerUid;
        this.immediately = immediately;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.controllerUid = stream.uidSafe(this.controllerUid);
        this.immediately = stream.boolean(this.immediately);
    }
    static is(msg) {
        return msg.type === UnloadRoomMessage.typestatic;
    }
}
MessageFactory.register(UnloadRoomMessage);
/**
 * Requests entering the specified room with optional spawn and return-point parameters.
 */
class EnterRoomMessage extends BusMessage {
    uid;
    params;
    static typestatic = "EnterRoomMessage";
    static subjectstatic = "room";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        Float32Array.BYTES_PER_ELEMENT * 3 + // position
        BOOLEAN_BYTES_PER_ELEMENT + // positionIsInRoomCoordinates
        BOOLEAN_BYTES_PER_ELEMENT; // flying
    /** Adds the variable-length size of the optional `params.returnPoint` to the fixed base size. */
    get byteLength() {
        return EnterRoomMessage.MAX_BYTE_LENGTH + calcTypeByteLength(this.params.returnPoint);
    }
    /**
     * @param uid Room uid to enter.
     * @param params Optional spawn and return-point parameters.
     */
    constructor(
    /**
     * Room uid to enter.
     */
    uid = 0, 
    /**
     * Optional spawn and return-point parameters.
     */
    params = {}) {
        super();
        this.uid = uid;
        this.params = params;
    }
    /**
     * On read, `params` is reset to `{}` before its fields are populated, since a deserialized instance
     * skips the constructor and would otherwise still have `params` undefined.
     */
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        if (stream.isReading) {
            this.params = {};
        }
        this.params.position = stream.number3Safe(this.params.position);
        this.params.positionIsInRoomCoordinates = stream.booleanSafe(this.params.positionIsInRoomCoordinates);
        this.params.flying = stream.booleanSafe(this.params.flying);
        this.params.returnPoint = stream.type(this.params.returnPoint);
    }
    static is(msg) {
        return msg.type === EnterRoomMessage.typestatic;
    }
}
MessageFactory.register(EnterRoomMessage);
/**
 * Requests leaving the currently active room.
 */
class ExitCurrentRoomMessage extends BusMessage {
    flying;
    static typestatic = "ExitCurrentRoomMessage";
    static subjectstatic = "room";
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param flying Whether the caller should keep a flying locomotion state after exiting.
     */
    constructor(
    /**
     * Whether the caller should keep a flying locomotion state after exiting.
     */
    flying = false) {
        super();
        this.flying = flying;
    }
    serialization(stream) {
        this.flying = stream.boolean(this.flying);
    }
    static is(msg) {
        return msg.type === ExitCurrentRoomMessage.typestatic;
    }
}
MessageFactory.register(ExitCurrentRoomMessage);
/**
 * Requests the UID of the currently active room.
 */
class RequestCurrentRoomMessage extends BusMessage {
    static typestatic = "RequestCurrentRoomMessage";
    static subjectstatic = "room";
    serialization() { }
    static is(msg) {
        return msg.type === RequestCurrentRoomMessage.typestatic;
    }
}
MessageFactory.register(RequestCurrentRoomMessage);
/**
 * Reports the UID of the currently active room.
 */
class CurrentRoomMessage extends BusMessage {
    roomUid;
    static typestatic = "CurrentRoomMessage";
    static subjectstatic = "room";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // roomUid
    get byteLength() {
        return CurrentRoomMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param roomUid Currently active room uid.
     */
    constructor(
    /**
     * Currently active room uid.
     */
    roomUid = 0) {
        super();
        this.roomUid = roomUid;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
    }
    static is(msg) {
        return msg.type === CurrentRoomMessage.typestatic;
    }
}
MessageFactory.register(CurrentRoomMessage);
/**
 * Requests the currently available subrooms for the active room context.
 */
class CurrentSubRoomsRequestMessage extends BusMessage {
    static typestatic = "CurrentSubRoomsRequestMessage";
    static subjectstatic = "room";
    serialization() { }
    static is(msg) {
        return msg.type === CurrentSubRoomsRequestMessage.typestatic;
    }
}
MessageFactory.register(CurrentSubRoomsRequestMessage);
/**
 * Returns the currently available subrooms for the active room context.
 */
class CurrentSubRoomsResponseMessage extends BusMessage {
    availableRooms;
    static typestatic = "CurrentSubRoomsResponseMessage";
    static subjectstatic = "room";
    get byteLength() {
        return calcArrayByteLength(this.availableRooms);
    }
    /**
     * @param availableRooms Available subroom uids for the current room context.
     */
    constructor(
    /**
     * Available subroom uids for the current room context.
     */
    availableRooms = []) {
        super();
        this.availableRooms = availableRooms;
    }
    serialization(stream) {
        this.availableRooms = stream.array(this.availableRooms);
    }
    static is(msg) {
        return msg.type === CurrentSubRoomsResponseMessage.typestatic;
    }
}
MessageFactory.register(CurrentSubRoomsResponseMessage);
/**
 * Cancels loading of the specified room.
 */
class CancelLoadRoomMessage extends BusMessage {
    roomUid;
    static typestatic = "CancelLoadRoomMessage";
    static subjectstatic = "room";
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    /**
     * @param roomUid Room uid whose loading should be canceled.
     */
    constructor(
    /**
     * Room uid whose loading should be canceled.
     */
    roomUid = 0) {
        super();
        this.roomUid = roomUid;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
    }
    static is(msg) {
        return msg.type === CancelLoadRoomMessage.typestatic;
    }
}
MessageFactory.register(CancelLoadRoomMessage);
/**
 * Requests the current loaded-state information for a room.
 */
class RequestRoomLoadedStateMessage extends BusMessage {
    roomUid;
    static typestatic = "RequestRoomLoadedStateMessage";
    static subjectstatic = "room";
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    /**
     * @param roomUid Room uid whose load state is requested.
     */
    constructor(
    /**
     * Room uid whose load state is requested.
     */
    roomUid) {
        super();
        this.roomUid = roomUid;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
    }
    static is(msg) {
        return msg.type === RequestRoomLoadedStateMessage.typestatic;
    }
}
MessageFactory.register(RequestRoomLoadedStateMessage);
/**
 * Coarse lifecycle state of a room load request.
 */
var RoomLoadedState;
(function (RoomLoadedState) {
    /** Room is not loaded. */
    RoomLoadedState[RoomLoadedState["NotLoaded"] = 0] = "NotLoaded";
    /** Room loading is planned but not started yet. */
    RoomLoadedState[RoomLoadedState["Planned"] = 1] = "Planned";
    /** Room is currently loading. */
    RoomLoadedState[RoomLoadedState["Loading"] = 2] = "Loading";
    /** Room is fully loaded. */
    RoomLoadedState[RoomLoadedState["Loaded"] = 3] = "Loaded";
})(RoomLoadedState || (RoomLoadedState = {}));
/**
 * Reports the current loaded-state of a room.
 */
class RoomLoadedStateMessage extends BusMessage {
    roomUid;
    state;
    static typestatic = "RoomLoadedStateMessage";
    static subjectstatic = "room";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // roomUid
        Int8Array.BYTES_PER_ELEMENT; // state
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param roomUid Room uid being reported.
     * @param state Current load state of that room.
     */
    constructor(
    /**
     * Room uid being reported.
     */
    roomUid, 
    /**
     * Current load state of that room.
     */
    state) {
        super();
        this.roomUid = roomUid;
        this.state = state;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
        this.state = stream.uint8(this.state);
    }
    static is(msg) {
        return msg.type === RoomLoadedStateMessage.typestatic;
    }
}
MessageFactory.register(RoomLoadedStateMessage);
/**
 * Requests clearing all dynamic content from the specified room.
 */
class ClearRoomMessage extends BusMessage {
    roomUid;
    static typestatic = "ClearRoomMessage";
    static subjectstatic = "room";
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    /**
     * @param roomUid Room uid whose dynamic content should be cleared.
     */
    constructor(
    /**
     * Room uid whose dynamic content should be cleared.
     */
    roomUid = 0) {
        super();
        this.roomUid = roomUid;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
    }
    static is(msg) {
        return msg.type === ClearRoomMessage.typestatic;
    }
}
MessageFactory.register(ClearRoomMessage);

/**
 * Requests subscribing to a room stream.
 */
class SubscribeRoomMessage extends BusMessage {
    roomUid;
    subscribe;
    static typestatic = "SubscribeRoomMessage";
    static subjectstatic = "server";
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param roomUid Room uid whose stream should be subscribed to or unsubscribed from.
     * @param subscribe Whether the room stream should be subscribed to.
     */
    constructor(
    /**
     * Room uid whose stream should be subscribed to or unsubscribed from.
     */
    roomUid, 
    /**
     * Whether the room stream should be subscribed to.
     */
    subscribe = true) {
        super();
        this.roomUid = roomUid;
        this.subscribe = subscribe;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
        this.subscribe = stream.boolean(this.subscribe);
    }
    static is(msg) {
        return msg.type === SubscribeRoomMessage.typestatic;
    }
}
MessageFactory.register(SubscribeRoomMessage);
/**
 * Returns the result of subscribing to a room stream.
 */
class SubscribeRoomResponseMessage extends BusMessage {
    accepted;
    static typestatic = "SubscribeRoomResponseMessage";
    static subjectstatic = "server";
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param accepted Whether the requested subscription change was accepted.
     */
    constructor(
    /**
     * Whether the requested subscription change was accepted.
     */
    accepted = false) {
        super();
        this.accepted = accepted;
    }
    serialization(stream) {
        this.accepted = stream.boolean(this.accepted);
    }
    static is(msg) {
        return msg.type === SubscribeRoomResponseMessage.typestatic;
    }
}
MessageFactory.register(SubscribeRoomResponseMessage);

/**
 * Resource-level permissions that may be granted within a session context.
 */
var ResourcePermission;
(function (ResourcePermission) {
    /** Permission to view the resource. */
    ResourcePermission["View"] = "view";
    /** Permission to modify the resource. */
    ResourcePermission["Edit"] = "edit";
    /** Permission to remove the resource. */
    ResourcePermission["Delete"] = "delete";
    /** Permission to share the resource with others. */
    ResourcePermission["Share"] = "share";
    /** Permission to execute the resource or associated action. */
    ResourcePermission["Exec"] = "exec";
})(ResourcePermission || (ResourcePermission = {}));
/**
 * Requests the list of available sessions.
 */
class RequestSessionListMessage extends BusMessage {
    static typestatic = "RequestSessionListMessage";
    static subjectstatic = "server";
    serialization() { }
    static is(msg) {
        return msg.type === RequestSessionListMessage.typestatic;
    }
}
MessageFactory.register(RequestSessionListMessage);
/**
 * Returns the list of available sessions.
 */
class SessionListMessage extends BusMessage {
    sessions;
    static typestatic = "SessionListMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcObjectByteLength(this.sessions);
    }
    /**
     * @param sessions Session info keyed by session name.
     */
    constructor(
    /**
     * Session info keyed by session name.
     */
    sessions = {}) {
        super();
        this.sessions = sessions;
    }
    serialization(stream) {
        this.sessions = stream.object(this.sessions);
    }
    static is(msg) {
        return msg.type === SessionListMessage.typestatic;
    }
}
MessageFactory.register(SessionListMessage);
/**
 * Requests the member list of a session.
 */
class RequestSessionMemberListMessage extends BusMessage {
    sessionName;
    static typestatic = "RequestSessionMemberListMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcStringByteLength(this.sessionName);
    }
    /**
     * @param sessionName Session whose member list is requested.
     */
    constructor(
    /**
     * Session whose member list is requested.
     */
    sessionName = "") {
        super();
        this.sessionName = sessionName;
    }
    serialization(stream) {
        this.sessionName = stream.utf16String(this.sessionName);
    }
    static is(msg) {
        return msg.type === RequestSessionMemberListMessage.typestatic;
    }
}
MessageFactory.register(RequestSessionMemberListMessage);
/**
 * Returns the member list of a session.
 */
class SessionMemberListMessage extends BusMessage {
    sessionName;
    members;
    static typestatic = "SessionMemberListMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcStringByteLength(this.sessionName) + calcArrayByteLength(this.members);
    }
    /**
     * @param sessionName Session whose members are being reported.
     * @param members Current members of that session.
     */
    constructor(
    /**
     * Session whose members are being reported.
     */
    sessionName = "", 
    /**
     * Current members of that session.
     */
    members = []) {
        super();
        this.sessionName = sessionName;
        this.members = members;
        this.type = SessionMemberListMessage.typestatic + sessionName;
    }
    /**
     * On read, also rebuilds `this.type` with the `sessionName` suffix, mirroring the constructor, so
     * `msg.type.startsWith(...)` subscriptions still match a deserialized instance.
     */
    serialization(stream) {
        this.sessionName = stream.utf16String(this.sessionName);
        this.members = stream.array(this.members);
        if (stream.isReading) {
            this.type = SessionMemberListMessage.typestatic + this.sessionName;
        }
    }
    static is(msg) {
        return msg.type.startsWith(SessionMemberListMessage.typestatic);
    }
}
MessageFactory.register(SessionMemberListMessage);
/**
 * Requests the room list of a session.
 */
class RequestSessionRoomListMessage extends BusMessage {
    sessionName;
    roomUid;
    static typestatic = "RequestSessionRoomListMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcStringByteLength(this.sessionName) + UID_SAFE_BYTES_PER_ELEMENT;
    }
    /**
     * @param sessionName Session whose room list is requested.
     * @param roomUid Optional room subtree root for the query.
     */
    constructor(
    /**
     * Session whose room list is requested.
     */
    sessionName = "", 
    /**
     * Optional room subtree root for the query.
     */
    roomUid) {
        super();
        this.sessionName = sessionName;
        this.roomUid = roomUid;
    }
    serialization(stream) {
        this.sessionName = stream.utf16String(this.sessionName);
        this.roomUid = stream.uidSafe(this.roomUid);
    }
    static is(msg) {
        return msg.type === RequestSessionRoomListMessage.typestatic;
    }
}
MessageFactory.register(RequestSessionRoomListMessage);
/**
 * Returns the room list of a session.
 */
class SessionRoomListMessage extends BusMessage {
    sessionName;
    roomUid;
    childRooms;
    static typestatic = "SessionRoomListMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcStringByteLength(this.sessionName) + UID_BYTES_PER_ELEMENT + calcArrayByteLength(this.childRooms);
    }
    /**
     * @param sessionName Session whose room list is being reported.
     * @param roomUid Queried room root.
     * @param childRooms Child rooms returned by the query.
     */
    constructor(
    /**
     * Session whose room list is being reported.
     */
    sessionName = "", 
    /**
     * Queried room root.
     */
    roomUid = 0, 
    /**
     * Child rooms returned by the query.
     */
    childRooms = []) {
        super();
        this.sessionName = sessionName;
        this.roomUid = roomUid;
        this.childRooms = childRooms;
        this.type = SessionRoomListMessage.typestatic + sessionName;
    }
    /**
     * On read, also rebuilds `this.type` with the `sessionName` suffix, mirroring the constructor, so
     * `msg.type.startsWith(...)` subscriptions still match a deserialized instance.
     */
    serialization(stream) {
        this.sessionName = stream.utf16String(this.sessionName);
        this.roomUid = stream.uid(this.roomUid);
        this.childRooms = stream.array(this.childRooms);
        if (stream.isReading) {
            this.type = SessionRoomListMessage.typestatic + this.sessionName;
        }
    }
    static is(msg) {
        return msg.type.startsWith(SessionRoomListMessage.typestatic);
    }
}
MessageFactory.register(SessionRoomListMessage);
/**
 * Subscribes the client to server-side session info updates.
 */
class ServerSubscribeMessage extends BusMessage {
    pattern;
    unsubscribe;
    static typestatic = "ServerSubscribeMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcArrayByteLength(this.pattern) + BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param pattern Subscription patterns to add or remove.
     * @param unsubscribe Whether the patterns should be removed instead of added.
     */
    constructor(
    /**
     * Subscription patterns to add or remove.
     */
    pattern = [], 
    /**
     * Whether the patterns should be removed instead of added.
     */
    unsubscribe = false) {
        super();
        this.pattern = pattern;
        this.unsubscribe = unsubscribe;
    }
    serialization(stream) {
        this.pattern = stream.array(this.pattern);
        this.unsubscribe = stream.boolean(this.unsubscribe);
    }
    static is(msg) {
        return msg.type === ServerSubscribeMessage.typestatic;
    }
}
MessageFactory.register(ServerSubscribeMessage);
/**
 * Requests a token that can be used to authenticate a user.
 */
class RequestUserAuthenticateTokenMessage extends BusMessage {
    static typestatic = "RequestUserAuthenticateTokenMessage";
    static subjectstatic = "server";
    serialization() { }
    static is(msg) {
        return msg.type === RequestUserAuthenticateTokenMessage.typestatic;
    }
}
MessageFactory.register(RequestUserAuthenticateTokenMessage);
/**
 * Carries a token that can be used to authenticate a user.
 */
class UserAuthenticateTokenMessage extends BusMessage {
    token;
    static typestatic = "UserAuthenticateTokenMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcStringByteLength(this.token);
    }
    /**
     * @param token Authentication token issued by the server.
     */
    constructor(
    /**
     * Authentication token issued by the server.
     */
    token) {
        super();
        this.token = token;
    }
    serialization(stream) {
        this.token = stream.utf16String(this.token);
    }
    static is(msg) {
        return msg.type === UserAuthenticateTokenMessage.typestatic;
    }
}
MessageFactory.register(UserAuthenticateTokenMessage);
/**
 * Requests a share link for a session.
 */
class RequestSessionShareLinkMessage extends BusMessage {
    initialRoom;
    cameraPosition;
    cameraOrientation;
    static typestatic = "RequestSessionShareLinkMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcStringByteLength(this.initialRoom) + (3 + 4) * Float32Array.BYTES_PER_ELEMENT; // initialCameraPosition/Orientation
    }
    /**
     * @param initialRoom Initial room path for the shared link.
     * @param cameraPosition Initial camera position embedded into the link.
     * @param cameraOrientation Initial camera orientation embedded into the link.
     */
    constructor(
    /**
     * Initial room path for the shared link.
     */
    initialRoom, 
    /**
     * Initial camera position embedded into the link.
     */
    cameraPosition, 
    /**
     * Initial camera orientation embedded into the link.
     */
    cameraOrientation) {
        super();
        this.initialRoom = initialRoom;
        this.cameraPosition = cameraPosition;
        this.cameraOrientation = cameraOrientation;
    }
    serialization(stream) {
        this.initialRoom = stream.utf16String(this.initialRoom);
        this.cameraPosition = stream.number3(this.cameraPosition);
        this.cameraOrientation = stream.number4(this.cameraOrientation);
    }
    static is(msg) {
        return msg.type === RequestSessionShareLinkMessage.typestatic;
    }
}
MessageFactory.register(RequestSessionShareLinkMessage);
/**
 * Returns a share link for a session.
 */
class SessionShareLinkMessage extends BusMessage {
    sessionName;
    link;
    code;
    static typestatic = "SessionShareLinkMessage";
    static subjectstatic = "general";
    get byteLength() {
        return calcStringByteLength(this.sessionName) + calcStringByteLength(this.link) + calcStringByteLength(this.code);
    }
    /**
     * @param sessionName Session the share link belongs to.
     * @param link Generated share link url.
     * @param code Optional short code associated with the link.
     */
    constructor(
    /**
     * Session the share link belongs to.
     */
    sessionName, 
    /**
     * Generated share link url.
     */
    link, 
    /**
     * Optional short code associated with the link.
     */
    code) {
        super();
        this.sessionName = sessionName;
        this.link = link;
        this.code = code;
    }
    serialization(stream) {
        this.sessionName = stream.utf16String(this.sessionName);
        this.link = stream.utf16String(this.link);
        this.code = stream.utf16StringSafe(this.code);
    }
    static is(msg) {
        return msg.type === SessionShareLinkMessage.typestatic;
    }
}
MessageFactory.register(SessionShareLinkMessage);
/**
 * Requests whether the current user may share the session.
 */
class RequestHasSessionSharePermissionMessage extends BusMessage {
    static typestatic = "RequestHasSessionSharePermissionMessage";
    static subjectstatic = "server";
    serialization() { }
    static is(msg) {
        return msg.type === RequestHasSessionSharePermissionMessage.typestatic;
    }
}
MessageFactory.register(RequestHasSessionSharePermissionMessage);
/**
 * Returns whether the current user may share the session.
 */
class HasSessionSharePermissionMessage extends BusMessage {
    hasSharePermission;
    static typestatic = "HasSessionSharePermissionMessage";
    static subjectstatic = "general";
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param hasSharePermission Whether the current user may share the session.
     */
    constructor(
    /**
     * Whether the current user may share the session.
     */
    hasSharePermission) {
        super();
        this.hasSharePermission = hasSharePermission;
    }
    serialization(stream) {
        this.hasSharePermission = stream.boolean(this.hasSharePermission);
    }
    static is(msg) {
        return msg.type === HasSessionSharePermissionMessage.typestatic;
    }
}
MessageFactory.register(HasSessionSharePermissionMessage);
/**
 * Reports that the client is currently not inside a session.
 */
class NotInSessionMessage extends BusMessage {
    static typestatic = "NotInSessionMessage";
    static subjectstatic = "general";
    serialization() { }
    static is(msg) {
        return msg.type === NotInSessionMessage.typestatic;
    }
}
MessageFactory.register(NotInSessionMessage);
/**
 * Associates a file ID with a session.
 */
class LinkFileIdToSessionMessage extends BusMessage {
    fileId;
    sessionName;
    static typestatic = "LinkFileIdToSessionMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcStringByteLength(this.fileId) + calcStringByteLength(this.sessionName);
    }
    /**
     * @param fileId File identifier that should be linked.
     * @param sessionName Session that should own the file.
     */
    constructor(
    /**
     * File identifier that should be linked.
     */
    fileId, 
    /**
     * Session that should own the file.
     */
    sessionName) {
        super();
        this.fileId = fileId;
        this.sessionName = sessionName;
    }
    serialization(stream) {
        this.fileId = stream.utf16String(this.fileId);
        this.sessionName = stream.utf16String(this.sessionName);
    }
    static is(msg) {
        return msg.type === LinkFileIdToSessionMessage.typestatic;
    }
}
MessageFactory.register(LinkFileIdToSessionMessage);

/**
 * This message forces the server to recreate a session based on its world. All uploaded models will be removed from
 * the session
 */
class ResetSessionMessage extends BusMessage {
    sessionName;
    static typestatic = "ResetSessionMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcStringByteLength(this.sessionName);
    }
    /**
     * @param sessionName Name of the session that should be recreated from its world.
     */
    constructor(
    /**
     * Name of the session that should be recreated from its world.
     */
    sessionName = "") {
        super();
        this.sessionName = sessionName;
    }
    serialization(stream) {
        this.sessionName = stream.utf16String(this.sessionName);
    }
    static is(msg) {
        return msg.type === ResetSessionMessage.typestatic;
    }
}
MessageFactory.register(ResetSessionMessage);
/**
 * Policy that determines when a session should be reset automatically.
 */
var SessionResetPolicy;
(function (SessionResetPolicy) {
    /** Never reset the session automatically. */
    SessionResetPolicy[SessionResetPolicy["NEVER"] = 0] = "NEVER";
    /** Reset the session once all clients have disconnected. */
    SessionResetPolicy[SessionResetPolicy["ALL_CLIENTS_DISCONNECTED"] = 1] = "ALL_CLIENTS_DISCONNECTED";
})(SessionResetPolicy || (SessionResetPolicy = {}));
/**
 * Updates the reset policy that should be used for the session.
 */
class UpdateSessionResetPolicyMessage extends BusMessage {
    sessionName;
    policy;
    static typestatic = "UpdateSessionResetPolicyMessage";
    static subjectstatic = "server";
    get byteLength() {
        return (calcStringByteLength(this.sessionName) +
            ENUM_BYTES_PER_ELEMENT);
    }
    /**
     * @param sessionName Session whose reset policy should change.
     * @param policy New reset policy.
     */
    constructor(
    /**
     * Session whose reset policy should change.
     */
    sessionName, 
    /**
     * New reset policy.
     */
    policy) {
        super();
        this.sessionName = sessionName;
        this.policy = policy;
    }
    serialization(stream) {
        this.sessionName = stream.utf16String(this.sessionName);
        this.policy = stream.enum(this.policy);
    }
    static is(msg) {
        return msg.type === UpdateSessionResetPolicyMessage.typestatic;
    }
}
MessageFactory.register(UpdateSessionResetPolicyMessage);
/**
 * Requests reloading the current session on the client.
 */
class ClientReloadSessionMessage extends BusMessage {
    clientId;
    sessionName;
    static typestatic = "ClientReloadSessionMessage";
    static subjectstatic = "server";
    get byteLength() {
        return (calcStringByteLength(this.clientId) +
            calcStringByteLength(this.sessionName));
    }
    /**
     * @param clientId Client that should reload the session.
     * @param sessionName Session that should be reloaded.
     */
    constructor(
    /**
     * Client that should reload the session.
     */
    clientId, 
    /**
     * Session that should be reloaded.
     */
    sessionName = "") {
        super();
        this.clientId = clientId;
        this.sessionName = sessionName;
    }
    serialization(stream) {
        this.clientId = stream.utf16String(this.clientId);
        this.sessionName = stream.utf16String(this.sessionName);
    }
    static is(msg) {
        return msg.type === ClientReloadSessionMessage.typestatic;
    }
}
MessageFactory.register(ClientReloadSessionMessage);

/**
 * Scope used when requesting stored model state from the state manager.
 */
var RequestModelStateFlag;
(function (RequestModelStateFlag) {
    /** Requests only the state directly stored on the target model. */
    RequestModelStateFlag[RequestModelStateFlag["ONLY_MODEL"] = 0] = "ONLY_MODEL";
    /** Requests only direct children that are on the same room depth as the target model. */
    RequestModelStateFlag[RequestModelStateFlag["ONLY_CHILDREN_SAME_ROOM_DEPTH"] = 1] = "ONLY_CHILDREN_SAME_ROOM_DEPTH";
    /** Requests the target model and related state on the same room depth. */
    RequestModelStateFlag[RequestModelStateFlag["SAME_ROOM_DEPTH"] = 2] = "SAME_ROOM_DEPTH";
})(RequestModelStateFlag || (RequestModelStateFlag = {}));
// Request the stored model messages from StateManager
/**
 * Requests the stored state of a model.
 */
class RequestModelStateMessage extends BusMessage {
    uid;
    flag;
    static typestatic = "RequestModelStateMessage";
    static subjectstatic = "state";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        Uint8Array.BYTES_PER_ELEMENT; // flag
    get byteLength() {
        return RequestModelStateMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Model uid whose stored state is requested.
     * @param flag Scope of the requested state.
     */
    constructor(
    /**
     * Model uid whose stored state is requested.
     */
    uid = 0, 
    /**
     * Scope of the requested state.
     */
    flag = RequestModelStateFlag.ONLY_MODEL) {
        super();
        this.uid = uid;
        this.flag = flag;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.flag = stream.uint8(this.flag);
    }
    static is(msg) {
        return msg.type === RequestModelStateMessage.typestatic;
    }
}
MessageFactory.register(RequestModelStateMessage);
// Request all model messages stored in the StateManager
/**
 * Requests the stored state of all models.
 */
class RequestAllModelStatesMessage extends BusMessage {
    static typestatic = "RequestAllModelStatesMessage";
    static subjectstatic = "state";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === RequestAllModelStatesMessage.typestatic;
    }
}
MessageFactory.register(RequestAllModelStatesMessage);
// Clear all model states in the StateManager
/**
 * Clears all stored model states.
 */
class ClearModelStatesMessage extends BusMessage {
    static typestatic = "ClearModelStatesMessage";
    static subjectstatic = "state";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === ClearModelStatesMessage.typestatic;
    }
}
MessageFactory.register(ClearModelStatesMessage);
// Request the stored model messages from Server
/**
 * Requests a model state from the server.
 */
class RequestModelStateFromServerMessage extends BusMessage {
    uid;
    flag;
    static typestatic = "RequestModelStateFromServerMessage";
    static subjectstatic = "server";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
        Uint8Array.BYTES_PER_ELEMENT; // flag
    get byteLength() {
        return RequestModelStateFromServerMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param uid Model uid whose stored state is requested from the server.
     * @param flag Scope of the requested state.
     */
    constructor(
    /**
     * Model uid whose stored state is requested from the server.
     */
    uid = 0, 
    /**
     * Scope of the requested state.
     */
    flag = RequestModelStateFlag.ONLY_MODEL) {
        super();
        this.uid = uid;
        this.flag = flag;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.flag = stream.uint8(this.flag);
    }
    static is(msg) {
        return msg.type === RequestModelStateFromServerMessage.typestatic;
    }
}
MessageFactory.register(RequestModelStateFromServerMessage);
/**
 * Error categories returned for server-backed state requests.
 */
var ServerRequestErrorType;
(function (ServerRequestErrorType) {
    /** No stored state data was available for the request. */
    ServerRequestErrorType[ServerRequestErrorType["NO_DATA"] = 0] = "NO_DATA";
    /** The server could not be reached or is offline. */
    ServerRequestErrorType[ServerRequestErrorType["OFFLINE"] = 1] = "OFFLINE";
    /** The request timed out before a response arrived. */
    ServerRequestErrorType[ServerRequestErrorType["TIMEOUT"] = 2] = "TIMEOUT";
})(ServerRequestErrorType || (ServerRequestErrorType = {}));
/**
 * Reports an error while processing a server-backed state request.
 */
class ServerRequestErrorMessage extends BusMessage {
    flag;
    static typestatic = "ServerRequestErrorMessage";
    static subjectstatic = "state";
    static MAX_BYTE_LENGTH = Uint8Array.BYTES_PER_ELEMENT; // flag
    get byteLength() {
        return ServerRequestErrorMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param flag Reason the server-backed request failed.
     */
    constructor(
    /**
     * Reason the server-backed request failed.
     */
    flag = ServerRequestErrorType.NO_DATA) {
        super();
        this.flag = flag;
    }
    serialization(stream) {
        this.flag = stream.uint8(this.flag);
    }
    static is(msg) {
        return msg.type === ServerRequestErrorMessage.typestatic;
    }
}
MessageFactory.register(ServerRequestErrorMessage);
/**
 * Requests creating a room on the server.
 */
class RequestCreateRoomFromServerMessage extends BusMessage {
    roomUid;
    static typestatic = "RequestCreateRoomFromServerMessage";
    static subjectstatic = "server";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // roomUid
    get byteLength() {
        return RequestCreateRoomFromServerMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param roomUid Room uid that should be created on the server.
     */
    constructor(
    /**
     * Room uid that should be created on the server.
     */
    roomUid = 0) {
        super();
        this.roomUid = roomUid;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
    }
    static is(msg) {
        return msg.type === RequestCreateRoomFromServerMessage.typestatic;
    }
}
MessageFactory.register(RequestCreateRoomFromServerMessage);
/**
 * Returns the result of creating a room.
 */
class ResponseCreateRoomMessage extends BusMessage {
    roomUid;
    canCreate;
    static typestatic = "ResponseCreateRoomMessage";
    static subjectstatic = "state";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // roomUid
        BOOLEAN_BYTES_PER_ELEMENT; // canCreate
    get byteLength() {
        return ResponseCreateRoomMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param roomUid Room uid the response refers to.
     * @param canCreate Whether creation is permitted or possible.
     */
    constructor(
    /**
     * Room uid the response refers to.
     */
    roomUid = 0, 
    /**
     * Whether creation is permitted or possible.
     */
    canCreate = false) {
        super();
        this.roomUid = roomUid;
        this.canCreate = canCreate;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
        this.canCreate = stream.boolean(this.canCreate);
    }
    static is(msg) {
        return msg.type === ResponseCreateRoomMessage.typestatic;
    }
}
MessageFactory.register(ResponseCreateRoomMessage);
/**
 * Requests the current layout creation state.
 */
class RequestLayoutCreationStateMessage extends BusMessage {
    roomUid;
    static typestatic = "RequestLayoutCreationStateMessage";
    static subjectstatic = "server";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // roomUid
    get byteLength() {
        return RequestLayoutCreationStateMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param roomUid Room uid whose layout creation state is requested.
     */
    constructor(
    /**
     * Room uid whose layout creation state is requested.
     */
    roomUid = 0) {
        super();
        this.roomUid = roomUid;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
    }
    static is(msg) {
        return msg.type === RequestLayoutCreationStateMessage.typestatic;
    }
}
MessageFactory.register(RequestLayoutCreationStateMessage);
/**
 * Lifecycle states used while creating a layout.
 */
var LayoutCreationState;
(function (LayoutCreationState) {
    /** Layout creation has not started yet. */
    LayoutCreationState[LayoutCreationState["NotStarted"] = 0] = "NotStarted";
    /** Layout creation is currently running. */
    LayoutCreationState[LayoutCreationState["InProgress"] = 1] = "InProgress";
    /** Layout creation finished successfully. */
    LayoutCreationState[LayoutCreationState["Finished"] = 2] = "Finished";
})(LayoutCreationState || (LayoutCreationState = {}));
/**
 * Returns the current layout creation state.
 */
class LayoutCreationStateMessage extends BusMessage {
    roomUid;
    state;
    static typestatic = "LayoutCreationStateMessage";
    static subjectstatic = "state";
    static MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // roomUid
        Uint8Array.BYTES_PER_ELEMENT; // state
    get byteLength() {
        return LayoutCreationStateMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param roomUid Room uid the response refers to.
     * @param state Current layout creation state.
     */
    constructor(
    /**
     * Room uid the response refers to.
     */
    roomUid, 
    /**
     * Current layout creation state.
     */
    state) {
        super();
        this.roomUid = roomUid;
        this.state = state;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
        this.state = stream.uint8(this.state);
    }
    static is(msg) {
        return msg.type === LayoutCreationStateMessage.typestatic;
    }
}
MessageFactory.register(LayoutCreationStateMessage);

/**
 * Requests the current frame statistics.
 */
class RequestFrameStatsMessage extends BusMessage {
    id;
    static typestatic = "RequestFrameStatsMessage";
    static subjectstatic = "system";
    get byteLength() {
        return calcStringByteLength(this.id);
    }
    /**
     * @param id Optional stats channel or source id.
     */
    constructor(
    /**
     * Optional stats channel or source id.
     */
    id = "") {
        super();
        this.id = id;
        this.type += id;
    }
    /**
     * On read, also rebuilds `this.type` with the `id` suffix, mirroring the constructor, so
     * `msg.type.startsWith(...)` subscriptions still match a deserialized instance.
     */
    serialization(stream) {
        this.id = stream.utf16String(this.id);
        if (stream.isReading) {
            this.type = `${RequestFrameStatsMessage.typestatic}${this.id}`;
        }
    }
    static is(msg) {
        return msg.type.startsWith(RequestFrameStatsMessage.typestatic);
    }
}
MessageFactory.register(RequestFrameStatsMessage);
/**
 * Returns the current frame statistics.
 */
class FrameStatsMessage extends BusMessage {
    id;
    fps;
    frameTime;
    avgTime;
    worstTime;
    bestTime;
    memory;
    static typestatic = "FrameStatsMessage";
    static subjectstatic = "system";
    static MAX_BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT * 6;
    get byteLength() {
        return FrameStatsMessage.MAX_BYTE_LENGTH + calcStringByteLength(this.id);
    }
    /**
     * @param id Stats source id.
     * @param fps Frames per second.
     * @param frameTime Latest frame duration.
     * @param avgTime Average frame duration.
     * @param worstTime Worst observed frame duration.
     * @param bestTime Best observed frame duration.
     * @param memory Memory usage figure associated with this source.
     */
    constructor(
    /**
     * Stats source id.
     */
    id = "", 
    /**
     * Frames per second.
     */
    fps = 0, 
    /**
     * Latest frame duration.
     */
    frameTime = 0, 
    /**
     * Average frame duration.
     */
    avgTime = 0, 
    /**
     * Worst observed frame duration.
     */
    worstTime = 0, 
    /**
     * Best observed frame duration.
     */
    bestTime = 0, 
    /**
     * Memory usage figure associated with this source.
     */
    memory = 0) {
        super();
        this.id = id;
        this.fps = fps;
        this.frameTime = frameTime;
        this.avgTime = avgTime;
        this.worstTime = worstTime;
        this.bestTime = bestTime;
        this.memory = memory;
    }
    serialization(stream) {
        this.id = stream.utf16String(this.id);
        this.fps = stream.float32(this.fps);
        this.frameTime = stream.float32(this.frameTime);
        this.avgTime = stream.float32(this.avgTime);
        this.worstTime = stream.float32(this.worstTime);
        this.bestTime = stream.float32(this.bestTime);
        this.memory = stream.float32(this.memory);
    }
    static is(msg) {
        return msg.type === FrameStatsMessage.typestatic;
    }
}
MessageFactory.register(FrameStatsMessage);

/**
 * Queues a toast notification.
 */
class ToastMessage extends BusMessage {
    message;
    severity;
    static typestatic = "ToastMessage";
    static subjectstatic = "general";
    get byteLength() {
        return calcLocalizationStringByteLength(this.message) + calcStringByteLength(this.severity);
    }
    /**
     * @param message Localized toast text to display.
     * @param severity Visual severity styling for the toast.
     */
    constructor(
    /**
     * Localized toast text shown to the user.
     */
    message, 
    /**
     * Visual severity preset used by the toast UI.
     */
    severity = "info") {
        super();
        this.message = message;
        this.severity = severity;
    }
    /**
     * Falls back to `severity: "error"` if decoding the severity string throws.
     */
    serialization(stream) {
        this.message = stream.localizationString(this.message);
        try {
            this.severity = stream.utf16String(this.severity);
        }
        catch (e) {
            this.severity = "error";
        }
    }
    static is(msg) {
        return msg.type === ToastMessage.typestatic;
    }
}
MessageFactory.register(ToastMessage);

/**
 * Requests the state of a toggle button.
 */
class RequestToggleButtonStateMessage extends BusMessage {
    requestedAttribute;
    uid;
    static typestatic = "RequestToggleButtonStateMessage";
    static subjectstatic = "userinterface";
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + calcStringByteLength(this.requestedAttribute);
    }
    /**
     * @param requestedAttribute Names the toggle attribute whose state should be queried.
     * @param uid Optionally scopes the query to a specific model.
     */
    constructor(
    /**
     * Names the toggle attribute whose state should be queried.
     */
    requestedAttribute = "", 
    /**
     * Optionally scopes the query to a specific model.
     */
    uid = 0) {
        super();
        this.requestedAttribute = requestedAttribute;
        this.uid = uid;
        this.type = RequestToggleButtonStateMessage.typestatic + requestedAttribute;
    }
    /**
     * On read, also rebuilds `this.type` with the `requestedAttribute` suffix, mirroring the
     * constructor, so `msg.type.startsWith(...)` subscriptions still match a deserialized instance.
     */
    serialization(stream) {
        this.requestedAttribute = stream.utf16String(this.requestedAttribute);
        this.uid = stream.uid(this.uid);
        if (stream.isReading) {
            this.type = RequestToggleButtonStateMessage.typestatic + this.requestedAttribute;
        }
    }
    static is(msg) {
        return msg.type.startsWith(RequestToggleButtonStateMessage.typestatic);
    }
}
MessageFactory.register(RequestToggleButtonStateMessage);
/**
 * Enumerates the possible UI toggle states returned by the client.
 */
var ToggleButtonStateType;
(function (ToggleButtonStateType) {
    /** Toggle is currently inactive. */
    ToggleButtonStateType[ToggleButtonStateType["Off"] = 0] = "Off";
    /** Toggle is currently active. */
    ToggleButtonStateType[ToggleButtonStateType["On"] = 1] = "On";
    /** Toggle cannot currently be changed. */
    ToggleButtonStateType[ToggleButtonStateType["Disable"] = 2] = "Disable";
})(ToggleButtonStateType || (ToggleButtonStateType = {}));
/**
 * Returns the state of a toggle button.
 */
class RequestToggleButtonStateResponseMessage extends BusMessage {
    state;
    static typestatic = "RequestToggleButtonStateResponseMessage";
    static subjectstatic = "userinterface";
    static MAX_BYTE_LENGTH = Uint8Array.BYTES_PER_ELEMENT; // state
    get byteLength() {
        return RequestToggleButtonStateResponseMessage.MAX_BYTE_LENGTH;
    }
    /**
     * @param state Whether the requested toggle is on, off, or disabled.
     */
    constructor(
    /**
     * Whether the requested toggle is on, off, or disabled.
     */
    state) {
        super();
        this.state = state;
    }
    serialization(stream) {
        this.state = stream.uint8(this.state);
    }
    static is(msg) {
        return msg.type.startsWith(RequestToggleButtonStateResponseMessage.typestatic);
    }
}
MessageFactory.register(RequestToggleButtonStateResponseMessage);
/**
 * Requests a user confirmation dialog.
 */
class RequestUserConfirmationRequestMessage extends BusMessage {
    popupText;
    static typestatic = "RequestUserConfirmationRequestMessage";
    static subjectstatic = "userinterface";
    get byteLength() {
        return calcStringByteLength(this.popupText);
    }
    /**
     * @param popupText Provides the text that should be shown in the confirmation dialog.
     */
    constructor(
    /**
     * Provides the text that should be shown in the confirmation dialog.
     */
    popupText = "Are you sure?") {
        super();
        this.popupText = popupText;
    }
    serialization(stream) {
        this.popupText = stream.utf16String(this.popupText);
    }
    static is(msg) {
        return msg.type.startsWith(RequestUserConfirmationRequestMessage.typestatic);
    }
}
MessageFactory.register(RequestUserConfirmationRequestMessage);

/**
 * Changes the active background configuration.
 */
class ChangeBackgroundMessage extends BusMessage {
    params;
    static typestatic = "ChangeBackgroundMessage";
    static subjectstatic = "renderer";
    get byteLength() {
        return calcObjectByteLength(this.params);
    }
    /**
     * @param params Defines the environment settings that should become active.
     */
    constructor(
    /**
     * Defines the environment settings that should become active.
     */
    params) {
        super();
        this.params = params;
    }
    serialization(stream) {
        this.params = stream.object(this.params);
    }
    static is(msg) {
        return msg.type === ChangeBackgroundMessage.typestatic;
    }
}
MessageFactory.register(ChangeBackgroundMessage);
/**
 * Enables or disables dust particle effects.
 */
class EnableDustParticlesMessage extends BusMessage {
    enable;
    fadeDuration;
    minY;
    static typestatic = "EnableDustParticlesMessage";
    static subjectstatic = "renderer";
    get byteLength() {
        return (BOOLEAN_BYTES_PER_ELEMENT + // enable
            Float32Array.BYTES_PER_ELEMENT + // fadeDuration
            Float32Array.BYTES_PER_ELEMENT); // minY
    }
    /**
     * @param enable Enables or disables the effect.
     * @param fadeDuration Optionally blends the effect in or out over time.
     * @param minY Limits particle spawning below the given height.
     */
    constructor(
    /**
     * Enables or disables the effect.
     */
    enable, 
    /**
     * Optionally blends the effect in or out over time.
     */
    fadeDuration, 
    /**
     * Limits particle spawning below the given height.
     */
    minY = 0) {
        super();
        this.enable = enable;
        this.fadeDuration = fadeDuration;
        this.minY = minY;
    }
    serialization(stream) {
        this.enable = stream.boolean(this.enable);
        this.fadeDuration = stream.float32Safe(this.fadeDuration);
        this.minY = stream.float32(this.minY);
    }
    static is(msg) {
        return msg.type === EnableDustParticlesMessage.typestatic;
    }
}
MessageFactory.register(EnableDustParticlesMessage);
/**
 * Requests an update of reflection probes.
 */
class UpdateReflectionProbesMessage extends BusMessage {
    static typestatic = "UpdateReflectionProbesMessage";
    static subjectstatic = "renderer";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === UpdateReflectionProbesMessage.typestatic;
    }
}
MessageFactory.register(UpdateReflectionProbesMessage);

/**
 * Starts avatar recording.
 */
class StartAvatarRecordingMessage extends BusMessage {
    static typestatic = "StartAvatarRecordingMessage";
    static subjectstatic = "general";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === StartAvatarRecordingMessage.typestatic;
    }
}
MessageFactory.register(StartAvatarRecordingMessage);
/**
 * Stops avatar recording.
 */
class StopAvatarRecordingMessage extends BusMessage {
    static typestatic = "StopAvatarRecordingMessage";
    static subjectstatic = "general";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === StopAvatarRecordingMessage.typestatic;
    }
}
MessageFactory.register(StopAvatarRecordingMessage);

/**
 * Starts recording for a camera.
 */
class StartCameraRecordingMessage extends BusMessage {
    uid;
    static typestatic = "StartCameraRecordingMessage";
    static subjectstatic = "general";
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    /**
     * @param uid Camera model uid whose recording should start.
     */
    constructor(
    /**
     * Camera model uid whose recording should start.
     */
    uid) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === StartCameraRecordingMessage.typestatic;
    }
}
MessageFactory.register(StartCameraRecordingMessage);
/**
 * Stops recording for a camera.
 */
class StopCameraRecordingMessage extends BusMessage {
    uid;
    static typestatic = "StopCameraRecordingMessage";
    static subjectstatic = "general";
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    /**
     * @param uid Camera model uid whose recording should stop.
     */
    constructor(
    /**
     * Camera model uid whose recording should stop.
     */
    uid) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === StopCameraRecordingMessage.typestatic;
    }
}
MessageFactory.register(StopCameraRecordingMessage);
/**
 * Starts recording for all configured cameras.
 */
class StartAllCameraRecordingMessage extends BusMessage {
    static typestatic = "StartAllCameraRecordingMessage";
    static subjectstatic = "general";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === StartAllCameraRecordingMessage.typestatic;
    }
}
MessageFactory.register(StartAllCameraRecordingMessage);
/**
 * Stops recording for all configured cameras.
 */
class StopAllCameraRecordingMessage extends BusMessage {
    static typestatic = "StopAllCameraRecordingMessage";
    static subjectstatic = "general";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === StopAllCameraRecordingMessage.typestatic;
    }
}
MessageFactory.register(StopAllCameraRecordingMessage);

/**
 * Invokes a script RPC function.
 */
class RPCScriptMessage extends BusMessage {
    scriptId;
    generated;
    param;
    static typestatic = "RPCScriptMessage";
    static subjectstatic = "general";
    get byteLength() {
        return (calcStringByteLength(this.scriptId) +
            BOOLEAN_BYTES_PER_ELEMENT + // generated
            calcObjectByteLength(this.param));
    }
    /**
     * @param scriptId Identifies the script function or handler to invoke.
     * @param generated Indicates whether the script identifier was generated dynamically.
     * @param param Carries the optional payload passed into the script call.
     */
    constructor(
    /**
     * Identifies the script function or handler to invoke.
     */
    scriptId, 
    /**
     * Indicates whether the script identifier was generated dynamically.
     */
    generated, 
    /**
     * Carries the optional payload passed into the script call.
     */
    param) {
        super();
        this.scriptId = scriptId;
        this.generated = generated;
        this.param = param;
    }
    serialization(stream) {
        this.scriptId = stream.utf16String(this.scriptId);
        this.generated = stream.boolean(this.generated);
        this.param = stream.objectSafe(this.param);
    }
    static is(msg) {
        return msg.type === RPCScriptMessage.typestatic;
    }
}
MessageFactory.register(RPCScriptMessage);
/**
 * Returns the result of a script RPC function call.
 */
class RPCScriptReturnMessage extends BusMessage {
    scriptId;
    result;
    static typestatic = "RPCScriptReturnMessage";
    static subjectstatic = "general";
    get byteLength() {
        return calcStringByteLength(this.scriptId) + calcTypeByteLength(this.result);
    }
    /**
     * @param scriptId Identifies the script invocation whose result is being returned.
     * @param result Carries the return value produced by the script.
     */
    constructor(
    /**
     * Identifies the script invocation whose result is being returned.
     */
    scriptId, 
    /**
     * Carries the return value produced by the script.
     */
    result) {
        super();
        this.scriptId = scriptId;
        this.result = result;
    }
    serialization(stream) {
        this.scriptId = stream.utf16String(this.scriptId);
        this.result = stream.type(this.result);
    }
    static is(msg) {
        return msg.type === RPCScriptReturnMessage.typestatic;
    }
}
MessageFactory.register(RPCScriptReturnMessage);
/**
 * Requests a script ID that is scoped to a user.
 */
class RequestScriptIdForUserMessage extends BusMessage {
    userId;
    scriptName;
    static typestatic = "RequestScriptIdForUserMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcStringByteLength(this.userId) + calcStringByteLength(this.scriptName);
    }
    /**
     * @param userId Identifies the user namespace the script should belong to.
     * @param scriptName Names the script for which an identifier is requested.
     */
    constructor(
    /**
     * Identifies the user namespace the script should belong to.
     */
    userId, 
    /**
     * Names the script for which an identifier is requested.
     */
    scriptName) {
        super();
        this.userId = userId;
        this.scriptName = scriptName;
    }
    serialization(stream) {
        this.userId = stream.utf16String(this.userId);
        this.scriptName = stream.utf16String(this.scriptName);
    }
    static is(msg) {
        return msg.type === RequestScriptIdForUserMessage.typestatic;
    }
}
MessageFactory.register(RequestScriptIdForUserMessage);
/**
 * Requests a script ID that is scoped to a session.
 */
class RequestScriptIdForSessionMessage extends BusMessage {
    scriptName;
    static typestatic = "RequestScriptIdForSessionMessage";
    static subjectstatic = "server";
    get byteLength() {
        return calcStringByteLength(this.scriptName);
    }
    /**
     * @param scriptName Names the session-scoped script for which an identifier is requested.
     */
    constructor(
    /**
     * Names the session-scoped script for which an identifier is requested.
     */
    scriptName) {
        super();
        this.scriptName = scriptName;
    }
    serialization(stream) {
        this.scriptName = stream.utf16String(this.scriptName);
    }
    static is(msg) {
        return msg.type === RequestScriptIdForSessionMessage.typestatic;
    }
}
MessageFactory.register(RequestScriptIdForSessionMessage);
/**
 * Returns a generated script ID.
 */
class ResponseScriptIdMessage extends BusMessage {
    scriptId;
    generated;
    static typestatic = "ResponseScriptIdMessage";
    static subjectstatic = "general";
    get byteLength() {
        return calcStringByteLength(this.scriptId) + BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param scriptId Resolved or generated script identifier.
     * @param generated Indicates whether the identifier was created on demand.
     */
    constructor(
    /**
     * Resolved or generated script identifier.
     */
    scriptId, 
    /**
     * Indicates whether the identifier was created on demand.
     */
    generated) {
        super();
        this.scriptId = scriptId;
        this.generated = generated;
    }
    serialization(stream) {
        this.scriptId = stream.utf16StringSafe(this.scriptId);
        this.generated = stream.boolean(this.generated || false);
    }
    static is(msg) {
        return msg.type === ResponseScriptIdMessage.typestatic;
    }
}
MessageFactory.register(ResponseScriptIdMessage);
/**
 * Requests collection of layout script infos.
 */
class CollectLayoutScriptInfosMessage extends BusMessage {
    static typestatic = "CollectLayoutScriptInfosMessage";
    static subjectstatic = "general";
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === CollectLayoutScriptInfosMessage.typestatic;
    }
}
MessageFactory.register(CollectLayoutScriptInfosMessage);
/**
 * Returns collected layout script infos.
 */
class ResponseLayoutScriptInfosMessage extends BusMessage {
    scriptInfos;
    static typestatic = "ResponseLayoutScriptInfosMessage";
    static subjectstatic = "general";
    get byteLength() {
        return calcArrayByteLength(this.scriptInfos);
    }
    /**
     * @param scriptInfos Lists the available layout scripts and their room state.
     */
    constructor(
    /**
     * Lists the available layout scripts and their room state.
     */
    scriptInfos) {
        super();
        this.scriptInfos = scriptInfos;
    }
    serialization(stream) {
        this.scriptInfos = stream.array(this.scriptInfos);
    }
    static is(msg) {
        return msg.type === ResponseLayoutScriptInfosMessage.typestatic;
    }
}
MessageFactory.register(ResponseLayoutScriptInfosMessage);
/**
 * Adds a layout script to the current room.
 */
class AddLayoutScriptToCurrentRoomMessage extends BusMessage {
    scriptId;
    layouterId;
    static typestatic = "AddLayoutScriptToCurrentRoomMessage";
    static subjectstatic = "general";
    get byteLength() {
        return calcStringByteLength(this.scriptId) + calcStringByteLength(this.layouterId);
    }
    /**
     * @param scriptId Identifies the layout script that should be attached.
     * @param layouterId Optionally limits the script to a specific layouter instance.
     */
    constructor(
    /**
     * Identifies the layout script that should be attached.
     */
    scriptId, 
    /**
     * Optionally limits the script to a specific layouter instance.
     */
    layouterId) {
        super();
        this.scriptId = scriptId;
        this.layouterId = layouterId;
    }
    serialization(stream) {
        this.scriptId = stream.utf16String(this.scriptId);
        this.layouterId = stream.utf16StringSafe(this.layouterId);
    }
    static is(msg) {
        return msg.type === AddLayoutScriptToCurrentRoomMessage.typestatic;
    }
}
MessageFactory.register(AddLayoutScriptToCurrentRoomMessage);

/**
 * Requests saving generated data into a file.
 */
class SaveDataFileMessage extends BusMessage {
    data;
    fileName;
    static typestatic = "SaveDataFileMessage";
    static subjectstatic = "general";
    get byteLength() {
        return calcStringByteLength(this.data) + calcStringByteLength(this.fileName);
    }
    /**
     * @param data File contents to persist.
     * @param fileName Suggested target file name.
     */
    constructor(
    /**
     * File contents to persist.
     */
    data, 
    /**
     * Suggested target file name.
     */
    fileName) {
        super();
        this.data = data;
        this.fileName = fileName;
    }
    serialization(stream) {
        this.data = stream.utf16String(this.data);
        this.fileName = stream.utf16String(this.fileName);
    }
    static is(msg) {
        return msg.type === SaveDataFileMessage.typestatic;
    }
}
MessageFactory.register(SaveDataFileMessage);

/**
 * Loads a GLTF layouter asset so it can be instantiated for room layout generation.
 */
class LoadGLTFLayouterMessage extends BusMessage {
    uid;
    url;
    static typestatic = "LoadGLTFLayouterMessage";
    static subjectstatic = "layout";
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + calcStringByteLength(this.url);
    }
    /**
     * @param uid Identifies the room or layouter context that needs the asset.
     * @param url Points to the GLTF layouter asset that should be loaded.
     */
    constructor(
    /**
     * Identifies the room or layouter context that needs the asset.
     */
    uid, 
    /**
     * Points to the GLTF layouter asset that should be loaded.
     */
    url) {
        super();
        this.uid = uid;
        this.url = url;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.url = stream.utf16String(this.url);
    }
    static is(msg) {
        return msg.type === LoadGLTFLayouterMessage.typestatic;
    }
}
MessageFactory.register(LoadGLTFLayouterMessage);
/**
 * Returns the requested GLTF layouter asset data.
 */
class ResponseGLTFLayouterMessage extends BusMessage {
    messages;
    static typestatic = "ResponseGLTFLayouterMessage";
    static subjectstatic = "layout";
    get byteLength() {
        return calcBusMessagesByteLength(this.messages);
    }
    /**
     * @param messages Carries the asset payload or follow-up messages for the requested layouter.
     */
    constructor(
    /**
     * Carries the asset payload or follow-up messages for the requested layouter.
     */
    messages) {
        super();
        this.messages = messages;
    }
    serialization(stream) {
        this.messages = stream.busMessages(this.messages);
    }
    static is(msg) {
        return msg.type === ResponseGLTFLayouterMessage.typestatic;
    }
}
MessageFactory.register(ResponseGLTFLayouterMessage);
/**
 * Adds a mesh layouter to the current room.
 */
class AddMeshLayouterToCurrentRoomMessage extends BusMessage {
    url;
    static typestatic = "AddMeshLayouterToCurrentRoomMessage";
    static subjectstatic = "layout";
    get byteLength() {
        return calcStringByteLength(this.url);
    }
    /**
     * @param url Points to the mesh layouter asset that should be attached to the current room.
     */
    constructor(
    /**
     * Points to the mesh layouter asset that should be attached to the current room.
     */
    url) {
        super();
        this.url = url;
    }
    serialization(stream) {
        this.url = stream.utf16String(this.url);
    }
    static is(msg) {
        return msg.type === AddMeshLayouterToCurrentRoomMessage.typestatic;
    }
}
MessageFactory.register(AddMeshLayouterToCurrentRoomMessage);
/**
 * Requests that an annotation should be added into the current room.
 */
class AddAnnotationIntoCurrentRoomRequestMessage extends BusMessage {
    position;
    rotation;
    scale;
    capturedText;
    color;
    static typestatic = "AddAnnotationIntoCurrentRoomRequestMessage";
    static subjectstatic = "layout";
    static CONST_BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT * 3 + // position
        Float32Array.BYTES_PER_ELEMENT * 4 + // rotation
        Float32Array.BYTES_PER_ELEMENT * 3 + // scale
        Int32Array.BYTES_PER_ELEMENT; // color
    get byteLength() {
        return AddAnnotationIntoCurrentRoomRequestMessage.CONST_BYTE_LENGTH + calcStringByteLength(this.capturedText);
    }
    /**
     * @param position Places the new annotation in room space.
     * @param rotation Orients the annotation in room space.
     * @param scale Scales the created annotation object.
     * @param capturedText Provides the text content that should appear in the annotation.
     * @param color Defines the annotation color.
     */
    constructor(
    /**
     * Places the new annotation in room space.
     */
    position = [0, 0, 0], 
    /**
     * Orients the annotation in room space.
     */
    rotation = [0, 0, 0, 1], 
    /**
     * Scales the created annotation object.
     */
    scale = [0, 0, 0], 
    /**
     * Provides the text content that should appear in the annotation.
     */
    capturedText, 
    /**
     * Defines the annotation color.
     */
    color) {
        super();
        this.position = position;
        this.rotation = rotation;
        this.scale = scale;
        this.capturedText = capturedText;
        this.color = color;
    }
    serialization(stream) {
        this.capturedText = stream.utf16String(this.capturedText);
        this.position = stream.number3(this.position);
        this.rotation = stream.number4(this.rotation);
        this.scale = stream.number3(this.scale);
        this.color = stream.float32(this.color);
    }
    static is(msg) {
        return msg.type === AddAnnotationIntoCurrentRoomRequestMessage.typestatic;
    }
}
MessageFactory.register(AddAnnotationIntoCurrentRoomRequestMessage);
/**
 * Returns the result of adding an annotation into the current room.
 */
class AddAnnotationIntoCurrentRoomResponseMessage extends BusMessage {
    uid;
    static typestatic = "AddAnnotationIntoCurrentRoomResponseMessage";
    static subjectstatic = "layout";
    get byteLength() {
        return Float32Array.BYTES_PER_ELEMENT;
    }
    /**
     * @param uid Identifies the annotation model that was created.
     */
    constructor(
    /**
     * Identifies the annotation model that was created.
     */
    uid) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.float32(this.uid);
    }
    static is(msg) {
        return msg.type === AddAnnotationIntoCurrentRoomResponseMessage.typestatic;
    }
}
MessageFactory.register(AddAnnotationIntoCurrentRoomResponseMessage);

/**
 * Sends client log output for collection or inspection.
 */
class ClientLogsMessage extends BusMessage {
    logs;
    static typestatic = "ClientLogsMessage";
    static subjectstatic = "general";
    get byteLength() {
        let byteLength = Uint32Array.BYTES_PER_ELEMENT; // logs.length)
        byteLength +=
            this.logs.length *
                (Uint8Array.BYTES_PER_ELEMENT + // level
                    Uint32Array.BYTES_PER_ELEMENT); // timestamp
        for (const log of this.logs) {
            byteLength += calcStringByteLength(log.msg);
        }
        return byteLength;
    }
    /**
     * @param logs Carries the collected client log entries to inspect or persist.
     */
    constructor(
    /**
     * Carries the collected client log entries to inspect or persist.
     */
    logs) {
        super();
        this.logs = logs;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.logs = [];
        }
        const logsLength = stream.uint32(this.logs.length);
        for (let i = 0; i < logsLength; i++) {
            if (stream.isReading) {
                this.logs[i] = {};
            }
            this.logs[i].level = stream.uint8(this.logs[i].level);
            this.logs[i].timestamp = stream.uint32(this.logs[i].timestamp);
            this.logs[i].msg = stream.utf16String(this.logs[i].msg);
        }
    }
    static is(msg) {
        return msg.type === ClientLogsMessage.typestatic;
    }
}
MessageFactory.register(ClientLogsMessage);

/**
 * Describes whether a peer contributes microphone audio, application audio, both, or neither.
 */
var MicState;
(function (MicState) {
    /** No microphone or application audio is active. */
    MicState[MicState["None"] = 0] = "None";
    /** Only microphone audio is active. */
    MicState[MicState["MicOnly"] = 1] = "MicOnly";
    /** Only application or desktop audio is active. */
    MicState[MicState["AudioOnly"] = 2] = "AudioOnly";
    /** Both microphone and application audio are active. */
    MicState[MicState["MicAndAudio"] = 3] = "MicAndAudio";
})(MicState || (MicState = {}));
/**
 * Identifies the browser family used for a peer connection.
 */
var PeerConnectionBrowserType;
(function (PeerConnectionBrowserType) {
    /** Google Chrome browser family. */
    PeerConnectionBrowserType[PeerConnectionBrowserType["Chrome"] = 0] = "Chrome";
    /** Chromium-based browser without Chrome branding. */
    PeerConnectionBrowserType[PeerConnectionBrowserType["Chromium"] = 1] = "Chromium";
    /** Oculus Browser on Meta devices. */
    PeerConnectionBrowserType[PeerConnectionBrowserType["OculusBrowser"] = 2] = "OculusBrowser";
    /** Mozilla Firefox browser family. */
    PeerConnectionBrowserType[PeerConnectionBrowserType["Firefox"] = 3] = "Firefox";
    /** Firefox Reality browser. */
    PeerConnectionBrowserType[PeerConnectionBrowserType["FirefoxReality"] = 4] = "FirefoxReality";
    /** HTC Vive Focus browser. */
    PeerConnectionBrowserType[PeerConnectionBrowserType["Focus"] = 5] = "Focus";
    /** Opera browser family. */
    PeerConnectionBrowserType[PeerConnectionBrowserType["Opera"] = 6] = "Opera";
    /** Apple Safari browser family. */
    PeerConnectionBrowserType[PeerConnectionBrowserType["Safari"] = 7] = "Safari";
    /** Microsoft Edge browser family. */
    PeerConnectionBrowserType[PeerConnectionBrowserType["Edge"] = 8] = "Edge";
    /** Microsoft Internet Explorer browser family. */
    PeerConnectionBrowserType[PeerConnectionBrowserType["InternetExplorer"] = 9] = "InternetExplorer";
    /** Samsung Internet browser family. */
    PeerConnectionBrowserType[PeerConnectionBrowserType["SamsungInternet"] = 10] = "SamsungInternet";
    /** Any other or unknown browser family. */
    PeerConnectionBrowserType[PeerConnectionBrowserType["Other"] = 11] = "Other";
})(PeerConnectionBrowserType || (PeerConnectionBrowserType = {}));
/**
 * Publishes client connection statistics.
 */
class ClientConnectionStatsMessage extends BusMessage {
    connectionStats;
    microphoneAllowed;
    microphoneSpeakerAllowed;
    browserType;
    static typestatic = "ClientConnectionStatsMessage";
    static subjectstatic = "server";
    get byteLength() {
        let byteLength = Uint16Array.BYTES_PER_ELEMENT + // connectionsLength
            BOOLEAN_BYTES_PER_ELEMENT + // microphoneAllowed
            BOOLEAN_BYTES_PER_ELEMENT + // microphoneSpeakerAllowed
            Uint8Array.BYTES_PER_ELEMENT; // browserType
        for (let i = 0, len = this.connectionStats.length; i < len; ++i) {
            byteLength +=
                Uint32Array.BYTES_PER_ELEMENT + // clientIdx
                    Uint8Array.BYTES_PER_ELEMENT + // connection
                    Uint8Array.BYTES_PER_ELEMENT + // signaling
                    Uint8Array.BYTES_PER_ELEMENT + // iceConnection
                    Uint8Array.BYTES_PER_ELEMENT + // iceGathering
                    Uint8Array.BYTES_PER_ELEMENT + // micState
                    BOOLEAN_BYTES_PER_ELEMENT; // isUsingTurn
        }
        return byteLength;
    }
    /**
     * @param connectionStats Lists the peer connection states observed by the client.
     * @param microphoneAllowed Indicates whether microphone capture is allowed.
     * @param microphoneSpeakerAllowed Indicates whether speaker capture is allowed.
     * @param browserType Identifies the browser family running the client.
     */
    constructor(
    /**
     * Lists the peer connection states observed by the client.
     */
    connectionStats, 
    /**
     * Indicates whether microphone capture is allowed.
     */
    microphoneAllowed, 
    /**
     * Indicates whether speaker capture is allowed.
     */
    microphoneSpeakerAllowed, 
    /**
     * Identifies the browser family running the client.
     */
    browserType) {
        super();
        this.connectionStats = connectionStats;
        this.microphoneAllowed = microphoneAllowed;
        this.microphoneSpeakerAllowed = microphoneSpeakerAllowed;
        this.browserType = browserType;
    }
    /**
     * On read, resets `connectionStats` to `[]` and creates a fresh entry object per connection, since
     * a deserialized instance skips the constructor.
     */
    serialization(stream) {
        if (stream.isReading) {
            this.connectionStats = [];
        }
        this.microphoneAllowed = stream.boolean(this.microphoneAllowed);
        this.microphoneSpeakerAllowed = stream.boolean(this.microphoneSpeakerAllowed);
        this.browserType = stream.uint8(this.browserType);
        const connectionsLength = stream.uint16(this.connectionStats.length);
        for (let i = 0; i < connectionsLength; i++) {
            let stats = this.connectionStats[i];
            if (stream.isReading) {
                stats = {};
                this.connectionStats[i] = stats;
            }
            stats.clientIdx = stream.uint32(stats.clientIdx);
            stats.connection = stream.uint8(stats.connection);
            stats.signaling = stream.uint8(stats.signaling);
            stats.iceConnection = stream.uint8(stats.iceConnection);
            stats.iceGathering = stream.uint8(stats.iceGathering);
            stats.micState = stream.uint8(stats.micState);
            stats.isUsingTurn = stream.boolean(stats.isUsingTurn);
        }
    }
    static is(msg) {
        return msg.type === ClientConnectionStatsMessage.typestatic;
    }
}
MessageFactory.register(ClientConnectionStatsMessage);
/**
 * Publishes server connection statistics.
 */
class ServerConnectionStatsMessage extends BusMessage {
    clients;
    static typestatic = "ServerConnectionStatsMessage";
    static subjectstatic = "server";
    get byteLength() {
        let byteLength = Uint16Array.BYTES_PER_ELEMENT; // clientsLength
        for (const client of this.clients) {
            byteLength +=
                Uint32Array.BYTES_PER_ELEMENT + // clientIdx
                    BOOLEAN_BYTES_PER_ELEMENT + // microphoneAllowed
                    BOOLEAN_BYTES_PER_ELEMENT + // microphoneSpeakerAllowed
                    Uint8Array.BYTES_PER_ELEMENT + // browserType
                    Uint16Array.BYTES_PER_ELEMENT; // connectionsLength
            for (let j = 0, conLen = client.connections.length; j < conLen; ++j) {
                byteLength +=
                    Uint32Array.BYTES_PER_ELEMENT + // clientIdx
                        Uint8Array.BYTES_PER_ELEMENT + // connection
                        Uint8Array.BYTES_PER_ELEMENT + // signaling
                        Uint8Array.BYTES_PER_ELEMENT + // iceConnection
                        Uint8Array.BYTES_PER_ELEMENT + // iceGathering
                        Uint8Array.BYTES_PER_ELEMENT + // micState
                        BOOLEAN_BYTES_PER_ELEMENT; // isUsingTurn
            }
        }
        return byteLength;
    }
    /**
     * @param clients Lists the server-side connection stats for all tracked clients.
     */
    constructor(
    /**
     * Lists the server-side connection stats for all tracked clients.
     */
    clients) {
        super();
        this.clients = clients;
    }
    /**
     * On read, resets `clients` to `[]` and creates a fresh entry object per client and per
     * connection, since a deserialized instance skips the constructor.
     */
    serialization(stream) {
        if (stream.isReading) {
            this.clients = [];
        }
        const clientsLength = stream.uint16(this.clients.length);
        for (let i = 0; i < clientsLength; i++) {
            let client = this.clients[i];
            if (stream.isReading) {
                client = {};
                client.connections = [];
                this.clients[i] = client;
            }
            client.clientIdx = stream.uint32(client.clientIdx);
            client.microphoneAllowed = stream.boolean(client.microphoneAllowed);
            client.microphoneSpeakerAllowed = stream.boolean(client.microphoneSpeakerAllowed);
            client.browserType = stream.uint8(client.browserType);
            const connectionsLength = stream.uint16(client.connections.length);
            for (let j = 0; j < connectionsLength; j++) {
                let stats = client.connections[j];
                if (stream.isReading) {
                    stats = {};
                    client.connections[j] = stats;
                }
                stats.clientIdx = stream.uint32(stats.clientIdx);
                stats.connection = stream.uint8(stats.connection);
                stats.signaling = stream.uint8(stats.signaling);
                stats.iceConnection = stream.uint8(stats.iceConnection);
                stats.iceGathering = stream.uint8(stats.iceGathering);
                stats.micState = stream.uint8(stats.micState);
                stats.isUsingTurn = stream.boolean(stats.isUsingTurn);
            }
        }
    }
    static is(msg) {
        return msg.type === ServerConnectionStatsMessage.typestatic;
    }
}
MessageFactory.register(ServerConnectionStatsMessage);

/**
 * Controls playback of a training video.
 */
class TrainingVideoPlaybackMessage extends BusMessage {
    trainingVideoId;
    command;
    newPostion;
    static typestatic = "TrainingVideoPlaybackMessage";
    static subjectstatic = "training";
    get byteLength() {
        return calcStringByteLength(this.trainingVideoId) + calcStringByteLength(this.command) + Float32Array.BYTES_PER_ELEMENT * 3;
    }
    /**
     * @param trainingVideoId Identifies the training video to control.
     * @param command Selects the playback command that should be applied.
     * @param newPostion Optionally repositions the video screen in world space.
     */
    constructor(
    /**
     * Identifies the training video to control.
     */
    trainingVideoId, 
    /**
     * Selects the playback command that should be applied.
     */
    command, 
    /**
     * Optionally repositions the video screen in world space.
     */
    newPostion) {
        super();
        this.trainingVideoId = trainingVideoId;
        this.command = command;
        this.newPostion = newPostion;
    }
    serialization(stream) {
        this.trainingVideoId = stream.utf16String(this.trainingVideoId);
        this.command = stream.utf16String(this.command);
        this.newPostion = stream.number3Safe(this.newPostion);
    }
    static is(msg) {
        return msg.type === TrainingVideoPlaybackMessage.typestatic;
    }
}
MessageFactory.register(TrainingVideoPlaybackMessage);
/**
 * Announces a change of training video state.
 */
class TrainingVideoStateChangedMessage extends BusMessage {
    trainingVideoId;
    newState;
    static typestatic = "TrainingVideoStateChangedMessage";
    static subjectstatic = "training";
    get byteLength() {
        return calcStringByteLength(this.trainingVideoId) + calcStringByteLength(this.newState);
    }
    /**
     * @param trainingVideoId Identifies the video whose state changed.
     * @param newState Reports the new playback state.
     */
    constructor(
    /**
     * Identifies the video whose state changed.
     */
    trainingVideoId, 
    /**
     * Reports the new playback state.
     */
    newState) {
        super();
        this.trainingVideoId = trainingVideoId;
        this.newState = newState;
    }
    serialization(stream) {
        this.trainingVideoId = stream.utf16String(this.trainingVideoId);
        this.newState = stream.utf16String(this.newState);
    }
    static is(msg) {
        return msg.type === TrainingVideoStateChangedMessage.typestatic;
    }
}
MessageFactory.register(TrainingVideoStateChangedMessage);
/**
 * Announces a change of wearable state in training.
 */
class TrainingWearableStateChangedMessage extends BusMessage {
    trainingWearableId;
    newState;
    static typestatic = "TrainingWearableStateChangedMessage";
    static subjectstatic = "training";
    get byteLength() {
        return calcStringByteLength(this.trainingWearableId) + calcStringByteLength(this.newState);
    }
    /**
     * @param trainingWearableId Identifies the wearable item whose state changed.
     * @param newState Reports the new wearable state in the training workflow.
     */
    constructor(
    /**
     * Identifies the wearable item whose state changed.
     */
    trainingWearableId, 
    /**
     * Reports the new wearable state in the training workflow.
     */
    newState) {
        super();
        this.trainingWearableId = trainingWearableId;
        this.newState = newState;
    }
    serialization(stream) {
        this.trainingWearableId = stream.utf16String(this.trainingWearableId);
        this.newState = stream.utf16String(this.newState);
    }
    static is(msg) {
        return msg.type === TrainingWearableStateChangedMessage.typestatic;
    }
}
MessageFactory.register(TrainingWearableStateChangedMessage);
/**
 * Announces that a wearable state change in training failed.
 */
class TrainingWearableStateChangeFailedMessage extends BusMessage {
    trainingWearableId;
    static typestatic = "TrainingWearableStateChangeFailedMessage";
    static subjectstatic = "training";
    get byteLength() {
        return calcStringByteLength(this.trainingWearableId);
    }
    /**
     * @param trainingWearableId Identifies the wearable item whose requested state transition failed.
     */
    constructor(
    /**
     * Identifies the wearable item whose requested state transition failed.
     */
    trainingWearableId) {
        super();
        this.trainingWearableId = trainingWearableId;
    }
    serialization(stream) {
        this.trainingWearableId = stream.utf16String(this.trainingWearableId);
    }
    static is(msg) {
        return msg.type === TrainingWearableStateChangeFailedMessage.typestatic;
    }
}
MessageFactory.register(TrainingWearableStateChangeFailedMessage);
/**
 * Requests the loading state of a training video.
 */
class TrainingVideoLoadingStateRequestMessage extends BusMessage {
    static typestatic = "TrainingVideoLoadingStateRequestMessage";
    static subjectstatic = "training";
    serialization() { }
    static is(msg) {
        return msg.type === TrainingVideoLoadingStateRequestMessage.typestatic;
    }
}
MessageFactory.register(TrainingVideoLoadingStateRequestMessage);
/**
 * Returns the loading state of a training video.
 */
class TrainingVideoLoadingStateResponseMessage extends BusMessage {
    numberOfVideosToLoad;
    numberOfVideosLoaded;
    numberOfFailedVideos;
    static typestatic = "TrainingVideoLoadingStateResponseMessage";
    static subjectstatic = "training";
    get byteLength() {
        return Uint32Array.BYTES_PER_ELEMENT * 3;
    }
    /**
     * @param numberOfVideosToLoad Reports how many videos should be loaded in total.
     * @param numberOfVideosLoaded Reports how many videos finished loading successfully.
     * @param numberOfFailedVideos Reports how many videos failed to load.
     */
    constructor(
    /**
     * Reports how many videos should be loaded in total.
     */
    numberOfVideosToLoad, 
    /**
     * Reports how many videos finished loading successfully.
     */
    numberOfVideosLoaded, 
    /**
     * Reports how many videos failed to load.
     */
    numberOfFailedVideos) {
        super();
        this.numberOfVideosToLoad = numberOfVideosToLoad;
        this.numberOfVideosLoaded = numberOfVideosLoaded;
        this.numberOfFailedVideos = numberOfFailedVideos;
    }
    serialization(stream) {
        this.numberOfVideosToLoad = stream.uint32(this.numberOfVideosToLoad);
        this.numberOfVideosLoaded = stream.uint32(this.numberOfVideosLoaded);
        this.numberOfFailedVideos = stream.uint32(this.numberOfFailedVideos);
    }
    static is(msg) {
        return msg.type === TrainingVideoLoadingStateResponseMessage.typestatic;
    }
}
MessageFactory.register(TrainingVideoLoadingStateResponseMessage);
/**
 * Announces that the active training stage changed.
 */
class TrainingStageChangedMessage extends BusMessage {
    oldStageId;
    newStageId;
    static typestatic = "TrainingStageChangedMessage";
    static subjectstatic = "training";
    get byteLength() {
        return calcStringByteLength(this.oldStageId) + calcStringByteLength(this.newStageId);
    }
    /**
     * @param oldStageId Identifies the stage that was active before the transition.
     * @param newStageId Identifies the stage that became active.
     */
    constructor(
    /**
     * Identifies the stage that was active before the transition.
     */
    oldStageId, 
    /**
     * Identifies the stage that became active.
     */
    newStageId) {
        super();
        this.oldStageId = oldStageId;
        this.newStageId = newStageId;
    }
    serialization(stream) {
        this.oldStageId = stream.utf16String(this.oldStageId);
        this.newStageId = stream.utf16String(this.newStageId);
    }
    static is(msg) {
        return msg.type === TrainingStageChangedMessage.typestatic;
    }
}
MessageFactory.register(TrainingStageChangedMessage);
/**
 * Announces that training finished.
 */
class TrainingFinishedMessage extends BusMessage {
    isRevisitMode;
    static typestatic = "TrainingFinishedMessage";
    static subjectstatic = "training";
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT;
    }
    /**
     * @param isRevisitMode Indicates whether training ended while revisiting completed content.
     */
    constructor(
    /**
     * Indicates whether training ended while revisiting completed content.
     */
    isRevisitMode) {
        super();
        this.isRevisitMode = isRevisitMode;
    }
    serialization(stream) {
        this.isRevisitMode = stream.boolean(this.isRevisitMode);
    }
    static is(msg) {
        return msg.type === TrainingFinishedMessage.typestatic;
    }
}
MessageFactory.register(TrainingFinishedMessage);
/**
 * Assigns a named training state to a model participating in the scenario.
 */
class SetTrainingObjectStateMessage extends BusMessage {
    stateName;
    static typestatic = "SetTrainingObjectStateMessage";
    static subjectstatic = "training";
    get byteLength() {
        return UID_SAFE_BYTES_PER_ELEMENT + calcStringByteLength(this.stateName);
    }
    /** Identifies the model whose training state should change. */
    modelUid = 0;
    /**
     * @param stateName Names the training state that should be applied.
     * @param modelUid Identifies the model that should receive that state.
     */
    constructor(
    /**
     * Names the training state that should be applied.
     */
    stateName, modelUid) {
        super();
        this.stateName = stateName;
        this.modelUid = modelUid;
    }
    serialization(stream) {
        this.modelUid = stream.uidSafe(this.modelUid);
        this.stateName = stream.utf16String(this.stateName);
    }
    static is(msg) {
        return msg.type === SetTrainingObjectStateMessage.typestatic;
    }
}
MessageFactory.register(SetTrainingObjectStateMessage);

/**
 * Placed on a room to override controller button labels/actions (click, double-click, hold, hold-released) while that room is active. `RoomSpecificInteractionModule` picks it up via `@FacetHandler` and, when the room is entered, sends the corresponding `SetControllerButtonDescription`/`SetControllerButtonActionsMessage` updates.
 */
class RoomSpecificButtonConfigFacet extends Facet {
    buttonConfig;
    static typestatic = "RoomSpecificButtonConfig";
    get byteLength() {
        let byteLength = Uint8Array.BYTES_PER_ELEMENT;
        for (const config of this.buttonConfig) {
            byteLength += Uint8Array.BYTES_PER_ELEMENT;
            byteLength += calcStringByteLength(config.description);
            byteLength += calcArrayByteLength(config.onClick);
            byteLength += calcArrayByteLength(config.onDoubleClick);
            byteLength += calcArrayByteLength(config.onHold);
            byteLength += calcArrayByteLength(config.onHoldReleased);
        }
        return byteLength;
    }
    /**
     * @param buttonConfig Room-specific button behavior overrides.
     */
    constructor(
    /** Room-specific button behavior overrides. */
    buttonConfig = []) {
        super();
        this.buttonConfig = buttonConfig;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.buttonConfig = [];
        }
        const numButtons = stream.uint8(this.buttonConfig.length);
        for (let i = 0; i < numButtons; ++i) {
            let config;
            if (stream.isReading) {
                this.buttonConfig.push((config = { butonType: 0 }));
            }
            else {
                config = this.buttonConfig[i];
            }
            config.butonType = stream.uint8(config.butonType);
            config.description = stream.utf16StringSafe(config.description);
            config.onClick = stream.arraySafe(config.onClick);
            config.onDoubleClick = stream.arraySafe(config.onDoubleClick);
            config.onHold = stream.arraySafe(config.onHold);
            config.onHoldReleased = stream.arraySafe(config.onHoldReleased);
        }
    }
    static is(facet) {
        return facet.type === RoomSpecificButtonConfigFacet.typestatic;
    }
}
FacetFactory.register(RoomSpecificButtonConfigFacet);
/**
 * Placed on a room to define which voice commands are available while that room is active. `RoomSpecificInteractionModule` reacts to it and, when the room is entered, broadcasts `SetAvailableSpeechCommandsMessage` with `speechCommands` so speech recognition can react to them.
 */
class RoomSpecificSpeechCommandsFacet extends Facet {
    speechCommands;
    static typestatic = "RoomSpecificSpeechCommands";
    get byteLength() {
        return calcArrayByteLength(this.speechCommands);
    }
    /**
     * @param speechCommands Speech commands available while the room is active.
     */
    constructor(
    /** Speech commands available while the room is active. */
    speechCommands = []) {
        super();
        this.speechCommands = speechCommands;
    }
    serialization(stream) {
        this.speechCommands = stream.array(this.speechCommands);
    }
    static is(facet) {
        return facet.type === RoomSpecificSpeechCommandsFacet.typestatic;
    }
}
FacetFactory.register(RoomSpecificSpeechCommandsFacet);
/**
 * Placed on a room to restrict which interaction gestures (grab-move, grab-rotate, grab-scale, display-info, etc.) are enabled while that room is active. `RoomSpecificInteractionModule` reacts to it and sends `SetEnabledGesturesMessage` with `gestures` when the room is entered.
 */
class RoomSpecificGesturesFacet extends Facet {
    gestures;
    static typestatic = "RoomSpecificGestures";
    get byteLength() {
        return calcArrayByteLength(this.gestures);
    }
    /**
     * @param gestures Gestures that remain enabled while the room is active.
     */
    constructor(
    /** Gestures that remain enabled while the room is active. */
    gestures = [GestureType.GrabMove3D, GestureType.GrabRotate, GestureType.GrabScale, GestureType.DisplayInfo]) {
        super();
        this.gestures = gestures;
    }
    serialization(stream) {
        this.gestures = stream.array(this.gestures);
    }
    static is(facet) {
        return facet.type === RoomSpecificGesturesFacet.typestatic;
    }
}
FacetFactory.register(RoomSpecificGesturesFacet);
/**
 * Placed on a room to configure its scroll-gesture behavior (`scrollGestureConfig`) and the info text shown for the display-info gesture (`infoText`). `RoomSpecificInteractionModule` reacts to it and sends `ConfigureScrollGestureMessage`/`SetInfoGestureTextMessage` when the room is entered.
 */
class RoomGestureConfigFacet extends Facet {
    scrollGestureConfig;
    infoText;
    static typestatic = "RoomGestureConfig";
    get byteLength() {
        return calcArrayByteLength(this.scrollGestureConfig) + calcStringByteLength(this.infoText);
    }
    /**
     * @param scrollGestureConfig Scroll-gesture configuration that should apply in the room.
     * @param infoText Informational text shown for the display-info gesture.
     */
    constructor(
    /** Scroll-gesture configuration that should apply in the room. */
    scrollGestureConfig = [], 
    /** Informational text shown for the display-info gesture. */
    infoText) {
        super();
        this.scrollGestureConfig = scrollGestureConfig;
        this.infoText = infoText;
    }
    serialization(stream) {
        this.scrollGestureConfig = stream.array(this.scrollGestureConfig);
        this.infoText = stream.utf16StringSafe(this.infoText);
    }
    static is(facet) {
        return facet.type === RoomGestureConfigFacet.typestatic;
    }
}
FacetFactory.register(RoomGestureConfigFacet);

/**
 * Makes (sub-objects of) the model continuously rotate. `RotatingRenderable` reads `params` - each entry naming a `subObjectName` (or the model root, if omitted) and an `angularVelocity` - and applies the rotation every frame.
 * @see {@link flavr-client!modules/rotatingMesh/RotatingRenderable.RotatingRenderable | RotatingRenderable}
 */
class RotatingFacet extends Facet {
    params;
    static typestatic = "RotatingFacet";
    static metaData = {
        export: true,
        description: "Makes a model rotate with a specified angular velocity",
        category: "Interaction"
    };
    get byteLength() {
        let byteLength = Uint32Array.BYTES_PER_ELEMENT;
        for (const param of this.params) {
            byteLength += calcStringByteLength(param.subObjectName) + Float32Array.BYTES_PER_ELEMENT * (2 + 1); // angularVelocity
        }
        return byteLength;
    }
    /**
     * @param params Rotation instructions for the root model or named sub-objects.
     */
    constructor(
    /** Rotation instructions for the root model or named sub-objects. */
    params) {
        super();
        this.params = params;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.params = [];
        }
        const length = stream.uint32(this.params.length);
        for (let i = 0; i < length; ++i) {
            if (stream.isReading) {
                this.params[i] = {};
            }
            const param = this.params[i];
            param.subObjectName = stream.utf16StringSafe(param.subObjectName);
            param.angularVelocity = stream.number3(param.angularVelocity);
        }
    }
    static is(facet) {
        return facet.type === RotatingFacet.typestatic;
    }
}
FacetFactory.register(RotatingFacet);

/**
 * Bit flags describing how a start point may be used within a room.
 */
var StartPointFlags;
(function (StartPointFlags) {
    /** Point may be used when a user spawns into the room. */
    StartPointFlags[StartPointFlags["SpawnPoint"] = 1] = "SpawnPoint";
    /** Point may be used when exiting toward a parent room. */
    StartPointFlags[StartPointFlags["ExitPoint"] = 2] = "ExitPoint";
    /** Point may be used as a generic navigation waypoint. */
    StartPointFlags[StartPointFlags["WayPoint"] = 4] = "WayPoint";
})(StartPointFlags || (StartPointFlags = {}));
/**
 * Marks a location in a room where a user can spawn and/or exit to a parent room, per the `SpawnPoint`/`ExitPoint`/`WayPoint` flags. `StartPointSystem` uses these (via `StartPointRenderable`) to place the local player when entering/leaving rooms, picking randomly among matching points, and falls back to auto-generating a default start point if a room has none.
 * @see {@link flavr-client!modules/rendering/startPoint/StartPointRenderable.StartPointRenderable | StartPointRenderable}
 */
class StartPointFacet extends Facet {
    flags;
    params;
    static typestatic = "StartPointFacet";
    static metaData = {
        export: true,
        description: "Specifies the position and orientation of where the player should enter or leave the room",
        category: "General"
    };
    static propTypes = {
        flags: {
            type: PropertyType.GroupUint32,
            items: {
                1: "SpawnPoint",
                2: "ExitPoint",
                4: "WayPoint"
            },
            default: StartPointFlags.SpawnPoint,
            description: "Specify if the start point is used as entry, exit or both"
        },
        params: {
            type: PropertyType.Struct,
            properties: {
                defaultHeight: {
                    type: PropertyType.Float32Safe,
                    description: "Offsets the player camera's y-position with respect to this object's position"
                },
                id: {
                    type: PropertyType.Utf16StringSafe,
                    description: "Currently unused feature. Allows to filter start points based on their id, if its specified here"
                }
            }
        }
    };
    get byteLength() {
        return (Uint32Array.BYTES_PER_ELEMENT + // StartPointFlag
            Float32Array.BYTES_PER_ELEMENT + // defaultHeight
            calcStringByteLength(this.params.id));
    }
    /**
     * @param flags Bitmask describing whether this point is used for spawning, exiting, or wayfinding.
     * @param params Optional spawn-point metadata such as default player height and lookup ID.
     */
    constructor(
    /** Bitmask describing whether this point is used for spawning, exiting, or wayfinding. */
    flags, 
    /** Optional spawn-point metadata such as default player height and lookup ID. */
    params = {}) {
        super();
        this.flags = flags;
        this.params = params;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.params = {};
        }
        this.flags = stream.uint32(this.flags);
        this.params.defaultHeight = stream.float32Safe(this.params.defaultHeight);
        this.params.id = stream.utf16StringSafe(this.params.id);
    }
    static is(facet) {
        return facet.type === StartPointFacet.typestatic;
    }
}
FacetFactory.register(StartPointFacet);

/**
 * Stores arbitrary script variables and pending RPC data on a model so it survives across sessions/reconnects
 * instead of only living in a script's in-memory state. `ScriptModule` reacts to changes on this facet by calling
 * `onUpdatePersistentVariables` on every script attached to the model, and `ModelScript` reads/writes it directly
 * (via `initPersistentVariables`/`syncPersistentVariables`) to seed and persist a script's declared persistent variables.
 */
class PersistentDataFacet extends Facet {
    data;
    static typestatic = "PersistentDataFacet";
    static metaData = {
        export: true,
        description: "Holds persistent script data",
        category: "Misc"
    };
    get byteLength() {
        return calcObjectByteLength(this.data);
    }
    /**
     * @param data Persisted script variables and RPC data stored on the model.
     */
    constructor(
    /** Persisted script variables and RPC data stored on the model. */
    data) {
        super();
        this.data = data;
    }
    serialization(stream) {
        this.data = stream.object(this.data);
    }
    static is(facet) {
        return facet.type === PersistentDataFacet.typestatic;
    }
}
FacetFactory.register(PersistentDataFacet);

/**
 * Supported kinds of values exposed as script properties.
 */
var ScriptPropertyType;
(function (ScriptPropertyType) {
    /** Plain value property passed directly to the script. */
    ScriptPropertyType[ScriptPropertyType["Property"] = 0] = "Property";
    /** Property that references another model. */
    ScriptPropertyType[ScriptPropertyType["Model"] = 1] = "Model";
    /** Property that references another script. */
    ScriptPropertyType[ScriptPropertyType["Script"] = 2] = "Script";
})(ScriptPropertyType || (ScriptPropertyType = {}));
/**
 * Attaches a single script to a model. `ScriptModule` reacts to this facet by instantiating/updating the script
 * identified by `scriptId` on the model (via `ModelScript`), passing along `properties` as the script's exposed
 * parameters and `autoExecute`/`active` to control whether it starts running immediately or waits to be triggered.
 * Removing the facet destroys the running script instance.
 */
class ScriptFacet extends Facet {
    scriptId;
    properties;
    autoExecute;
    static typestatic = "ScriptFacet";
    static propTypes = {
        scriptId: {
            type: PropertyType.Utf16String,
            isRequired: true
        },
        properties: {
            type: PropertyType.Dictionary,
            elementType: {
                type: PropertyType.Struct,
                properties: {
                    scriptId: {
                        type: PropertyType.AssetPath,
                        isRequired: true
                    },
                    value: {
                        type: PropertyType.ObjectSafe
                    }
                }
            }
        },
        autoExecute: {
            type: PropertyType.Boolean,
            default: true,
        },
        active: {
            type: PropertyType.Boolean,
            default: false,
        }
    };
    get byteLength() {
        return (calcStringByteLength(this.scriptId) + // scriptId
            calcObjectByteLength(this.properties) + // properties
            BOOLEAN_BYTES_PER_ELEMENT * 2 // autoExecute, active
        );
    }
    /**
     * @param scriptId Asset ID/path of the script to attach to the model.
     * @param properties Optional serialized property values passed into the script.
     * @param autoExecute Whether the script should start automatically when attached.
     */
    constructor(
    /** Asset ID/path of the script to attach to the model. */
    scriptId, 
    /** Optional serialized property values passed into the script. */
    properties, 
    /** Whether the script should start automatically when attached. */
    autoExecute = true) {
        super();
        this.scriptId = scriptId;
        this.properties = properties;
        this.autoExecute = autoExecute;
    }
    /**
     * Whether a non-auto-executing script is currently active.
     */
    active = false;
    serialization(stream) {
        this.scriptId = stream.utf16String(this.scriptId);
        this.properties = stream.objectSafe(this.properties);
        if (stream.isReading && stream instanceof ByteStreamReader && stream.getUnreadBytes() < (BOOLEAN_BYTES_PER_ELEMENT * 2)) {
            this.autoExecute = this.autoExecute ?? true;
            this.active = this.active ?? false;
            return;
        }
        this.autoExecute = stream.boolean(this.autoExecute ?? true);
        this.active = stream.boolean(this.active ?? false);
    }
    clone(cloneContext) {
        const facet = super.clone(cloneContext);
        if (cloneContext === undefined || facet.properties == null)
            return facet;
        for (const name in facet.properties) {
            const propertyInfo = facet.properties[name];
            if (propertyInfo === undefined)
                continue;
            if (propertyInfo.type !== ScriptPropertyType.Model)
                continue;
            const value = propertyInfo.value;
            const valueType = typeof value;
            if (valueType === "string") {
                throw new Error("[model] ToDo: Think about how to handle rewriting model paths");
            }
            else if (valueType === "number") {
                const cloneUID = cloneContext.findEquivalentDescendant(value);
                if (cloneUID !== undefined)
                    facet.properties[name].value = cloneUID;
            }
            else if (valueType === "object") {
                console.warn(`[common] ScriptFacet property ${value} of type ${valueType} is not cloned`);
            }
        }
        return facet;
    }
    static is(facet) {
        return facet.type === ScriptFacet.typestatic;
    }
}
FacetFactory.register(ScriptFacet);
/**
 * Attaches multiple scripts to a single model (the composed equivalent of {@link ScriptFacet}). `ScriptModule`
 * reacts to this facet by syncing the whole `scripts` list against the model's running script instances, each entry
 * carrying its own `scriptId`, `properties`, `autoExecute` and `active` state. On clone, numeric property values that
 * reference another model's UID are rewritten to point at the equivalent descendant in the cloned hierarchy.
 */
class ScriptComposeFacet extends Facet {
    scripts;
    static typestatic = "ScriptCompositeFacet";
    static propTypes = {
        scripts: {
            type: PropertyType.Array,
            elementType: {
                type: PropertyType.Struct,
                properties: {
                    scriptId: {
                        type: PropertyType.AssetPath,
                        isRequired: true
                    },
                    properties: {
                        type: PropertyType.DictionarySafe,
                        elementType: {
                            type: PropertyType.Struct,
                            properties: {
                                type: {
                                    type: PropertyType.EnumUint8,
                                    items: enumToPropertyItems(ScriptPropertyType)
                                },
                                value: { type: PropertyType.Any }
                            }
                        }
                    },
                    autoExecute: {
                        type: PropertyType.Boolean,
                        default: true,
                        isRequired: true
                    },
                    active: {
                        type: PropertyType.Boolean,
                        default: false,
                        isRequired: true
                    }
                }
            }
        },
    };
    get byteLength() {
        let byteLength = Uint32Array.BYTES_PER_ELEMENT; // scripts.length
        for (const info of this.scripts) {
            byteLength += (calcStringByteLength(info.scriptId) + // scriptId
                calcObjectByteLength(info.properties) + // properties
                BOOLEAN_BYTES_PER_ELEMENT * 2 // autoExecute + active
            );
        }
        return byteLength;
    }
    /**
     * @param scripts All scripts that should be attached to the model together.
     */
    constructor(
    /** All scripts that should be attached to the model together. */
    scripts) {
        super();
        this.scripts = scripts;
    }
    static canDeserializeWithScriptFlags(stream) {
        try {
            const length = stream.uint32(0);
            for (let i = 0; i < length; ++i) {
                stream.utf16String("");
                stream.objectSafe(undefined);
                stream.boolean(true);
                stream.boolean(false);
            }
            return true;
        }
        catch {
            return false;
        }
    }
    serialization(stream) {
        let shouldReadScriptFlags = true;
        if (stream.isReading && stream instanceof ByteStreamReader) {
            const probeBuffer = new Uint8Array(stream.getUnreadArrayBuffer()).slice().buffer;
            shouldReadScriptFlags = ScriptComposeFacet.canDeserializeWithScriptFlags(new ByteStreamReader(probeBuffer));
        }
        if (stream.isReading)
            this.scripts = [];
        const length = stream.uint32(this.scripts.length);
        for (let i = 0; i < length; ++i) {
            const scriptInfo = this.scripts[i] ?? (this.scripts[i] = {});
            this.scripts[i].scriptId = stream.utf16String(scriptInfo.scriptId);
            this.scripts[i].properties = stream.objectSafe(scriptInfo.properties);
            if (shouldReadScriptFlags) {
                this.scripts[i].autoExecute = stream.boolean(scriptInfo.autoExecute ?? true);
                this.scripts[i].active = stream.boolean(scriptInfo.active ?? false);
            }
            else {
                this.scripts[i].autoExecute = scriptInfo.autoExecute ?? true;
                this.scripts[i].active = scriptInfo.active ?? false;
            }
        }
    }
    /**
     * Clones the composed script list and remaps model-reference properties to cloned descendants when possible.
     */
    clone(cloneContext) {
        const facet = super.clone(cloneContext);
        if (cloneContext === undefined)
            return facet;
        for (const script of facet.scripts) {
            if (script.properties == null)
                continue;
            for (const name in script.properties) {
                const propertyInfo = script.properties[name];
                if (propertyInfo === undefined)
                    continue;
                if (propertyInfo.type !== ScriptPropertyType.Model)
                    continue;
                const value = propertyInfo.value;
                const valueType = typeof value;
                if (valueType === "string") {
                    // if (!ModelId.hasModelIdWithAbsolutePath(value)) continue
                    // script.properties[name].value = ModelId.replacePathIfInChildren(value, rootSrcModelId, rootDstModelId)
                    throw new Error("[model] ToDo: Think about how to handle rewriting model paths");
                }
                else if (valueType === "number") {
                    const cloneUID = cloneContext.findEquivalentDescendant(value);
                    if (cloneUID !== undefined)
                        script.properties[name].value = cloneUID;
                }
                else if (valueType === "object") {
                    // ToDO: This wasn't implemented before, we might want to handle this otherwise its a shallow copy?
                    console.warn(`[common] ScriptFacet property ${value} of type ${valueType} is not cloned`);
                }
            }
        }
        return facet;
    }
    static is(facet) {
        return facet.type === ScriptComposeFacet.typestatic;
    }
}
FacetFactory.register(ScriptComposeFacet);

/**
 * Draws one or more floating text labels next to a model, each connected to it by a line from `anchorPoint` to
 * `position`. `AnnotationRenderable` renders each entry's `text` with `troika-three-text` and draws the connecting
 * line using the given `lineColor`/`lineWidth`, sizing/wrapping the text via `textSize`, `maxWidth`/`maxHeight` and
 * `minLineLength`.
 * @see {@link flavr-client!modules/annotation/AnnotationRenderable.AnnotationRenderable | AnnotationRenderable}
 */
class Annotations extends Facet {
    annotations;
    static typestatic = "Annotations";
    static metaData = {
        export: true,
        description: "Shows an annotation message on an object",
        category: "Misc"
    };
    static propTypes = {
        annotations: {
            type: PropertyType.Struct,
            properties: {
                text: {
                    type: PropertyType.LocalizationString,
                    isRequired: true
                },
                anchorPoint: {
                    type: PropertyType.Number3,
                    isRequired: true
                },
                position: {
                    type: PropertyType.Number3,
                    isRequired: true
                },
                textSize: { type: PropertyType.Float32Safe },
                maxWidth: { type: PropertyType.Float32Safe },
                minLineLength: { type: PropertyType.Float32Safe },
                lineColor: { type: PropertyType.Uint32Safe },
                lineWidth: { type: PropertyType.Float32Safe }
            }
        }
    };
    static BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT * 3 + // anchorPoint
        Float32Array.BYTES_PER_ELEMENT * 3 + // position
        Float32Array.BYTES_PER_ELEMENT * 4 +
        Uint32Array.BYTES_PER_ELEMENT + // lineColor
        Float32Array.BYTES_PER_ELEMENT; // lineWidth
    get byteLength() {
        let byteLength = Uint32Array.BYTES_PER_ELEMENT; // length
        for (const a of this.annotations) {
            byteLength += calcLocalizationStringByteLength(a.text);
            byteLength += Annotations.BYTE_LENGTH;
        }
        return byteLength;
    }
    /**
     * @param annotations All callout labels that should be rendered for the model.
     */
    constructor(
    /** All callout labels that should be rendered for the model. */
    annotations) {
        super();
        this.annotations = annotations;
    }
    serialization(stream) {
        if (stream.isReading)
            this.annotations = [];
        const length = stream.uint32(this.annotations.length);
        for (let i = 0; i < length; ++i) {
            let annotation;
            if (stream.isReading) {
                annotation = {
                    text: "",
                    anchorPoint: [0, 0, 0],
                    position: [0, 0, 0]
                };
                this.annotations.push(annotation);
            }
            else {
                annotation = this.annotations[i];
            }
            annotation.text = stream.localizationString(annotation.text);
            annotation.anchorPoint = stream.number3(annotation.anchorPoint);
            annotation.position = stream.number3(annotation.position);
            annotation.textSize = stream.float32Safe(annotation.textSize);
            annotation.maxWidth = stream.float32Safe(annotation.maxWidth);
            annotation.maxHeight = stream.float32Safe(annotation.maxHeight);
            annotation.minLineLength = stream.float32Safe(annotation.minLineLength);
            annotation.lineColor = stream.uint32Safe(annotation.lineColor);
            annotation.lineWidth = stream.float32Safe(annotation.lineWidth);
        }
    }
    static is(facet) {
        return facet.type === Annotations.typestatic;
    }
}
FacetFactory.register(Annotations);

/**
 * Records the `fileId` of the blueprint asset a model was created from/is linked to. No client or server module
 * currently reacts to this facet with a `@FacetHandler`; it is carried as descriptive metadata rather than driving
 * live behavior (unlike the top-level `Blueprint.fileId`, which `BlueprintParser`/`BlueprintParserClient` use to
 * detect a blueprint that only references an external file instead of an inline model tree).
 */
class BlueprintFacet extends Facet {
    fileId;
    static typestatic = "BlueprintFacet";
    static metaData = {
        export: true,
        description: "",
        category: "General"
    };
    static propTypes = {
        fileId: { type: PropertyType.Utf16String }
    };
    get byteLength() {
        return calcStringByteLength(this.fileId);
    }
    /**
     * @param fileId Asset ID of the blueprint file associated with this model.
     */
    constructor(
    /** Asset ID of the blueprint file associated with this model. */
    fileId) {
        super();
        this.fileId = fileId;
    }
    serialization(stream) {
        this.fileId = stream.utf16String(this.fileId);
    }
    static is(facet) {
        return facet.type === BlueprintFacet.typestatic;
    }
}
FacetFactory.register(BlueprintFacet);

/**
 * Turns the model's mesh into a live viewfinder for another camera in the scene, identified by `cameraUid`.
 * `CameraRecorderViewRenderable` renders that camera's view into a `WebGLRenderTarget` and swaps the model's mesh
 * material for one textured with that render target, restoring the original material when the facet is removed.
 * On clone, `cameraUid` is rewritten to the equivalent camera in the cloned hierarchy.
 * @see {@link flavr-client!modules/rendering/camera/CameraRecorderViewRenderable.CameraRecorderViewRenderable | CameraRecorderViewRenderable}
 */
class CameraRecorderViewFacet extends Facet {
    cameraUid;
    static typestatic = "CameraRecorderViewFacet";
    static metaData = {
        export: true,
        description: "",
        category: "Misc"
    };
    static propTypes = {
        cameraUid: { type: PropertyType.Uid, isRequired: true }
    };
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    /**
     * @param cameraUid UID of the camera model whose rendered view should be shown on this model.
     */
    constructor(
    /** UID of the camera model whose rendered view should be shown on this model. */
    cameraUid) {
        super();
        this.cameraUid = cameraUid;
    }
    serialization(stream) {
        this.cameraUid = stream.uid(this.cameraUid);
    }
    /**
     * Clones the facet and remaps the referenced camera to the cloned hierarchy when possible.
     */
    clone(cloneContext) {
        const clonedFacet = super.clone(cloneContext);
        const cameraCloneUID = cloneContext.findEquivalentDescendant(this.cameraUid);
        if (cameraCloneUID !== undefined)
            clonedFacet.cameraUid = cameraCloneUID;
        return clonedFacet;
    }
    static is(facet) {
        return facet.type === CameraRecorderViewFacet.typestatic;
    }
}
FacetFactory.register(CameraRecorderViewFacet);

/**
 * Shows a small recording-icon plane above a controller while its microphone is being recorded, with `volume`
 * driving how bright the icon is drawn. `ControllerModule` attaches/updates this facet on the local controller
 * model while recording is active, pushing the live audio volume on every `AudioVolumeMessage`; `RecordIndicatorRenderer`
 * renders the icon and maps `volume` to the material's lightness.
 * @see {@link flavr-client!modules/recording/RecordIndicatorRenderer.RecordIndicatorRenderer | RecordIndicatorRenderer}
 */
class RecordingIndicator extends Facet {
    volume;
    static typestatic = "RecordingIndicator";
    static metaData = {
        export: true,
        description: "",
        category: "Rendering"
    };
    get byteLength() {
        return Float32Array.BYTES_PER_ELEMENT;
    }
    /**
     * @param volume Current normalized microphone volume used to drive the icon brightness.
     */
    constructor(
    /** Current normalized microphone volume used to drive the icon brightness. */
    volume) {
        super();
        this.volume = volume;
    }
    serialization(stream) {
        this.volume = stream.float32(this.volume);
    }
    static is(facet) {
        return facet.type === RecordingIndicator.typestatic;
    }
}
FacetFactory.register(RecordingIndicator);

/**
 * Registers a model as a tool that can be shown in the in-world tool belt/viewer. `ToolViewerModule` reacts to this
 * facet by recording the tool (keyed by its `name` and localized `label`) under the model's room, tracking when it
 * was added so the tool belt UI can list it; removing the facet, destroying the model, or removing its sub-model
 * unregisters it again.
 */
class ToolInfoFacet extends Facet {
    name;
    label;
    static typestatic = "ToolInfoFacet";
    static metaData = {
        export: true,
        description: "",
        category: "Misc"
    };
    get byteLength() {
        return calcStringByteLength(this.name) + calcLocalizationStringByteLength(this.label);
    }
    /**
     * @param name Stable tool identifier used by the tool viewer and tool belt.
     * @param label Localized label shown to users in the tool UI.
     */
    constructor(
    /** Stable tool identifier used by the tool viewer and tool belt. */
    name, 
    /** Localized label shown to users in the tool UI. */
    label) {
        super();
        this.name = name;
        this.label = label;
    }
    serialization(stream) {
        this.name = stream.utf16String(this.name);
        this.label = stream.localizationString(this.label);
    }
    static is(facet) {
        return facet.type === ToolInfoFacet.typestatic;
    }
}
FacetFactory.register(ToolInfoFacet);

/**
 * Acts as a lock marking that a training scenario is currently occupied, recording who is running it
 * (`activeUserName`/`activeClientIdx`), which scenario (`trainingScenarioName`) and when it started. `TrainingOrchestratorModule`
 * sets this facet on the scenario's TOML model when a user activates a scenario, checks for it before letting
 * another user start the same scenario (falling back to spectator mode if one is already found), and removes it
 * when the scenario finishes.
 */
class ActiveUserInTrainingScenarioFacet extends Facet {
    activeUserInTrainingScenarioFacetData;
    static typestatic = "ActiveUserInTrainingScenarioFacet";
    static metaData = {
        export: false,
    };
    /**
     * @param activeUserInTrainingScenarioFacetData Current training-scenario occupancy metadata.
     */
    constructor(
    /** Current training-scenario occupancy metadata. */
    activeUserInTrainingScenarioFacetData) {
        super();
        this.activeUserInTrainingScenarioFacetData = activeUserInTrainingScenarioFacetData;
    }
    get byteLength() {
        return calcObjectByteLength(this.activeUserInTrainingScenarioFacetData);
    }
    serialization(stream) {
        this.activeUserInTrainingScenarioFacetData = stream.object(this.activeUserInTrainingScenarioFacetData);
    }
    static is(facet) {
        return facet.type === ActiveUserInTrainingScenarioFacet.typestatic;
    }
}
FacetFactory.register(ActiveUserInTrainingScenarioFacet);

/**
 * Declares the named states a training scenario object can be in, each optionally swapping in a child model
 * (`modelName`) when that state becomes active. `TrainingOrchestratorModule` subscribes to this facet and forwards
 * it to `ScenarioFactory.addModelStateFacet`, which uses the state list to build the object's state machine and
 * resolve state transitions defined in the scenario's TOML file; facets received before the TOML has loaded are
 * buffered and applied once initialization completes.
 */
class TrainingModelStateFacet extends Facet {
    states;
    copyCollider;
    static typestatic = "TrainingModelStateFacet";
    static metaData = {
        export: true,
        description: `
      Defines an object with different states. Each state can have an optional object that is swapped out on a state
      change. These objects have to be children of this object to work properly.
    `,
        category: "PharmaXR"
    };
    static propTypes = {
        states: {
            type: PropertyType.Array,
            default: [],
            elementType: {
                type: PropertyType.Struct,
                properties: {
                    stateName: {
                        type: PropertyType.Utf16String,
                        isRequired: true
                    },
                    modelName: {
                        type: PropertyType.Utf16StringSafe
                    }
                }
            }
        },
        copyCollider: {
            type: PropertyType.Boolean,
            default: true
        }
    };
    get byteLength() {
        let byteLength = Uint32Array.BYTES_PER_ELEMENT + BOOLEAN_BYTES_PER_ELEMENT;
        for (const state of this.states) {
            byteLength += calcStringByteLength(state.stateName) + calcStringByteLength(state.modelName);
        }
        return byteLength;
    }
    /**
     * @param states All supported named states for this training model.
     * @param copyCollider Whether collider data should be copied to swapped state models.
     */
    constructor(
    /** All supported named states for this training model. */
    states = [], 
    /** Whether collider data should be copied to swapped state models. */
    copyCollider = true) {
        super();
        this.states = states;
        this.copyCollider = copyCollider;
    }
    serialization(stream) {
        if (stream.isReading)
            this.states = [];
        const numberOfStates = stream.uint32(this.states.length);
        for (let i = 0; i < numberOfStates; ++i) {
            if (stream.isReading)
                this.states[i] = { stateName: "", modelName: undefined };
            this.states[i].stateName = stream.utf16String(this.states[i].stateName);
            this.states[i].modelName = stream.utf16StringSafe(this.states[i].modelName);
        }
        this.copyCollider = stream.boolean(this.copyCollider);
    }
    static is(facet) {
        return facet.type === TrainingModelStateFacet.typestatic;
    }
}
FacetFactory.register(TrainingModelStateFacet);

/**
 * Body slots a training wearable can be attached to.
 */
var WearingPosition;
(function (WearingPosition) {
    /** Head slot. */
    WearingPosition[WearingPosition["Head"] = 0] = "Head";
    /** Chin or face-lower slot. */
    WearingPosition[WearingPosition["Chin"] = 1] = "Chin";
    /** Torso slot. */
    WearingPosition[WearingPosition["Torso"] = 2] = "Torso";
    /** Hand slot. */
    WearingPosition[WearingPosition["Hands"] = 3] = "Hands";
    /** Foot slot. */
    WearingPosition[WearingPosition["Feet"] = 4] = "Feet";
    /** Generic accessory slot. */
    WearingPosition[WearingPosition["Accessory"] = 5] = "Accessory";
    /** Eye or visor slot. */
    WearingPosition[WearingPosition["Eyes"] = 6] = "Eyes";
})(WearingPosition || (WearingPosition = {}));
/**
 * Marks a model as a wearable clothing item for a training scenario, giving the body slot it attaches to
 * (`wearingPosition`) and an optional `videoId` for an instructional video shown with it. `TrainingOrchestratorModule`
 * registers it as a "worn"/"unworn" item with `TrainingWearableService` and forwards it to `ScenarioFactory` so the
 * scenario's state machine can track whether the item has been put on.
 */
class TrainingWearableFacet extends Facet {
    wearingPosition;
    videoId;
    static typestatic = "TrainingWearableFacet";
    static metaData = {
        export: true,
        description: "Marks the model as a wearable item for training purposes",
        category: "PharmaXR"
    };
    /**
     * @param wearingPosition Body slot this wearable belongs to.
     * @param videoId Optional instructional video identifier associated with the wearable.
     */
    constructor(
    /** Body slot this wearable belongs to. */
    wearingPosition = WearingPosition.Head, 
    /** Optional instructional video identifier associated with the wearable. */
    videoId) {
        super();
        this.wearingPosition = wearingPosition;
        this.videoId = videoId;
    }
    static propTypes = {
        wearingPosition: {
            type: PropertyType.EnumUint8,
            items: enumToPropertyItems(WearingPosition)
        },
        videoId: {
            type: PropertyType.Utf16StringSafe
        }
    };
    get byteLength() {
        return Uint8Array.BYTES_PER_ELEMENT + // wearingPosition
            calcStringByteLength(this.videoId);
    }
    serialization(stream) {
        this.wearingPosition = stream.uint8(this.wearingPosition);
        this.videoId = stream.utf16StringSafe(this.videoId);
    }
    static is(facet) {
        return facet.type === TrainingWearableFacet.typestatic;
    }
}
FacetFactory.register(TrainingWearableFacet);

/**
 * Carries the raw contents of a training scenario's TOML definition file. Server-side, `TomlFileParser` creates this
 * facet from a model's parsed data during layout loading; client-side, `TrainingOrchestratorModule` subscribes to it
 * to detect that a training scenario is present and feeds `tomlFileContents` into `ScenarioFactory.setToml` to build
 * the runnable scenario.
 */
class TomlFileFacet extends Facet {
    tomlFileContents;
    static typestatic = "TomlFileFacet";
    /**
     * @param tomlFileContents Raw TOML source describing the training scenario.
     */
    constructor(
    /** Raw TOML source describing the training scenario. */
    tomlFileContents) {
        super();
        this.tomlFileContents = tomlFileContents;
    }
    get byteLength() {
        return calcStringByteLength(this.tomlFileContents);
    }
    serialization(stream) {
        this.tomlFileContents = stream.utf16String(this.tomlFileContents);
    }
    static is(facet) {
        return facet.type === TomlFileFacet.typestatic;
    }
}
FacetFactory.register(TomlFileFacet);

/**
 * Turns the model's mesh into a video playback surface, fed by a video file (`videoFileName`) fetched from a Minio
 * `bucketName`. `TrainingVideoRenderable` loads the video onto a plane as a `VideoTexture`, optionally playing its
 * audio, and honors `autoPlay`, `loop` and initial `hidden` state; `TrainingOrchestratorModule` also tracks this
 * facet to drive video playback/loading messages during a training scenario. Intended for use on plane geometry.
 * @see {@link flavr-client!modules/training/renderable/TrainingVideoRenderable.TrainingVideoRenderable | TrainingVideoRenderable}
 */
class TrainingVideoFacet extends RenderableFacet {
    id;
    bucketName;
    videoFileName;
    autoPlay;
    loop;
    playAudio;
    hidden;
    static typestatic = "TrainingVideoFacet";
    static metaData = {
        export: true,
        description: "Turns the model into a video playback surface. Works only well with planes",
        category: "PharmaXR"
    };
    static propTypes = {
        id: { type: PropertyType.Utf16String, default: "TrainingVideoRenderableIdentifier", description: "Identifier for the TrainingVideo" },
        bucketName: { type: PropertyType.Utf16String, default: "video", description: "Name of the bucket in Minio" },
        videoFileName: { type: PropertyType.Utf16String, default: "video.mp4", description: "Name of the video file in the bucket" },
        autoPlay: { type: PropertyType.Boolean, default: true, description: "Whether the video should start playing automatically" },
        loop: { type: PropertyType.Boolean, default: true, description: "Whether the video should loop" },
        playAudio: { type: PropertyType.Boolean, default: true, description: "Whether to play audio from the video" },
        hidden: { type: PropertyType.Boolean, default: false, description: "Whether the video is initially hidden" }
    };
    get byteLength() {
        return calcStringByteLength(this.id) + calcStringByteLength(this.bucketName) + calcStringByteLength(this.videoFileName) + BOOLEAN_BYTES_PER_ELEMENT * 4;
    }
    /**
     * @param id Identifier used to distinguish this training video instance.
     * @param bucketName MinIO bucket containing the video asset.
     * @param videoFileName File name of the video asset inside the bucket.
     * @param autoPlay Whether playback should start automatically.
     * @param loop Whether the video should loop when it reaches the end.
     * @param playAudio Whether the video's audio track should be played.
     * @param hidden Whether the video surface starts hidden.
     */
    constructor(
    /** Identifier used to distinguish this training video instance. */
    id = "TrainingVideoRenderableIdentifier", 
    /** MinIO bucket containing the video asset. */
    bucketName = "video", 
    /** File name of the video asset inside the bucket. */
    videoFileName = "video.mp4", 
    /** Whether playback should start automatically. */
    autoPlay = true, 
    /** Whether the video should loop when it reaches the end. */
    loop = true, 
    /** Whether the video's audio track should be played. */
    playAudio = true, 
    /** Whether the video surface starts hidden. */
    hidden = true) {
        super();
        this.id = id;
        this.bucketName = bucketName;
        this.videoFileName = videoFileName;
        this.autoPlay = autoPlay;
        this.loop = loop;
        this.playAudio = playAudio;
        this.hidden = hidden;
    }
    serialization(stream) {
        this.id = stream.utf16String(this.id);
        this.bucketName = stream.utf16String(this.bucketName);
        this.videoFileName = stream.utf16String(this.videoFileName);
        this.autoPlay = stream.boolean(this.autoPlay);
        this.loop = stream.boolean(this.loop);
        this.playAudio = stream.boolean(this.playAudio);
        this.hidden = stream.boolean(this.hidden);
    }
    static is(facet) {
        return facet.type === TrainingVideoFacet.typestatic;
    }
}
FacetFactory.register(TrainingVideoFacet);

/**
 * Intended to associate a model with a React component/script (`scriptId`) and a set of `externalPackages` URLs to
 * load alongside it, for rendering React content onto a canvas. No client or server module (handler, renderer, or
 * direct facet lookup) currently reacts to this facet anywhere in the codebase - it appears to be unused/dead code.
 */
class ReactCanvasFacet extends Facet {
    scriptId;
    params;
    static typestatic = "ReactCanvasFacet";
    static metaData = {
        export: true,
        description: "",
        category: "Misc"
    };
    get byteLength() {
        return calcStringByteLength(this.scriptId) + calcArrayByteLength(this.params.externalPackages);
    }
    /**
     * @param scriptId Identifier of the React canvas script/component to load.
     * @param params Optional React canvas settings such as external package URLs.
     */
    constructor(
    /** Identifier of the React canvas script/component to load. */
    scriptId, 
    /** Optional React canvas settings such as external package URLs. */
    params = {}) {
        super();
        this.scriptId = scriptId;
        this.params = params;
    }
    serialization(stream) {
        this.scriptId = stream.utf16String(this.scriptId);
        if (stream.isReading)
            this.params = {};
        this.params.externalPackages = stream.arraySafe(this.params.externalPackages);
    }
    static is(facet) {
        return facet.type === ReactCanvasFacet.typestatic;
    }
}
FacetFactory.register(ReactCanvasFacet);

/**
 * Intended to expose a numeric parameter (`params.label`, `min`/`max`/`step`, grouped/positioned via `group`/`position`)
 * as a UI slider control for a model. No client or server module (handler, renderer, or direct facet lookup)
 * currently reacts to this facet anywhere in the codebase - it appears to be unused/dead code.
 */
class ValueInputSliderFacet extends Facet {
    params;
    static typestatic = "ValueInputFacet";
    get byteLength() {
        return (calcStringByteLength(this.params.label) + // label
            Float32Array.BYTES_PER_ELEMENT + // min
            Float32Array.BYTES_PER_ELEMENT + // max
            // Uint8Array.BYTES_PER_ELEMENT +               // log
            Float32Array.BYTES_PER_ELEMENT + // step
            calcStringByteLength(this.params.group) + // group
            Int8Array.BYTES_PER_ELEMENT); // position
    }
    /**
     * @param params Slider label, range, and layout configuration.
     */
    constructor(
    /** Slider label, range, and layout configuration. */
    params) {
        super();
        this.params = params;
    }
    serialization(stream) {
        if (stream.isReading)
            this.params = {};
        this.params.label = stream.utf16String(this.params.label);
        this.params.min = stream.float32(this.params.min);
        this.params.max = stream.float32(this.params.max);
        // this.params.log = stream.uint8Safe(this.params.log);
        this.params.step = stream.float32Safe(this.params.step);
        this.params.group = stream.utf16StringSafe(this.params.group);
        this.params.position = stream.int8(this.params.position || 0);
        if (stream.isReading && this.params.step && Math.abs(this.params.step) < 0.5) {
            // Most javascript engines can accurately represent number as as 0.1
            // by if we encoding these number as a float32 in a ByteStream we get inaccuracys.
            // To get ride of some of these inaccuracys we round to the nearest fraction of the form
            // 1/n. @todo find a better solution
            this.params.step = 1 / Math.round(1 / this.params.step);
        }
    }
    static is(facet) {
        return facet.type === ValueInputSliderFacet.typestatic;
    }
}
FacetFactory.register(ValueInputSliderFacet);
/**
 * Metadata entry storing the current numeric value of a slider-driven property. Declared alongside
 * {@link ValueInputSliderFacet} but, like it, not currently read by any handler in the codebase.
 */
class SliderValueMetaData extends MetaDataBase {
    value;
    static typestatic = "SliderValueMetaData";
    /**
     * @param value Current numeric value associated with the slider metadata entry.
     */
    constructor(
    /** Current numeric value associated with the slider metadata entry. */
    value) {
        super();
        this.value = value;
    }
}

/**
 * Renders an interactive HTML document onto a `width`-sized plane in the scene. `UI3DRenderable` (registered for
 * this facet type via `RenderableFactory`) rasterizes the `htmlAssetId` asset onto the plane as a live texture, and
 * forwards clicks/changes on elements tagged with `data-event="methodName"` to any `ScriptFacet`/`ScriptComposeFacet`
 * scripts on the same model as a `ScriptRPCOnlyHostMessage` call to that method.
 * @see {@link flavr-client!modules/ui3d/UI3DRenderable.UI3DRenderable | UI3DRenderable}
 */
class UI3DFacet extends RenderableFacet {
    width;
    htmlAssetId;
    static typestatic = "UI3DFacet";
    static metaData = {
        export: true,
        description: "Renders an HTML element onto a 3D plane in the scene",
        category: "UI",
    };
    static propTypes = {
        width: {
            type: PropertyType.Float32,
            default: 1,
            description: "Width of the plane in world units. Height is computed automatically from the HTML content aspect ratio.",
        },
        htmlAssetId: {
            type: PropertyType.Utf16String,
            default: "",
            description: "Asset ID of the HTML file to render. The file may contain a <style> block for CSS.",
        },
    };
    get byteLength() {
        return FLOAT32_BYTES_PER_ELEMENT + calcStringByteLength(this.htmlAssetId);
    }
    constructor(
    /** Width of the generated plane in world units. */
    width = 1, 
    /** Asset ID of the HTML document to render onto the plane. */
    htmlAssetId = "") {
        super();
        this.width = width;
        this.htmlAssetId = htmlAssetId;
    }
    serialization(stream) {
        this.width = stream.float32(this.width);
        this.htmlAssetId = stream.utf16String(this.htmlAssetId);
    }
    static is(facet) {
        return facet.type === UI3DFacet.typestatic;
    }
}
FacetFactory.register(UI3DFacet);

/** Recreates blueprint data as real models and facets under a target parent model. */
class BlueprintParser {
    /**
     * Parses a blueprint and attaches its contents below the given parent model.
     * @param blueprint Blueprint document to recreate.
     * @param parent Parent model helper that should receive the recreated content.
     * @returns Newly created model helpers.
     */
    static async parse(blueprint, parent) {
        const models = [];
        if (blueprint.fileId) {
            return models;
        }
        if (!blueprint.models)
            return models;
        if (!Array.isArray(blueprint.models))
            throw new TypeError(`models isn't a array`);
        for (const blueprintModel of blueprint.models) {
            this.parseModel(blueprintModel, parent);
        }
        return models;
    }
    /**
     * Parses a single blueprint model node and all of its descendants.
     * @param blueprintModel Serialized model node to recreate.
     * @param parent Parent model helper that should receive the recreated child.
     */
    static parseModel(blueprintModel, parent) {
        if (typeof blueprintModel.relativeId !== "string")
            throw new TypeError("relativeId isn't a string");
        const model = parent.createChildModel(blueprintModel.relativeId);
        this.parseFacets(blueprintModel, model);
        if (blueprintModel.children) {
            if (!Array.isArray(blueprintModel.children))
                throw new TypeError(`children isn't an array`);
            for (const blueprintChildren of blueprintModel.children) {
                this.parseModel(blueprintChildren, model);
            }
        }
    }
    /**
     * Applies every facet stored on a blueprint model.
     * @param blueprintModel Serialized blueprint model carrying the facet payloads.
     * @param model Target model helper that should receive the parsed facets.
     */
    static parseFacets(blueprintModel, model) {
        if (!blueprintModel.facets) {
            return;
        }
        if (typeof blueprintModel.facets !== "object")
            throw new TypeError(`facets isn't a object`);
        for (const facetType in blueprintModel.facets) {
            const obj = blueprintModel.facets[facetType];
            this.parseFacet(facetType, obj, model);
        }
    }
    /**
     * Recreates one facet from blueprint data and sets it on the target model.
     * @param facetType Serialized facet type string.
     * @param blueprintFacet Plain serialized facet payload.
     * @param model Target model helper that should receive the recreated facet.
     */
    static parseFacet(facetType, blueprintFacet, model) {
        if (!FacetTypeTable.getHash(facetType)) {
            return;
        }
        const facet = FacetFactory.createFromObjectByTypeString(blueprintFacet, facetType, true);
        if (!facet) {
            return;
        }
        model.setFacet(facet);
    }
}

/**
 * Built-in named color slots that layouts commonly rely on.
 */
var DefaultColors;
(function (DefaultColors) {
    /** Primary accent color used by the current scheme. */
    DefaultColors["Base"] = "Base";
    /** Secondary accent color used for variation and grouping. */
    DefaultColors["Secondary"] = "Secondary";
    /** Third accent color for additional visual differentiation. */
    DefaultColors["Tertiary"] = "Tertiary";
    /** Fourth accent color for richer palette-based layouts. */
    DefaultColors["Quaternary"] = "Quaternary";
    /** Fifth accent color for richer palette-based layouts. */
    DefaultColors["Quinary"] = "Quinary";
    /** Neutral color for low-emphasis surfaces and text. */
    DefaultColors["Neutral"] = "Neutral";
    /** Complementary highlight color that contrasts with the base tone. */
    DefaultColors["Complement"] = "Complement";
    /** Warning color for cautionary states. */
    DefaultColors["WarningSignal"] = "WarningSignal";
    /** Positive color for success states. */
    DefaultColors["PositivSignal"] = "PositivSignal";
    /** Negative color for error or failure states. */
    DefaultColors["NegativSignal"] = "NegativSignal";
})(DefaultColors || (DefaultColors = {}));
/**
 * Built-in scalar mapping names that layouts can use for gradients or status colors.
 */
var DefaultColorMappings;
(function (DefaultColorMappings) {
    /** Generic mapping aligned with the scheme's primary color family. */
    DefaultColorMappings["Base"] = "Base";
    /** Mapping for subdued, neutral-valued ranges. */
    DefaultColorMappings["Neutral"] = "Neutral";
    /** Mapping for quality-like values from poor to good. */
    DefaultColorMappings["Quality"] = "Quality";
    /** Mapping for positive score semantics. */
    DefaultColorMappings["Goodness"] = "Goodness";
    /** Mapping for negative score semantics. */
    DefaultColorMappings["Badness"] = "Badness";
})(DefaultColorMappings || (DefaultColorMappings = {}));
/**
 * A ColorScheme meant to be used in Layouts.
 * Could be used for other things, too!
 */
class ColorScheme {
    colors = new Map();
    scalarToColors = new Map();
    /**
     * This color will be returned whenever a non-existant color-name is requested.
     */
    defaultColor = new Color(0xff00ff);
    /**
     * Registers or replaces a named color in this scheme.
     * @param colorName Usually one of {@link DefaultColors}.
     * @param color Color value that should be returned for the given name.
     */
    setColor(colorName, color) {
        this.colors.set(colorName, color);
    }
    /**
     * Registers a scalar-to-color mapping such as a gradient or status transfer function.
     * @param name Mapping identifier, often one of {@link DefaultColorMappings}.
     * @param scalarToColor Mapping implementation that converts scalar values into colors.
     */
    setScalarToColorFunction(name, scalarToColor) {
        this.scalarToColors.set(name, scalarToColor);
    }
    /**
     * Resolves a named color or falls back to the scheme's default debug color.
     * @param colorName Name of the color to resolve.
     */
    getColor(colorName) {
        return this.colors.get(colorName) || this.defaultColor;
    }
    /**
     * Resolves one named color directly to a hex color value.
     * @param colorName Name of the color to resolve.
     */
    getHexColor(colorName) {
        return this.getColor(colorName).getHex();
    }
    /**
     * Resolves a scalar value through one of the registered color mappings.
     * @param colorMapping Mapping name, often one of {@link DefaultColorMappings}.
     * @param value Scalar input, typically normalized to the configured range.
     * @param outColor Optional target color that receives the result.
     */
    getColorForValue(colorMapping, value, outColor) {
        const scalarToColor = this.scalarToColors.get(colorMapping);
        if (scalarToColor) {
            return scalarToColor.mapValue(value, outColor);
        }
        else {
            if (outColor) {
                return outColor.copy(this.defaultColor);
            }
            else {
                return this.defaultColor.clone();
            }
        }
    }
    /**
     * Resolves a scalar mapping directly to a hex color value.
     * @param colorMapping Mapping name, often one of {@link DefaultColorMappings}.
     * @param value Scalar input, typically normalized to the configured range.
     */
    getHexColorForValue(colorMapping, value) {
        const scalarToColor = this.scalarToColors.get(colorMapping);
        if (scalarToColor) {
            return scalarToColor.mapValueToHexColor(value);
        }
        else {
            return this.defaultColor.getHex();
        }
    }
}

const tmpColor = new Color();
/**
 * Color spaces available for gradient interpolation.
 */
var ColorSpace;
(function (ColorSpace) {
    /** Interpolates directly in red, green, and blue channels. */
    ColorSpace[ColorSpace["RGB"] = 0] = "RGB";
    /** Interpolates in hue, saturation, and lightness space. */
    ColorSpace[ColorSpace["HSL"] = 1] = "HSL";
})(ColorSpace || (ColorSpace = {}));
/**
 * Interpolates between configured colors to turn scalar values into colors.
 */
class LookupTable {
    minValue = 0;
    maxValue = 1;
    interpolationSpace = ColorSpace.HSL;
    colors = [];
    /**
     * @param colors Control colors used for interpolation across the configured range.
     */
    constructor(colors) {
        this.colors = colors;
    }
    /**
     * Replaces the control colors used by the lookup table.
     * @param colorValues New control colors ordered across the scalar range.
     */
    setColors(colorValues) {
        this.colors = colorValues;
    }
    /**
     * Returns how many control colors the lookup table currently contains.
     */
    getNumColors() {
        return this.colors.length;
    }
    /**
     * Returns one control color by index.
     * @param index Index of the control color to read.
     */
    getColorForIndex(index) {
        return this.colors[index];
    }
    /**
     * Chooses the color space used for interpolation between neighboring control colors.
     * @param interpolationSpace Interpolation strategy used between control colors.
     */
    setInterpolationColorSpace(interpolationSpace) {
        this.interpolationSpace = interpolationSpace;
    }
    /**
     * Defines the scalar input range covered by this lookup table.
     * @param range Inclusive minimum and maximum scalar values.
     */
    setRange(range) {
        this.minValue = range[0];
        this.maxValue = range[1];
    }
    /**
     * Returns the currently configured scalar range.
     */
    getRange() {
        return [this.minValue, this.maxValue];
    }
    /**
     * Maps a scalar value into an interpolated color.
     * @param value Scalar input value to map.
     * @param outColor Optional target color that receives the result.
     */
    mapValue(value, outColor) {
        let color = outColor || new Color();
        if (this.colors.length === 0) {
            return color;
        }
        const maxIndex = this.colors.length - 1;
        const f = clamp((value - this.minValue) / (this.maxValue - this.minValue), 0, 1);
        const index = Math.floor(maxIndex * f);
        if (index === maxIndex) {
            color.copy(this.colors[index]);
        }
        else {
            color = this.interpolateColors(this.colors[index], this.colors[index + 1], maxIndex * f - index, color);
        }
        return color;
    }
    /**
     * Maps a scalar value directly to a hex color.
     * @param value Scalar input value to map.
     */
    mapValueToHexColor(value) {
        return this.mapValue(value, tmpColor).getHex();
    }
    /**
     * Interpolates between two colors in the active interpolation space.
     */
    interpolateColors(color1, color2, interpolationFactor, outColor) {
        switch (this.interpolationSpace) {
            case ColorSpace.RGB:
                return outColor.copy(color1).lerp(color2, interpolationFactor);
            case ColorSpace.HSL: {
                const hsl1 = color1.getHSL({ h: 1, s: 1, l: 1 });
                const hsl2 = color2.getHSL({ h: 1, s: 1, l: 1 });
                return outColor.setHSL(this.interpolateHue(hsl1.h, hsl2.h, interpolationFactor), (1 - interpolationFactor) * hsl1.s + interpolationFactor * hsl2.s, (1 - interpolationFactor) * hsl1.l + interpolationFactor * hsl2.l);
            }
        }
    }
    /**
     * Interpolates hue while wrapping correctly around the hue circle.
     */
    interpolateHue(hue1, hue2, f) {
        if (Math.abs(hue1 - hue2) < 0.5) {
            return (1 - f) * hue1 + f * hue2;
        }
        else if (hue1 < hue2) {
            const hue = (1 - f) * (hue1 + 1) + f * hue2;
            return hue > 1 ? hue - 1 : hue;
        }
        else {
            const hue = (1 - f) * hue1 + f * (hue2 + 1);
            return hue > 1 ? hue - 1 : hue;
        }
    }
}

const BASE_HUES = [215 / 360, 239 / 360, 191 / 360, 264 / 360, 166 / 360];
const SATURATION = 0.6;
/**
 * Default FlaVR color scheme with named brand colors and common value mappings.
 */
class FlaviaDefaultColorScheme extends ColorScheme {
    /**
     * Primary brand color.
     */
    static baseColor = new Color().setHSL(BASE_HUES[0], SATURATION, 0.5);
    /**
     * Secondary brand color.
     */
    static secondaryColor = new Color().setHSL(BASE_HUES[1], SATURATION, 0.5);
    /**
     * Tertiary brand color.
     */
    static tertiaryColor = new Color().setHSL(BASE_HUES[2], SATURATION, 0.5);
    /**
     * Quaternary brand color.
     */
    static quaternaryColor = new Color().setHSL(BASE_HUES[3], SATURATION, 0.5);
    /**
     * Quinary brand color.
     */
    static quinaryColor = new Color().setHSL(BASE_HUES[4], SATURATION, 0.5);
    /**
     * Neutral UI color used for subdued accents.
     */
    static neutralColor = new Color(0xd7d7d7);
    /**
     * Complementary highlight color.
     */
    static complementColor = new Color(0x7d6100);
    /**
     * Positive feedback color.
     */
    static positivSignalColor = new Color(0x4bad00);
    /**
     * Negative feedback color.
     */
    static negativSignalColor = new Color(0xcc001b);
    /**
     * Warning feedback color.
     */
    static warningSignalColor = new Color(0xffbb33);
    /**
     * Gradient used for the primary scalar color mapping.
     */
    static primaryColorTransferFunction = new LookupTable([
        new Color().setHSL(BASE_HUES[0], SATURATION, 0.35),
        new Color().setHSL(BASE_HUES[0], SATURATION, 0.65)
    ]);
    /**
     * Gradient used for neutral scalar values.
     */
    static neutralValueToColor = new LookupTable([new Color().setHSL(BASE_HUES[0], 0.05, 0.2), new Color().setHSL(BASE_HUES[0], 0.05, 0.8)]);
    /**
     * Gradient used for quality indicators.
     */
    static qualityToColor = new LookupTable([
        FlaviaDefaultColorScheme.negativSignalColor,
        FlaviaDefaultColorScheme.warningSignalColor,
        FlaviaDefaultColorScheme.positivSignalColor
    ]);
    /**
     * Gradient used for goodness or positive scores.
     */
    static goodnessToColor = new LookupTable([
        FlaviaDefaultColorScheme.desaturateColor(FlaviaDefaultColorScheme.positivSignalColor, 0.5),
        FlaviaDefaultColorScheme.positivSignalColor
    ]);
    /**
     * Gradient used for badness or negative scores.
     */
    static badnessToColor = new LookupTable([
        FlaviaDefaultColorScheme.desaturateColor(FlaviaDefaultColorScheme.negativSignalColor, 0.1),
        FlaviaDefaultColorScheme.negativSignalColor
    ]);
    /**
     * Registers the built-in named colors and scalar mappings.
     */
    constructor() {
        super();
        this.setColor(DefaultColors.Base, FlaviaDefaultColorScheme.baseColor);
        this.setColor(DefaultColors.Secondary, FlaviaDefaultColorScheme.secondaryColor);
        this.setColor(DefaultColors.Tertiary, FlaviaDefaultColorScheme.tertiaryColor);
        this.setColor(DefaultColors.Quaternary, FlaviaDefaultColorScheme.quaternaryColor);
        this.setColor(DefaultColors.Quinary, FlaviaDefaultColorScheme.quinaryColor);
        this.setColor(DefaultColors.Complement, FlaviaDefaultColorScheme.complementColor);
        this.setColor(DefaultColors.Neutral, FlaviaDefaultColorScheme.neutralColor);
        this.setColor(DefaultColors.WarningSignal, FlaviaDefaultColorScheme.warningSignalColor);
        this.setColor(DefaultColors.NegativSignal, FlaviaDefaultColorScheme.negativSignalColor);
        this.setColor(DefaultColors.PositivSignal, FlaviaDefaultColorScheme.positivSignalColor);
        this.setScalarToColorFunction(DefaultColorMappings.Base, FlaviaDefaultColorScheme.primaryColorTransferFunction);
        this.setScalarToColorFunction(DefaultColorMappings.Neutral, FlaviaDefaultColorScheme.neutralValueToColor);
        this.setScalarToColorFunction(DefaultColorMappings.Quality, FlaviaDefaultColorScheme.qualityToColor);
        this.setScalarToColorFunction(DefaultColorMappings.Goodness, FlaviaDefaultColorScheme.goodnessToColor);
        this.setScalarToColorFunction(DefaultColorMappings.Badness, FlaviaDefaultColorScheme.badnessToColor);
    }
    /**
     * Creates a dimmer, less saturated variant of a base color.
     * @param color Source color to soften.
     * @param lightnessOffset Lightness offset applied after desaturation.
     */
    static desaturateColor(color, lightnessOffset) {
        return color.clone().offsetHSL(0, -color.getHSL({ h: 1, s: 1, l: 1 }).s * 0.99, lightnessOffset);
    }
}

/**
 * Named collection of readily available color scheme instances.
 */
const DEFAULT_COLOR_SCHEMES = {
    /** Default FlaVR application color scheme. */
    flavia: new FlaviaDefaultColorScheme()
};

/**
 * A decorator that manages access to an asynchronous function. A function has to complete before others can call this
 * function again. If this function is called repeatedly while still being executed only the ***most recent*** call is
 * saved and executed once the function completes. This decorator can only be used on asynchronous functions.
 *
 * Usage:
 * ```TS
 * @MutexLatest
 * async myAsyncFn(a: number, b: bool): Promise<void> {
 *  //...
 * }
 * ```
 */
function MutexLatest(_target, key, descriptor) {
    const original = descriptor.value;
    if (typeof original !== "function") {
        throw new Error("@MutexLatest can only be used on async methods.");
    }
    // Per-method symbols (unique per decorated member, shared across instances via 'this')
    const IN_USE = Symbol(String(key) + ":inUse");
    const LATEST = Symbol(String(key) + ":latestArgs");
    descriptor.value = async function (...args) {
        // Lazy-init per instance
        if (!(IN_USE in this)) {
            Object.defineProperty(this, IN_USE, { value: false, writable: true });
            Object.defineProperty(this, LATEST, { value: undefined, writable: true });
        }
        // If already processing: keep only most recent args and return
        if (this[IN_USE] === true) {
            this[LATEST] = args;
            return;
        }
        this[IN_USE] = true;
        try {
            // Execute until we have no pending args
            let runArgs = args;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                await original.apply(this, runArgs);
                const pending = this[LATEST];
                if (pending !== undefined) {
                    this[LATEST] = undefined; // consume
                    runArgs = pending; // replay with latest
                    continue;
                }
                break;
            }
        }
        finally {
            this[IN_USE] = false;
        }
    };
    return descriptor;
}

const tmpVector31 = new Vector3();
const tmpVector32 = new Vector3();
const tmpVector33 = new Vector3();
const tmpQuaternion = new Quaternion();
const tmpMatrix4 = new Matrix4();
const tmpEuler = new Euler();
/**
 * Fluent helper for building a model hierarchy and the facet update messages needed to spawn it.
 */
class ModelHelper {
    modelId;
    isExistingModel;
    children = [];
    behavior = [];
    onClick;
    onDoubleClick;
    interactionDescription;
    /** Local transform facet that will be applied to the model when present. */
    transform;
    /** Optional local transform applied to the collision shape instead of the render transform. */
    collisionShapeLocalTransform;
    renderShape;
    collisionShape;
    color;
    material;
    renderable;
    mass;
    friction;
    /** Facets currently configured for this helper, keyed by their unique pattern. */
    facets = new Map();
    roomInteraction;
    connections = {};
    contextMenuUid;
    highlightSelection;
    highlightSelectionColor;
    networkLocalOnly = false;
    /**
     * @param modelId Identifier of the model represented by this helper.
     * @param isExistingModel Marks the helper as targeting an already existing model instead of creating a new one.
     */
    constructor(
    /** Identifier of the model represented by this helper. */
    modelId, 
    /** Marks the helper as targeting an already existing model instead of creating a new one. */
    isExistingModel = false) {
        this.modelId = modelId;
        this.isExistingModel = isExistingModel;
    }
    /**
     * Registers or replaces a facet that should be applied to the model.
     */
    setFacet(facet) {
        this.facets.set(facet.pattern, facet);
        return this;
    }
    /**
     * Creates a child helper below this model using a regular submodel id.
     */
    createChildModel(name, isExistingModel = false) {
        const childModelId = ModelId.create(name ?? this.children.length.toString(), this.modelId.uid);
        const child = new ModelHelper(childModelId, isExistingModel);
        this.children.push(child);
        return child;
    }
    /**
     * Creates a child room helper and preconfigures its layout and room interaction facet.
     * @param name Relative room name used to derive the room model id.
     * @param layouterParams Layouter parameters that define the room contents.
     * @param roomEntryPoint Optional room entry position used by room interaction.
     * @param isExistingModel Marks the helper as targeting an already existing room model.
     * @param autoload Controls whether the room should auto-load when selected.
     */
    createChildRoom(name, layouterParams, roomEntryPoint, isExistingModel = false, autoload = true) {
        const childRoomId = ModelId.createRoomId(name, this.modelId.uid);
        const child = new ModelHelper(childRoomId, isExistingModel);
        child.setFacet(new LayoutFacet(layouterParams));
        child.roomInteraction = new RoomInteraction(autoload, roomEntryPoint);
        this.children.push(child);
        return child;
    }
    /**
     * Looks up a direct child by its relative id.
     * @param name Relative child id to resolve.
     */
    getChildModel(name) {
        for (let i = 0; i < this.children.length; ++i) {
            if (this.children[i].modelId.relativeId === name)
                return this.children[i];
        }
        return undefined;
    }
    /**
     * Returns the current direct children of this helper.
     */
    getChildren() {
        return this.children;
    }
    /**
     * Sets layout parameters for room-style content placement.
     */
    setLayouterParams(layouterParams) {
        return this.setFacet(new LayoutFacet(layouterParams));
    }
    /**
     * Sets the overall visibility of the model.
     * @param visible Whether the model should be visible.
     */
    setVisibility(visible) {
        return this.setFacet(new VisibilityFacet(visible));
    }
    /**
     * Overrides visibility for a specific facet type.
     * @param type Facet type whose visibility should be overridden.
     * @param visible Whether the facet should be visible.
     */
    setFacetVisibility(type, visible) {
        return this.setFacet(new FacetVisibility(type, visible));
    }
    /**
     * Replaces the full local transform in one step.
     * @param position Local position to apply.
     * @param orientation Local orientation to apply.
     * @param scale Local scale to apply.
     */
    setTransform(position, orientation, scale) {
        if (this.transform === undefined)
            (this.transform = new LocalTransformFacet());
        copyToNumber3(position, this.transform.position);
        copyToNumber4(orientation, this.transform.orientation);
        copyToNumber3(scale, this.transform.scale);
        return this;
    }
    /**
     * Updates the local position from a vector.
     * @param position Local position to apply.
     */
    setPositionVector(position) {
        if (this.transform === undefined)
            (this.transform = new LocalTransformFacet());
        copyToNumber3(position, this.transform.position);
        return this;
    }
    /**
     * Updates the local position from scalar components.
     * @param x Local X position.
     * @param y Local Y position.
     * @param z Local Z position.
     */
    setPosition(x, y, z) {
        if (this.transform === undefined)
            (this.transform = new LocalTransformFacet());
        this.transform.position[0] = x;
        this.transform.position[1] = y;
        this.transform.position[2] = z;
        return this;
    }
    /**
     * Updates the local orientation from a quaternion.
     * @param orientation Local orientation to apply.
     */
    setOrientation(orientation) {
        if (this.transform === undefined)
            (this.transform = new LocalTransformFacet());
        copyToNumber4(orientation, this.transform.orientation);
        return this;
    }
    /**
     * Updates the local orientation from an axis-angle pair.
     * @param axisX X component of the rotation axis.
     * @param axisY Y component of the rotation axis.
     * @param axisZ Z component of the rotation axis.
     * @param angle Rotation angle in radians.
     */
    setOrientationAxisAndAngle(axisX, axisY, axisZ, angle) {
        if (this.transform === undefined)
            (this.transform = new LocalTransformFacet());
        copyToNumber4(tmpQuaternion.setFromAxisAngle(tmpVector31.set(axisX, axisY, axisZ), angle), this.transform.orientation);
        return this;
    }
    /**
     * Updates the local orientation from an axis vector and angle.
     * @param axis Rotation axis.
     * @param angle Rotation angle in radians.
     */
    setOrientationAxisVectorAndAngle(axis, angle) {
        if (this.transform === undefined)
            (this.transform = new LocalTransformFacet());
        copyToNumber4(tmpQuaternion.setFromAxisAngle(axis, angle), this.transform.orientation);
        return this;
    }
    /**
     * Updates the local orientation from Euler angles.
     * @param x Rotation around the X axis.
     * @param y Rotation around the Y axis.
     * @param z Rotation around the Z axis.
     * @param order Optional Euler order.
     */
    setOrientationEuler(x, y, z, order) {
        if (this.transform === undefined)
            this.transform = new LocalTransformFacet();
        copyToNumber4(tmpQuaternion.setFromEuler(tmpEuler.set(x, y, z, order)), this.transform.orientation);
        return this;
    }
    /**
     * Rotates the model so its forward direction points at the provided target position.
     * @param x Target X position.
     * @param y Target Y position.
     * @param z Target Z position.
     */
    lookAt(x, y, z) {
        if (this.transform === undefined)
            (this.transform = new LocalTransformFacet());
        tmpMatrix4.lookAt(tmpVector33.fromArray(this.transform.position), tmpVector31.set(x, y, z), tmpVector32.set(0, 1, 0));
        copyToNumber4(tmpQuaternion.setFromRotationMatrix(tmpMatrix4), this.transform.orientation);
        return this;
    }
    /**
     * Updates the local scale from a vector.
     * @param scale Local scale to apply.
     */
    setScaleVector(scale) {
        if (this.transform === undefined)
            (this.transform = new LocalTransformFacet());
        copyToNumber3(scale, this.transform.scale);
        return this;
    }
    /**
     * Updates the local scale from scalar components.
     * @param width Local X scale.
     * @param height Local Y scale.
     * @param depth Local Z scale.
     */
    setScale(width, height, depth) {
        if (this.transform === undefined)
            (this.transform = new LocalTransformFacet());
        this.transform.scale[0] = width;
        this.transform.scale[1] = height;
        this.transform.scale[2] = depth;
        return this;
    }
    /**
     * Applies the same scale factor to all axes.
     * @param scale Uniform scale factor.
     */
    setUniformScale(scale) {
        this.setScaleVector(tmpVector31.setScalar(scale));
        return this;
    }
    /**
     * Sets the RenderShape and CollisionShape of the model.
     * @see setRenderShape()
     * @see setCollisionShape()
     * @param shape Primitive shape used for both rendering and collision.
     */
    setShape(shape) {
        return this.setRenderShape(shape).setCollisionShape(shape);
    }
    /**
     * Sets the RenderShape of the model.
     * This shape will be used for the PrimitiveMesh-facet of the model.
     * The ModelHelper will generate a PrimitiveMesh-facet for the model, if
     *  a RenderShape is set and no other renderable (Mesh, MapFacet, Text, Image or Arc) is specified.
     */
    setRenderShape(shape) {
        this.renderShape = shape;
        return this;
    }
    /**
     * Set the shape that will be used for the CollisionFacet of the model.
     * @param shape Collision shape to assign.
     */
    setCollisionShape(shape) {
        this.collisionShape = shape;
        return this;
    }
    /**
     * Sets a shared color hint and updates compatible renderables immediately when possible.
     * @param color Packed color value to store.
     * @param materialName Optional mesh material name that should receive the color override.
     */
    setColor(color, materialName) {
        this.color = color;
        if ((this.renderable !== undefined) && ArcFacet.is(this.renderable)) {
            this.renderable.color = color;
        }
        if ((this.renderable !== undefined) && MeshFacet.is(this.renderable) && materialName !== undefined) {
            this.renderable.params.colors = { materialName: color };
        }
        return this;
    }
    /**
     * Stores material properties used by generated primitive renderables.
     * @param material Material properties to store.
     */
    setMaterial(material) {
        this.material = material;
        return this;
    }
    /**
     * Configures the model to render an imported mesh.
     * @param meshName Mesh asset name or identifier.
     */
    setMesh(meshName, castShadow, receiveShadow) {
        this.renderable = new MeshFacet(meshName, undefined, castShadow, receiveShadow, {
            needsRescale: false
        });
        return this;
    }
    /**
     * Configures the model to render as an arc.
     * @param arcLength Arc length in radians or world units, depending on the renderable.
     * @param material Optional material properties for the arc.
     */
    setArc(arcLength, material) {
        this.renderable = new ArcFacet(arcLength, this.color !== undefined ? this.color : 0xff00ff, material);
        return this;
    }
    /**
     * Configures the model to render as a truncated cone primitive.
     * @param ratio Radius ratio used by the truncated cone.
     * @param color Packed color applied to the cone.
     * @param material Optional material properties for the cone.
     */
    setTruncatedCone(ratio, poitUp, color, material) {
        this.renderable = new TruncatedConeFacet(ratio, poitUp, color, material);
        return this;
    }
    /**
     * Configures the model to render a map texture for the specified geo location.
     * @param latitude Latitude at the map center.
     * @param longitude Longitude at the map center.
     * @param zoom Map zoom level.
     * @param width Target map texture width.
     * @param height Target map texture height.
     */
    setMap(latitude, longitude, zoom, width = 4096, height = 4096) {
        this.renderable = new MapFacet(latitude, longitude, zoom, width, height);
        return this;
    }
    /**
     * Configures the model to render localized text.
     * @param text Localized text payload to render.
     * @param params Optional text-rendering parameters.
     */
    setText(text, params) {
        this.renderable = new TextFacet(text, params);
        return this;
    }
    /**
     * Configures the model to render an image.
     * @param url Image URL or asset path.
     * @param param Optional image rendering parameters.
     */
    setImage(url, param) {
        this.renderable = new ImageFacet(url, param);
        return this;
    }
    /**
     * Adds a label facet with common label presentation options.
     * @param params Label text and presentation options.
     */
    setLabel(params) {
        return this.setFacet(new LabelFacet(params.text, params));
    }
    /**
     * Adds a tooltip facet to the model.
     * @param text Tooltip text.
     * @param align Optional text alignment.
     * @param avatar Optional avatar reference used by the tooltip.
     * @param placement Optional tooltip placement.
     * @param color Optional tooltip background color.
     * @param borderColor Optional tooltip border color.
     */
    setTooltip(text, align, avatar, placement, color, borderColor) {
        return this.setFacet(new TooltipInfoFacet(text, align, avatar, placement, color, borderColor));
    }
    /**
     * Sets the mass used by physics-related facets.
     * @param mass Physical mass value to store.
     */
    setMass(mass) {
        this.mass = mass;
        return this;
    }
    /**
     * Sets the friction used by physics-related facets.
     * @param friction Physical friction value to store.
     */
    setFriction(friction) {
        this.friction = friction;
        return this;
    }
    /**
     * Adds a line strip facet for drawing a polyline through multiple points.
     * @param color Packed line color.
     * @param fussyness Optional noise/fuzziness amount.
     * @param noiseFrequency Optional procedural noise frequency.
     * @param noiseVelocity Optional procedural noise velocity.
     */
    setLineStrip(points, color, thickness, fussyness, noiseFrequency, noiseVelocity, noShading) {
        return this.setFacet(new LineStrip(points, color, thickness, fussyness, noiseFrequency, noiseVelocity, noShading));
    }
    /**
     * Registers a named connection line from this model to another model.
     * @param targetModelId Target model that should receive the connection.
     * @param name Unique connection name for that target.
     * @param params Visual connection-line parameters.
     */
    addConnection(targetModelId, name, params) {
        const target = targetModelId.uid;
        const connections = (this.connections[target] = this.connections[target] ?? {});
        connections[name] = params;
        return this;
    }
    /**
     * Configures whether the model should receive a selection highlight.
     * @param b Whether the model should be highlighted when selected.
     * @param color Optional override color for the selection highlight.
     */
    setHighlightWhenSelected(b, color) {
        this.highlightSelection = b;
        this.highlightSelectionColor = color;
        return this;
    }
    /**
     * Marks the model so its creation and updates stay local to the current peer.
     * @param b Whether the model should stay local-only.
     */
    setNetworkLocalOnly(b = true) {
        this.networkLocalOnly = b;
        return this;
    }
    /**
     * Adds one interaction behavior to the model.
     * @param behavior Behavior to append.
     */
    addInteractionBehavior(behavior) {
        this.behavior.push(behavior);
        return this;
    }
    /**
     * Adds several interaction behaviors to the model.
     * @param behaviors Behaviors to append.
     */
    addInteractionBehaviors(behaviors) {
        this.behavior.push(...behaviors);
        return this;
    }
    /**
     * Sets actions triggered by a click interaction.
     * @param actions Actions to run on click.
     */
    setOnClickActions(actions) {
        this.onClick = actions;
        return this;
    }
    /**
     * Sets actions triggered by a double click interaction.
     * @param actions Actions to run on double click.
     */
    setOnDoubleClickActions(actions) {
        this.onDoubleClick = actions;
        return this;
    }
    /**
     * Supplies descriptive text for interaction UIs.
     * @param description Human-readable interaction description.
     */
    setInteractionDescription(description) {
        this.interactionDescription = description;
        return this;
    }
    /**
     * Adds metadata to the model.
     * @param metadata Metadata payload to store.
     */
    addMetaData(metadata) {
        return this.setFacet(new MetaDataFacet(metadata));
    }
    /**
     * Adds an audio source facet to the model.
     * @param url Audio URL or asset path.
     * @param params Optional audio source parameters.
     */
    setAudioSource(url, params) {
        return this.setFacet(new AudioSourceFacet(url, params));
    }
    /**
     * Adds a light source facet to the model.
     * @param params Light source parameters to store.
     */
    setLightSource(params) {
        return this.setFacet(new LightSource(params));
    }
    /**
     * Collects create and update messages for this helper, optionally including its descendants.
     * @param busMessages If set, the messages will be added to the given array instead of creating a new one.
     * @param includeChildren Whether child helpers should contribute their messages as well.
     */
    getMessages(busMessages, includeChildren = true) {
        const messages = busMessages ?? [];
        this.getCreateSubModelMessages(messages, includeChildren);
        this.getUpdateFacetOnModelMessages(messages, includeChildren);
        return messages;
    }
    /**
     * Collects the model creation messages required for this helper and optionally its descendants.
     */
    getCreateSubModelMessages(messages, includeChildren = true) {
        if (!this.isExistingModel) {
            messages.push(new CreateSubModelMessage(this.modelId.uid));
        }
        if (this.networkLocalOnly) {
            messages.push(new NetworkLocalOnly(this.modelId.uid));
        }
        if (includeChildren) {
            for (const child of this.children) {
                child.getCreateSubModelMessages(messages, true);
            }
        }
    }
    /**
     * Collects facet update messages representing the current helper state.
     */
    getUpdateFacetOnModelMessages(messages, includeChildren = true) {
        for (const facet of this.facets.values()) {
            this.addUpdateFacetOnModelMessage(messages, facet);
        }
        this.addUpdateFacetOnModelMessage(messages, this.transform);
        if (Object.keys(this.connections).length > 0) {
            this.addUpdateFacetOnModelMessage(messages, new ConnectionLines(this.connections));
        }
        if (this.renderable !== undefined) {
            this.addUpdateFacetOnModelMessage(messages, this.renderable);
        }
        else if (this.renderShape !== undefined) {
            this.addUpdateFacetOnModelMessage(messages, new PrimitiveMeshFacet(this.renderShape, this.color !== undefined ? this.color : 0xff00ff, this.material));
        }
        const behaviors = this.getInteractionBehavior();
        const hasInteraction = behaviors.length > 0 || this.onClick !== undefined || this.onDoubleClick !== undefined || this.contextMenuUid !== undefined;
        if (hasInteraction) {
            this.addUpdateFacetOnModelMessage(messages, new InteractionFacet({
                behaviors,
                onClick: this.onClick,
                onDoubleClick: this.onDoubleClick,
                description: this.interactionDescription
            }));
        }
        const selectable = hasInteraction || this.highlightSelection !== undefined;
        if (this.highlightSelection !== undefined) {
            this.addUpdateFacetOnModelMessage(messages, new SelectionHighlightFacet(this.highlightSelection, this.highlightSelectionColor));
        }
        // TODO: Do we need this? CollisionFacets are already in this.facets
        if (selectable || this.mass !== undefined) {
            if (this.collisionShape !== undefined) {
                this.addUpdateFacetOnModelMessage(messages, new CollisionFacet(this.collisionShape, selectable));
            }
        }
        if (includeChildren) {
            for (const child of this.children) {
                child.getUpdateFacetOnModelMessages(messages, true);
            }
        }
    }
    /**
     * Builds the final list of interaction behaviors implied by the helper configuration.
     */
    getInteractionBehavior() {
        const behavior = [];
        behavior.push(...this.behavior);
        (this.roomInteraction !== undefined) && behavior.push(this.roomInteraction);
        this.facets.has(TooltipInfoFacet.typestatic) && behavior.push(new ShowTooltipsBehavior());
        return behavior;
    }
    /**
     * Appends a facet update message when a facet is present.
     */
    addUpdateFacetOnModelMessage(messages, facet) {
        (facet !== undefined) && messages.push(new UpdateFacetOnModelMessage(this.modelId.uid, facet));
    }
}

/**
 * Specialized model helper for room models and room-specific configuration facets.
 */
class RoomHelper extends ModelHelper {
    autoScaleChildrenParams;
    roomParameterModel;
    /**
     * @param modelId Identifier of the room model.
     * @param isExistingModel Marks the helper as targeting an already existing room.
     */
    constructor(modelId, isExistingModel) {
        super(modelId, isExistingModel);
        if (!modelId.isRoom()) {
            throw new Error(`modelId '${modelId.toString()}' does not define a room`);
        }
    }
    /**
     * Updates how peers can enter and auto-load the room.
     * @param autoload Whether selecting the room should auto-load it.
     * @param entry_point Optional room entry position.
     */
    updateRoomInteraction(autoload, entry_point) {
        this.roomInteraction = new RoomInteraction(autoload, entry_point);
        return this;
    }
    /**
     * Lazily creates the internal child model that stores room parameter facets.
     */
    getParameterModel() {
        if (!this.roomParameterModel) {
            this.roomParameterModel = this.createChildModel("roomParameters");
        }
        return this.roomParameterModel;
    }
    /**
     * Stores room-specific button and speech command configuration.
     * @param buttonConfig Button configuration available inside the room.
     * @param availableSpeechCommands Speech commands available inside the room.
     */
    setRoomInteraction(buttonConfig, availableSpeechCommands) {
        this.getParameterModel().setFacet(new RoomSpecificButtonConfigFacet(buttonConfig));
        this.getParameterModel().setFacet(new RoomSpecificSpeechCommandsFacet(availableSpeechCommands));
    }
    /**
     * Stores environment parameters that should be applied when the room is active.
     * @param params Environment parameters to apply while the room is active.
     */
    setRoomEnvironmentParameters(params) {
        this.getParameterModel().setFacet(new RoomEnvironmentFacet(params));
    }
    /**
     * Forwards room creation message collection to the base helper.
     */
    getCreateSubModelMessages(messages, includeChildren = true) {
        super.getCreateSubModelMessages(messages, includeChildren);
    }
    /**
     * Adds room-specific facet update messages after the standard model helper updates.
     */
    getUpdateFacetOnModelMessages(messages, includeChildren = true) {
        super.getUpdateFacetOnModelMessages(messages, includeChildren);
        if (this.autoScaleChildrenParams) {
            messages.push(new UpdateFacetOnModelMessage(this.modelId.uid, new AutoScaleChildrenFacet(this.autoScaleChildrenParams.relativeMargin, this.autoScaleChildrenParams.relativeCenter)));
        }
    }
}

/** Reuses temporary objects so hot paths can avoid repeated allocations. */
class DataPool {
    /** Pool entries that are currently available for reuse. */
    objects = [];
    capacity = 0;
    createdObjects = new WeakSet();
    lendObjects = new WeakSet();
    /**
     * Creates a pool and preallocates an initial average working size.
     * @param initialSize Expected average number of concurrently needed objects.
     */
    constructor(initialSize) {
        this.grow(initialSize);
    }
    /// Getter and setter ///
    /**
     * Returns how many pooled instances are currently idle and ready to be acquired.
     */
    get size() {
        return this.objects.length;
    }
    /**
     * Returns the total number of instances currently owned by the pool.
     */
    get totalSize() {
        return this.capacity;
    }
    /** Returns whether at least one pooled instance is currently checked out by a caller. */
    inUse() {
        return this.size !== this.capacity;
    }
    /// Public pool methods ///
    /**
     * Retrieves a pooled object, growing the pool automatically when necessary.
     * @returns Reusable object instance owned by the caller until released again.
     */
    acquire() {
        if (this.empty())
            this.grow(Math.max(1, this.capacity * 2)); // double capacity
        const object = this.objects.pop();
        if (object === undefined)
            throw new Error("Out of bounds");
        this.lendObjects.add(object);
        return object;
    }
    /**
     * Returns a previously acquired object to the pool.
     * @param object Object previously obtained through {@link acquire}.
     */
    release(object) {
        if (!this.createdObjects.has(object))
            return; // foreign object
        if (!this.lendObjects.has(object))
            return; // double release
        this.lendObjects.delete(object);
        this.objects.push(object);
    }
    /**
     * Destroys every currently stored instance after all borrowed objects have been returned.
     * This method throws an error if the pool is still in use.
     */
    clear() {
        if (this.inUse())
            throw new Error("Pool is still in use");
        for (const object of this.objects) {
            this.destroyObject?.(object);
            this.createdObjects.delete(object);
        }
    }
    /// Private helper methods ///
    empty() {
        return this.objects.length === 0;
    }
    grow(newCapacity) {
        for (let i = this.capacity; i < newCapacity; i++) {
            const object = this.createObject();
            this.createdObjects.add(object);
            this.objects.push(object);
            this.capacity++;
        }
    }
}

/**
 * Takes a number, returns the name of the month.
 * Begins with 1 for January/"Januar" (unless you set the zeroBased-parameter to true).
 * Todo: Not yet language-sensitive.
 * @param month Number between 1 and 12. (Or between 0 and 11 if zeroBased is true.)
 * @param zeroBased If set to true, counting starts at 0 for January.
 */
function monthIndexToName(month, zeroBased = false) {
    if (!zeroBased && (month < 1 || month > 12)) {
        console.warn(`[Common] Invalid month: ${month} (not zero-based)`);
    }
    if (zeroBased && (month < 0 || month > 11)) {
        console.warn(`[Common] Invalid month: ${month} (zero-based)`);
    }
    const months = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
    if (zeroBased) {
        return months[month];
    }
    else {
        return months[month - 1];
    }
}
/** Returns whether a year has an extra leap day in February. */
function isLeapYear(year) {
    if (year % 4 != 0)
        return false;
    return year % 100 != 0 || year % 400 == 0;
}
/** Returns the day number within the year for a given date. */
function getDayOfYear(date) {
    const dayCount = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
    const month = date.getMonth();
    let dayOfYear = dayCount[month] + date.getDate();
    if (month > 1 && isLeapYear(date.getFullYear()))
        dayOfYear++;
    return dayOfYear;
}

/**
 * Number of spaces added for each nested formatting level.
 */
const INDENT_STEP = 2;
/**
 * Creates the indentation prefix used by the formatter helpers.
 */
const formatIndent = (indent, symbol = " ") => symbol.repeat(indent);
/**
 * Type guard for plain objects
 */
const isObject = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
/**
 * Pretty prints any value (core recursive formatter)
 */
const formatValue = (value, indent) => {
    // Facet
    if (value instanceof Facet) {
        return formatFacet(value, indent);
    }
    // Array
    if (Array.isArray(value)) {
        if (value.length === 0)
            return "[]";
        return `\n` + value
            .map((item) => `${formatIndent(indent)} - ${formatValue(item, indent + INDENT_STEP)}`)
            .join("\n");
    }
    // Object
    if (isObject(value)) {
        return formatObject(value, indent);
    }
    // Primitive
    return String(value);
};
/**
 * Pretty prints a plain object into the indented debug format used throughout FlaVR logs.
 * @param obj Object whose enumerable properties should be rendered.
 * @param indent Initial indentation level in spaces.
 */
const formatObject = (obj, indent = 0) => {
    const entries = Object.entries(obj);
    if (entries.length === 0)
        return "{}";
    return `\n` + entries
        .map(([key, value]) => `${formatIndent(indent)} - ${key}=${formatValue(value, indent + INDENT_STEP)}`)
        .join("\n");
};
/**
 * Pretty prints a facet including its type and enumerable data fields.
 * @param facet Facet instance to format for logs or diagnostics.
 * @param indent Initial indentation level in spaces.
 */
const formatFacet = (facet, indent = 0) => {
    const header = `${formatIndent(indent)}${facet.type}`;
    const properties = Object.entries(facet)
        .filter(([key]) => key !== "type")
        .map(([key, value]) => `${formatIndent(indent + INDENT_STEP)} - ${key}=${formatValue(value, indent + INDENT_STEP * 2)}`)
        .join("\n");
    return properties ? `${header}\n${properties}` : header;
};
/**
 * Pretty prints a bus message including its type and enumerable payload fields.
 * @param message Message instance to format for logs or diagnostics.
 * @param indent Initial indentation level in spaces.
 */
const formatMessage = (message, indent = 0) => {
    const header = `${formatIndent(indent)}${message.type}`;
    const properties = Object.entries(message)
        .filter(([key]) => key !== "type")
        .map(([key, value]) => `${formatIndent(indent + INDENT_STEP)} - ${key}=${formatValue(value, indent + INDENT_STEP * 2)}`)
        .join("\n");
    return properties ? `${header}\n${properties}` : header;
};

/**
 * Calls a provided callback every N-milliseconds
 */
class RateLimiter {
    intervalMilliseconds;
    lastTimestamp = 0;
    elapsedTime = 0;
    wasReset = true;
    /**
     * Creates a limiter that spaces executions by the given interval.
     * @param intervalMilliseconds Minimum time that should pass between callback executions.
     */
    constructor(intervalMilliseconds) {
        this.intervalMilliseconds = intervalMilliseconds;
    }
    /**
     * Updates the interval and resets the internal timing state.
     * @param milliseconds New minimum time between executions.
     */
    setInterval(milliseconds) { this.intervalMilliseconds = milliseconds; this.reset(); }
    /** Resets the limiter so the next due-check executes immediately. */
    reset() { this.elapsedTime = 0; this.wasReset = true; }
    /**
     * Calls the provided function if a specified time interval has been exceeded. TimestampMilliseconds is used to
     * compute the delta time since the function is called last.
     * @param timestampMilliseconds Current timestamp used to advance the internal timer state.
     * @param func Callback that should run when the limiter becomes due.
     */
    callIfDue(timestampMilliseconds, func) {
        this.lastTimestamp = (this.wasReset) ? timestampMilliseconds : this.lastTimestamp;
        let dtMilliseconds = timestampMilliseconds - this.lastTimestamp;
        this.lastTimestamp = timestampMilliseconds;
        // We guarantee that the function is called the first time callIfDue is invoked
        if (this.wasReset) {
            this.wasReset = false;
            func();
            dtMilliseconds = 0;
        }
        this.elapsedTime += dtMilliseconds;
        if (this.elapsedTime >= this.intervalMilliseconds) {
            this.elapsedTime -= this.intervalMilliseconds;
            func();
        }
    }
}

const PI_TIMES_TAU = Math.PI * (3 - Math.sqrt(5));
/**
 * Distributes elements on a sphere using Fibonacci sampling for even coverage.
 */
class FibonacciSphereArrangement {
    sphereRadius;
    sorting;
    yAxisMapping;
    /**
     * @param sphereRadius Radius of the sphere that receives the arranged elements.
     * @param sorting Required ordering used before the spherical positions are assigned.
     * @param yAxisMapping Optional custom vertical placement for each element in the range `[-1, 1]`.
     */
    constructor(sphereRadius, sorting, yAxisMapping) {
        this.sphereRadius = sphereRadius;
        this.sorting = sorting;
        this.yAxisMapping = yAxisMapping;
    }
    /**
     * Repositions the elements on the surface of the configured sphere.
     */
    arrangeElements(elements) {
        const sortedElements = elements.sort(this.sorting);
        for (let i = 0, l = sortedElements.length; i < l; ++i) {
            const element = sortedElements[i];
            const pos = element.position;
            const phi = i * PI_TIMES_TAU;
            const y = this.yAxisMapping ? this.yAxisMapping(element) : (2 * i) / (l - 1) - 1;
            const r = Math.sqrt(1 - y * y);
            pos.x = this.sphereRadius * r * Math.sin(phi);
            pos.y = this.sphereRadius * y;
            pos.z = this.sphereRadius * r * Math.cos(phi);
        }
    }
}

/**
 * Splits elements into horizontal bands and arranges each band on a sphere ring.
 */
class LayerdSphereArrangement {
    numLayers;
    sphereRadius;
    yAxisMapping;
    sorting;
    useQuantiles;
    /**
     * @param numLayers Target number of vertical layers.
     * @param sphereRadius Radius of the sphere that defines the layer rings.
     * @param yAxisMapping Maps each element to the value used for layer assignment.
     * @param sorting Ordering applied within each individual layer.
     * @param useQuantiles When enabled, fills layers by rank instead of absolute mapped values.
     */
    constructor(numLayers, sphereRadius, yAxisMapping, sorting, useQuantiles) {
        this.numLayers = numLayers;
        this.sphereRadius = sphereRadius;
        this.yAxisMapping = yAxisMapping;
        this.sorting = sorting;
        this.useQuantiles = useQuantiles;
    }
    /**
     * Repositions the elements by first assigning them to layers and then spreading each layer around the sphere.
     */
    arrangeElements(elements) {
        const layers = this.assingElementsToLayers(elements);
        const numLayers = layers.length;
        let distanceScale = this.sphereRadius;
        if (numLayers === 1) {
            distanceScale *= (layers[0].length - 1) / (this.numLayers - 1);
        }
        const yDistance = 2 / numLayers;
        let yOffset = -1 + 0.5 * yDistance;
        for (let elements of layers) {
            const y = yOffset;
            const r = Math.sqrt(1 - y * y);
            elements = elements.sort(this.sorting);
            for (let i = 0, l = elements.length; i < l; ++i) {
                const pos = elements[i].position;
                const alpha = (2 * Math.PI * i) / l;
                pos.x = distanceScale * r * Math.sin(alpha);
                pos.y = distanceScale * y;
                pos.z = distanceScale * r * Math.cos(alpha);
            }
            yOffset += yDistance;
        }
    }
    /**
     * Buckets elements into layers either by quantiles or by mapping values into equal-width ranges.
     */
    assingElementsToLayers(elements) {
        if (elements.length < this.numLayers)
            return [elements];
        const layers = [];
        if (this.useQuantiles) {
            elements = elements.sort((a, b) => this.yAxisMapping(a) - this.yAxisMapping(b));
            for (let i = 0, l = elements.length; i < l; ++i) {
                const layerIndex = Math.floor((this.numLayers * i) / l);
                while (layers.length <= layerIndex)
                    layers.push([]);
                layers[layerIndex].push(elements[i]);
            }
        }
        else {
            let min = Number.POSITIVE_INFINITY;
            let max = Number.NEGATIVE_INFINITY;
            for (const element of elements) {
                const value = this.yAxisMapping(element);
                min = Math.min(min, value);
                max = Math.max(max, value);
            }
            for (const element of elements) {
                const value = this.yAxisMapping(element);
                let layerIndex = Math.floor(mapLinear(value, min, max, 0, this.numLayers));
                layerIndex = clamp(layerIndex, 0, this.numLayers - 1);
                while (layers.length <= layerIndex)
                    layers.push([]);
                layers[layerIndex].push(element);
            }
        }
        return layers;
    }
}

/**
 * Computes the 2D position for an item on a square spiral.
 * The first point is centered at `(0, 0)` and subsequent points expand ring by ring.
 * @param index Index in the square spiral.
 * @param distance Distance between adjacent spiral positions.
 * @returns Spiral position in 2D space.
 */
function calculatePositionOnSquareSpiral(index, distance = 1) {
    // p the sum of point in inner square
    // a the position on the current square
    // n = p + a
    // when you input index, the first entry will be centered
    if (index <= 0) {
        return new Vector2();
    }
    const r = Math.floor((Math.sqrt(index) - 1) / 2) + 1;
    // compute radius : inverse arithmetic sum of 8+16+24+...=
    const p = (8 * r * (r - 1)) / 2;
    // compute total point on radius -1 : arithmetic sum of 8+16+24+...
    const en = r * 2;
    const a = (index - p) % (r * 8);
    const pos = new Vector2();
    switch (Math.floor(a / (r * 2))) {
        // find the face : 0 top, 1 right, 2, bottom, 3 left
        case 0:
            {
                pos.x = a - r;
                pos.y = -r;
            }
            break;
        case 1:
            {
                pos.x = r;
                pos.y = (a % en) - r;
            }
            break;
        case 2:
            {
                pos.x = r - (a % en);
                pos.y = r;
            }
            break;
        case 3:
            {
                pos.x = -r;
                pos.y = r - (a % en);
            }
            break;
    }
    pos.x *= distance;
    pos.y *= distance;
    return pos;
}
/**
 * Places elements on the XZ plane in an expanding square spiral.
 */
class SquareSpiralArrangement {
    distance;
    sorting;
    /**
     * @param distance Spacing between neighboring spiral positions.
     * @param sorting Optional ordering applied before elements are positioned.
     */
    constructor(distance = 1, sorting) {
        this.distance = distance;
        this.sorting = sorting;
    }
    /**
     * Repositions each element on the spiral while keeping the Y coordinate at zero.
     */
    arrangeElements(elements) {
        const sortedElements = this.sorting ? elements.sort(this.sorting) : elements;
        for (let i = 0, l = sortedElements.length; i < l; ++i) {
            const element = sortedElements[i];
            const pos = element.position;
            const pos2D = calculatePositionOnSquareSpiral(i, this.distance);
            pos.x = pos2D.x;
            pos.y = 0;
            pos.z = pos2D.y;
        }
    }
}

const sin30 = Math.sin(Math.PI / 3);
const cos30 = Math.cos(Math.PI / 3);
/**
 * Computes the 2D position for an item on a hexagonal spiral.
 * The first point is centered at `(0, 0)` and subsequent points expand ring by ring.
 * @param index Index in the hexagonal spiral.
 * @param distance Distance between adjacent spiral positions.
 * @returns Spiral position in 2D space.
 */
function calculatePositionOnHexagonalSpiral(index, distance) {
    // p the sum of point in inner hexagon
    // a the position on the current hexagon
    // n = p + a
    // when you input index, the first entry will be centered
    if (index <= 0) {
        return new Vector2(0, 0);
    }
    // num hexagons in radius = 1 + 6 + 12 + 18 ... = 1 + SUM(6(i-1)² : 0<i<=r) = 1 + 3*(r-)*(r-1);
    const r = Math.ceil(0.5 + Math.sqrt(index / 3 + 0.25)) - 1;
    const p = 1 + 3 * r * (r - 1);
    const offset = index - p;
    const pos = new Vector2(0, 0);
    const a = offset % r;
    switch (Math.floor(offset / r)) {
        // find the face : 0 top, 1 right, 2, bottom, 3 left
        case 0: {
            pos.x = r - a * cos30;
            pos.y = a * sin30;
            break;
        }
        case 1: {
            pos.x = r * cos30 - a;
            pos.y = r * sin30;
            break;
        }
        case 2: {
            pos.x = -r * cos30 - a * cos30;
            pos.y = r * sin30 - a * sin30;
            break;
        }
        case 3: {
            pos.x = -r + a * cos30;
            pos.y = -a * sin30;
            break;
        }
        case 4: {
            pos.x = -r * cos30 + a;
            pos.y = -r * sin30;
            break;
        }
        case 5: {
            pos.x = +r * cos30 + a * cos30;
            pos.y = -r * sin30 + a * sin30;
            break;
        }
    }
    pos.x *= distance;
    pos.y *= distance;
    return pos;
}
/**
 * Places elements on the XZ plane in concentric hexagonal rings.
 */
class HexagonalSpiralArrangement {
    distance;
    sorting;
    /**
     * @param distance Spacing between neighboring spiral positions.
     * @param sorting Optional ordering applied before elements are positioned.
     */
    constructor(distance = 1, sorting) {
        this.distance = distance;
        this.sorting = sorting;
    }
    /**
     * Repositions each element on the spiral while keeping the Y coordinate at zero.
     */
    arrangeElements(elements) {
        const sortedElements = this.sorting ? elements.sort(this.sorting) : elements;
        for (let i = 0, l = sortedElements.length; i < l; ++i) {
            const element = sortedElements[i];
            const pos = element.position;
            const pos2D = calculatePositionOnHexagonalSpiral(i, this.distance);
            pos.x = pos2D.x;
            pos.y = 0;
            pos.z = pos2D.y;
        }
    }
}

/** Stores at most one callback and invokes it later on demand. */
class Delegate {
    callback;
    /**
     * Replaces the current callback.
     * @param callback Callback that should be invoked later.
     */
    set(callback) {
        this.callback = callback;
        return this;
    }
    /** Removes the current callback. */
    clear() {
        this.callback = undefined;
        return this;
    }
    /** Invokes the current callback if one is registered. */
    invoke() {
        if (!this.callback)
            return;
        this.callback();
        return this;
    }
    /**
     * Invokes a callback that resolves or rejects asynchronously via continuation arguments.
     * @returns Promise that settles through the callback's continuation parameters.
     */
    async invokeAsPromise() {
        if (!this.callback)
            return;
        return await new Promise((resolve, reject) => {
            try {
                ;
                this.callback(resolve, reject);
            }
            catch (error) {
                reject(error);
            }
        });
    }
}
/** Single-listener delegate for callbacks with one argument. */
class Delegate1 {
    callback;
    /**
     * Replaces the current callback.
     * @param callback Callback that should receive the delegate argument.
     */
    set(callback) {
        this.callback = callback;
        return this;
    }
    /** Removes the current callback. */
    clear() {
        this.callback = undefined;
        return this;
    }
    /**
     * Invokes the current callback if one is registered.
     * @param arg Argument forwarded to the callback.
     */
    invoke(arg) {
        if (!this.callback)
            return;
        this.callback(arg);
        return this;
    }
    /**
     * Invokes an async-style callback that settles through resolve and reject parameters.
     * @param arg Argument forwarded to the callback.
     * @returns Promise that settles through the callback's continuation parameters.
     */
    async invokeAsPromise(arg) {
        if (!this.callback)
            return;
        return await new Promise((resolve, reject) => {
            try {
                ;
                this.callback(arg, resolve, reject);
            }
            catch (error) {
                reject(error);
            }
        });
    }
}
/** Single-listener delegate for callbacks with two arguments. */
class Delegate2 {
    callback;
    /**
     * Replaces the current callback.
     * @param callback Callback that should receive the delegate arguments.
     */
    set(callback) {
        this.callback = callback;
        return this;
    }
    /** Removes the current callback. */
    clear() {
        this.callback = undefined;
        return this;
    }
    /**
     * Invokes the current callback if one is registered.
     * @param arg1 First argument forwarded to the callback.
     * @param arg2 Second argument forwarded to the callback.
     */
    invoke(arg1, arg2) {
        if (!this.callback)
            return;
        this.callback(arg1, arg2);
        return this;
    }
    /**
     * Invokes an async-style callback that settles through resolve and reject parameters.
     * @param arg First argument forwarded to the callback.
     * @param arg2 Second argument forwarded to the callback.
     * @returns Promise that settles through the callback's continuation parameters.
     */
    async invokeAsPromise(arg, arg2) {
        if (!this.callback)
            return;
        return await new Promise((resolve, reject) => {
            try {
                ;
                this.callback(arg, arg2, resolve, reject);
            }
            catch (error) {
                reject(error);
            }
        });
    }
}
/** Single-listener delegate for callbacks with three arguments. */
class Delegate3 {
    callback;
    /**
     * Replaces the current callback.
     * @param callback Callback that should receive the delegate arguments.
     */
    set(callback) {
        this.callback = callback;
        return this;
    }
    /** Removes the current callback. */
    clear() {
        this.callback = undefined;
        return this;
    }
    /**
     * Invokes the current callback if one is registered.
     * @param arg1 First argument forwarded to the callback.
     * @param arg2 Second argument forwarded to the callback.
     * @param arg3 Third argument forwarded to the callback.
     */
    invoke(arg1, arg2, arg3) {
        if (!this.callback)
            return;
        this.callback(arg1, arg2, arg3);
        return this;
    }
    /**
     * Invokes an async-style callback that settles through resolve and reject parameters.
     * @param arg First argument forwarded to the callback.
     * @param arg2 Second argument forwarded to the callback.
     * @param arg3 Third argument forwarded to the callback.
     * @returns Promise that settles through the callback's continuation parameters.
     */
    async invokeAsPromise(arg, arg2, arg3) {
        if (!this.callback)
            return;
        return await new Promise((resolve, reject) => {
            try {
                ;
                this.callback(arg, arg2, arg3, resolve, reject);
            }
            catch (error) {
                reject(error);
            }
        });
    }
}
/** Single-listener delegate for callbacks with four arguments. */
class Delegate4 {
    callback;
    /**
     * Replaces the current callback.
     * @param callback Callback that should receive the delegate arguments.
     */
    set(callback) {
        this.callback = callback;
        return this;
    }
    /** Removes the current callback. */
    clear() {
        this.callback = undefined;
        return this;
    }
    /**
     * Invokes the current callback if one is registered.
     * @param arg1 First argument forwarded to the callback.
     * @param arg2 Second argument forwarded to the callback.
     * @param arg3 Third argument forwarded to the callback.
     * @param arg4 Fourth argument forwarded to the callback.
     */
    invoke(arg1, arg2, arg3, arg4) {
        if (!this.callback)
            return;
        this.callback(arg1, arg2, arg3, arg4);
        return this;
    }
    /**
     * Invokes an async-style callback that settles through resolve and reject parameters.
     * @param arg First argument forwarded to the callback.
     * @param arg2 Second argument forwarded to the callback.
     * @param arg3 Third argument forwarded to the callback.
     * @param arg4 Fourth argument forwarded to the callback.
     * @returns Promise that settles through the callback's continuation parameters.
     */
    async invokeAsPromise(arg, arg2, arg3, arg4) {
        if (!this.callback)
            return;
        return await new Promise((resolve, reject) => {
            try {
                ;
                this.callback(arg, arg2, arg3, arg4, resolve, reject);
            }
            catch (error) {
                reject(error);
            }
        });
    }
}

const UNIT_X = new Vector3(1, 0, 0);
const UNIT_Y = new Vector3(0, 1, 0);
const UNIT_Z = new Vector3(0, 0, 1);
const FORWARD = new Vector3(0, 0, -1);
/// Temporary variables to reduce GC ///
const TEMP_VEC3_1 = new Vector3();
const TEMP_VEC3_2 = new Vector3();
const TEMP_VEC3_3 = new Vector3();
const TEMP_VEC3_4 = new Vector3();
const TEMP_QUAT_1 = new Quaternion();
const TEMP_QUAT_2 = new Quaternion();
const TEMP_MAT = new Matrix4();
/// Pools ///
/** Pool of reusable `Vector3` instances for allocation-sensitive code paths. */
class Vector3Pool extends DataPool {
    /** Creates a fresh vector instance for the pool. */
    createObject() { return new Vector3(); }
    /** Leaves pooled vectors as-is when they are discarded. */
    destroyObject(_mat) { }
}
/** Pool of reusable `Quaternion` instances for allocation-sensitive code paths. */
class QuaternionPool extends DataPool {
    /** Creates a fresh quaternion instance for the pool. */
    createObject() { return new Quaternion(); }
    /** Leaves pooled quaternions as-is when they are discarded. */
    destroyObject(_mat) { }
}
/** Pool of reusable `Matrix4` instances for allocation-sensitive code paths. */
class Matrix4Pool extends DataPool {
    /** Creates a fresh matrix instance for the pool. */
    createObject() { return new Matrix4(); }
    /** Leaves pooled matrices as-is when they are discarded. */
    destroyObject(_mat) { }
}
/// Formating ///
/** Formats vectors of different dimensions into a readable debug string. */
const formatVector = (vector) => {
    if (vector instanceof Vector2)
        return `(${vector.x}, ${vector.y})`;
    else if (vector instanceof Vector3)
        return `(${vector.x}, ${vector.y}, ${vector.z})`;
    else /* (vector instanceof Vector4) */
        return `(${vector.x}, ${vector.y}, ${vector.z}, ${vector.w})`;
};
/** Formats a quaternion into a readable debug string. */
const formatQuaternion = (quaternion) => {
    return `(${quaternion.x}, ${quaternion.y}, ${quaternion.z}, ${quaternion.w})`;
};
/** Formats a matrix by decomposing it into translation, rotation and scale for inspection. */
const formatMatrix = (matrix) => {
    matrix.decompose(TEMP_VEC3_1, TEMP_QUAT_1, TEMP_VEC3_2);
    return `Matrix4={ p=${formatVector(TEMP_VEC3_1)}, q=${formatQuaternion(TEMP_QUAT_1)}, s=${formatVector(TEMP_VEC3_2)} }`;
};
/// Quaternion helper methods ///
/**
 * Returns a new quaternion that only contains the rotational part around the world x-axis
 */
const extractXRotation = (orientation, result = new Quaternion()) => {
    const matrix = TEMP_MAT.makeRotationFromQuaternion(orientation);
    const mElements = matrix.elements;
    const pitch = Math.atan2(-mElements[9], mElements[10]);
    return result.setFromAxisAngle(UNIT_X, pitch);
};
/**
 * Returns a new quaternion that only contains the rotational part around the world y-axis
 */
const extractYRotation = (orientation, result = new Quaternion()) => {
    const matrix = TEMP_MAT.makeRotationFromQuaternion(orientation);
    const mElements = matrix.elements;
    const yaw = Math.atan2(mElements[8], mElements[10]);
    return result.setFromAxisAngle(UNIT_Y, yaw);
};
/**
 * Returns a new quaternion that only contains the rotational part around the world z-axis
 */
const extractZRotation = (orientation, result = new Quaternion()) => {
    const matrix = TEMP_MAT.makeRotationFromQuaternion(orientation);
    const mElements = matrix.elements;
    const roll = Math.atan2(-mElements[4], mElements[0]);
    return result.setFromAxisAngle(UNIT_Z, roll);
};
const removePitchFromQuaternion = (q, result = new Quaternion()) => {
    // Forward direction in world space
    const forward = TEMP_VEC3_1.copy(FORWARD).applyQuaternion(q);
    // Remove vertical component (flatten forward)
    const flatForward = TEMP_VEC3_2.copy(forward);
    flatForward.y = 0;
    // If looking straight up/down, pitch is undefined
    if (flatForward.lengthSq() < 1e-6)
        return result.copy(q);
    flatForward.normalize();
    // Compute pitch⁻¹
    const pitch = Math.atan2(forward.y, Math.sqrt(forward.x * forward.x + forward.z * forward.z));
    const invertedPitch = TEMP_QUAT_1.setFromAxisAngle(UNIT_X, pitch).invert();
    // Remove pitch: q_noPitch = q × pitch⁻¹
    return result.copy(q).multiply(invertedPitch);
};
/**
 * Removes pitch from a transform matrix while keeping position, yaw, roll, and scale intact.
 * @param matrix Matrix whose orientation should be flattened.
 * @param result Target matrix receiving the flattened transform.
 */
const removePitchFromMatrix = (matrix, result = new Matrix4()) => {
    matrix.decompose(TEMP_VEC3_3, TEMP_QUAT_2, TEMP_VEC3_4);
    const orientationWithoutPitch = removePitchFromQuaternion(TEMP_QUAT_2, TEMP_QUAT_2);
    return result.compose(TEMP_VEC3_3, orientationWithoutPitch, TEMP_VEC3_4);
};
/**
 * Computes the local transformation between parent and child from two world transformations.
 * @param childWorldTransform Child transform expressed in world space.
 * @param parentWorldTransform Parent transform expressed in world space.
 * @param result Target matrix receiving the local transform.
 */
const worldToLocalTransform = (childWorldTransform, parentWorldTransform, result = new Matrix4()) => {
    return result.copy(parentWorldTransform).invert().multiply(childWorldTransform);
};
/**
 * Converts a world position into local coordinates relative to a reference transform.
 * @param worldPosition Position expressed in world space.
 * @param worldTransform World transform that defines the local reference frame.
 * @param result Target vector receiving the local position.
 */
const worldToLocalPosition = (worldPosition, worldTransform, result = new Vector3()) => {
    return result.copy(worldPosition).applyMatrix4(TEMP_MAT.copy(worldTransform).invert());
};

/** Tracks multiple delegate subscriptions so they can be removed together later. */
class DelegateSubscription {
    subscriptions = [];
    /**
     * Remembers a delegate/callback pair for later bulk removal.
     * @param delegate Delegate that owns the callback.
     * @param callback Callback that should later be unsubscribed.
     */
    add(delegate, callback) {
        this.subscriptions.push({
            delegate,
            callback
        });
    }
    /** Unsubscribes every remembered callback from its delegate. */
    removeAll() {
        for (const sub of this.subscriptions) {
            sub.delegate.remove(sub.callback);
        }
        this.subscriptions = [];
    }
    /** Forgets remembered subscriptions without touching the delegates. */
    clear() {
        this.subscriptions = [];
    }
}
class MulticastDelegateBase {
    callbacks = new Set();
    /**
     * Registers a callback and optionally records the subscription.
     * @param callback Callback that should be added.
     * @param subsciption Optional subscription collector that should track the callback.
     */
    add(callback, subsciption) {
        this.callbacks.add(callback);
        if (subsciption) {
            subsciption.add(this, callback);
        }
    }
    /**
     * Registers a callback in the same way as {@link add}.
     * @param callback Callback that should be added.
     * @param subsciption Optional subscription collector that should track the callback.
     */
    addSafe(callback, subsciption) {
        this.callbacks.add(callback);
        if (subsciption) {
            subsciption.add(this, callback);
        }
    }
    /**
     * Removes a callback from the delegate.
     * @param callback Callback that should be unsubscribed.
     */
    remove(callback) {
        this.callbacks.delete(callback);
    }
    /** Returns the number of currently subscribed callbacks. */
    length() {
        return this.callbacks.size;
    }
    /** Removes every subscribed callback. */
    clear() {
        this.callbacks.clear();
    }
}
/** Multi-listener delegate for zero-argument callbacks. */
class MulticastDelegate extends MulticastDelegateBase {
    /** Invokes every subscribed callback. */
    invoke() {
        for (const cb of this.callbacks) {
            cb();
        }
    }
    /**
     * Invokes every callback using promise-style continuation parameters.
     * @returns Promise that resolves once every subscribed callback has settled.
     */
    async invokeAsPromise() {
        const promises = [];
        for (const cb of this.callbacks) {
            promises.push(new Promise((resolve, reject) => {
                try {
                    ;
                    cb(resolve, reject);
                }
                catch (error) {
                    reject(error);
                }
            }));
        }
        return await Promise.all(promises);
    }
}
/** Multi-listener delegate for one-argument callbacks. */
class MulticastDelegate1 extends MulticastDelegateBase {
    /** Invokes every subscribed callback. */
    invoke(arg) {
        for (const cb of this.callbacks) {
            cb(arg);
        }
    }
    /**
     * Invokes every callback using promise-style continuation parameters.
     * @param arg Argument forwarded to every callback.
     * @returns Promise that resolves once every subscribed callback has settled.
     */
    async invokeAsPromise(arg) {
        const promises = [];
        for (const cb of this.callbacks) {
            promises.push(new Promise((resolve, reject) => {
                try {
                    ;
                    cb(arg, resolve, reject);
                }
                catch (error) {
                    reject(error);
                }
            }));
        }
        return await Promise.all(promises);
    }
}
/** Multi-listener delegate for two-argument callbacks. */
class MulticastDelegate2 extends MulticastDelegateBase {
    /** Invokes every subscribed callback. */
    invoke(arg1, arg2) {
        for (const cb of this.callbacks) {
            cb(arg1, arg2);
        }
    }
    /**
     * Invokes every callback using promise-style continuation parameters.
     * @param arg First argument forwarded to every callback.
     * @param arg2 Second argument forwarded to every callback.
     * @returns Promise that resolves once every subscribed callback has settled.
     */
    async invokeAsPromise(arg, arg2) {
        const promises = [];
        for (const cb of this.callbacks) {
            promises.push(new Promise((resolve, reject) => {
                try {
                    ;
                    cb(arg, arg2, resolve, reject);
                }
                catch (error) {
                    reject(error);
                }
            }));
        }
        return await Promise.all(promises);
    }
}
/** Multi-listener delegate for three-argument callbacks. */
class MulticastDelegate3 extends MulticastDelegateBase {
    /** Invokes every subscribed callback. */
    invoke(arg1, arg2, arg3) {
        for (const cb of this.callbacks) {
            cb(arg1, arg2, arg3);
        }
    }
    /**
     * Invokes every callback using promise-style continuation parameters.
     * @param arg First argument forwarded to every callback.
     * @param arg2 Second argument forwarded to every callback.
     * @param arg3 Third argument forwarded to every callback.
     * @returns Promise that resolves once every subscribed callback has settled.
     */
    async invokeAsPromise(arg, arg2, arg3) {
        const promises = [];
        for (const cb of this.callbacks) {
            promises.push(new Promise((resolve, reject) => {
                try {
                    ;
                    cb(arg, arg2, arg3, resolve, reject);
                }
                catch (error) {
                    reject(error);
                }
            }));
        }
        return await Promise.all(promises);
    }
}
/** Multi-listener delegate for four-argument callbacks. */
class MulticastDelegate4 extends MulticastDelegateBase {
    /** Invokes every subscribed callback. */
    invoke(arg1, arg2, arg3, arg4) {
        for (const cb of this.callbacks) {
            cb(arg1, arg2, arg3, arg4);
        }
    }
    /**
     * Invokes every callback using promise-style continuation parameters.
     * @param arg First argument forwarded to every callback.
     * @param arg2 Second argument forwarded to every callback.
     * @param arg3 Third argument forwarded to every callback.
     * @param arg4 Fourth argument forwarded to every callback.
     * @returns Promise that resolves once every subscribed callback has settled.
     */
    async invokeAsPromise(arg, arg2, arg3, arg4) {
        const promises = [];
        for (const cb of this.callbacks) {
            promises.push(new Promise((resolve, reject) => {
                try {
                    ;
                    cb(arg, arg2, arg3, arg4, resolve, reject);
                }
                catch (error) {
                    reject(error);
                }
            }));
        }
        return await Promise.all(promises);
    }
}

/**
 * Base class for types that should only ever be instantiated once.
 */
class OnlyOneInstance {
    /**
     * Tracks whether a subclass has already created an instance.
     */
    static _wasCreated = false;
    /**
     * Throws when a second instance of the same subclass is constructed.
     */
    constructor() {
        const constructor = this.constructor;
        if (constructor._wasCreated) {
            throw new Error(`only one instance of the ${this.constructor.name} class may be created`);
        }
        constructor._wasCreated = true;
    }
}

/**
 * Data structure that lazily synchronizes the transform matrix with the components (position, orientation, scale)
 * and vice versa. Because of this the getter methods `transform.position`, `transform.orientation`, `transform.scale`
 * as well as `transform.matrix` return clones. The user is therefore responsible to call the respective setter
 * methods to update the state of this transform
 */
class Transform {
    pMatrix;
    pPosition;
    pOrientation;
    pScale;
    matrixChanged = false;
    componentsChanged = false;
    /**
     * Creates a transform that can be driven either from a matrix or from decomposed components.
     * @param matrix Optional initial matrix. When provided, components are derived lazily on first access.
     */
    constructor(matrix) {
        this.pMatrix = matrix ?? new Matrix4();
        this.pPosition = new Vector3();
        this.pOrientation = new Quaternion();
        this.pScale = new Vector3(1, 1, 1);
        if (matrix !== undefined)
            this.matrixChanged = true;
    }
    /**
     * Returns whether two transforms are equal up to configurable vector and quaternion tolerances.
     * @param other Transform that should be compared against this one.
     * @param props Optional tolerance overrides for vector and quaternion comparisons.
     */
    equals(other, props) {
        const vectorDelta = props?.vectorDelta ?? Number.EPSILON;
        const quaternionDelta = props?.quaternionDelta ?? Number.EPSILON;
        return (checkVector3Near(this.position, other.position, vectorDelta) &&
            checkQuaternionNear(this.orientation, other.orientation, quaternionDelta) &&
            checkVector3Near(this.scale, other.scale, vectorDelta));
    }
    /**
     * Returns a clone of the matrix, you have to set the matrix explicitly to update this transform
     * ```TS
     * const modifiedMatrix = transform.matrix.multiply(otherMatrix) // does not mutate transform.matrix
     * transform.matrix = modifiedMatrix // explicitly update transform.matrix
     * ```
     */
    get matrix() {
        if (this.componentsChanged)
            this.recomputeMatrixFromComponents();
        return this.pMatrix.clone();
    }
    /**
     * Replaces the matrix and marks the decomposed components for lazy recomputation.
     * @param matrix Matrix instance or packed matrix array that should become the new transform.
     */
    set matrix(matrix) {
        if (isNumber16(matrix)) {
            this.pMatrix.fromArray(matrix);
        }
        else {
            this.pMatrix.copy(matrix);
        }
        this.matrixChanged = true;
        // We ignore that the components might have changed
        this.componentsChanged = false;
    }
    /**
     * Copies the current matrix into the provided target or into a new matrix.
     * @param mat Optional target matrix to reuse.
     */
    getMatrix(mat = new Matrix4()) {
        if (this.componentsChanged)
            this.recomputeMatrixFromComponents();
        return mat.copy(this.pMatrix);
    }
    /**
     * Returns a clone of the position, you have to set the position explicitly to update this transform
     * ```TS
     * const modifiedPosition = transform.position.add(someVector3) // does not mutate transform.matrix
     * transform.position = modifiedPosition // explicitly update transform.position
     * ```
     */
    get position() {
        if (this.matrixChanged)
            this.recomputeComponentsFromMatrix();
        return this.pPosition.clone();
    }
    /**
     * Replaces the position component and marks the matrix for lazy recomputation.
     * @param position Vector or tuple that should become the new position.
     */
    set position(position) {
        if (this.matrixChanged)
            this.recomputeComponentsFromMatrix();
        if (isVector3(position))
            this.pPosition.copy(position);
        else
            this.pPosition.set(position[0], position[1], position[2]);
        this.componentsChanged = true;
    }
    /**
     * Copies the current position into the provided target or into a new vector.
     * @param pos Optional target vector to reuse.
     */
    getPosition(pos = new Vector3()) {
        if (this.matrixChanged)
            this.recomputeComponentsFromMatrix();
        return pos.copy(this.pPosition);
    }
    /**
     * Returns a clone of the orientation, you have to set the orientation explicitly to update this transform
     * ```TS
     * const modifiedOrientation = transform.orientation.multiply(someQuaternion) // does not mutate transform.orientation
     * transform.orientation = modifiedPosition // explicitly update transform.orientation
     * ```
     */
    get orientation() {
        if (this.matrixChanged)
            this.recomputeComponentsFromMatrix();
        return this.pOrientation.clone();
    }
    /**
     * Replaces the orientation component and marks the matrix for lazy recomputation.
     * @param orientation Quaternion or tuple that should become the new orientation.
     */
    set orientation(orientation) {
        if (this.matrixChanged)
            this.recomputeComponentsFromMatrix();
        if (isQuaternion(orientation))
            // this.pOrientation.copy(orientation).normalize()
            this.pOrientation.copy(orientation);
        else
            this.pOrientation.set(orientation[0], orientation[1], orientation[2], orientation[3]).normalize();
        this.componentsChanged = true;
    }
    /**
     * Copies the current orientation into the provided target or into a new quaternion.
     * @param ori Optional target quaternion to reuse.
     */
    getOrientation(ori = new Quaternion()) {
        if (this.matrixChanged)
            this.recomputeComponentsFromMatrix();
        return ori.copy(this.pOrientation);
    }
    /**
     * Returns a clone of the scale, you have to set the scale explicitly to update this transform
     * ```TS
     * const modifiedScale = transform.scale.add(someVector3) // does not mutate transform.scale
     * transform.scale = modifiedScale // explicitly update transform.scale
     * ```
     */
    get scale() {
        if (this.matrixChanged)
            this.recomputeComponentsFromMatrix();
        return this.pScale.clone();
    }
    /**
     * Replaces the scale component and marks the matrix for lazy recomputation.
     * @param scale Vector or tuple that should become the new scale.
     */
    set scale(scale) {
        if (this.matrixChanged)
            this.recomputeComponentsFromMatrix();
        if (isVector3(scale))
            this.pScale.copy(scale);
        else
            this.pScale.set(scale[0], scale[1], scale[2]);
        this.componentsChanged = true;
    }
    /**
     * Copies the current scale into the provided target or into a new vector.
     * @param scale Optional target vector to reuse.
     */
    getScale(scale = new Vector3()) {
        if (this.matrixChanged)
            this.recomputeComponentsFromMatrix();
        return scale.copy(this.pScale);
    }
    /**
     * Returns a clone of the components, you have to set the components explicitly to update this transform
     * ```TS
     * const comps = transform.getComponents().position.add(someVector3) // does not mutate components of transform
     * transform.setComponents(comps.position, comps.orientation, comps.scale) // explicitly update transform components
     * ```
     * @param pos Optional target vector that receives the position.
     * @param ori Optional target quaternion that receives the orientation.
     * @param sca Optional target vector that receives the scale.
     */
    getComponents(pos, ori, sca) {
        if (this.matrixChanged)
            this.recomputeComponentsFromMatrix();
        const position = pos?.copy(this.pPosition) ?? this.pPosition.clone();
        const orientation = ori?.copy(this.pOrientation) ?? this.pOrientation.clone();
        const scale = sca?.copy(this.pScale) ?? this.pScale.clone();
        return { position, orientation, scale };
    }
    /**
     * Replaces one or more transform components at once.
     * @param position Optional new position component.
     * @param orientation Optional new orientation component.
     * @param scale Optional new scale component.
     */
    setComponents(position, orientation, scale) {
        const allUndefined = position === undefined && orientation === undefined && scale === undefined;
        if (allUndefined)
            return;
        const anyUndefined = position === undefined || orientation === undefined || scale === undefined;
        if (anyUndefined && this.matrixChanged)
            this.recomputeComponentsFromMatrix();
        if (position !== undefined)
            this.position = position;
        if (orientation !== undefined)
            this.orientation = orientation;
        if (scale !== undefined)
            this.scale = scale;
        this.componentsChanged = true;
    }
    /// Private helper methods ///
    recomputeComponentsFromMatrix() {
        this.pMatrix.decompose(this.pPosition, this.pOrientation, this.pScale);
        this.matrixChanged = false;
    }
    recomputeMatrixFromComponents() {
        this.pMatrix.compose(this.pPosition, this.pOrientation, this.pScale);
        this.componentsChanged = false;
    }
}

/**
 * Serializes typed values into a binary byte stream.
 */
class ByteStreamWriter extends ByteStream {
    isReading = false;
    getUsedByteLength() {
        return this.offset;
    }
    getUnusedByteLength() {
        return this.view.byteLength - this.offset;
    }
    /**
     * Returns a slice of the backing buffer for inspection or transport.
     * @param offset Start offset of the requested slice.
     * @param size Number of bytes to include in the slice.
     */
    getBufferChunk(offset, size) {
        return this.view.buffer.slice(offset, offset + size);
    }
    uid(value) {
        this.view.setInt32(this.offset, value);
        this.offset += UID_BYTES_PER_ELEMENT;
        return value;
    }
    uidSafe(value) {
        if (this.boolean(value !== undefined)) {
            value = this.uid(value ?? 0);
        }
        else {
            this.uid(0); // We write anything because uid safe must be BOOLEAN_BYTES_PER_ELEMENT + UID_BYTES_PER_ELEMENT
        }
        return value;
    }
    typeHash(value) {
        this.view.setInt32(this.offset, value);
        this.offset += TYPE_HASH_BYTES_PER_ELEMENT;
        return value;
    }
    boolean(value) {
        this.view.setUint8(this.offset, value ? 1 : 0);
        this.offset += Uint8Array.BYTES_PER_ELEMENT;
        return value;
    }
    booleanSafe(value) {
        this.view.setUint8(this.offset, value === undefined ? 2 : value ? 1 : 0);
        this.offset += Uint8Array.BYTES_PER_ELEMENT;
        return value;
    }
    int8(value) {
        this.view.setInt8(this.offset, value);
        this.offset += Int8Array.BYTES_PER_ELEMENT;
        return value;
    }
    int8Safe(value) {
        this.view.setInt8(this.offset, value === undefined ? INT8_MIN_VALUE : value);
        this.offset += Int8Array.BYTES_PER_ELEMENT;
        return value;
    }
    uint8(value) {
        this.view.setUint8(this.offset, value);
        this.offset += Uint8Array.BYTES_PER_ELEMENT;
        return value;
    }
    uint8Safe(value) {
        this.view.setUint8(this.offset, value === undefined ? UINT8_MAX_VALUE : value);
        this.offset += Uint8Array.BYTES_PER_ELEMENT;
        return value;
    }
    enum(value) {
        return this.uint8(value);
    }
    enumSafe(value) {
        return this.uint8Safe(value);
    }
    int16(value) {
        this.view.setInt16(this.offset, value);
        this.offset += Int16Array.BYTES_PER_ELEMENT;
        return value;
    }
    uint16(value) {
        this.view.setUint16(this.offset, value);
        this.offset += Uint16Array.BYTES_PER_ELEMENT;
        return value;
    }
    uint16Safe(value) {
        this.view.setUint16(this.offset, value === undefined ? UINT16_MAX_VALUE : value);
        this.offset += Uint16Array.BYTES_PER_ELEMENT;
        return value;
    }
    int32(value) {
        this.view.setInt32(this.offset, value);
        this.offset += Int32Array.BYTES_PER_ELEMENT;
        return value;
    }
    uint32(value) {
        this.view.setUint32(this.offset, value);
        this.offset += Uint32Array.BYTES_PER_ELEMENT;
        return value;
    }
    uint32Safe(value) {
        this.view.setUint32(this.offset, value === undefined ? UINT32_MAX_VALUE : value);
        this.offset += Uint32Array.BYTES_PER_ELEMENT;
        return value;
    }
    float32(value) {
        this.view.setFloat32(this.offset, value);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        return value;
    }
    float32Safe(value) {
        this.view.setFloat32(this.offset, value === undefined ? NaN : value);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        return value;
    }
    float64(value) {
        this.view.setFloat64(this.offset, value);
        this.offset += Float64Array.BYTES_PER_ELEMENT;
        return value;
    }
    float64Safe(value) {
        this.view.setFloat64(this.offset, value === undefined ? NaN : value);
        this.offset += Float64Array.BYTES_PER_ELEMENT;
        return value;
    }
    number2(value) {
        this.view.setFloat32(this.offset, value[0]);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        this.view.setFloat32(this.offset, value[1]);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        return value;
    }
    number3(value) {
        this.view.setFloat32(this.offset, value[0]);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        this.view.setFloat32(this.offset, value[1]);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        this.view.setFloat32(this.offset, value[2]);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        return value;
    }
    number3Safe(value) {
        this.number3(value ?? [NaN, NaN, NaN]);
        return value;
    }
    number4(value) {
        this.view.setFloat32(this.offset, value[0]);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        this.view.setFloat32(this.offset, value[1]);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        this.view.setFloat32(this.offset, value[2]);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        this.view.setFloat32(this.offset, value[3]);
        this.offset += Float32Array.BYTES_PER_ELEMENT;
        return value;
    }
    number4Safe(value) {
        this.number4(value ?? [NaN, NaN, NaN, NaN]);
        return value;
    }
    number16(value) {
        for (let i = 0; i < 16; ++i) {
            this.view.setFloat32(this.offset, value[i]);
            this.offset += Float32Array.BYTES_PER_ELEMENT;
        }
        return value;
    }
    number16Safe(value) {
        this.number16(value ?? [NaN, NaN, NaN, NaN, NaN, NaN, NaN, NaN, NaN, NaN, NaN, NaN, NaN, NaN, NaN, NaN]);
        return value;
    }
    numberFloat32Array(value, length) {
        for (let i = 0; i < length; ++i) {
            value[i] = this.float32(value[i]);
        }
        return value;
    }
    numberFloat32ArraySafe(length, value) {
        if (!value) {
            value = [];
            for (let i = 0; i < length; ++i) {
                value.push(NaN);
            }
        }
        value = this.numberFloat32Array(value, length);
        return value;
    }
    /**
     * Writes a length-prefixed UTF-16 string.
     */
    utf16String(str) {
        const strLen = str.length;
        this.view.setUint32(this.offset, strLen);
        this.offset += Uint32Array.BYTES_PER_ELEMENT;
        for (let i = 0; i < strLen; ++i) {
            this.view.setUint16(this.offset, str.charCodeAt(i), true);
            this.offset += Uint16Array.BYTES_PER_ELEMENT;
        }
        return str;
    }
    utf16StringSafe(str) {
        if (str === undefined) {
            this.view.setUint32(this.offset, UINT32_MAX_VALUE);
            this.offset += Uint32Array.BYTES_PER_ELEMENT;
        }
        else {
            return this.utf16String(str);
        }
        return str;
    }
    localizationString(localization) {
        if (typeof localization === "string") {
            // localization is from type string
            this.uint8(UINT8_MAX_VALUE - 1);
            this.utf16String(localization);
        }
        else {
            let count = 0;
            const lengthOffset = this.offset;
            this.offset += Uint8Array.BYTES_PER_ELEMENT;
            this.utf16String(localization.key);
            if (localization.params) {
                for (const key in localization.params) {
                    const value = localization.params[key];
                    this.utf16String(key);
                    this.utf16String(value);
                    ++count;
                }
            }
            this.view.setUint8(lengthOffset, count);
        }
        return localization;
    }
    localizationStringSafe(localization) {
        if (localization === undefined) {
            // localization is undefined
            this.uint8(UINT16_MAX_VALUE);
            return localization;
        }
        this.localizationString(localization);
        return localization;
    }
    /**
     * Serializes one bus message including its type hash.
     */
    busMessage(msg) {
        if (msg.typeHash === undefined)
            throw new Error(`typeHash is not defined in msg ${msg.type}`);
        if (msg.serialization === undefined)
            throw new Error(`serialization is not defined in msg ${msg.type}`);
        this.typeHash(msg.typeHash);
        msg.serialization(this);
        return msg;
    }
    busMessages(messages) {
        this.uint32(messages.length);
        for (const msg of messages) {
            if (msg.typeHash === undefined)
                continue;
            if (msg.serialization === undefined)
                continue;
            this.typeHash(msg.typeHash);
            msg.serialization(this);
        }
        return messages;
    }
    busMessagesSafe(messages) {
        if (messages === undefined) {
            this.uint32(UINT32_MAX_VALUE);
            return messages;
        }
        messages = this.busMessages(messages);
        return messages;
    }
    array(arr) {
        this.uint32(arr.length);
        for (const value of arr) {
            this.type(value);
        }
        return arr;
    }
    arraySafe(arr) {
        if (arr === undefined) {
            this.uint32(UINT32_MAX_VALUE);
            return arr;
        }
        arr = this.array(arr);
        return arr;
    }
    fastFloat32Array(arr) {
        this.uint32(arr.length);
        const arrayView = new Float32Array(this.view.buffer, this.offset, arr.length);
        arrayView.set(arr);
        this.offset += Float32Array.BYTES_PER_ELEMENT * arr.length;
        return arr;
    }
    map(map) {
        this.uint32(map.size);
        for (const [key, value] of map) {
            this.type(key);
            this.type(value);
        }
        return map;
    }
    mapSafe(map) {
        if (map === undefined) {
            this.uint32(UINT32_MAX_VALUE);
            return map;
        }
        map = this.map(map);
        return map;
    }
    set(set) {
        this.uint32(set.size);
        for (const value of set) {
            this.type(value);
        }
        return set;
    }
    setSafe(set) {
        if (set === undefined) {
            this.uint32(UINT32_MAX_VALUE);
            return set;
        }
        set = this.set(set);
        return set;
    }
    class(c) {
        // Class not yet supported
        this.object(c);
        return c;
    }
    object(obj) {
        const countOffset = this.offset;
        this.offset += Uint32Array.BYTES_PER_ELEMENT;
        let propertyCount = 0;
        for (const [key, value] of Object.entries(obj)) {
            // alternative zu "as any": funktion umändern zu: object<T extends object>(obj:T):T {
            ++propertyCount;
            this.utf16String(key);
            this.type(value);
        }
        this.view.setUint32(countOffset, propertyCount);
        return obj;
    }
    objectSafe(obj) {
        if (obj === undefined) {
            this.uint32(UINT32_MAX_VALUE);
            return obj;
        }
        return this.object(obj);
    }
    arrayBuffer(buffer) {
        const data = new Uint8Array(buffer);
        this.uint32(data.byteLength);
        for (const value of data.values()) {
            this.view.setUint8(this.offset, value);
            this.offset += Uint8Array.BYTES_PER_ELEMENT;
        }
        return buffer;
    }
    type(value) {
        const typeOffset = this.offset;
        this.offset += Uint8Array.BYTES_PER_ELEMENT;
        let type = ObjectType.Undefined;
        switch (typeof value) {
            case "number":
                type = ObjectType.Number;
                this.float64(value);
                break;
            case "string":
                type = ObjectType.String;
                this.utf16String(value);
                break;
            case "boolean":
                type = ObjectType.Boolean;
                this.boolean(value);
                break;
            case "undefined":
                type = ObjectType.Undefined;
                break;
            case "object":
                if (Array.isArray(value)) {
                    type = ObjectType.Array;
                    this.array(value);
                }
                else if (value instanceof BusMessage) {
                    type = ObjectType.BusMessage;
                    this.busMessage(value);
                }
                else if (value instanceof Map) {
                    type = ObjectType.Map;
                    this.map(value);
                }
                else if (value instanceof Set) {
                    type = ObjectType.Set;
                    this.set(value);
                }
                else if (isArrayOrNodeBuffer(value)) {
                    type = ObjectType.ArrayBuffer;
                    this.arrayBuffer(value);
                }
                else if (value.constructor.name !== "Object") {
                    type = ObjectType.Class;
                    this.class(value);
                }
                else {
                    type = ObjectType.Object;
                    this.object(value);
                }
                break;
            default:
                console.error(`[Common] Type ${typeof value} is not supported`);
                break;
        }
        this.view.setUint8(typeOffset, type);
        return value;
    }
    knownType(descriptor, value) {
        switch (descriptor.type) {
            case PropertyType.Uid:
                return this.uid(value);
            case PropertyType.UidSafe:
                return this.uidSafe(value);
            case PropertyType.Boolean:
                return this.boolean(value);
            case PropertyType.Int8:
                return this.int8(value);
            case PropertyType.Uint8:
                return this.uint8(value);
            case PropertyType.Uint8Safe:
                return this.uint8Safe(value);
            case PropertyType.Int16:
                return this.int16(value);
            case PropertyType.Uint16:
                return this.uint16(value);
            case PropertyType.Uint16Safe:
                return this.uint16Safe(value);
            case PropertyType.Int32:
                return this.int32(value);
            case PropertyType.Uint32:
                return this.uint32(value);
            case PropertyType.Uint32Safe:
                return this.uint32Safe(value);
            case PropertyType.Float32:
                return this.float32(value);
            case PropertyType.Float32Safe:
                return this.float32Safe(value);
            case PropertyType.Float64:
                return this.float64(value);
            case PropertyType.Float64Safe:
                return this.float64Safe(value);
            case PropertyType.Utf16String:
                return this.utf16String(value);
            case PropertyType.Utf16StringSafe:
                return this.utf16StringSafe(value);
            case PropertyType.BusMessage:
                return this.busMessage(value);
            case PropertyType.Array:
                if (!value === undefined)
                    throw new Error(`Value is not an Array`);
                break;
            case PropertyType.ArraySafe: {
                if (value !== undefined && !Array.isArray(value))
                    throw new Error(`Value is not an Array`);
                const arrayLength = this.uint32(value ? value.length : 0);
                if (arrayLength === 0)
                    return value;
                for (let i = 0; i < arrayLength; ++i) {
                    value[i] = this.knownType(descriptor.elementType, value[i]);
                }
                return value;
            }
            case PropertyType.FixedSizeArray: {
                if (!Array.isArray(value))
                    throw new Error(`Value is not an Array`);
                if (value.length !== descriptor.length)
                    throw new Error(`Value.length doesn't match the secivied length of the FixedSizeArray`);
                for (let i = 0; i < descriptor.length; ++i) {
                    value[i] = this.knownType(descriptor.elementType, value[i]);
                }
                return value;
            }
            case PropertyType.Dictionary: {
                if (typeof value !== "object")
                    throw new Error(`Value is not an Object`);
                const keys = Object.keys(value);
                this.uint32(keys.length);
                for (const key of keys) {
                    this.utf16String(key);
                    value[key] = this.knownType(descriptor.elementType, value[key]);
                }
                return value;
            }
            case PropertyType.Struct: {
                if (typeof value !== "object")
                    throw new Error(`Value is not an Object`);
                for (const propertyName in descriptor.properties) {
                    value[propertyName] = this.knownType(descriptor.properties[propertyName], value[propertyName]);
                }
                return value;
            }
        }
    }
}

/**
 * Base class for executors that turn serialized action parameters into runtime behavior.
 */
class ActionExecutor {
    /** Identifies which serialized action type this executor can handle. */
    actionType;
    /**
     * @param type Provides the runtime action type handled by this executor.
     */
    constructor(type) {
        this.actionType = type.typestatic;
    }
}

/**
 * Base class for room layouters that create or update room content from typed parameters.
 */
class Layouter {
    needsBusyIndicator;
    /** Uniquely identifies the concrete layouter implementation. */
    layouterId;
    /** Provides the metadata shown for this layouter in UI and tooling. */
    layouterInfo;
    /**
     * @param type Provides the parameter definition and metadata of the concrete layouter.
     * @param needsBusyIndicator Indicates whether the UI should show a busy indicator while the layouter runs.
     */
    constructor(type, 
    /** Indicates whether the UI should show a busy indicator while the layouter runs. */
    needsBusyIndicator = false) {
        this.needsBusyIndicator = needsBusyIndicator;
        this.layouterId = type.layouterId;
        this.layouterInfo = type.info;
    }
    async updateLayout() {
        await Promise.resolve();
    }
}

/**
 * Base class for all layouter parameter objects exchanged through the layout system.
 */
class LayouterParameters {
    /**
     * Static identifier of the layouter type these parameters belong to.
     */
    layouterId;
    /**
     * Resolves the static layouter id from the subclass constructor.
     */
    constructor() {
        this.layouterId = this.constructor.layouterId;
        if (this.layouterId === undefined)
            throw new Error(`Class '${this.constructor.name}' extends LayouterParameters but dosen't have a static member layouterId`);
    }
}
/**
 * Narrows layouter parameters to a concrete subtype by comparing layouter ids.
 * @param params Runtime parameter object that should be checked.
 * @param type Constructor/type object of the desired layouter parameter subtype.
 */
function isLayouterParameterType(params, type) {
    return params.layouterId === type.layouterId;
}
/**
 * Parameters for the built-in menu layouter.
 */
class MenuLayouterParams extends LayouterParameters {
    elements;
    margin;
    /** Stable identifier of the built-in menu layouter. */
    static layouterId = "menu";
    /** Descriptive metadata shown for the built-in menu layouter. */
    static info = {
        name: "Main Menu",
        requiredRoles: ["flavr-admin"]
    };
    /**
     * @param elements Menu entries that should create or open rooms.
     * @param margin Optional margins used when auto-scaling the generated menu content.
     */
    constructor(
    /**
     * Menu entries that should create or open rooms.
     */
    elements, 
    /**
     * Optional margins used when auto-scaling the generated menu content.
     */
    margin) {
        super();
        this.elements = elements;
        this.margin = margin;
    }
}

/**
 * Helper functions for configuring common interaction-related aspects of a model.
 */
var ModelUtils;
(function (ModelUtils) {
    /**
     * Sets the collision shape of a model.
     * __Creates a new `CollisionFacet` for the model if it doesn't have one already.__
     */
    function setCollisionShape(model, shape) {
        const collisionFacet = model.getOrCreateFacet(CollisionFacet);
        collisionFacet.shape = shape;
    }
    ModelUtils.setCollisionShape = setCollisionShape;
    /**
     * Sets whether the model is selectable.
     * __Creates a new `CollisionFacet` for the model if it doesn't have one already.__
     */
    function setIsSelectable(model, selectable) {
        const collisionFacet = model.getOrCreateFacet(CollisionFacet);
        collisionFacet.isSelectable = selectable;
    }
    ModelUtils.setIsSelectable = setIsSelectable;
    /**
     * Adds an interaction behavior to the model when it is not already present.
     * __Creates a new InteractionFacet for the model if it doesn't have one already.__
     *
     * __You can only interact with a model if it is selectable or__
     * __if another model forwards its interaction to it__
     */
    function addBehavior(model, behavior) {
        const interaction = model.getOrCreateFacet(InteractionFacet);
        if (!interaction.behaviors.find((it) => it.behavior === behavior.behavior)) {
            interaction.behaviors.push(behavior);
        }
    }
    ModelUtils.addBehavior = addBehavior;
    /**
     * Sets the actions that should run when the model is clicked.
     * __Creates a new InteractionFacet for the model if it doesn't have one already.__
     *
     * __You can only interact with a model if it is selectable or if an ohter model frowards its interaction to it__
     */
    function setOnClickActions(model, ...actions) {
        const interaction = model.getOrCreateFacet(InteractionFacet);
        interaction.onClick = actions;
    }
    ModelUtils.setOnClickActions = setOnClickActions;
    /**
     * Sets the actions that should run when the model is double-clicked.
     * __Creates a new InteractionFacet for the model if it doesn't have one already.__
     *
     * __You can only interact with a model if it is selectable or if an ohter model frowards its interaction to it__
     */
    function setOnDoubleClickActions(model, ...actions) {
        const interaction = model.getOrCreateFacet(InteractionFacet);
        interaction.onDoubleClick = actions;
    }
    ModelUtils.setOnDoubleClickActions = setOnDoubleClickActions;
    /**
     * Configures a tooltip by attaching tooltip data and the behavior that displays it.
     * __Creates a new InteractionFacet for the model if it doesn't have one already.__
     *
     * __You can only interact with a model if it is selectable or if another model forwards its interaction to it__
     */
    function setTooltip(model, text, params = {}) {
        model.setFacet(new TooltipInfoFacet(text, params.align, params.avatar, params.placement, params.color, params.borderColor));
        addBehavior(model, new ShowTooltipsBehavior());
    }
    ModelUtils.setTooltip = setTooltip;
    /**
     * Adds a named connection line from one model to another.
     * @param fromModel The model where the connection line starts.
     * @param toModel The model where the connection line ends.
     * @param name Unique name of the connection between the two models.
     * @param params Visual parameters that define how the connection line should look.
     */
    function addConnection(fromModel, toModel, name, params) {
        const connectionLines = fromModel.getOrCreateFacet(ConnectionLines);
        let lines = connectionLines.connections[toModel.id.uid];
        if (lines === undefined) {
            connectionLines.connections[toModel.id.uid] = lines = {};
        }
        lines[name] = params;
    }
    ModelUtils.addConnection = addConnection;
    /**
     * Sets some MetaData for a model.
     * A model can have one MetaDataFacet per MetaData-Type.
     */
    function setMetaData(model, metaData) {
        model.setFacet(new MetaDataFacet(metaData));
    }
    ModelUtils.setMetaData = setMetaData;
    /**
     * Returns metadata of the requested type stored on the model.
     * @param metaDataType The metadata type that should be resolved.
     */
    function getMetaData(model, metaDataType) {
        const facet = model.getFacet(MetaDataFacet, metaDataType.typestatic);
        return (facet !== undefined) ? facet.data : undefined;
    }
    ModelUtils.getMetaData = getMetaData;
    /**
     * Returns read-only metadata of the requested type stored on the model.
     * @param model The model to retrieve the metadata from.
     * @param metaDataType The metadata type that should be resolved.
     */
    function getMetaDataReadonly(model, metaDataType) {
        const facet = model.getFacetReadonly(MetaDataFacet, metaDataType.typestatic);
        return (facet !== undefined) ? facet.data : undefined;
    }
    ModelUtils.getMetaDataReadonly = getMetaDataReadonly;
})(ModelUtils || (ModelUtils = {}));

/**
 * Shared identifiers for modules that participate in message routing and worker setup.
 */
var ModuleId;
(function (ModuleId) {
    /**
     * Avatar loading worker.
     */
    ModuleId["AVATAR_LOADER"] = "avatar_loader";
    /**
     * Controller input module.
     */
    ModuleId["CONTROLLER"] = "controller";
    /**
     * Debug wireframe rendering worker.
     */
    ModuleId["DEBUG_WIREFRAME_RENDERER"] = "debug_wireframe_renderer";
    /**
     * Boolean mesh operation worker.
     */
    ModuleId["BOOLEAN_MESH_OPERATION"] = "boolean_mesh_operation";
    /**
     * Gamepad detection service.
     */
    ModuleId["GAMEPAD_DETECTION_SERVICE"] = "gamepad_detection";
    /**
     * Interaction module.
     */
    ModuleId["INTERACTION"] = "interaction";
    /**
     * Locomotion module.
     */
    ModuleId["LOCOMOTION"] = "locomotion";
    /**
     * Gesture processing module.
     */
    ModuleId["GESTURE"] = "gesture";
    /**
     * Mesh loading worker.
     */
    ModuleId["MESH_LOADER"] = "mesh_loader";
    /**
     * Microphone capture module.
     */
    ModuleId["MICROPHONE"] = "microphone";
    /**
     * Peer networking module.
     */
    ModuleId["PEER"] = "PeerModule";
    /**
     * Collision simulation module.
     */
    ModuleId["COLLISION"] = "collision";
    /**
     * Collision debug visualization module.
     */
    ModuleId["COLLISION_DEBUG"] = "CollisionDebug";
    /**
     * Shared collision service.
     */
    ModuleId["COLLISION_SERVICE"] = "CollisionService";
    /**
     * Main renderer module.
     */
    ModuleId["RENDER"] = "renderer";
    /**
     * Room management module.
     */
    ModuleId["ROOM_MANAGER"] = "room_manager";
    /**
     * Selection handling module.
     */
    ModuleId["SELECTION"] = "selection";
    /**
     * Session management module.
     */
    ModuleId["SESSION"] = "session";
    /**
     * Speech parsing worker.
     */
    ModuleId["SPEECH_PARSER"] = "speech_parser";
    /**
     * State synchronization module.
     */
    ModuleId["STATE_MANAGER"] = "state_manager";
    /**
     * User interface module.
     */
    ModuleId["USER_INTERFACE"] = "user_interface";
    /**
     * Initial view bootstrap module.
     */
    ModuleId["VIEW_INIT"] = "view_init";
    /**
     * Web Speech based speech-to-text module.
     */
    ModuleId["WEBSPEECH_STT"] = "webspeech_stt";
    /**
     * Worklogger data module.
     */
    ModuleId["WORKLOGGER_DATA"] = "worklogger_data";
    /**
     * Server signaling connection module.
     */
    ModuleId["SERVER_CONNECTION"] = "server_connection";
    /**
     * Move data selection module.
     */
    ModuleId["MOVE_DATA_SELECTION"] = "move_data_selection";
    /**
     * Virtual platform info module.
     */
    ModuleId["VIRTUAL_PLATFORM_INFO"] = "virtual_platform_info";
    /**
     * VR toast UI module.
     */
    ModuleId["VRTOAST"] = "vrtoast";
    /**
     * Particle effects module.
     */
    ModuleId["PARTICLES"] = "particles";
    /**
     * Room border visualization module.
     */
    ModuleId["ROOM_BORDER_VIEW"] = "RoomBorderView";
    /**
     * Gizmo interaction module.
     */
    ModuleId["GIZMO"] = "GizmoModule";
    /**
     * Layout worker/module.
     */
    ModuleId["LAYOUT"] = "layout";
    /**
     * Recording module.
     */
    ModuleId["RECORDER"] = "recorder";
    /**
     * Replay playback module.
     */
    ModuleId["REPLAY"] = "replay";
    /**
     * Voice recording module.
     */
    ModuleId["VOICE_RECORDER"] = "voice_recorder";
    /**
     * 3D UI module.
     */
    ModuleId["UI3D"] = "ui3d";
})(ModuleId || (ModuleId = {}));

/**
 * Prefix tags used when sending raw binary buffers over the server transport.
 */
var ServerBufferMessageType;
(function (ServerBufferMessageType) {
    /**
     * Buffer contains one serialized bus message.
     */
    ServerBufferMessageType[ServerBufferMessageType["BusMsg"] = 0] = "BusMsg";
    /**
     * Buffer contains one serialized ask message.
     */
    ServerBufferMessageType[ServerBufferMessageType["AskMsg"] = 1] = "AskMsg";
    /**
     * Buffer contains one serialized answer message.
     */
    ServerBufferMessageType[ServerBufferMessageType["AnswerMsg"] = 2] = "AnswerMsg";
    /**
     * Buffer is a keepalive packet without application payload.
     */
    ServerBufferMessageType[ServerBufferMessageType["Keepalive"] = 3] = "Keepalive";
    /**
     * Buffer requests or announces a room change.
     */
    ServerBufferMessageType[ServerBufferMessageType["ChangeRoom"] = 4] = "ChangeRoom";
})(ServerBufferMessageType || (ServerBufferMessageType = {}));
/**
 * Error codes used when a server-side ask/answer roundtrip fails.
 */
var AskMessageErrorCode;
(function (AskMessageErrorCode) {
    /**
     * Ask roundtrip completed without an error.
     */
    AskMessageErrorCode[AskMessageErrorCode["NoError"] = 0] = "NoError";
    /**
     * Failure reason could not be determined.
     */
    AskMessageErrorCode[AskMessageErrorCode["Unknown"] = 1] = "Unknown";
    /**
     * The target message type does not implement binary serialization.
     */
    AskMessageErrorCode[AskMessageErrorCode["SerializationNotDefined"] = 2] = "SerializationNotDefined";
    /**
     * The ask response contained more than one message.
     */
    AskMessageErrorCode[AskMessageErrorCode["MoreThanOneMessage"] = 3] = "MoreThanOneMessage";
    /**
     * The remote client responded with an explicit error.
     */
    AskMessageErrorCode[AskMessageErrorCode["ClientAnswerWithError"] = 4] = "ClientAnswerWithError";
    /**
     * The system could not allocate or build the transport buffer.
     */
    AskMessageErrorCode[AskMessageErrorCode["CantCreateBuffer"] = 5] = "CantCreateBuffer";
    /**
     * The received buffer did not contain the expected message type.
     */
    AskMessageErrorCode[AskMessageErrorCode["WrongMessageType"] = 6] = "WrongMessageType";
})(AskMessageErrorCode || (AskMessageErrorCode = {}));

/**
 * Severity levels for structured log messages.
 */
var LogLevel;
(function (LogLevel) {
    /** Informational output without a dedicated severity. */
    LogLevel[LogLevel["Log"] = 0] = "Log";
    /** Informational event worth surfacing to users or operators. */
    LogLevel[LogLevel["Info"] = 1] = "Info";
    /** Warning about an unexpected but recoverable situation. */
    LogLevel[LogLevel["Warn"] = 2] = "Warn";
    /** Error describing a failed operation or invalid state. */
    LogLevel[LogLevel["Error"] = 3] = "Error";
})(LogLevel || (LogLevel = {}));

export { ANSWER_MESSAGE_SEPERATOR, AVATAR_ID_PREFIX, AVATAR_RELATIVE_ID_PREFIX, ActionExecutor, ActiveUserInTrainingScenarioFacet, AddAnnotationIntoCurrentRoomRequestMessage, AddAnnotationIntoCurrentRoomResponseMessage, AddLayoutScriptToCurrentRoomMessage, AddMeshLayouterToCurrentRoomMessage, AddToolFromBlueprintMessage, AnimationClipFinishedMessage, AnimationFacet, AnimationStateMessage, Annotations, AnswerMessage, ApplyRecordedLabelTextToModelAction, ArcFacet, AskErrorMessage, AskMessage, AskMessageErrorCode, AskOkMessage, AskRejectMessage, AssetParserContext, AttachToControllerFacet, AttachToFacet, AttachToLocalPlayerFacet, AttachToModelFacet, AudioSourceFacet, AutoScaleChildrenFacet, AvailableLayoutersMessage, AvatarFacet, AvatarStyle, AxisHelperFacet, AxisType, BLOOM_NUMBER_SETTINGS, BLOOM_RESOLUTION_SETTING, BOOLEAN_BYTES_PER_ELEMENT, BillboardFacet, BlueprintFacet, BlueprintParser, BroadcastToPeerMessage, BulkMessage, BusMessage, BusyIndicator, ButtonSignal, ButtonType, ByteStream, ByteStreamReader, ByteStreamWriter, CONTROLLER_ID_PREFIX, CONTROLLER_ROOT_ID, CONTROLLER_ROOT_UID, CameraLayers, CameraRecorderViewFacet, CancelLoadRoomMessage, ChangeBackgroundMessage, ChangeInputDeviceButtonMappingMessage, ChangeModelColorAction, ChangeModelMaterialAction, ChangeModelShapeAction, ChangeRoomSupervisionMessage, ChangeScatterplotDataMappingAction, ChangeWorldMessage, ClearModelStatesMessage, ClearRoomMessage, ClientChangeRoomSupervisionMessage, ClientConnectionStatsMessage, ClientLogsMessage, ClientReloadSessionMessage, ClippingMeshFacet, CloneableFacet, CollectLayoutScriptInfosMessage, CollisionFacet, CollisionMeshShapeType, CollisionShapeLocalTransformFacet, ColorScheme, ColorSpace, ConditionalSendMessagesAction, ConfigureLayoutMetaData, ConfigureScrollGestureMessage, ConnectionLineTypes, ConnectionLines, ConstraintType, ConstraintsAxis, ControlAnimationMessage, ControllerAxisMessage, ControllerButton, ControllerButtonMessage, ControllerFacet, ControllerHand, CreateLabelAction, CreateLabelSpeechCommand, CreateLayoutMessage, CreateModelAction, CreateModelFromBlueprintMessage, CreateModelSpeechCommand, CreateSubModelMessage, CullingMode, CurrentRoomMessage, CurrentSubRoomsRequestMessage, CurrentSubRoomsResponseMessage, CustomControllerFacet, DEFAULT_COLOR_SCHEMES, DEG2RAD, DataPool, DatabaseSchemaMessage, DatabaseSchemesListMessage, DebugMessage, DefaultColorMappings, DefaultColors, DeferredPromise, Delegate, Delegate1, Delegate2, Delegate3, Delegate4, DelegateSubscription, DeleteModelAction, DeleteModelMessage, DeleteModelOnDisconnectFacet, DemoLayouterInteractionMetaData, DestroyLayoutMessage, DestroyOnDisconnectFacet, DetachModelMessage, DisableAllGesturesMessage, DisableGestureMessage, DisablePeerGizmoMessage, DisableRenderingMessage, DisableSelectionHighlight, DropOffAreaFacet, DropOffMode, ENUM_BYTES_PER_ELEMENT, EditorElementMetaData, EditorMetaData, EnableControllerAnnotationMessage, EnableControllerJoystickMessage, EnableDustParticlesMessage, EnableInputDevicesMessage, EnableModelTransformHandlesAction, EnableRenderingMessage, EnableSnapToGridAction, EndLineConnectionAction, EndMoveDataSelectionMessage, EnterRoomMessage, ExitCurrentRoomMessage, FLOAT32_BYTES_PER_ELEMENT, Facet, FacetFactory, FacetTypeTable, FacetVisibility, FadeAnimationMetaData, FibonacciSphereArrangement, FlaviaDefaultColorScheme, FlyToHitpointAction, FontStyle, FontWeight, ForceBasedGraphLayout, ForwardInteraction, FrameStatsMessage, GTAO_NUMBER_SETTINGS, GestureType, GizmoLockFacet, Grabbable, HTMLCanvasFacet, HasSessionSharePermissionMessage, HexagonalSpiralArrangement, HideWhenQualityFacet, HighlightAdditionalModels, HighlightLineStrip, INT32_BYTES_PER_ELEMENT, INT8_MIN_VALUE, INVALID_UID, ImageFacet, ImageToTextureFacet, InitLayoutMessage, InputDeviceType, InputEventTrigger, InputMappingFacet, InteractionFacet, InteractionFacetActivationMessage, LOCAL_PLAYER_CONTROLLERS_ID, LOCAL_PLAYER_CONTROLLERS_UID, LOCAL_PLAYER_ROOT_ID, LOCAL_PLAYER_ROOT_UID, LabelAlign, LabelFacet, LabelSpace, LayerdSphereArrangement, LayoutCompleteMessage, LayoutCreationFailedMessage, LayoutCreationFinishedMessage, LayoutCreationStartedMessage, LayoutCreationState, LayoutCreationStateMessage, LayoutFacet, LayoutUpdatedMessage, Layouter, LayouterAssetFacet, LayouterParameters, LightProbeFacet, LightSource, LightSourceType, LineFacet, LineStrip, LinkFileIdToSessionMessage, LinkToModelFacet, LoadBlueprintMessage, LoadGLTFLayouterMessage, LoadRoomMessage, LocalTransformFacet, LogLevel, LookupTable, MATERIAL_PROPERTIES_BYTE_LENGTH, MESSAGE_SEPERATOR, METADATA_SEPERATOR, MapFacet, MappingTarget, MaterialQuality, Matrix4Pool, MediaScreenFacet, MediaScreenFacetType, MenuLayouterParams, MeshFacet, MeshGroupFacet, MessageFactory, MessageTypeTable, MetaDataBase, MetaDataFacet, MicState, MirrorFacet, ModelChildChangedMessage, ModelChildChangedType, ModelHelper, ModelId, ModelMessage, ModelParentChangedMessage, ModelRoomChangedMessage, ModelUtils, ModuleId, MoveGestureMessage, MovementConstraintFacet, MovementInputMappingFacet, MovingTextureFacet, MovingToFacet, MulticastDelegate, MulticastDelegate1, MulticastDelegate2, MulticastDelegate3, MulticastDelegate4, MutexLatest, NEW_MODELS_ARE_ROOMS_ATTRIBUTE, NUMBER16_BYTES_PER_ELEMENT, NUMBER3_BYTES_PER_ELEMENT, NUMBER4_BYTES_PER_ELEMENT, NavMeshFacet, NetworkFacet, NetworkLocalOnly, NewRoomHostMessage, NotInSessionMessage, ObjectType, OnlyOneInstance, OpenContextMenuAction, OutlineMode, PATH_SEPERATOR, PLAYERS_ROOM_ID, PLAYERS_ROOM_UID, POST_EFFECT_NUMBER_KEYS, POST_EFFECT_NUMBER_SETTINGS, PdfToTextureFacet, PeerConnectionBrowserType, PeersSelectionVisibilityMessage, PendingMessages, PerformActionsMessage, PerformServerActionMessage, PersistentDataFacet, PickupFacet, PlaySoundMessage, PointCloudFacet, PointCloudType, PrimitiveMeshFacet, PrimitiveMeshMaterial, PrimitiveShape, ProjTreeProjectLabelMetaData, ProjTreeTooltipMetaData, ProjectTreeProjectView, PropertyParser, PropertyType, ProximitySphere, QuaternionPool, RAD2DEG, REPLAY_ROOM_ID, REPLAY_ROOM_UID, ROOM_POSTFIX, ROOM_PREFIX, ROOT_MODEL_ID, ROOT_MODEL_UID, RPCScriptMessage, RPCScriptReturnMessage, RateLimiter, RayCurveTeleporterFacet, RaycastLine, ReactCanvasFacet, RecordingIndicator, ReferencePlaneAxis, ReferencePlaneMetaData, ReflectionProbeFacet, ReflectionProbeRefreshMode, RemoveFacetFromModelMessage, RemoveFacetsOnDisconnectFacet, RemoveModelMessage, RemoveSubModelMessage, RemoveToolFromBlueprintMessage, RenderColorMessage, RenderTransparencyMessage, RenderVisibilityMessage, RenderableFacet, RequestAllModelStatesMessage, RequestAnimationStateMessage, RequestAvailableLayoutersMessage, RequestAvatarModelUIDMessage, RequestControllerTransformMessage, RequestCreateRoomFromServerMessage, RequestCurrentRoomMessage, RequestDatabaseSchemaMessage, RequestDatabaseSchemesListMessage, RequestFrameStatsMessage, RequestHasSessionSharePermissionMessage, RequestLayoutCreationStateMessage, RequestModelStateFlag, RequestModelStateFromServerMessage, RequestModelStateMessage, RequestRoomLoadedStateMessage, RequestScriptIdForSessionMessage, RequestScriptIdForUserMessage, RequestSessionDatbaseConnected, RequestSessionListMessage, RequestSessionMemberListMessage, RequestSessionRoomListMessage, RequestSessionShareLinkMessage, RequestToggleButtonStateMessage, RequestToggleButtonStateResponseMessage, RequestTransparencyAnswerMessage, RequestTransparencyMessage, RequestUserAuthenticateTokenMessage, RequestUserConfirmationRequestMessage, RequestVisibilityAnswerMessage, RequestVisibilityMessage, ResetSessionMessage, ResourcePermission, ResponseAvatarModelUIDMessage, ResponseControllerTransformMessage, ResponseCreateModelFromBlueprint, ResponseCreateRoomMessage, ResponseGLTFLayouterMessage, ResponseLayoutScriptInfosMessage, ResponseScriptIdMessage, ResponseSessionDatbaseConnected, ResponseTestDatabaseConnectionsMessage, RoomChangedMessage, RoomEnvironmentFacet, RoomGestureConfigFacet, RoomHelper, RoomInteraction, RoomLoadedState, RoomLoadedStateMessage, RoomSpecificButtonConfigFacet, RoomSpecificGesturesFacet, RoomSpecificSpeechCommandsFacet, RootModelId, RopeFacet, RotatingFacet, SELFCARE_HOST, SNAP_TO_GRID_ENABLED_ATTRIBUTE, SUBJECT_TYPE_SEPERATOR, SaveDataFileMessage, ScatterplotElementMetaData, ScatterplotMappingMetaData, ScatterplotMetaData, ScriptComposeFacet, ScriptFacet, ScriptPropertyType, SelectEventMessage, SelectionHighlightFacet, SendDelayedMessagesAction, SendMessageCondition, SendMessagesAction, SendMessagesAfterConfirmationAction, SendToServerMessage, ServerBufferMessageType, ServerConnectionStatsMessage, ServerRequestErrorMessage, ServerRequestErrorType, ServerSubscribeMessage, SessionListMessage, SessionMemberListMessage, SessionResetPolicy, SessionRoomListMessage, SessionShareLinkMessage, SetColorForNewModels, SetDefaultLineColorAction, SetEnabledGesturesMessage, SetInfoGestureTextMessage, SetNewModelsAreRooms, SetShapeForNewModels, SetSnapToGridParameterAction, SetToolViewerVisibilityMessage, SetTrainingObjectStateMessage, ShadingMode, ShadowMapResolution, ShowTooltipsBehavior, SliderValueMetaData, SnapToGridParm, SquareSpiralArrangement, StartAllCameraRecordingMessage, StartAvatarRecordingMessage, StartCameraRecordingMessage, StartFadeInAnimationAction, StartFadeOutAnimationAction, StartIntervalAction, StartLineConnectionAction, StartMoveDataSelectionMessage, StartPerformanceLoggingMessage, StartPointFacet, StartPointFlags, StartRoomTransitionMessage, StopAllCameraRecordingMessage, StopAvatarRecordingMessage, StopCameraRecordingMessage, StopIntervalAction, StopPerformanceLoggingMessage, SubscribeRoomMessage, SubscribeRoomResponseMessage, SwitchTransformModeAction, TRANSFORM_HANDLES_ENABLED_ATTRIBUTE, TYPE_HASH_BYTES_PER_ELEMENT, TestDatabaseConnectionsMessage, TestDatabaseConnectionsMode, TextAlign, TextAnchorX, TextAnchorY, TextFacet, TextHorizontalAlign, TextToTextureFacet, TextToTextureTextAlign, TextToTextureTextBaseline, TextVerticalAlign, ThrowDownGestureMessage, ToastMessage, ToggleButtonStateType, ToggleReferencePlaneAction, TomlFileFacet, ToolInfoFacet, ToolViewerActivationMessage, ToolViewerViewportChangedMessage, Tooltip, TooltipInfoFacet, TooltipPlacement, TooltipTextAlign, TrainingFinishedMessage, TrainingModelStateFacet, TrainingStageChangedMessage, TrainingVideoFacet, TrainingVideoLoadingStateRequestMessage, TrainingVideoLoadingStateResponseMessage, TrainingVideoPlaybackMessage, TrainingVideoStateChangedMessage, TrainingWearableFacet, TrainingWearableStateChangeFailedMessage, TrainingWearableStateChangedMessage, Transform, TransformWorldModifierInteraction, TruncatedConeFacet, UI3DFacet, UID_BYTES_PER_ELEMENT, UID_SAFE_BYTES_PER_ELEMENT, UINT16_BYTES_PER_ELEMENT, UINT16_MAX_VALUE, UINT32_BYTES_PER_ELEMENT, UINT32_MAX_VALUE, UINT8_BYTES_PER_ELEMENT, UINT8_MAX_VALUE, UNIT_X$1 as UNIT_X, UNIT_Y$1 as UNIT_Y, UNIT_Z$1 as UNIT_Z, UTILITY_ROOM_ID, UTILITY_ROOM_UID, UnloadRoomMessage, UpdateFacetOnModelMessage, UpdateGizmoMessage, UpdateGizmoModeMessage, UpdateGizmoTransformMessage, UpdateReflectionProbesMessage, UpdateSessionResetPolicyMessage, UserAuthenticateTokenMessage, ValueInputSliderFacet, Vector3Pool, VisibilityFacet, WearingPosition, WorkerLocalMessage, asPosixPath, avatarUrl, calcArrayBufferByteLength, calcArrayByteLength, calcBusMessageByteLength, calcBusMessagesByteLength, calcCollisionShapeByteLength, calcFastFloat32ByteLength, calcKnownTypeByteLength, calcLocalizationStringByteLength, calcMapByteLength, calcObjectByteLength, calcSetByteLength, calcStringArrayByteLength, calcStringByteLength, calcTypeByteLength, calculatePositionOnHexagonalSpiral, calculatePositionOnSquareSpiral, canFacetPropertyBeUndefined, ceilPowerOfTwo, changeQueryParamter, changeQueryParamters, checkMatrix4Near, checkQuaternionNear, checkScalarNear, checkVector3Near, clamp, clamp01, clampAngle, collisionShapeSerialization, convertToNumber16, convertToNumber2, convertToNumber3, convertToNumber4, copyToNumber16, copyToNumber2, copyToNumber3, copyToNumber4, cutIntoLines, deepClone, degToRad, deltaAngle, enumToPropertyItems, equalsNumber, extractXRotation, extractYRotation, extractZRotation, facetPropertyTypeOf, floorPowerOfTwo, formatFacet, formatMatrix, formatMessage, formatObject, formatQuaternion, formatVector, generateUUID, getColorBrightness, getDayOfYear, getQueryParameter, getQueryParameterAll, getRegExpForSubscriptionPattern, getRgbBrightness, hashCode, hashMaterialProperties, hrefHasParameter, hslToHexColor, isArrayOrNodeBuffer, isCollisionMeshShape, isCompoundShape, isFacetType, isLayouterParameterType, isLeapYear, isModelIdPartOfScene, isNumber16, isNumber2, isNumber3, isNumber4, isPowerOfTwo, isPromise, isQuaternion, isString, isVector3, lerp, lerpAngle, mapLinear, monthIndexToName, moveTowards, moveTowardsAngle, normalizePostEffectNumber, overridePrivateProperty, pingPong, promiseWithTimeout, radToDeg, randFloat, randInt, removePitchFromMatrix, repeat, serializationMaterialProperties, sign, Simplify as simplify2dLineStrip, smoothstep, timestampToString, unwrapMessage, validFacetPropertyFromObject, worldToLocalPosition, worldToLocalTransform, wrapMessage, wrapMessageWithoutMetaData };
